/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════
// HINDLE CONSULTANTS — Full SaaS Platform  build b260320a
// SMS Alerts · Agent Magic Links · Plan Gating · GitHub Sync
// ═══════════════════════════════════════════════════════════════

// ── Theme ──────────────────────────────────────────────────────
// Tokens stay hex strings (code appends alpha, e.g. `${B}20`). Values are
// swapped in place for light/dark; the app re-renders on change.
const PALETTES={
  light:{
    B:"#2563EB",BL:"#EEF4FF",BM:"#D6E4FF",CS:"#0EA5E9",
    T:{ink:"#0B1220",mid:"#334155",lt:"#5B6475",fnt:"#8B95A5"},
    S:{w:"#FFFFFF",bg:"#F5F6F8",brd:"#E4E7EC",sub:"#F1F3F6",side:"#FBFBFD",hov:"#F3F5F9",shadow:"0 1px 2px rgba(16,24,40,.05),0 1px 3px rgba(16,24,40,.06)",shadowLg:"0 12px 32px rgba(16,24,40,.12)"},
    G:{g:"#16A34A",gb:"#ECFDF3",gbd:"#ABEFC6",gt:"#067647",r:"#DC2626",rb:"#FEF3F2",rbd:"#FECDCA",rt:"#B42318",a:"#D97706",ab:"#FFFAEB",abd:"#FEDF89",at:"#93370D",p:"#7C3AED",pb:"#F4F3FF",pbd:"#D9D6FE",pt:"#5925DC"},
  },
  dark:{
    B:"#5B8CFF",BL:"#162036",BM:"#253559",CS:"#38BDF8",
    T:{ink:"#E7EBF2",mid:"#C2C9D6",lt:"#98A2B3",fnt:"#6B7588"},
    S:{w:"#141922",bg:"#0C1017",brd:"#252C39",sub:"#1A202B",side:"#10141C",hov:"#1C2330",shadow:"0 1px 2px rgba(0,0,0,.4)",shadowLg:"0 12px 32px rgba(0,0,0,.55)"},
    G:{g:"#3FB96C",gb:"#0E2619",gbd:"#1D4A31",gt:"#86E5A8",r:"#F26464",rb:"#2B1416",rbd:"#5B2427",rt:"#FCA5A5",a:"#E8AA3C",ab:"#2A2010",abd:"#5A4417",at:"#FCD48A",p:"#A78BFA",pb:"#1F1936",pbd:"#3D3170",pt:"#C9BBFD"},
  },
};
let B,BL,BM,CS;
const T={},S={},G={};
const THEME_KEY="hindle_theme";
function getThemePref(){
  try{
    const v=localStorage.getItem(THEME_KEY);
    if(v==="light"||v==="dark"||v==="system")return v;
    if(localStorage.getItem("hindle_dark")==="1")return "dark"; // legacy setting
  }catch(e){}
  return "system";
}
function resolveTheme(pref){
  if(pref==="system"){try{return window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";}catch(e){return "light";}}
  return pref;
}
// Status / priority badge colours ({bg, fg}) per theme
const TONES={
  light:{green:["#DCFCE7","#166534"],amber:["#FEF3C7","#92400E"],purple:["#F3E8FF","#6B21A8"],red:["#FEE2E2","#991B1B"],slate:["#F1F5F9","#475569"],orange:["#FFEDD5","#9A3412"],yellow:["#FEF9C3","#854D0E"]},
  dark:{green:["#0F2E1E","#86E5A8"],amber:["#2E2410","#FCD48A"],purple:["#261A3F","#D4BBFF"],red:["#331719","#FCA5A5"],slate:["#1E2532","#B7C1D1"],orange:["#33200F","#FDBA74"],yellow:["#2E2A10","#FDE68A"]},
};
const STATUS_COLORS={},PRIORITY_COLORS={},AR_STATUS_COLORS={};
function fillBadgeMaps(mode){
  const t=k=>({bg:TONES[mode][k][0],fg:TONES[mode][k][1]});
  Object.assign(STATUS_COLORS,{open:t("green"),in_progress:t("amber"),pending:t("purple"),resolved:t("red"),closed:t("slate")});
  Object.assign(PRIORITY_COLORS,{urgent:t("red"),high:t("orange"),medium:t("yellow"),low:t("green")});
  Object.assign(AR_STATUS_COLORS,{pending:t("amber"),approved:t("green"),revoked:t("red"),expired:t("slate"),denied:t("slate")});
}
let THEME_MODE="light";
function applyTheme(pref){
  const mode=resolveTheme(pref||getThemePref());
  const P=PALETTES[mode];
  B=P.B;BL=P.BL;BM=P.BM;CS=P.CS;
  Object.assign(T,P.T);Object.assign(S,P.S);Object.assign(G,P.G);
  THEME_MODE=mode;
  fillBadgeMaps(mode);
  try{document.documentElement.dataset.theme=mode;document.documentElement.style.colorScheme=mode;}catch(e){}
  return mode;
}
applyTheme();
// Components call useTheme() via App; setThemePref broadcasts a change.
const themeListeners=new Set();
function setThemePref(pref){
  try{localStorage.setItem(THEME_KEY,pref);localStorage.removeItem("hindle_dark");}catch(e){}
  applyTheme(pref);
  themeListeners.forEach(fn=>fn(THEME_MODE+":"+pref+":"+Date.now()));
}
try{window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change",()=>{if(getThemePref()==="system")setThemePref("system");});}catch(e){}

const API_URL="https://hindleapi-production.up.railway.app";
const AUTH_TOKEN_KEY="hindle_token";
const getToken=()=>{try{return localStorage.getItem(AUTH_TOKEN_KEY)||"";}catch(e){return "";}};
const setToken=t=>{try{t?localStorage.setItem(AUTH_TOKEN_KEY,t):localStorage.removeItem(AUTH_TOKEN_KEY);}catch(e){}};
(function(){
  if(window.__hindleFetchPatched) return;
  window.__hindleFetchPatched=true;
  const orig=window.fetch.bind(window);
  window.fetch=function(input,init){
    try{
      const url=typeof input==="string"?input:(input&&input.url)||"";
      if(url.indexOf(API_URL)===0){
        const t=localStorage.getItem(AUTH_TOKEN_KEY);
        if(t){
          init=init||{};
          const h=new Headers(init.headers||(typeof input==="object"?input.headers:undefined)||{});
          if(!h.has("Authorization")) h.set("Authorization","Bearer "+t);
          init.headers=h;
        }
      }
    }catch(e){}
    return orig(input,init);
  };
})();

// ── API helper ─────────────────────────────────────────────────
// Keys that survive sign-out (device preferences, not account data)
const KEEP_KEYS=["hindle_theme"];
function clearHindleStorage(){
  try{Object.keys(localStorage).filter(k=>k.startsWith("hindle_")&&!KEEP_KEYS.includes(k)).forEach(k=>localStorage.removeItem(k));}catch(e){}
}
function handleUnauthorized(){
  clearHindleStorage();
  try{sessionStorage.setItem("hindle_signin_reason","Your session ended — please sign in again.");}catch(e){}
  if(typeof window!=="undefined") window.location.href="/";
}
// GET with timeout + retry. A laptop waking from sleep or the database waking
// from idle often fails the first request; retrying avoids blank screens.
const sleep=ms=>new Promise(r=>setTimeout(r,ms));
async function fetchRetry(url,opts={},{tries=3,timeout=20000}={}){
  let lastErr;
  for(let i=0;i<tries;i++){
    const ctrl=new AbortController();
    const t=setTimeout(()=>ctrl.abort(),timeout);
    try{
      const r=await fetch(url,{...opts,signal:ctrl.signal});
      clearTimeout(t);
      if((r.status===502||r.status===503||r.status===504)&&i<tries-1){lastErr=new Error(`${r.status} ${r.statusText}`);}
      else return r;
    }catch(e){clearTimeout(t);lastErr=e.name==="AbortError"?new Error("Request timed out"):e;}
    await sleep(700*Math.pow(2,i));
  }
  throw new Error(lastErr&&/Failed to fetch|NetworkError|Load failed/i.test(lastErr.message)?"Can't reach the server — check your connection.":(lastErr?.message||"Request failed"));
}
const api={
  get:async(path)=>{
    const r=await fetchRetry(`${API_URL}${path}`);
    if(r.status===401){handleUnauthorized();throw new Error("Session expired");}
    if(!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  },
  post:async(path,body)=>{
    const r=await fetch(`${API_URL}${path}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
    if(r.status===401){handleUnauthorized();throw new Error("Session expired");}
    const d=await r.json().catch(()=>({}));
    if(!r.ok) throw new Error(d.error||`${r.status} ${r.statusText}`);
    return d;
  },
  patch:async(path,body)=>{
    const r=await fetch(`${API_URL}${path}`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
    if(r.status===401){handleUnauthorized();throw new Error("Session expired");}
    const d=await r.json().catch(()=>({}));
    if(!r.ok) throw new Error(d.error||`${r.status} ${r.statusText}`);
    return d;
  },
  del:async(path)=>{
    const r=await fetch(`${API_URL}${path}`,{method:"DELETE"});
    if(r.status===401){handleUnauthorized();throw new Error("Session expired");}
    const d=await r.json().catch(()=>({}));
    if(!r.ok) throw new Error(d.error||`${r.status} ${r.statusText}`);
    return d;
  },
};


// ── PLAN FEATURE LISTS ────────────────────────────────────────
// Each item: [label, included true/false]
// Build the 3 limit-derived feature rows for a given plan from live config
// Falls back to PLAN_LIMITS hardcoded values if not configured
function getLimitFeatures(planId, configPlanLimits) {
  const HARD = {
    free:         {agents:1,   conversations_month:100,   kb_docs:0},
    starter:      {agents:5,   conversations_month:500,   kb_docs:5},
    professional: {agents:10,  conversations_month:2000,  kb_docs:999},
    enterprise:   {agents:999, conversations_month:999999,kb_docs:999},
  };
  const lim = (configPlanLimits && configPlanLimits[planId]) || HARD[planId] || HARD.free;
  const fmtN = n => {
    if(n >= 999999) return "Unlimited";
    if(n >= 999)    return planId === "enterprise" ? "Unlimited" : n.toLocaleString();
    return String(n);
  };
  return [
    [`${fmtN(lim.agents)} Agent seat${lim.agents===1?"":"s"}`, true],
    [`${fmtN(lim.conversations_month)} conversations / month`, true],
    [lim.kb_docs === 0 ? "No knowledge base" : lim.kb_docs >= 999 ? "Knowledge base (unlimited)" : `Knowledge base (${lim.kb_docs} docs)`, lim.kb_docs > 0],
  ];
}

// Open Hindle chat widget and pre-fill enterprise sales message
function openSalesChat() {
  // Try to open Hindle widget programmatically
  if (window.HindleChat && typeof window.HindleChat.open === "function") {
    window.HindleChat.open("Hi! I'm interested in the Enterprise plan. Can we discuss pricing and features?");
    return;
  }
  // Try dispatching custom event that widget.js listens for
  try {
    window.dispatchEvent(new CustomEvent("hindle:open", {
      detail: { message: "Hi! I'm interested in the Enterprise plan. Can we discuss pricing and features?" }
    }));
  } catch(_) {}
  // Fallback: click the widget bubble if it exists in the DOM
  const bubble = document.getElementById("hindle-bubble") || document.querySelector("[data-hindle-widget]");
  if (bubble) { bubble.click(); return; }
  // Last fallback: open mailto
  window.location.href = "mailto:sales@hindleconsultants.com?subject=Enterprise%20Plan%20Enquiry";
}

const PLAN_FEATURES={
  free:[
    ["1 Agent seat",true],
    ["100 conversations / month",true],
    ["Basic AI chatbot",true],
    ["Community support",true],
    ["Widget customisation",false],
    ["SMS Agent Alerts",false],
    ["Analytics dashboard",false],
    ["Email support",false],
    ["Webhooks & API access",false],
    ["Custom domain",false],
    ["SLA guarantee",false],
    ["Dedicated account manager",false],
  ],
  starter:[
    ["5 Agent seats",true],
    ["500 conversations / month",true],
    ["Advanced AI models",true],
    ["Widget customisation",true],
    ["SMS Alerts — primary agent",true],
    ["Analytics dashboard",true],
    ["Email support",true],
    ["Knowledge base (5 docs)",true],
    ["Webhooks & API access",false],
    ["Custom domain",false],
    ["SLA guarantee",false],
    ["Dedicated account manager",false],
  ],
  professional:[
    ["10 Agent seats",true],
    ["2,000 conversations / month",true],
    ["Advanced AI models",true],
    ["Widget customisation",true],
    ["SMS Alerts — all agents",true],
    ["Full analytics + exports",true],
    ["Priority email & chat support",true],
    ["Knowledge base (unlimited)",true],
    ["Webhooks & API access",true],
    ["Custom domain",true],
    ["SLA guarantee",false],
    ["Dedicated account manager",false],
  ],
  enterprise:[
    ["Unlimited agent seats",true],
    ["Unlimited conversations",true],
    ["Custom AI fine-tuning",true],
    ["White-label widget",true],
    ["SMS Alerts — all agents",true],
    ["Full analytics + exports",true],
    ["24 / 7 dedicated support",true],
    ["Knowledge base (unlimited)",true],
    ["Webhooks & API access",true],
    ["Custom domain + SSO",true],
    ["99.9% uptime SLA",true],
    ["Dedicated account manager",true],
  ],
};

// ══════════════════════════════════════════════════════════════
// HELP SYSTEM — 55 articles embedded, searchable slide-out panel
// ══════════════════════════════════════════════════════════════
const HELP_ARTICLES=[
  {id:1,title:'Welcome to Hindle – Platform Overview',content:`<div><p>Hindle is a multi-tenant AI-powered live chat platform that lets businesses deploy a customisable chat widget on their website. Visitors can chat with an AI assistant, request a human agent, and receive real-time support.</p><br><p>The platform has two main access levels:</p><br><p><b>Super Admin</b> — the platform owner. Full access to all tenants, billing, integrations, pricing, and platform configuration.</p><br><p><b>Tenant Admin</b> — a business customer. Access to their own chatbot settings, conversations, knowledge base, agents, and analytics.</p><br><p>Agents are staff members created by a tenant admin. They receive SMS alerts when a visitor requests human handoff and can join conversations via a magic link.</p><br><p>The visitor-facing chat widget is a lightweight JavaScript snippet embedded on any website. It loads your tenant configuration automatically on every page visit.</p></div>`,category:'Getting Started - Overview - Introduction'},
  {id:2,title:'Signing In – Super Admin',content:`<div><p>Navigate to <b>chatbot.hindleconsultants.com</b> and enter your super admin email and password.</p><br><p>If 2FA is enabled, you will be taken to a verification screen after your password. A 6-digit code is sent to your email or mobile depending on your 2FA method setting. Enter the code to complete sign-in. Codes expire after 10 minutes.</p><br><p>Additional super admin accounts can be created in <b>Settings → Admin Accounts</b>. Each account has its own email, phone, and password and logs in from the same sign-in screen.</p><br><p><b>Forgot password?</b> Use Reset PW on another admin account in Settings → Admin Accounts, or use the Change Password card in Settings → Security.</p></div>`,category:'Getting Started - Sign In - Super Admin Login'},
  {id:3,title:'Signing In – Tenant Admin',content:`<div><p>Tenant admins sign in at <b>chatbot.hindleconsultants.com</b> using the email and password set when their account was created.</p><br><p>On first login you may be directed to the <b>Setup Wizard</b> to complete your organisation profile, chatbot configuration, and agent setup.</p><br><p>If you cannot sign in, contact your platform administrator to verify your account status or reset your password via the Agents panel.</p><br><p>Agent accounts have limited access — they can view and respond to conversations but cannot change chatbot settings or billing.</p></div>`,category:'Getting Started - Sign In - Tenant Login'},
  {id:4,title:'Signing In – Agent',content:`<div><p>Agents primarily access conversations via a <b>one-click magic link</b> sent by SMS when a visitor requests handoff. Clicking the link opens the conversation directly — no password required. Links are single-use and expire after 30 minutes.</p><br><p>For ongoing dashboard access, the tenant admin can provide login credentials via <b>Settings → Agents</b>. Agents log in from the same sign-in page as tenant admins.</p></div>`,category:'Getting Started - Sign In - Agent Login'},
  {id:5,title:'Super Admin Dashboard – Overview',content:`<div><p>The super admin dashboard shows a platform-wide snapshot immediately after login.</p><br><p><b>KPI cards</b> show: Total Tenants, Active Paid accounts, In Trial, and Expired.</p><br><p><b>Recent Tenants</b> lists the most recently created accounts with plan and status chips.</p><br><p><b>SMS Alert Log</b> shows recent handoff alerts across all tenants with delivery status (pending, sent, clicked, expired).</p></div>`,category:'Super Admin - Dashboard - Overview'},
  {id:6,title:'Managing Tenants – Creating an Account',content:`<div><p>Navigate to <b>Tenants</b> in the super admin sidebar. Click <b>Add Tenant</b> to create a new account.</p><br><p>Fill in the organisation name, email, and select an initial plan. Status options:</p><br><p><b>Trial</b> — 14-day free trial with automatic reminder emails on configured days.</p><br><p><b>Paid</b> — immediately active on the selected plan.</p><br><p><b>Suspended</b> — created but access blocked until manually activated.</p><br><p>Once created, the tenant admin can sign in and complete setup via the Setup Wizard.</p></div>`,category:'Super Admin - Tenants - Creating Accounts'},
  {id:7,title:'Managing Tenants – Info Tab',content:`<div><p>Click any tenant in the Tenants list to open their detail panel. The <b>Info</b> tab shows all stored organisation and billing details.</p><br><p><b>Organisation section</b> — name, support email, phone, website, industry, timezone, and full address as entered by the tenant admin in their Organisation settings.</p><br><p><b>Billing &amp; Contact section</b> — billing name, company registration number, invoice email, and full international billing address.</p><br><p><b>Account Details section</b> — current plan, status, trial day (if applicable), account creation date, and the tenant's unique ID.</p><br><p>This tab is read-only from the super admin perspective. Tenants update these details from their own Settings pages.</p></div>`,category:'Super Admin - Tenants - Info Tab'},
  {id:8,title:'Managing Tenants – Editing and Suspending',content:`<div><p>From the <b>Manage</b> tab in a tenant's detail panel you can:</p><br><p><b>Change plan</b> — switch between Free, Starter, Professional, and Enterprise.</p><br><p><b>Change status</b> — activate, suspend, or expire an account.</p><br><p><b>Custom limits</b> — override standard plan limits for a specific tenant without upgrading their plan.</p><br><p><b>Account notes</b> — internal notes visible only to super admins.</p><br><p><b>Delete</b> — permanently removes the tenant and all associated data. This cannot be undone.</p></div>`,category:'Super Admin - Tenants - Editing Accounts'},
  {id:9,title:'Trial Monitor – How It Works',content:`<div><p>The <b>Trial Monitor</b> (super admin sidebar → Trials) shows all tenants currently in their free trial.</p><br><p>Trials run for 14 days. The monitor shows each tenant's trial day, days remaining, and reminder status.</p><br><p><b>Automatic reminders</b> are sent via ClickSend email on the days configured in Settings → Platform (default: days 12, 13, 14).</p><br><p>On day 15 the system automatically sets the tenant status to <b>expired</b>. The tenant sees a red banner prompting them to upgrade.</p><br><p>You can manually extend a trial or convert it to paid at any time from the Manage tab.</p></div>`,category:'Super Admin - Trial Monitor - How Trials Work'},
  {id:10,title:'Pricing & Plans – Managing Plans',content:`<div><p>Navigate to <b>Pricing</b> in the super admin sidebar to manage the plan structure.</p><br><p>Four plans are available: <b>Free, Starter, Professional,</b> and <b>Enterprise</b>. Each has configurable USD monthly pricing and an annual discount percentage.</p><br><p><b>Plan limits</b> — set maximum agents, monthly conversations, and KB documents per plan. These are enforced server-side.</p><br><p><b>Plan features</b> — an editable feature matrix controls which features appear as included or excluded on the public pricing page.</p><br><p>All changes are saved to the database and reflected immediately on the public landing page.</p></div>`,category:'Super Admin - Pricing - Managing Plans'},
  {id:11,title:'Pricing & Plans – Promo Codes',content:`<div><p>Promo codes give prospective tenants a percentage discount at checkout. Manage them from <b>Pricing → Promo Codes</b> tab.</p><br><p>Each code has: code string (stored uppercase), discount %, applicable plans, usage limit, expiry date, and an internal description.</p><br><p>When used at checkout, the usage counter increments and a Stripe coupon is created automatically. The code is then invalid once the usage limit is reached or the expiry date passes.</p></div>`,category:'Super Admin - Pricing - Promo Codes'},
  {id:12,title:'Integrations – ClickSend Setup',content:`<div><p>Hindle uses <b>ClickSend</b> for SMS handoff alerts and transactional email. ClickSend credentials are set by the super admin and apply to all tenants on the platform.</p><br><p>1. Log in to ClickSend, copy your <b>username</b> (login email) and <b>API key</b>.</p><br><p>2. In Hindle: Super Admin → Integrations → SMS &amp; Email. Enter credentials and click Save.</p><br><p>3. Set your <b>SMS Sender Name</b> (max 11 chars, no spaces).</p><br><p>4. Set <b>Email From Name</b> and <b>From Email Address</b>. The from address must be a verified sender domain in ClickSend.</p><br><p>5. Use <b>Test SMS</b> and <b>Test Email</b> buttons to confirm delivery before going live.</p><br><p><b>Note:</b> Tenants can customise their sender name and from address, but cannot change the global API credentials.</p></div>`,category:'Super Admin - Integrations - ClickSend Setup'},
  {id:13,title:'Integrations – Testing SMS and Email',content:`<div><p><b>Test SMS</b> — enter a mobile in international format and click Send Test Alert. A real SMS is sent with a sample handoff message and magic link. Click the link to test the full agent flow.</p><br><p><b>Test Email</b> — enter any email address and click Send Test Email. A 2FA-style code email is dispatched. Check Railway logs for <code>[2FA] ClickSend result:</code> if it does not arrive.</p><br><p><b>Common issues:</b></p><br><p><b>401 Unauthorized</b> — API credentials incorrect.</p><br><p><b>From address rejected</b> — domain not verified in ClickSend → Email → Senders.</p><br><p><b>SMS not delivered</b> — sender name has spaces or exceeds 11 characters.</p></div>`,category:'Super Admin - Integrations - Testing Delivery'},
  {id:14,title:'Payments – Stripe Integration',content:`<div><p>Hindle uses <b>Stripe</b> for payment processing. Your secret key is stored in Railway as <code>STRIPE_SECRET_KEY</code> and never exposed to the frontend.</p><br><p><b>Checkout flow</b> — tenant clicks Upgrade, is redirected to hosted Stripe Checkout. On successful payment, Stripe fires a webhook that activates the tenant's plan in the database.</p><br><p><b>Webhook setup</b> — in Stripe Dashboard → Developers → Webhooks, add endpoint <code>https://hindleapi-production.up.railway.app/api/stripe/webhook</code> and listen for <code>checkout.session.completed</code>. Copy the signing secret to Railway as <code>STRIPE_WEBHOOK_SECRET</code>.</p><br><p><b>Test mode</b> — use a <code>sk_test_...</code> key and test card <code>4242 4242 4242 4242</code> to verify the full flow without real charges.</p></div>`,category:'Super Admin - Payments - Stripe Integration'},
  {id:15,title:'Super Admin Settings – Admin Accounts',content:`<div><p>Super Admin → Settings → <b>Admin Accounts</b> manages additional super admin users.</p><br><p>Click <b>Add Admin</b> to create an account. Fields: full name, phone number, email, password (min 6 characters).</p><br><p>Each admin has their own password stored securely in the database. The phone number is used for SMS 2FA if that method is selected.</p><br><p><b>Reset PW</b> — sets a new password for any account via a prompt.</p><br><p><b>Remove</b> — permanently deletes the account. The primary <code>admin@hindleconsultants.com</code> account is managed via Settings → Security.</p></div>`,category:'Super Admin - Settings - Admin Accounts'},
  {id:16,title:'Super Admin Settings – Two-Factor Authentication',content:`<div><p>Super Admin → Settings → <b>Security</b> controls 2FA for super admin logins.</p><br><p><b>Enable/Disable</b> — toggle 2FA on or off. When enabled, every login requires a verification code after the password.</p><br><p><b>Delivery Method</b> — Email (code sent to login address) or SMS (code sent to the phone number in your Profile). Both require ClickSend to be configured.</p><br><p>Codes expire in 10 minutes. After 5 incorrect attempts the code is invalidated and you must request a new one using the Resend link.</p><br><p>Note: 2FA is currently disabled while ClickSend email is being configured. Enable it from Settings → Security once delivery is confirmed working via the Test Email button.</p></div>`,category:'Super Admin - Settings - Two-Factor Authentication'},
  {id:17,title:'Super Admin Settings – Platform Configuration',content:`<div><p>Super Admin → Settings → <b>Platform</b> controls global settings.</p><br><p><b>Platform Name</b> — shown in emails and the dashboard header.</p><br><p><b>Primary Domain</b> — your main domain.</p><br><p><b>Support Email</b> — contact address shown to tenants in error messages.</p><br><p><b>Email From Name</b> — sender display name in all outbound platform emails.</p><br><p><b>Trial Length (days)</b> — length of the free trial period. Default 14.</p><br><p><b>Reminder Days</b> — comma-separated list of trial days for reminder emails (e.g. 12,13,14).</p></div>`,category:'Super Admin - Settings - Platform Config'},
  {id:18,title:'Super Admin Settings – GitHub Sync',content:`<div><p>Super Admin → Settings → <b>GitHub Sync</b> pushes the latest frontend build from the dashboard directly to your GitHub repository, triggering a Vercel deployment.</p><br><p>Required: Personal Access Token (PAT) from GitHub → Settings → Developer Settings → Fine-grained tokens. Grant read/write on Contents for the target repo.</p><br><p>Set Owner, Repository, Branch, and File Path (e.g. <code>jsx-viewer/src/components/chatbot_1.jsx</code>). Upload the new file and click Push to GitHub.</p></div>`,category:'Super Admin - Settings - GitHub Sync'},
  {id:19,title:'Alert Log – Monitoring Handoff Alerts',content:`<div><p>Super Admin → <b>Alert Log</b> shows every SMS handoff alert sent across all tenants.</p><br><p>Each entry shows: time, visitor name, page URL, agent alerted, mobile number, token (one-time magic link), and status (pending / clicked / expired).</p><br><p>Use this log to diagnose missed handoffs, verify SMS delivery, and monitor agent response times.</p></div>`,category:'Super Admin - Alert Log - Monitoring Alerts'},
  {id:20,title:'Tenant Dashboard – Overview',content:`<div><p>The tenant dashboard shows a snapshot of your chatbot activity: open conversations, resolved today, active agents, unread messages.</p><br><p><b>Recent conversations</b> lists the latest chat sessions with visitor details, status, and last activity time.</p><br><p><b>Onboarding checklist</b> — for new accounts, guides you through essential setup steps. Each item is clickable and navigates directly to the relevant page. Click <b>Re-run Setup</b> to restart the wizard at any time.</p><br><p><b>Trial banner</b> — shows days remaining with an Upgrade button when on a free trial. A red expired banner appears when the trial ends.</p></div>`,category:'Tenant Admin - Dashboard - Overview'},
  {id:21,title:'Conversations – Managing Live Chats',content:`<div><p>Tenant Admin → <b>Conversations</b> shows all chat sessions. Filter by All, Open, Claimed, Resolved, or Handoff. Switching filters re-fetches from the database to show full history.</p><br><p>Click any conversation to open the detail panel. The message thread shows visitor messages, AI responses, agent messages, and internal notes in chronological order, anchored to the most recent message.</p><br><p><b>Claim</b> — assign the conversation to yourself. Other agents can see it is taken.</p><br><p><b>Resolve</b> — marks the conversation finished. The visitor sees a CSAT rating prompt.</p><br><p><b>Park</b> — temporarily suspends the conversation without resolving it.</p><br><p><b>Transfer</b> — reassigns to another agent via the Transfer dropdown in the compose bar.</p></div>`,category:'Tenant Admin - Conversations - Managing Chats'},
  {id:22,title:'Conversations – Compose Bar Features',content:`<div><p>The compose bar at the bottom of the conversation panel has several tools:</p><br><p><b>💬 Reply / 🔒 Note toggle</b> — switch between sending a visible agent reply and an internal note. Internal notes appear in amber and are never shown to the visitor.</p><br><p><b>Priority picker</b> — set Urgent, High, Normal, or Low. Urgent and High show coloured badges in the conversation list.</p><br><p><b>Transfer</b> — drop-down to transfer the conversation to another agent immediately.</p><br><p><b>🔔 Sound toggle</b> — enable or disable the audio ping when new visitor messages arrive.</p><br><p><b>Paperclip</b> — attach and send a file or image to the visitor.</p><br><p>Press Enter to send, Shift+Enter for a new line.</p></div>`,category:'Tenant Admin - Conversations - Compose Bar'},
  {id:23,title:'Conversations – Sound & Browser Notifications',content:`<div><p>When a new visitor or bot message arrives in an open conversation, Hindle plays a soft audio ping using the Web Audio API.</p><br><p>If your browser tab is in the background, a browser desktop notification appears with the visitor's name. You will be prompted to allow notifications on your first message — click Allow to enable this.</p><br><p>Use the 🔔 button in the compose bar to mute or unmute the sound alert at any time. The setting applies for your current browser session.</p></div>`,category:'Tenant Admin - Conversations - Notifications'},
  {id:24,title:'Conversations – Internal Notes',content:`<div><p>Internal notes are private messages visible only to agents — never shown to the visitor in the widget.</p><br><p>To add a note, click the <b>💬 Reply</b> toggle in the compose bar to switch it to <b>🔒 Note</b>. Type your note and press Enter or the send button.</p><br><p>Notes appear in the message thread with an amber dashed border and the label <b>Internal Note</b> along with the agent's name and timestamp.</p><br><p>Use notes to hand off context between agents, record investigation steps, or flag issues for review.</p></div>`,category:'Tenant Admin - Conversations - Internal Notes'},
  {id:25,title:'Conversations – Priority',content:`<div><p>Conversations can be assigned a priority level to help agents triage their queue.</p><br><p>Select a priority from the dropdown in the compose toolbar: <b>🔴 Urgent</b>, <b>🟠 High</b>, <b>🟡 Normal</b>, or <b>🔵 Low</b>.</p><br><p>Urgent and High priority conversations show coloured badges in the conversation list so they stand out immediately.</p><br><p>Priority is saved to the database and persists across sessions.</p></div>`,category:'Tenant Admin - Conversations - Priority'},
  {id:26,title:'Conversations – File Attachments',content:`<div><p>Both agents and visitors can send file attachments in conversations.</p><br><p><b>Agent side</b> — click the paperclip icon in the compose bar. Select a file from your device. It is read as a base64 data URL and sent immediately. The file name appears as a clickable link in the message thread.</p><br><p><b>Visitor side</b> — the chat widget shows a paperclip button in the input row. Visitors can attach images, PDFs, Word docs, and text files. Their attachment appears in the agent's message thread as a downloadable link.</p><br><p>Accepted types: images, PDF, DOC, DOCX, TXT.</p></div>`,category:'Tenant Admin - Conversations - File Attachments'},
  {id:27,title:'Conversations – Transfer Between Agents',content:`<div><p>To transfer a conversation to another agent, click <b>Transfer</b> in the compose toolbar. A dropdown lists all active agents on your account.</p><br><p>Selecting an agent immediately updates the conversation's claimed status to the new agent and adds a system message in the thread: <em>Transferred to [Name]</em>.</p><br><p>The new agent will see the conversation in their Claimed filter. If SMS alerts are enabled, they will receive a notification the next time a visitor sends a message.</p></div>`,category:'Tenant Admin - Conversations - Transfer'},
  {id:28,title:'Conversations – CSAT Ratings',content:`<div><p>When a conversation is resolved, the visitor's widget displays a thumbs up / thumbs down prompt.</p><br><p>The rating is recorded against the conversation and included in Analytics. Individual ratings are visible in the conversation detail panel.</p><br><p>CSAT data appears in Analytics as an overall score (% positive) over the selected time period.</p></div>`,category:'Tenant Admin - Conversations - CSAT Ratings'},
  {id:29,title:'Analytics – Understanding Your Data',content:`<div><p>Tenant Admin → <b>Analytics</b> shows performance data for your chatbot and agents.</p><br><p><b>Time range</b> — select 7, 14, or 30 days.</p><br><p><b>Summary cards</b> — Total Conversations, Handoffs to Agents, CSAT Score (%), Average First Response Time.</p><br><p><b>Volume chart</b> — daily conversation volume split by AI-handled and agent-handled.</p><br><p><b>Agent performance</b> — agents ranked by conversations handled, average response time, and CSAT scores.</p><br><p><b>First Response Time (FRT)</b> — measured from visitor's first message to agent's first reply. AI responses are not counted.</p></div>`,category:'Tenant Admin - Analytics - Understanding Data'},
  {id:30,title:'Knowledge Base – Adding Documents',content:`<div><p>Tenant Admin → <b>Chatbot → KB tab</b> lets you add content the AI uses to answer visitor questions.</p><br><p><b>Manual Add</b> — click Manual Add, enter a title and content (question and answer). Click Add Article. The article is indexed immediately.</p><br><p><b>Bulk CSV</b> — click Bulk CSV and enter rows in the format <code>title,content</code> (one per line). Multiple articles are added at once.</p><br><p><b>Upload Doc</b> — upload PDFs, Word docs, plain text, JSON, XML, or CSV files. The system indexes the content automatically.</p><br><p><b>Import URL</b> — paste a public webpage URL. The system fetches and saves the page content as a KB article.</p><br><p>Plan limits apply: Free (0 docs), Starter (5), Professional (unlimited).</p></div>`,category:'Tenant Admin - Knowledge Base - Adding Documents'},
  {id:31,title:'Chatbot Settings – AI Configuration',content:`<div><p>Tenant Admin → <b>Chatbot → Config tab</b> controls AI behaviour.</p><br><p><b>Greeting message</b> — the first message visitors see when they open the widget.</p><br><p><b>Fallback message</b> — sent when the AI cannot answer confidently.</p><br><p><b>Tone</b> — Friendly, Professional, or Formal. Included in the AI system prompt.</p><br><p><b>System prompt</b> — advanced field for detailed AI instructions. If blank, a default prompt using your greeting and tone is used.</p><br><p><b>Additional Bot Instructions</b> — in the Personality &amp; Language card. Appended to every conversation's system prompt. Use for specific rules, topics to avoid, or references to always include.</p><br><p><b>Trigger phrases</b> — comma-separated phrases that force the human handoff bar to appear.</p><br><p><b>Confidence threshold</b> — AI rates its own confidence 0–100%. Below this value, the handoff bar appears with a suggestion to speak to an agent.</p><br><p><b>AI silence after handoff</b> — how long the AI stays silent after a human takes over. Configurable in 30-second increments up to 10 minutes.</p></div>`,category:'Tenant Admin - Chatbot Settings - AI Configuration'},
  {id:32,title:'Chatbot Settings – Widget Appearance',content:`<div><p>Tenant Admin → <b>Chatbot → Branding tab</b> controls the visual appearance of the chat widget.</p><br><p><b>Primary colour</b> — used for the bubble, header, and sent message bubbles.</p><br><p><b>Widget name</b> — shown in the chat header.</p><br><p><b>Bubble position</b> — bottom-right (default) or bottom-left.</p><br><p><b>Bubble size</b> — XS, Small, Medium, Large, XL.</p><br><p><b>Chat window size</b> — controls width and height of the open panel.</p><br><p>A live preview updates in real-time as you make changes. Changes apply to the live widget immediately after saving — no embed code changes required.</p></div>`,category:'Tenant Admin - Chatbot Settings - Widget Appearance'},
  {id:33,title:'Chatbot Settings – Pre-Chat Form',content:`<div><p>The pre-chat form is shown to visitors before the chat begins to collect contact details.</p><br><p>Enable it in Tenant Admin → Chatbot → Pre-Chat tab.</p><br><p><b>Available fields</b> — Name, Email, Phone, Company, Message. Each can be toggled on/off and marked required or optional.</p><br><p>When a visitor submits the form, their details are saved to the conversation record and appear in the Visitor tab of the right panel.</p><br><p>If the visitor also requests human handoff, their form details are included in the SMS alert to agents.</p><br><p>The form also triggers when the visitor clicks Speak to a Human — if the form is enabled and not yet submitted, it is shown as a Contact screen before the handoff fires.</p></div>`,category:'Tenant Admin - Chatbot Settings - Pre-Chat Form'},
  {id:34,title:'Chatbot Settings – Collect Visitor Email',content:`<div><p>The <b>Collect visitor email before chatting</b> checkbox is in Chatbot → Behaviour tab.</p><br><p>When ticked, an inline email input appears below the greeting message in the widget. The visitor can enter their email or click Skip.</p><br><p>When unticked, the email input does not appear and the widget goes straight to chat. If the config is loaded after the widget first opens, the input is removed immediately.</p><br><p>The email captured here is stored against the conversation and used for handoff notifications and follow-ups.</p></div>`,category:'Tenant Admin - Chatbot Settings - Collect Email'},
  {id:35,title:'Agents – Creating and Managing Agents',content:`<div><p>Tenant Admin → <b>Agents</b> manages staff members who respond to live chat handoffs.</p><br><p>Click <b>Add Agent</b>. Required fields: name, email, mobile number (international format), role.</p><br><p><b>Roles</b> — Agent (conversation access only) or Admin (full dashboard access including settings).</p><br><p><b>SMS Alerts</b> — toggle whether this agent receives SMS when handoff is requested.</p><br><p><b>Deactivate</b> — suspends access without deleting. Inactive agents do not receive SMS alerts.</p><br><p><b>Reset PW</b> — sets a new password via a prompt.</p></div>`,category:'Tenant Admin - Agents - Managing Agents'},
  {id:36,title:'Install & Embed – Adding the Widget',content:`<div><p>Tenant Admin → <b>Install</b> provides the embed snippet. Paste the script tag before the closing <code>&lt;/body&gt;</code> tag on your website.</p><br><p>Key data attributes:</p><br><p><code>data-tenant</code> — your unique tenant ID. Required.</p><br><p><code>data-color</code> — override bubble colour.</p><br><p><code>data-position</code> — <code>bottom-right</code> or <code>bottom-left</code>.</p><br><p><code>data-label</code> — chat header text.</p><br><p><code>data-hide-mobile</code> — set to <code>true</code> to hide on mobile.</p><br><p>The widget loads your full config from the server on every page visit. No embed code changes needed when you update settings.</p></div>`,category:'Tenant Admin - Install - Embedding the Widget'},
  {id:37,title:'Install & Embed – Platform Instructions',content:`<div><p><b>WordPress</b> — Appearance → Theme Editor → footer.php, or use Insert Headers and Footers plugin.</p><br><p><b>Shopify</b> — Online Store → Themes → Edit Code → theme.liquid before <code>&lt;/body&gt;</code>.</p><br><p><b>Squarespace</b> — Settings → Advanced → Code Injection → Footer.</p><br><p><b>Wix</b> — Settings → Custom Code → Add Code → All Pages → Body End.</p><br><p><b>Webflow</b> — Project Settings → Custom Code → Footer Code.</p><br><p><b>React / Next.js</b> — use <code>next/script</code> or a <code>useEffect</code> client-side.</p><br><p>After adding the snippet, use the <b>Verify Installation</b> button on the Install page to confirm the widget is detected.</p></div>`,category:'Tenant Admin - Install - Platform Instructions'},
  {id:38,title:'Tenant Settings – Organisation',content:`<div><p>Tenant Admin → Settings → <b>Organisation</b> stores your business details.</p><br><p>Fields include: Organisation Name, Phone, Website, Support Email, Address, City, State/Province, Zip/Postcode, Country, Timezone.</p><br><p>These details appear in the super admin Info tab, automated email templates, and can be referenced in your chatbot system prompt.</p></div>`,category:'Tenant Admin - Settings - Organisation'},
  {id:39,title:'Tenant Settings – Billing',content:`<div><p>Tenant Admin → Settings → <b>Billing</b> shows your current subscription and manages billing details.</p><br><p><b>Current Subscription</b> — shows your plan, billing cycle, next invoice date, amount due, and payment method.</p><br><p><b>Upgrade Plan</b> — navigates to the purchase flow to upgrade or change your plan.</p><br><p><b>Update Card</b> — contact support to update your payment method. Your account email is shown for reference.</p><br><p><b>Billing Details</b> — full international address form with country selector, state/region dropdown (for AU, NZ, US, GB), and Zip/Postcode field. Used for invoice generation.</p></div>`,category:'Tenant Admin - Settings - Billing'},
  {id:40,title:'FAQ Manager – Adding Visitor FAQs',content:`<div><p>Tenant Admin → <b>FAQ Manager</b> creates frequently asked questions surfaced as quick-reply suggestions in the widget.</p><br><p>Click <b>Add FAQ</b>, enter a question and answer, assign a category, and save.</p><br><p>FAQs are also used by the AI as supplementary context — if a visitor asks something close to a FAQ question, the AI uses the stored answer.</p><br><p>Toggle individual FAQs active or inactive without deleting them. FAQs apply to the live widget immediately after saving.</p></div>`,category:'Tenant Admin - FAQ Manager - Adding FAQs'},
  {id:41,title:'Human Handoff – How It Works',content:`<div><p>1. Visitor clicks <b>Speak to a Human</b>, types a trigger phrase, or the AI confidence drops below your threshold.</p><br><p>2. If a pre-chat form is configured and not yet submitted, it is shown first.</p><br><p>3. The widget fires a handoff request to the server. SMS alerts are sent to all active agents.</p><br><p>4. The SMS contains a one-click magic link. Clicking opens the dashboard on that conversation directly.</p><br><p>5. The first agent to claim the conversation is shown as the assigned agent.</p><br><p>6. The visitor's widget polls for agent messages and shows them in green bubbles. An agent typing indicator appears when the agent is composing.</p><br><p>7. When the agent resolves the conversation, a CSAT prompt appears in the widget.</p></div>`,category:'Tenant Admin - Handoff - How Handoff Works'},
  {id:42,title:'Human Handoff – Magic Links',content:`<div><p>Magic links allow agents to access a specific conversation without a password. One click from the SMS goes directly to the live chat.</p><br><p><b>Security</b> — each link contains a cryptographically random token, valid for 30 minutes, invalidated immediately after first use.</p><br><p>Magic link status (pending / clicked / expired) is tracked in the Alert Log.</p></div>`,category:'Tenant Admin - Handoff - Magic Links'},
  {id:43,title:'AI Confidence and Escalation',content:`<div><p>Hindle's AI rates its own confidence after every response (0–100%). Below the configured threshold (default 60%), the human handoff bar appears with the message <em>Not sure? Our team can help directly.</em></p><br><p>The confidence rating is never shown to the visitor — it is used purely to trigger the escalation UI.</p><br><p>After a human handoff, the AI stays silent for the <b>AI silence period</b> configured in Chatbot → Behaviour. If the agent does not respond within that time, the AI resumes. The slider goes from 30 seconds to 10 minutes in 30-second increments.</p></div>`,category:'Tenant Admin - AI Behaviour - Confidence and Escalation'},
  {id:44,title:'Conversation Memory – How the AI Remembers Context',content:`<div><p>The AI maintains conversation memory across messages within a session so visitors do not have to repeat themselves.</p><br><p><b>Within a session</b> — the widget stores the last 20 messages in sessionStorage. Each new message includes the last 14 turns as context.</p><br><p><b>Across page refreshes</b> — sessionStorage persists across refreshes within the same tab.</p><br><p><b>New browser session</b> — closing and reopening the browser clears session history and starts a new conversation.</p></div>`,category:'Tenant Admin - AI Behaviour - Conversation Memory'},
  {id:45,title:'Widget – Visitor Experience Overview',content:`<div><p>The Hindle widget provides a seamless chat experience.</p><br><p><b>Chat bubble</b> — floating button in the corner. After 5 seconds a red unread badge appears. If auto-open is configured, the window opens automatically after the set delay.</p><br><p><b>Opening the chat</b> — if a pre-chat form is configured, it shows first. Otherwise the greeting message appears and the AI is ready.</p><br><p><b>Email capture</b> — if collect email is enabled, an inline email input appears below the greeting with an OK and Skip button.</p><br><p><b>Agent messages</b> — once an agent joins, their messages appear in green bubbles. An agent typing indicator shows when the agent is composing.</p><br><p><b>Visitor file upload</b> — visitors can attach files using the paperclip in the input bar.</p><br><p><b>Rating</b> — when the conversation is resolved, a CSAT thumbs up/down prompt appears briefly.</p></div>`,category:'Widget - Visitor Experience - Overview'},
  {id:46,title:'Widget – Offline Message Form',content:`<div><p>When no agents are available, the widget can show an offline message form instead of the standard chat interface.</p><br><p>Visitors fill in their name, email, and message. The submission is stored as a conversation in the database and flagged as an offline message.</p><br><p>An email notification is sent to the tenant's notification email address via ClickSend when an offline message is received.</p></div>`,category:'Widget - Offline Messages - How It Works'},
  {id:47,title:'Purchasing a Plan – How to Upgrade',content:`<div><p>Click <b>Upgrade</b> in the trial banner or navigate to Settings → Billing → Upgrade Plan.</p><br><p><b>Purchase wizard</b> — 3 steps: select plan → enter details → pay via Stripe.</p><br><p>Step 1: Choose Starter or Professional, and monthly or annual billing. Annual plans include a discount set by the platform administrator.</p><br><p>Step 2: Enter company name, full name, email, and password.</p><br><p>Step 3: Click Pay via Stripe. You are redirected to a secure Stripe Checkout page. Hindle never handles card data directly.</p><br><p>After payment you are redirected back with a confirmation banner. Your plan activates immediately.</p><br><p>If you have a promo code, enter it on Step 1 before proceeding.</p></div>`,category:'Billing - Purchasing - How to Upgrade'},
  {id:48,title:'Billing – Plan Limits and Enforcement',content:`<div><p>Each plan has limits on agents, monthly conversations, and KB documents. These are enforced server-side.</p><br><p><b>Conversations</b> — when the limit is reached, the AI responds with the fallback message and the widget suggests contacting the business directly.</p><br><p><b>Agents</b> — creating more agents than your plan allows is blocked with an error message.</p><br><p><b>KB documents</b> — adding more documents than your plan allows is blocked.</p><br><p>To increase limits, upgrade your plan or contact your platform administrator for a custom limit override.</p></div>`,category:'Billing - Plans - Limits and Enforcement'},
  {id:49,title:'Troubleshooting – Widget Not Appearing',content:`<div><p><b>Script placement</b> — ensure the script is before <code>&lt;/body&gt;</code>.</p><br><p><b>Tenant ID</b> — verify <code>data-tenant</code> matches your organisation ID exactly (from the Install page).</p><br><p><b>Console errors</b> — open browser dev tools (F12) → Console. Look for <code>[HindleWidget]</code> messages.</p><br><p><b>Content Security Policy</b> — whitelist <code>hindleapi-production.up.railway.app</code> if your site has a strict CSP.</p><br><p><b>Ad blockers</b> — test in incognito without extensions.</p><br><p><b>Mobile hiding</b> — if <code>data-hide-mobile=&quot;true&quot;</code> is set, the widget is intentionally hidden on small screens.</p></div>`,category:'Troubleshooting - Widget - Not Appearing'},
  {id:50,title:'Troubleshooting – AI Not Responding',content:`<div><p><b>API key</b> — confirm <code>ANTHROPIC_API_KEY</code> is set in Railway environment variables.</p><br><p><b>Rate limiting</b> — each tenant is limited to 30 AI requests per minute. Wait 60 seconds and retry.</p><br><p><b>Handoff active</b> — if a human handoff is in progress the AI stays silent. This is expected behaviour during the AI silence period.</p><br><p><b>Server logs</b> — check Railway logs for <code>[Chat]</code> error messages.</p></div>`,category:'Troubleshooting - AI - Not Responding'},
  {id:51,title:'Troubleshooting – SMS Alerts Not Received',content:`<div><p><b>ClickSend credentials</b> — verify username and API key in Super Admin → Integrations. Use Test SMS to confirm.</p><br><p><b>Agent mobile number</b> — must be in international format, no spaces (e.g. +447700900100).</p><br><p><b>SMS alerts toggle</b> — ensure SMS alerts are enabled on the agent's account.</p><br><p><b>Sender name</b> — max 11 characters, no spaces, letters and numbers only.</p><br><p><b>Alert log</b> — check Super Admin → Alert Log. If an entry shows &quot;pending&quot; the SMS was fired but may not have been delivered.</p></div>`,category:'Troubleshooting - SMS - Alerts Not Received'},
  {id:52,title:'Troubleshooting – Dashboard Not Loading Data',content:`<div><p><b>API connection</b> — open browser console (F12). Look for errors to <code>hindleapi-production.up.railway.app</code>.</p><br><p><b>Railway status</b> — check if the server deployment is running. A crashed server will show as stopped.</p><br><p><b>Session expired</b> — sign out and back in to restore your session and reload data.</p><br><p><b>DB error banner</b> — if the dashboard shows a red error banner, click Retry.</p></div>`,category:'Troubleshooting - Dashboard - Not Loading'},
  {id:53,title:'Security – Data Isolation Between Tenants',content:`<div><p>Every database record is associated with an organisation UUID. All API queries filter by this ID — tenant admins and agents can only access data belonging to their own organisation.</p><br><p>The super admin can view all tenants but uses dedicated super admin endpoints. Tenant credentials do not grant cross-tenant access.</p><br><p>The widget uses only the public tenant ID to load config and create conversations. Sensitive data from other tenants is never returned to the widget.</p></div>`,category:'Security - Data - Tenant Isolation'},
  {id:54,title:'Security – Password Policy',content:`<div><p>Minimum password length is 6 characters for all account types. We strongly recommend 12+ characters with a mix of uppercase, lowercase, numbers, and symbols for production accounts.</p><br><p>Tenant admin and agent passwords are stored as bcrypt hashes. Passwords are never stored or transmitted in plain text after initial creation.</p><br><p>Super admin password is stored in the platform database and cached in localStorage. The database version is always the authority.</p></div>`,category:'Security - Accounts - Password Policy'},
  {id:55,title:'API & Infrastructure – Railway Deployment',content:`<div><p>The Hindle API is deployed on Railway at <code>hindleapi-production.up.railway.app</code>.</p><br><p><b>Required Railway environment variables:</b> <code>DATABASE_URL</code>, <code>ANTHROPIC_API_KEY</code>, <code>STRIPE_SECRET_KEY</code>, <code>STRIPE_WEBHOOK_SECRET</code>.</p><br><p><b>Deployment</b> — push to the main branch of <code>Kuhlekt/hindle.api</code>. Railway auto-deploys on every push.</p><br><p><b>nixpacks.toml</b> — overrides the install command to use <code>npm install</code> instead of <code>npm ci</code>.</p></div>`,category:'Infrastructure - Railway - Deployment'},
  {id:56,title:'API & Infrastructure – Frontend Deployment',content:`<div><p>The Hindle frontend is deployed on Vercel at <code>chatbot.hindleconsultants.com</code>.</p><br><p><b>Repository</b> — <code>Kuhlekt/Hindle_Chatbot</code>. Main file: <code>jsx-viewer/src/components/chatbot_1.jsx</code>.</p><br><p><b>Deploy process</b> — copy the updated file to the repository path, commit, and push to main. Vercel rebuilds automatically.</p><br><p><b>Vercel settings</b> — Root Directory: <code>Chatbot_hc/jsx-viewer</code>. Framework: Create React App.</p><br><p><b>Build number</b> — the <code>BUILD</code> constant at the top of chatbot_1.jsx increments on every release and is shown under the user login row in the sidebar.</p></div>`,category:'Infrastructure - Vercel - Frontend Deployment'},
  {id:57,title:'SMTP Email – Sending from Your Own Domain',content:`<div><p>Hindle supports per-tenant SMTP configuration so your emails come from your own domain and name, not the platform sender.</p><br><p><b>Where to configure:</b> Tenant Admin → Integrations → SMTP Email card, or during the Setup Wizard → Email Setup (SMTP) step.</p><br><p><b>Required fields:</b> SMTP Host, Port, Username, Password, From Name, From Email.</p><br><p><b>Gmail:</b> Use an App Password (not your login password). Google Account → Security → 2-Step Verification → App Passwords. Host: smtp.gmail.com, Port: 587.</p><br><p><b>Office 365:</b> Host: smtp.office365.com, Port: 587. Use your Microsoft 365 email and password. You may need to enable SMTP AUTH in the Microsoft 365 admin centre.</p><br><p><b>cPanel / Hosting:</b> Host: mail.yourdomain.com, Port: 465. Use your full email address as username.</p><br><p><b>Fallback:</b> If SMTP is not configured or the connection fails, the platform ClickSend account is used as a fallback. A warning is logged when this happens.</p><br><p><b>Test button:</b> Click Test SMTP to send a real verification email and confirm the connection works before saving.</p></div>`,category:'Tenant Admin - Integrations - SMTP Email'},
  {id:58,title:'CRM Integrations – Connecting Your CRM',content:`<div><p>The CRM Integrations page (sidebar → CRM Integrations) provides API references for connecting Hindle to 12 popular platforms.</p><br><p><b>Available integrations:</b> Salesforce, HubSpot, Pipedrive, Zoho CRM, Freshsales, Monday.com, Zendesk, Intercom, ActiveCampaign, Mailchimp, Microsoft Dynamics 365, and Custom Webhook.</p><br><p>Each entry shows the API endpoint, authentication method, key field names, and a quick start guide. A sample Hindle visitor payload is provided so you know exactly what data to map.</p><br><p><b>Visitor data available for mapping:</b> visitor_name, visitor_email, visitor_phone, visitor_company, visitor_location, status, page, timestamp.</p><br><p><b>Custom Webhook:</b> Send conversation data to any endpoint — your own CRM, internal system, or automation platform like Zapier or Make.</p><br><p>Use the search bar and category filter (CRM / Support / Marketing / Project / Custom) to find the integration you need.</p></div>`,category:'Tenant Admin - Integrations - CRM Connections'},
  {id:59,title:'Knowledge Base – JSON Import and Field Mapper',content:`<div><p>The JSON Import feature lets you bulk-load FAQ or knowledge content from a JSON file.</p><br><p><b>Supported shapes:</b></p><br><p>• Array of objects: <code>[{"question":"...", "answer":"..."}]</code></p><br><p>• Nested by category: <code>{"Billing": [{"q":"...", "a":"..."}]}</code></p><br><p>• Flat key-value: <code>{"Q: How do I...": "A: You can..."}</code></p><br><p><b>How it works:</b> Click Import JSON, select your file. Hindle parses it client-side and opens the Field Mapper modal. Select which fields map to Title, Content, Category, Sub-category, and Tertiary. A live preview shows how the first 3 articles will look. Click Import to save all articles to the knowledge base.</p><br><p><b>Large files:</b> Files over 100 items are batched in groups of 100 to avoid payload limits.</p><br><p><b>Category assignment:</b> After uploading any document (PDF, DOCX, URL), a category mapper appears so you can assign the article to a category before it is saved.</p></div>`,category:'Tenant Admin - Knowledge Base - JSON Import'},
  {id:60,title:'Knowledge Base – PDF and DOCX Upload',content:`<div><p>Hindle extracts text from PDF and DOCX files server-side and indexes the content for AI use.</p><br><p><b>PDF:</b> Uses pdf-parse to extract all text content. Tables and images are not extracted — only text is indexed.</p><br><p><b>DOCX / DOC:</b> Uses mammoth to extract raw text from Word documents. Formatting is stripped; only the text content is saved.</p><br><p><b>File size limit:</b> 20MB per file.</p><br><p><b>After upload:</b> A category mapper modal appears so you can assign the document to a category, sub-category, and tertiary before it is saved to the knowledge base.</p><br><p><b>Supported formats:</b> PDF, DOCX, DOC, TXT, MD, XML, CSV. JSON files use the dedicated JSON importer with field mapping.</p></div>`,category:'Tenant Admin - Knowledge Base - File Upload'},
  {id:61,title:'Knowledge Base – Conversations Panel',content:`<div><p>The KB tab in the conversation right panel gives agents quick access to knowledge base articles while handling a live chat.</p><br><p>Articles are loaded directly from the database (not localStorage) and grouped by category. Each category is collapsible.</p><br><p><b>Expanding an article:</b> Click the article row to expand and read the full content.</p><br><p><b>Insert button:</b> Click Insert next to any article to paste its content directly into the compose box. The agent can edit it before sending.</p><br><p><b>Search:</b> The search box filters across article title and content. A refresh button (↻) re-fetches articles from the database.</p><br><p>Articles are tenant-specific — each tenant only sees their own knowledge base.</p></div>`,category:'Tenant Admin - Knowledge Base - Conversations Panel'},
  {id:62,title:'Conversations – Visitor Details Panel',content:`<div><p>The right panel in Conversations → Visitor tab shows all collected visitor information.</p><br><p><b>Fields shown:</b> Name, Email, Phone (clickable tel: link), Company, Location (with Google Maps link), Page visited, Status.</p><br><p><b>Location detection:</b> Visitor IP is automatically resolved to a city/region/country using ip-api.com (primary) with ipapi.co as fallback. Location is detected server-side on conversation create and also patched when the widget resolves it client-side.</p><br><p><b>Claimed By:</b> Shows the agent who claimed the conversation with a green badge.</p><br><p><b>Assign Agent:</b> Select an agent from the dropdown to assign them. This sets the assigned agent, claimed_by, and changes the conversation status to Claimed. An Unassign button clears all three fields and returns the status to Open.</p></div>`,category:'Tenant Admin - Conversations - Visitor Details'},
  {id:63,title:'Conversations – Filters and Search',content:`<div><p>The conversation list has two filter rows above the list.</p><br><p><b>Row 1:</b> Search box (filters by visitor name, email, or subject) and a Me toggle button.</p><br><p><b>Row 2:</b> Status filter pills — Active (default, excludes resolved), Open, Claimed, Handoff, Parked, Resolved, All. These are horizontally scrollable and never wrap.</p><br><p><b>Active filter:</b> The default view hides Resolved and Finished conversations. Switch to Resolved or All to see them.</p><br><p><b>Me toggle:</b> When active, shows only conversations claimed by or assigned to the currently logged-in agent.</p><br><p><b>Search:</b> Filters the visible list in real time by visitor name, email address, or conversation subject.</p></div>`,category:'Tenant Admin - Conversations - Filters'},
  {id:64,title:'Audit Log – Platform Event History',content:`<div><p>The Audit Log records significant events across the platform for accountability and troubleshooting.</p><br><p><b>Access:</b></p><br><p>• Super Admin → Audit Log (sidebar) — all events across all tenants</p><br><p>• Super Admin → Tenants → select tenant → Audit Log tab — events for that tenant only</p><br><p><b>Events recorded:</b> Tenant created, plan changed, tenant suspended/reactivated, tenant deleted, payment confirmed, trial reminder sent/failed, and any custom events added by platform admins.</p><br><p><b>Filtering:</b> Search by description or tenant ID, or filter by event type using the dropdown.</p><br><p><b>Meta:</b> Click View on any event row to see the full metadata JSON (plan changes show from/to plan, payment events show amount and email).</p><br><p>Audit events are written automatically by the platform — no manual action required.</p></div>`,category:'Super Admin - Audit Log - Event History'},
  {id:65,title:'Row Level Security – Tenant Data Isolation',content:`<div><p>Hindle uses PostgreSQL Row Level Security (RLS) to enforce tenant data isolation at the database level.</p><br><p><b>Tables protected:</b> conversations, messages, kb_documents, agents, alert_log.</p><br><p><b>How it works:</b> Each request sets a session variable (app.current_org_id) to the tenant's UUID before running queries. RLS policies on each table ensure that rows are only returned when org_id matches the current session. Super admin sessions use an empty string which allows access to all rows.</p><br><p><b>Benefit:</b> Even if there is a bug in the application code, the database will not return another tenant's data. Cross-tenant data leakage is impossible at the DB layer.</p><br><p><b>Neon verification:</b> You can verify isolation in the Neon SQL Editor by running a transaction with set_config and checking counts per tenant.</p></div>`,category:'Security - Data - Row Level Security'},
  {id:66,title:'Upgrade Plan – Existing Customers',content:`<div><p>Existing tenant admins can upgrade their plan from Settings → Billing → Upgrade Plan button, or via any plan card on the landing page.</p><br><p>The purchase wizard detects that the account already exists from the browser session and skips the account creation steps.</p><br><p><b>Existing customer flow:</b></p><br><p>1. Choose Plan — select the new plan, billing cycle, and currency.</p><br><p>2. Pay — a summary shows your account email, selected plan, billing cycle, and amount. A single Pay via Stripe button redirects to Stripe Checkout.</p><br><p>3. On successful payment, the Stripe webhook activates the new plan and sends a payment confirmation email.</p><br><p><b>New customer flow:</b> Goes through the full wizard — Company Details, password, terms, then payment or trial start.</p></div>`,category:'Billing - Purchasing - Existing Customer Upgrade'},
  {id:67,title:'Email Templates – Customising Outbound Emails',content:`<div><p>Email templates control the content of automated emails sent by the platform.</p><br><p><b>Available templates:</b> Welcome Email (on account creation), Trial Reminder #1 (Day 12), Trial Reminder #2 (Day 13), Trial Final Notice (Day 14), Payment Confirmation (after Stripe payment), Monthly Invoice.</p><br><p><b>Where to edit:</b> Super Admin → SMS & Email → Email Templates tab. Tenants can also view templates in their Integrations page.</p><br><p><b>Variables available:</b> {name}, {plan}, {amount}, {days}, {last4}, {expiry}, {month}, {company}.</p><br><p><b>Example:</b> Hi {name}, your {plan} plan is now active. Payment of {amount} received.</p><br><p>Templates are rendered with real tenant data before sending. If a variable is not available for a given email type, it is replaced with a blank string.</p></div>`,category:'Super Admin - Integrations - Email Templates'},
  {id:68,title:'Setup Wizard – Complete Guide',content:`<div><p>The Setup Wizard walks new tenants through configuring every aspect of Hindle before going live.</p><br><p><b>Steps:</b></p><br><p>1. Company Profile — name, industry, website, support email.</p><br><p>2. Branding — primary colour, welcome message.</p><br><p>3. Widget Setup — visitor fields to collect, widget features.</p><br><p>4. AI Chatbot — model, tone, language, confidence threshold, fallback message.</p><br><p>5. Ask a Human — handoff button label, form fields, silence timer.</p><br><p>6. Agent Mobile Numbers — add agent names and mobile numbers for SMS alerts.</p><br><p>7. Notifications — email and SMS alert preferences.</p><br><p>8. Email Setup (SMTP) — connect your own mail server so emails come from your domain.</p><br><p>9. Knowledge Base — upload documents, PDFs, or JSON to train the AI.</p><br><p>10. Widget Embed Code — copy and paste the snippet to your website.</p><br><p>11. Test Widget — verify the chatbot is responding.</p><br><p>12. Security — 2FA confirmation.</p><br><p>13. Go Live — final checklist before launch.</p><br><p>You can click any step in the left sidebar to jump back and edit it at any time. Click <b>Minimise</b> in the wizard header at any point to return to the dashboard without losing progress — resume any time via Re-run Setup.</p></div>`,category:'Getting Started - Setup Wizard - Complete Guide'},
  {id:69,title:'Navigation – Sidebar Sections',content:`<div><p>The tenant sidebar is grouped into three sections:</p><br><p><b>Chatbot</b> — Dashboard and Conversations, for the AI-powered live chat widget on your website.</p><br><p><b>Helpdesk</b> — Dashboard, Tickets, and Access Requests, for structured support ticket management.</p><br><p><b>Settings</b> — AI Chatbot configuration, Agent Management, Install & Embed, CRM Integrations, Analytics, Integrations, FAQ Manager, and Settings itself.</p><br><p>Chatbot and Helpdesk are genuinely separate systems under the hood — a website visitor chatting with your AI is a <b>conversation</b>, while a structured support issue with status/priority/assignment tracking is a <b>ticket</b>. Many tenants use both side by side.</p></div>`,category:'Getting Started - Navigation - Sidebar Sections'},
  {id:70,title:'Tickets – Overview',content:`<div><p>Tenant Admin → Helpdesk → <b>Tickets</b> shows every support ticket for your organisation.</p><br><p><b>Filters</b> — All, Open, In Progress, Pending, Resolved, Closed. The list opens to <b>Open</b> by default. A <b>My Tickets only</b> checkbox restricts the list to tickets assigned to you.</p><br><p><b>Search</b> — searches ticket subject and number.</p><br><p><b>Pagination</b> — 15 tickets per page with Prev/Next controls once you have more than one page.</p><br><p><b>Collapse</b> — use the Minimise button at the top of the list, or the thin arrow between the list and detail panel, to hide the list and give the ticket detail more room.</p><br><p>Click any ticket to open its detail panel on the right.</p></div>`,category:'Helpdesk - Tickets - Overview'},
  {id:71,title:'Tickets – Creating a New Ticket',content:`<div><p>Click <b>+ New Ticket</b> at the top of the ticket list to log a ticket manually — useful for phone-in or email requests that didn't come through the customer portal.</p><br><p><b>Required:</b> Subject and Requester Email.</p><br><p><b>Optional:</b> Description, Requester Name, Priority (defaults to Medium), Category.</p><br><p>If the category you need doesn't exist yet, select <b>+ Add new category…</b> at the bottom of the Category dropdown — type a name and it's created and selected immediately, no need to leave the form.</p><br><p>On save, the new ticket opens automatically so you can start working it right away.</p></div>`,category:'Helpdesk - Tickets - Creating a Ticket'},
  {id:72,title:'Tickets – Status, Priority, Category & Assignment',content:`<div><p>The ticket header has four controls, each colour-coded for quick scanning:</p><br><p><b>Status</b> — Open (green), In Progress (yellow), Pending (purple), Resolved (red), Closed (grey).</p><br><p><b>Priority</b> — Low (green), Medium (yellow), High (orange), Urgent (red).</p><br><p><b>Category</b> — organises tickets by type. Add a new one inline via <b>+ Add new category…</b> at the bottom of the dropdown.</p><br><p><b>Assigned To</b> — shows your organisation's own active agents and admins only (not customers, and not staff from other tenants). If someone you expect isn't listed, check they're marked active in Agent Management.</p><br><p>Every change here is logged automatically in the ticket's Audit Trail.</p></div>`,category:'Helpdesk - Tickets - Status and Assignment'},
  {id:73,title:'Tickets – Reply, Internal Note, Cause & Resolution',content:`<div><p>The vertical icon rail on the right of an open ticket gives you four ways to add to the ticket, each opening as a slide-in panel:</p><br><p><b>💬 Reply</b> — a customer-visible message. Use <b>⚡ Canned</b> to search and insert a saved canned response.</p><br><p><b>🔒 Note</b> — an internal note, never shown to the customer. Shown with a yellow background in the timeline.</p><br><p><b>🔍 Cause</b> — records what caused the issue, for reporting.</p><br><p><b>✅ Resolve</b> — records the resolution and sets status to Resolved in one step. Optionally pick a <b>Resolution Type</b> (see the Resolution Types article) or add a new one inline.</p><br><p>Sending a genuine customer-visible reply on a Resolved or Closed ticket automatically reopens it to Open — the conversation clearly isn't finished if someone's still replying.</p></div>`,category:'Helpdesk - Tickets - Reply and Resolution'},
  {id:74,title:'Tickets – Resolution Types',content:`<div><p>Resolution Types let you tag <b>how</b> a ticket was resolved for structured reporting — separate from the free-text resolution summary itself.</p><br><p>Pick one from the dropdown when resolving a ticket (in the <b>✅ Resolve</b> panel), or select <b>+ Add new type…</b> to create one on the spot.</p><br><p>Starter types include Referred to IT, Referred to Sales, Fixed — Config Change, Fixed — Bug, User Error / Training, Duplicate / No Action Needed, Escalated to Vendor, and Feature Request Logged — but this list is fully yours to edit and extend per organisation.</p><br><p>The chosen type shows next to the resolution text on a resolved ticket, and is available for future reporting/analytics work.</p></div>`,category:'Helpdesk - Tickets - Resolution Types'},
  {id:75,title:'Tickets – Collision Detection ("Changed by Someone Else")',content:`<div><p>If two agents are working the same ticket at once, Hindle prevents one from silently overwriting the other's change.</p><br><p>When you save a change (status, priority, category, resolution), the system checks that nothing else has changed the ticket since you opened it. If it has, you'll see: <i>"This ticket was updated by someone else — please review the latest changes before saving."</i></p><br><p>Click OK, and the ticket automatically refreshes to show the latest version. Your original change is <b>not</b> applied — review what changed, then make your edit again.</p><br><p>This is a safety feature, not a bug — if you see it often on a specific ticket, it likely means multiple agents are actively working it at the same time.</p></div>`,category:'Helpdesk - Tickets - Collision Detection'},
  {id:76,title:'Tickets – Audit Trail',content:`<div><p>Click <b>🕒 Audit</b> in the ticket's right-hand rail to see a complete, timestamped history of every change to that ticket.</p><br><p>Logged events include: status/priority/category/assignment changes (showing old value → new value, resolved to readable names not raw IDs), replies sent, internal notes added, cause recorded, and any blocked save attempts from the collision-detection safety check.</p><br><p>Each entry shows who made the change and exactly when — useful for compliance, for handovers between agents, and for understanding a ticket's full history at a glance.</p></div>`,category:'Helpdesk - Tickets - Audit Trail'},
  {id:77,title:'Tickets – Side Conversations',content:`<div><p>Click <b>🧵 Side</b> in the ticket's rail to start a private thread with a colleague, without it becoming part of the customer-visible ticket or cluttering the main timeline.</p><br><p>Click <b>+ New Thread</b>, give it a subject (e.g. "Need billing input from Sam"), optionally add an opening message, and start it. Multiple side conversations can exist on one ticket — click any thread in the list to open and reply within it.</p><br><p>Side conversations are never visible to the customer under any circumstances — they exist purely for internal collaboration around a ticket.</p></div>`,category:'Helpdesk - Tickets - Side Conversations'},
  {id:78,title:'Tickets – Merging Duplicate Tickets',content:`<div><p>If the same issue was accidentally logged as two separate tickets, use <b>🔗 Merge into…</b> (in the ticket header) to combine them.</p><br><p>Enter the target ticket's number and click Merge. All messages from the current ticket move into the target ticket's timeline, with a system note marking where they came from. The source ticket becomes <b>read-only</b> and shows a banner linking to where it went — you can still view its history, but no further replies or changes are possible on it.</p><br><p>Always merge the newer/duplicate ticket <b>into</b> the original, not the other way around, to keep the most complete history in one place.</p></div>`,category:'Helpdesk - Tickets - Merging Tickets'},
  {id:79,title:'Tickets – Splitting a Ticket',content:`<div><p>If one ticket ends up covering two unrelated issues, use <b>✂️ Split…</b> (in the ticket header) to separate them.</p><br><p>Click Split to enter split mode — a checkbox appears next to every message. Select only the messages that belong to the separate issue, give the new ticket a subject, and confirm. A brand-new ticket is created with just those messages, and a system note links both tickets to each other so the history stays traceable.</p><br><p>The original ticket keeps everything you didn't select.</p></div>`,category:'Helpdesk - Tickets - Splitting a Ticket'},
  {id:80,title:'Tickets – Screen Share (View-Only Remote Assist)',content:`<div><p>Click <b>📺 Screen</b> in a ticket's rail to start a live, view-only screen share with the customer — no software install required on their end.</p><br><p>A session link is generated automatically. Copy it and send it to the customer (by SMS or email — outside the app, using whatever channel suits). When they open the link and click Start Sharing, their browser asks which window/tab/screen to share — you'll see it live in the panel within a few seconds.</p><br><p>This is genuinely <b>view-only</b> — you can see the customer's screen to talk them through a fix by phone, but you cannot click, type, or control anything on their end. Click End Session when you're done.</p></div>`,category:'Helpdesk - Tickets - Screen Share'},
  {id:81,title:'Helpdesk – Dashboard Overview',content:`<div><p>Helpdesk → <b>Dashboard</b> gives a ticket-specific overview, separate from the Chatbot dashboard.</p><br><p><b>Stat cards</b> — Open Tickets (open + in progress + pending combined), Resolved, Closed, New in the Last 7 Days, and Average Resolution Time.</p><br><p><b>By Status</b> and <b>By Priority</b> breakdowns show the full split across every ticket, not just what's currently filtered in the ticket list.</p><br><p>Use this page for a quick health check on ticket volume and turnaround before diving into the individual ticket list.</p></div>`,category:'Helpdesk - Dashboard - Overview'},
  {id:82,title:'Access Requests – Creating a Request',content:`<div><p>Helpdesk → <b>Access Requests</b> manages time-limited, approved access into a customer's own systems for support purposes — a genuine audit trail for sensitive access, not just a note.</p><br><p>Click <b>+ New Request</b>, or click <b>+ Request Access</b> from inside a specific ticket (which auto-links the request to that ticket and pre-fills a reason referencing it).</p><br><p>Fill in Requester Name, who you're On Behalf Of, and the approver's Name/Email/Mobile — a real contact at the customer who needs to approve this. Choose a Reason, Scope, Duration (1 hour up to 3 days), and how to notify the approver — SMS, Email, or Both.</p><br><p>When linked to a ticket, an internal note recording the request is added automatically to that ticket's history.</p></div>`,category:'Helpdesk - Access Requests - Creating a Request'},
  {id:83,title:'Access Requests – Approval Flow',content:`<div><p>Once submitted, the approver receives an SMS and/or email (per your delivery choice) with a link to a clean, standalone approval page — <b>no login required</b>, since the approver is the customer's own contact, not a Hindle user. The link is valid for <b>1 hour</b>.</p><br><p><b>Resend</b> — if the approver missed it or the link expired, click <b>Resend</b> on any Pending or Expired request to send a fresh 1-hour link. The approver can also request a new link themselves from the email or SMS (\"Send me a new link\"). Resends are limited to 5 per request, one per minute.</p><br><p>The page shows the request details (who's asking, why, what access level, for how long) and two buttons: Approve or Deny. The approver enters their name for the record, then decides.</p><br><p>Approved access is time-limited to exactly the duration requested — it does not need to be manually revoked when it naturally expires, though you can revoke it early at any time from the Access Requests list.</p></div>`,category:'Helpdesk - Access Requests - Approval Flow'},
  {id:84,title:'Access Requests – Managing Existing Requests',content:`<div><p>The Access Requests list shows every request with its status (Pending, Approved, Revoked, Expired, Denied), which org and ticket it relates to, and when it was made.</p><br><p><b>Revoke</b> — immediately ends an active approved grant early, before its natural expiry.</p><br><p><b>Delete</b> — permanently removes the request record. Use this to clean up test data or requests you no longer need on file; it cannot be undone.</p><br><p>Click any request to see its full audit trail on the right — every action taken against it, timestamped.</p></div>`,category:'Helpdesk - Access Requests - Managing Requests'},
  {id:85,title:'Customer Portal – Self-Serve Support & SSO',content:`<div><p>Your customers can access support directly at <b>helpdesk.hindleconsultants.com/portal/{your-slug}</b> — a branded page showing your own logo, where they can sign up, log in, and submit or track their own tickets without needing an agent.</p><br><p><b>Self-serve</b> — works today for any tenant, no extra setup. Just share the link.</p><br><p><b>SSO handoff</b> — if a customer is already logged into your own application, you can hand them off already-authenticated: your backend signs a short-lived token with a secret we provide, redirects to a special SSO URL, and the customer lands straight into a ticket submission form — no separate password. Ask your account contact for the integration details and secret if you want this set up.</p></div>`,category:'Helpdesk - Customer Portal - Self-Serve and SSO'},
];

// PAGE → HELP CATEGORY mapping
const HELP_PAGE_MAP={
  dash:          ["Dashboard","Overview"],
  convos:        ["Conversations"],
  agents:        ["Agents"],
  chatbot:       ["Chatbot Settings","Knowledge Base","AI Behaviour","FAQ"],
  install:       ["Install"],
  alertlog:      ["Alert Log"],
  analytics:     ["Analytics"],
  integrations:  ["Integrations","ClickSend"],
  trials:        ["Trial Monitor"],
  pricing:       ["Pricing"],
  tenants:       ["Tenants"],
  settings:      ["Settings"],
  purchase:      ["Purchasing","Billing"],
};

const helpSearch=(query,page)=>{
  const q=(query||"").toLowerCase().trim();
  return HELP_ARTICLES.filter(a=>{
    const catMatch=page&&HELP_PAGE_MAP[page]?HELP_PAGE_MAP[page].some(k=>a.category.toLowerCase().includes(k.toLowerCase())):true;
    if(!q) return catMatch;
    return a.title.toLowerCase().includes(q)||a.content.replace(/<[^>]+>/g,"").toLowerCase().includes(q)||a.category.toLowerCase().includes(q);
  });
};

const HelpPanel=({page,onClose})=>{
  const [query,setQuery]=useState("");
  const [selected,setSelected]=useState(null);
  const results=helpSearch(query,selected?null:page);
  return(
    <div style={{position:"fixed",top:0,right:0,bottom:0,width:400,maxWidth:"100vw",background:S.w,boxShadow:"-4px 0 24px rgba(0,0,0,.13)",zIndex:9999,display:"flex",flexDirection:"column",animation:"slideInRight .2s ease"}}>
      <style>{`@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
      {/* Header */}
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        {selected?(
          <button onClick={()=>setSelected(null)} style={{background:"transparent",border:`1px solid ${S.brd}`,borderRadius:6,padding:"4px 8px",cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontSize:12,color:T.fnt}}>
            <Ic n="arrL" s={11}/> Back
          </button>
        ):(
          <div style={{width:30,height:30,borderRadius:8,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Ic n="q" s={15} c={B}/>
          </div>
        )}
        <div style={{flex:1}}>
          <p style={{fontSize:14,fontWeight:700}}>{selected?selected.title:"Help Centre"}</p>
          {!selected&&<p style={{fontSize:11.5,color:T.fnt,marginTop:1}}>{results.length} article{results.length!==1?"s":""} {query?"found":"for this page"}</p>}
        </div>
        <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,display:"flex",padding:4}}><Ic n="x" s={16}/></button>
      </div>

      {!selected?(
        <>
          {/* Search */}
          <div style={{padding:"10px 14px",borderBottom:`1px solid ${S.brd}`,flexShrink:0}}>
            <input className="inp" placeholder="Search help articles…" value={query} onChange={e=>setQuery(e.target.value)}
              style={{fontSize:13}} autoFocus/>
          </div>
          {/* Article list */}
          <div style={{flex:1,overflowY:"auto"}}>
            {results.length===0?(
              <div style={{padding:32,textAlign:"center",color:T.fnt}}>
                <Ic n="q" s={28} c={S.brd}/>
                <p style={{marginTop:12,fontSize:13}}>No articles found for "{query}"</p>
                <button className="btn bo sm" style={{marginTop:12}} onClick={()=>setQuery("")}>Clear search</button>
              </div>
            ):results.map(a=>{
              const parts=a.category.split(" - ");
              return(
                <div key={a.id} onClick={()=>setSelected(a)} style={{padding:"12px 16px",borderBottom:`1px solid ${S.bg}`,cursor:"pointer",transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=BL} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <p style={{fontSize:13,fontWeight:600,color:T.ink,marginBottom:3}}>{a.title}</p>
                  <p style={{fontSize:11,color:T.fnt}}>{parts.slice(0,2).join(" › ")}</p>
                </div>
              );
            })}
          </div>
          {/* Footer */}
          {!query&&(
            <div style={{padding:"10px 16px",borderTop:`1px solid ${S.brd}`,flexShrink:0}}>
              <button className="btn bo sm" style={{width:"100%",fontSize:12}} onClick={()=>setQuery(" ")}>
                <Ic n="search" s={11}/> Browse all {HELP_ARTICLES.length} articles
              </button>
            </div>
          )}
        </>
      ):(
        /* Article view */
        <div style={{flex:1,overflowY:"auto",padding:"16px 18px"}}>
          <div style={{padding:"6px 10px",background:BL,border:`1px solid ${BM}`,borderRadius:6,marginBottom:14,display:"inline-block"}}>
            <p style={{fontSize:11,color:B,fontWeight:600}}>{selected.category.split(" - ").join(" › ")}</p>
          </div>
          <div style={{fontSize:13.5,lineHeight:1.7,color:T.ink}} dangerouslySetInnerHTML={{__html:selected.content}}/>
        </div>
      )}
    </div>
  );
};

const HelpButton=({page,style={}})=>{
  const [open,setOpen]=useState(false);
  return(
    <>
      <button onClick={()=>setOpen(v=>!v)}
        style={{background:open?BL:"transparent",border:`1px solid ${open?BM:S.brd}`,borderRadius:20,padding:"4px 10px",cursor:"pointer",display:"flex",alignItems:"center",gap:5,color:open?B:T.fnt,fontSize:12,fontWeight:600,transition:"all .15s",...style}}
        title="Help">
        <Ic n="q" s={13} c={open?B:T.fnt}/> Help
      </button>
      {open&&<HelpPanel page={page} onClose={()=>setOpen(false)}/>}
    </>
  );
};


const INIT_CONFIG={
  annualDiscountPct:20,
  plans:{
    free:        {usd:0,   name:"Free",         desc:"Try it out",           smsAlerts:false,smsNote:"Upgrade for SMS alerts",  enterprise:false},
    starter:     {usd:30,  name:"Starter",       desc:"Growing teams",        smsAlerts:true, smsLimit:"Primary agent only",         enterprise:false},
    professional:{usd:80,  name:"Professional",  desc:"Scaling businesses",   smsAlerts:true, smsLimit:"All agents simultaneously",  enterprise:false},
    enterprise:  {usd:null,name:"Enterprise",    desc:"Large-scale & custom", smsAlerts:true, smsLimit:"All agents simultaneously",  enterprise:true},
  },
  currencies:{
    USD:{symbol:"$",  rate:1,     label:"USD — US Dollar"},
    CAD:{symbol:"C$", rate:1.36,  label:"CAD — Canadian Dollar"},
    GBP:{symbol:"£",  rate:0.79,  label:"GBP — British Pound"},
    EUR:{symbol:"€",  rate:0.92,  label:"EUR — Euro"},
    ZAR:{symbol:"R",  rate:18.62, label:"ZAR — South African Rand"},
    AUD:{symbol:"A$", rate:1.53,  label:"AUD — Australian Dollar"},
  },
  promoCodes:[
    {code:"HINDLE20",pct:20,limit:100,used:14,expiry:"2026-12-31",plans:["starter","professional"],active:true,desc:"20% off for partners"},
    {code:"LAUNCH50",pct:50,limit:50, used:49,expiry:"2026-03-31",plans:["starter"],               active:true,desc:"Launch special"},
    {code:"ANNUAL10",pct:10,limit:200,used:88,expiry:"2026-12-31",plans:["professional"],          active:true,desc:"Extra 10% on annual Pro"},
  ],
  clicksend:{username:"",apiKey:"",smsSender:"HINDLE",emailFrom:"noreply@hindleconsultants.com",emailName:"Hindle Consultants",emailAddressId:"",configured:false},
};

const TRIAL_TENANTS=[];
const INIT_AGENTS=[];
const CONVOS=[];

const genToken=()=>Math.random().toString(36).slice(2,10);
// Round to nearest $5 in local currency
const roundTo5=(val)=>Math.round(val/5)*5;
const getPrice=(planId,currency,billing,promoCode,config)=>{
  const plan=config.plans[planId];
  if(!plan||plan.usd===0||plan.usd===null) return {base:0,monthly:0,annualMonthly:0,annualTotal:0,saving:0,final:0,promo:null,sym:"$",cur:config.currencies.USD,enterprise:!!plan?.enterprise};
  const cur=config.currencies[currency]||config.currencies.USD;
  const monthly=roundTo5(plan.usd*cur.rate);
  const annualMonthly=roundTo5(monthly*(1-config.annualDiscountPct/100));
  const annualTotal=annualMonthly*12;
  const saving=(monthly*12)-annualTotal;
  let promo=null;
  if(promoCode){const pc=config.promoCodes.find(p=>p.code===promoCode.toUpperCase()&&p.active&&p.plans.includes(planId)&&p.used<p.limit&&new Date(p.expiry)>new Date());if(pc)promo=pc;}
  const base=billing==="annual"?annualMonthly:monthly;
  const final=promo?roundTo5(base*(1-promo.pct/100)):base;
  return {base,monthly,annualMonthly,annualTotal,saving,final,promo,sym:cur.symbol,cur,enterprise:false};
};
// Global stylesheet — rebuilt from the active theme tokens on every theme change.
const buildCSS=()=>`
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
:root{--b:${B};--bl:${BL};--bm:${BM};--ink:${T.ink};--mid:${T.mid};--lt:${T.lt};--fnt:${T.fnt};--w:${S.w};--bg:${S.bg};--brd:${S.brd};--sub:${S.sub};--hov:${S.hov};--side:${S.side};--sh:${S.shadow};--shl:${S.shadowLg}}
*{box-sizing:border-box;margin:0;padding:0}
html,body,#root{height:100%;font-family:'Inter',system-ui,-apple-system,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;color:var(--ink);background:var(--bg);font-feature-settings:"cv11","ss01"}
body{transition:background .2s ease,color .2s ease}
a{color:var(--b)}
::selection{background:${B}33}
::-webkit-scrollbar{width:8px;height:8px}::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:${T.fnt}55;border-radius:8px;border:2px solid transparent;background-clip:content-box}
::-webkit-scrollbar-thumb:hover{background:${T.fnt}99;background-clip:content-box}
*{scrollbar-width:thin;scrollbar-color:${T.fnt}55 transparent}
@keyframes up{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes in{from{opacity:0}to{opacity:1}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
@keyframes pop{0%{transform:scale(.8);opacity:0}100%{transform:scale(1);opacity:1}}
@keyframes typingDot{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-5px);opacity:1}}
@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
.au{animation:up .28s cubic-bezier(.2,.8,.2,1) both}.ai{animation:in .2s ease both}
.mono,code,pre{font-family:'JetBrains Mono',ui-monospace,Menlo,monospace}
h1,h2,h3,h4{letter-spacing:-.01em;color:var(--ink)}
.btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;font-family:inherit;font-weight:600;border:1px solid transparent;border-radius:9px;cursor:pointer;transition:background .15s,border-color .15s,color .15s,box-shadow .15s,transform .1s;white-space:nowrap;font-size:13px;padding:8px 15px;line-height:1.2}
.btn:active{transform:translateY(1px)}
.btn:focus-visible{outline:none;box-shadow:0 0 0 3px ${B}40}
.btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
.bp{background:var(--b);color:#fff;box-shadow:0 1px 2px rgba(16,24,40,.08),inset 0 1px 0 rgba(255,255,255,.12)}.bp:hover:not(:disabled){filter:brightness(1.07);box-shadow:0 4px 14px ${B}40}
.bo{background:var(--w);color:var(--ink);border-color:var(--brd);box-shadow:var(--sh)}.bo:hover:not(:disabled){border-color:${B}80;color:var(--b);background:var(--bl)}
.bg{background:transparent;color:var(--lt);border-color:transparent}.bg:hover:not(:disabled){background:var(--hov);color:var(--ink)}
.bcs{background:${CS};color:#fff}.bcs:hover:not(:disabled){filter:brightness(1.07);box-shadow:0 4px 14px ${CS}40}
.bd{background:${G.rb};color:${G.r};border-color:${G.rbd}}.bd:hover:not(:disabled){filter:brightness(${THEME_MODE==="dark"?1.25:.97})}
.bg-g{background:${G.gb};color:${G.g};border-color:${G.gbd}}
.sm{font-size:12px!important;padding:5px 10px!important;border-radius:7px!important}
.lg{font-size:15px!important;padding:12px 26px!important;border-radius:11px!important}
.inp{font-family:inherit;font-size:13px;color:var(--ink);background:var(--w);border:1px solid var(--brd);border-radius:9px;padding:8px 11px;width:100%;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;box-shadow:inset 0 1px 1px rgba(16,24,40,.03)}
.inp:hover{border-color:${T.fnt}88}
.inp:focus{border-color:var(--b);box-shadow:0 0 0 3px ${B}26}
.inp::placeholder{color:var(--fnt)}
select.inp{appearance:none;-webkit-appearance:none;padding-right:30px;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23${T.fnt.slice(1)}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center}
.inp-err{border-color:${G.r}!important;box-shadow:0 0 0 3px ${G.r}22!important}
.inp-ok{border-color:${G.g}!important;box-shadow:0 0 0 3px ${G.g}22!important}
input[type=checkbox],input[type=radio]{accent-color:var(--b)}
.card{background:var(--w);border:1px solid var(--brd);border-radius:14px;box-shadow:var(--sh)}
.tbl{width:100%;border-collapse:separate;border-spacing:0}
.tbl th{padding:10px 14px;text-align:left;font-size:11px;font-weight:600;color:var(--lt);text-transform:uppercase;letter-spacing:.06em;background:var(--sub);border-bottom:1px solid var(--brd);position:sticky;top:0;z-index:1}
.tbl td{padding:12px 14px;font-size:13px;color:var(--ink);border-bottom:1px solid var(--brd)}
.tbl tr:last-child td{border-bottom:none}
.tbl tbody tr{transition:background .12s}
.tbl tbody tr:hover td{background:var(--hov)}
.nl{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:9px;font-size:13px;font-weight:500;color:var(--lt);cursor:pointer;transition:background .12s,color .12s;border:1px solid transparent;user-select:none;position:relative}
.nl:hover{background:var(--hov);color:var(--ink)}
.nl.act{background:var(--bl);color:var(--b);font-weight:600}
.nl.act::before{content:"";position:absolute;left:-10px;top:8px;bottom:8px;width:3px;border-radius:0 3px 3px 0;background:var(--b)}
.badge{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600}
.toggle{display:flex;align-items:center;background:var(--sub);border:1px solid var(--brd);border-radius:10px;padding:3px}
.toggle-opt{padding:6px 14px;border-radius:7px;font-size:13px;font-weight:600;cursor:pointer;transition:all .18s;border:none;background:transparent;color:var(--lt);font-family:inherit}
.toggle-opt.active{background:var(--w);color:var(--b);box-shadow:var(--sh)}
.stripe-frame{background:${THEME_MODE==="dark"?"#1a1b33":"linear-gradient(135deg,#f8f9ff,#eef0ff)"};border:1px solid ${THEME_MODE==="dark"?"#3b3d7a":"#c7d2fe"};border-radius:12px;padding:16px 18px}
.stripe-badge{display:inline-flex;align-items:center;gap:5px;background:#635bff;color:#fff;font-size:10.5px;font-weight:700;padding:3px 10px;border-radius:5px}
.cs-frame{background:${THEME_MODE==="dark"?"#0f2230":"linear-gradient(135deg,#f0faff,#e0f4fc)"};border:1px solid ${THEME_MODE==="dark"?"#1f4a63":"#7dd3ee"};border-radius:12px;padding:16px 18px}
.cs-badge{display:inline-flex;align-items:center;gap:5px;background:${CS};color:#fff;font-size:10.5px;font-weight:700;padding:3px 10px;border-radius:5px;letter-spacing:.3px}
.step-done{background:${G.gb};border:1px solid ${G.gbd};border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:12px;cursor:pointer;transition:all .15s}
.step-active{background:var(--w);border:2px solid var(--b);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:12px;cursor:pointer;box-shadow:0 4px 16px ${B}22}
.step-pending{background:var(--w);border:1px solid var(--brd);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:12px;cursor:pointer;opacity:.65}
.theme-seg{display:inline-flex;background:var(--sub);border:1px solid var(--brd);border-radius:9px;padding:2px;gap:2px}
.theme-seg button{display:flex;align-items:center;justify-content:center;width:30px;height:26px;border:none;border-radius:7px;background:transparent;color:var(--lt);cursor:pointer;transition:all .15s}
.theme-seg button:hover{color:var(--ink)}
.theme-seg button.on{background:var(--w);color:var(--b);box-shadow:var(--sh)}
textarea.inp{line-height:1.5}
.iconbtn{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:8px;border:1px solid transparent;background:transparent;color:var(--lt);cursor:pointer;transition:all .15s;flex-shrink:0}
.iconbtn:hover{background:var(--hov);color:var(--ink);border-color:var(--brd)}
.topbar{height:56px;display:flex;align-items:center;gap:12px;padding:0 20px;background:${THEME_MODE==="dark"?S.bg:S.w}cc;backdrop-filter:saturate(1.4) blur(8px);-webkit-backdrop-filter:saturate(1.4) blur(8px);border-bottom:1px solid var(--brd);flex-shrink:0;position:relative;z-index:5}
`;
const Ic=({n,s=15,c="currentColor"})=>{
  const d={
    arr:<><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>,
    arrL:<><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></>,
    chk:<polyline points="20 6 9 17 4 12" strokeWidth="2.5"/>,
    chR:<polyline points="9 18 15 12 9 6"/>,chL:<polyline points="15 18 9 12 15 6"/>,chD:<polyline points="6 9 12 15 18 9"/>,
    x:<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    plus:<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    chat:<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
    users:<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    user:<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    cog:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    bar:<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    bot:<><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/></>,
    bell:<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
    send:<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
    search:<><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    logout:<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    bld:<><rect x="2" y="3" width="20" height="18" rx="1"/><line x1="8" y1="21" x2="8" y2="8"/><line x1="16" y1="21" x2="16" y2="8"/><line x1="2" y1="12" x2="22" y2="12"/></>,
    zap:<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
    star:<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="currentColor"/>,
    shld:<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
    eye:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    edit:<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:<><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>,
    copy:<><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
    clip:<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" strokeWidth="2" fill="none"/>,
    ticket:<path d="M3 9a2 2 0 0 0 2-2V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v1a2 2 0 0 0 0 4v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1a2 2 0 0 0-2-2z"/>,
    plan:<><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></>,
    book:<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>,
    wiz:<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>,
    hand:<><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></>,
    mail:<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    lock:<><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    globe:<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    rocket:<><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/></>,
    trend:<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>,
    tag:<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>,
    pct:<><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    cal:<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    warn:<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    q:<><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    clip:<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>,
    hdst:<><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/><path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></>,
    rep:<><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></>,
    reload:<><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>,
    more:<><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></>,
    moon:<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>,
    sun:<><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></>,
    monitor:<><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    chU:<polyline points="18 15 12 9 6 15"/>,
    home:<><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/></>,
    inbox:<><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>,
    expand:<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>,
    compress:<path d="M8 3v5H3M21 8h-5V3M3 16h5v5M16 21v-5h5"/>,
    card:<><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></>,
    phone:<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>,
    link:<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
    sms:<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="9.01" y2="10" strokeWidth="3"/><line x1="12" y1="10" x2="12.01" y2="10" strokeWidth="3"/><line x1="15" y1="10" x2="15.01" y2="10" strokeWidth="3"/></>,
    intg:<><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    key:<path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>,
  };
  return <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{d[n]||null}</svg>;
};
const BUILD = "b261023a";
const LOGO_SRC="data:image/png;base64,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";
const Logo=({sm,light,height})=>{
  const h=height||(sm?28:36);
  const customSrc=(()=>{try{const b=localStorage.getItem("hindle_tenant_brand");if(b){const p=JSON.parse(b);return p.customLogoUrl||null;}}catch(e){}return null;})();
  const src=customSrc||LOGO_SRC;
  return(
    <img
      src={src}
      alt="Hindle Consultants"
      style={{
        height:h,
        width:"auto",
        maxWidth:customSrc?180:undefined,
        display:"block",
        objectFit:"contain",
        filter:(!customSrc&&(light||THEME_MODE==="dark"))?"brightness(0) invert(1)":"none",
        flexShrink:0,
      }}
    />
  );
};
const Av=({name,size=32,col=B})=>{
  const i=(name||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
  return <div style={{width:size,height:size,borderRadius:"50%",background:`${col}18`,border:`1.5px solid ${col}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.36,fontWeight:700,color:col,flexShrink:0}}>{i}</div>;
};
const Chip=({s})=>{
  const m={open:{bg:BL,c:B,bd:BM,l:"Open"},claimed:{bg:G.gb,c:G.g,bd:G.gbd,l:"Claimed"},assigned:{bg:G.pb,c:G.p,bd:G.pbd,l:"Assigned"},resolved:{bg:G.gb,c:G.g,bd:G.gbd,l:"Resolved"},finished:{bg:S.sub,c:T.mid,bd:S.brd,l:"Finished"},parked:{bg:G.ab,c:G.a,bd:G.abd,l:"Parked"},handoff:{bg:G.ab,c:THEME_MODE==="dark"?"#FB923C":"#C2410C",bd:G.abd,l:"Handoff"},online:{bg:G.gb,c:G.g,bd:G.gbd,l:"Online"},offline:{bg:S.bg,c:T.fnt,bd:S.brd,l:"Offline"},active:{bg:G.gb,c:G.g,bd:G.gbd,l:"Active"},inactive:{bg:G.rb,c:G.r,bd:G.rbd,l:"Inactive"},trial:{bg:G.ab,c:G.a,bd:G.abd,l:"Trial"},expired:{bg:G.rb,c:G.r,bd:G.rbd,l:"Expired"},paid:{bg:G.gb,c:G.g,bd:G.gbd,l:"Paid"},free:{bg:S.bg,c:T.lt,bd:S.brd,l:"Free"},starter:{bg:BL,c:B,bd:BM,l:"Starter"},professional:{bg:G.pb,c:G.p,bd:G.pbd,l:"Pro"},clicked:{bg:G.gb,c:G.g,bd:G.gbd,l:"Clicked"},pending:{bg:G.ab,c:G.a,bd:G.abd,l:"Pending"},expired_link:{bg:S.bg,c:T.fnt,bd:S.brd,l:"Expired"}};
  const t=m[s]||m.open;
  return <span className="badge" style={{background:t.bg,color:t.c,border:`1px solid ${t.bd}`}}><span style={{width:5,height:5,borderRadius:"50%",background:t.c,display:"inline-block",animation:s==="open"||s==="trial"||s==="pending"?"blink 2s infinite":"none"}}/>{t.l}</span>;
};
const Metric=({label,value,sub,icon,col=B,d=0})=>(
  <div className="card au" style={{padding:18,animationDelay:`${d}s`}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
      <div><p style={{fontSize:10.5,fontWeight:600,color:T.fnt,textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>{label}</p>
        <p style={{fontSize:24,fontWeight:800,letterSpacing:-.5,lineHeight:1}}>{value}</p>
        {sub&&<p style={{fontSize:11,color:sub.u?G.g:sub.d?G.r:T.lt,marginTop:4}}>{sub.u?"▲":sub.d?"▼":""} {sub.t}</p>}
      </div>
      <div style={{width:36,height:36,borderRadius:9,background:`${col}12`,border:`1px solid ${col}25`,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n={icon} s={16} c={col}/></div>
    </div>
  </div>
);
const GreenCheck=({size=22})=>(<div style={{width:size,height:size,borderRadius:"50%",background:G.g,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,animation:"pop .3s ease"}}><Ic n="chk" s={size*.5} c="#fff"/></div>);
const Toggle=({options,value,onChange})=>(
  <div className="toggle">{options.map(o=><button key={o.value} className={`toggle-opt${value===o.value?" active":""}`} onClick={()=>onChange(o.value)}>{o.label}</button>)}</div>
);
// CsBadge removed
const Spinner=({size=13})=>(<div style={{width:size,height:size,border:"2px solid rgba(255,255,255,.35)",borderTop:"2px solid #fff",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>);
// ══════════════════════════════════════════════════════════════
// LANDING PAGE
// ══════════════════════════════════════════════════════════════
const Landing=({go,config,pricingLoaded=true})=>{
  const [currency,setCurrency]=useState("USD");
  const [billing,setBilling]=useState("monthly");
  const [promoInput,setPromoInput]=useState("");
  const [appliedPromo,setAppliedPromo]=useState(null);
  const [promoStatus,setPromoStatus]=useState(null); // "ok" | "err" | null
  const [promoMsg,setPromoMsg]=useState("");
  const cur=config.currencies[currency];
  const proSavings=roundTo5(roundTo5(config.plans.professional.usd*cur.rate)*(config.annualDiscountPct/100)*12);
  const validatePromo=()=>{
    const code=promoInput.trim().toUpperCase();
    if(!code){setPromoStatus("err");setPromoMsg("Please enter a promo code");return;}
    const found=config.promoCodes.find(p=>p.code===code&&p.active&&p.used<p.limit&&new Date(p.expiry)>new Date());
    if(found){setAppliedPromo(found);setPromoStatus("ok");setPromoMsg(`${found.pct}% discount applied — ${found.desc}`);}
    else{setAppliedPromo(null);setPromoStatus("err");setPromoMsg("Invalid, expired or unrecognised code");}
  };
  const clearPromo=()=>{setAppliedPromo(null);setPromoStatus(null);setPromoMsg("");setPromoInput("");};
  return(
  <div style={{minHeight:"100vh",background:S.w,overflowY:"auto"}}>
    <nav style={{position:"sticky",top:0,zIndex:50,background:"rgba(255,255,255,.96)",backdropFilter:"blur(10px)",borderBottom:`1px solid ${S.brd}`}}>
      <div style={{maxWidth:1140,margin:"0 auto",padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:60}}>
        <Logo sm/>
        <div style={{display:"flex",gap:28}}>{[["Features","features"],["Pricing","pricing"],["Help","help"]].map(([l,id])=><span key={l} onClick={()=>document.getElementById(id)?.scrollIntoView({behavior:"smooth"})} style={{fontSize:13.5,fontWeight:500,color:T.mid,cursor:"pointer"}} onMouseEnter={e=>e.target.style.color=B} onMouseLeave={e=>e.target.style.color=T.mid}>{l}</span>)}</div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn bo sm" onClick={()=>go("signin")}>Sign In</button>
          <button className="btn bp sm" onClick={()=>go("purchase")}>Start Free <Ic n="arr" s={12} c="#fff"/></button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section style={{maxWidth:720,margin:"0 auto",padding:"68px 24px 52px",textAlign:"center"}}>
      <div className="au" style={{display:"inline-flex",alignItems:"center",gap:7,background:BL,border:`1px solid ${BM}`,borderRadius:40,padding:"5px 14px",fontSize:12.5,color:B,fontWeight:600,marginBottom:24}}>
        <span style={{width:6,height:6,borderRadius:"50%",background:B,animation:"blink 2s infinite",display:"inline-block"}}/>14-Day Free Trial — No Credit Card Required
      </div>
      <h1 className="au" style={{fontSize:46,fontWeight:800,lineHeight:1.08,letterSpacing:-1.5,color:T.ink,marginBottom:18,animationDelay:".07s"}}>Customer Support Automation<br/><span style={{color:B}}>Made Simple</span></h1>
      <p className="au" style={{fontSize:16,color:T.lt,lineHeight:1.75,marginBottom:34,animationDelay:".14s"}}>Deploy AI chatbots, route handoffs to your team via SMS magic links, and manage everything from one powerful dashboard.</p>
      <div className="au" style={{display:"flex",gap:10,justifyContent:"center",marginBottom:10,animationDelay:".2s"}}>
        <button className="btn bp lg" onClick={()=>go("purchase")}>Start For Free <Ic n="arr" s={15} c="#fff"/></button>
        <button className="btn bo lg" onClick={()=>go("signin")}>Sign In</button>
      </div>
      <p style={{fontSize:12,color:T.fnt}}>14-day free trial · No credit card required · Cancel anytime</p>
    </section>

    {/* Features */}
    <section id="features" style={{background:S.bg,borderTop:`1px solid ${S.brd}`,borderBottom:`1px solid ${S.brd}`,padding:"52px 24px"}}>
      <div style={{maxWidth:1040,margin:"0 auto"}}>
        <h2 style={{fontSize:28,fontWeight:800,textAlign:"center",marginBottom:32,letterSpacing:-.5}}>Everything your business needs</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
          {[["🤖","AI-Powered Responses","Handle 80% of inquiries automatically with chatbots trained on your knowledge base."],["📱","SMS Magic Links","When a visitor requests a human, offline agents get an instant SMS with a magic link. Tap to join the live chat immediately."],["📊","Powerful Analytics","Real-time CSAT, response times, resolution rates and agent performance."],["🏢","Multi-Tenant Ready","Manage multiple organisations from one super-admin dashboard."],["⚡","One-Line Widget","Paste one snippet. Fully branded chat widget, live in minutes."],["🔒","Enterprise Security","SOC 2 compliant. Stripe payments. Full data isolation per tenant."]].map(([e,t,d],i)=>(
            <div key={i} className="card" style={{padding:22}}><div style={{fontSize:26,marginBottom:12}}>{e}</div><h3 style={{fontSize:14,fontWeight:700,marginBottom:6}}>{t}</h3><p style={{fontSize:13,color:T.lt,lineHeight:1.6}}>{d}</p></div>
          ))}
        </div>
      </div>
    </section>

    {/* Pricing */}
    <section id="pricing" style={{padding:"60px 24px"}}>
      <div style={{maxWidth:1140,margin:"0 auto"}}>
        <h2 style={{fontSize:28,fontWeight:800,textAlign:"center",marginBottom:4,letterSpacing:-.5}}>Simple, Transparent Pricing</h2>
        <p style={{fontSize:14,color:T.lt,textAlign:"center",marginBottom:28}}>Start free. Scale as you grow. Cancel anytime.</p>

        {/* Controls row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:14,marginBottom:36,flexWrap:"wrap"}}>
          {/* Currency picker */}
          <div style={{display:"flex",alignItems:"center",gap:7,background:S.w,border:`1px solid ${S.brd}`,borderRadius:8,padding:"5px 10px 5px 8px"}}>
            <Ic n="globe" s={14} c={T.fnt}/>
            <select value={currency} onChange={e=>setCurrency(e.target.value)} style={{border:"none",outline:"none",fontSize:13,fontWeight:600,color:T.ink,background:"transparent",cursor:"pointer",fontFamily:"inherit"}}>
              {Object.entries(config.currencies).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {/* Billing toggle */}
          <div style={{display:"flex",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,padding:3,gap:2}}>
            {[["monthly","Monthly"],["annual",`Annual — save ${config.annualDiscountPct}%`]].map(([val,lbl])=>(
              <button key={val} onClick={()=>setBilling(val)} style={{padding:"6px 16px",borderRadius:6,fontSize:13,fontWeight:600,cursor:"pointer",background:billing===val?S.w:"transparent",color:billing===val?T.ink:T.fnt,border:billing===val?`1px solid ${S.brd}`:"1px solid transparent",boxShadow:billing===val?"0 1px 3px rgba(0,0,0,.06)":"none",transition:"all .15s",whiteSpace:"nowrap"}}>{lbl}</button>
            ))}
          </div>

          {/* Annual savings pill */}
          {billing==="annual"&&(
            <div style={{background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:20,padding:"4px 12px",fontSize:12.5,fontWeight:700,color:G.g,display:"flex",alignItems:"center",gap:5}}>
              <Ic n="trend" s={12} c={G.g}/>Save up to {cur.symbol}{proSavings}/yr on Professional
            </div>
          )}
        </div>

        {/* Promo code row */}
        <div style={{display:"flex",justifyContent:"center",marginBottom:32}}>
          {appliedPromo?(
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:10}}>
              <Ic n="tag" s={14} c={G.g}/>
              <div>
                <p style={{fontSize:13,fontWeight:700,color:G.g}}>{appliedPromo.code} — {appliedPromo.pct}% off all plans</p>
                <p style={{fontSize:11.5,color:T.lt}}>{appliedPromo.desc}</p>
              </div>
              <button onClick={clearPromo} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:"2px 4px",display:"flex",marginLeft:6}}><Ic n="x" s={13}/></button>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{position:"relative"}}>
                  <div style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",display:"flex"}}><Ic n="tag" s={13} c={T.fnt}/></div>                  <input
                    className={`inp${promoStatus==="err"?" inp-err":promoStatus==="ok"?" inp-ok":""}`}
                    placeholder="Have a promo code?"
                    value={promoInput}
                    onChange={e=>{setPromoInput(e.target.value);setPromoStatus(null);setPromoMsg("");}}
                    onKeyDown={e=>e.key==="Enter"&&validatePromo()}
                    style={{fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:1,paddingLeft:32,width:220}}
                  />
                </div>
                <button className="btn bp sm" onClick={validatePromo}>Apply</button>
              </div>
              {promoMsg&&<p style={{fontSize:12,color:promoStatus==="ok"?G.g:G.r,fontWeight:500}}>{promoMsg}</p>}
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,position:"relative"}}>
          {/* Skeleton shimmer overlay while live pricing loads */}
          {!pricingLoaded&&(
            <div style={{position:"absolute",inset:0,zIndex:10,borderRadius:14,background:S.w+"dd",display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
              {[0,1,2,3].map(i=>(
                <div key={i} style={{background:S.w,border:`1px solid ${S.brd}`,borderRadius:14,padding:22,overflow:"hidden",position:"relative"}}>
                  <div style={{background:`linear-gradient(90deg,${S.bg} 25%,${S.brd} 50%,${S.bg} 75%)`,backgroundSize:"200% 100%",animation:"shimmer 1.2s infinite",borderRadius:6,height:18,marginBottom:10,width:"60%"}}/>
                  <div style={{background:`linear-gradient(90deg,${S.bg} 25%,${S.brd} 50%,${S.bg} 75%)`,backgroundSize:"200% 100%",animation:"shimmer 1.2s infinite",borderRadius:6,height:40,marginBottom:16,width:"80%"}}/>
                  {[1,2,3,4].map(j=>(
                    <div key={j} style={{background:`linear-gradient(90deg,${S.bg} 25%,${S.brd} 50%,${S.bg} 75%)`,backgroundSize:"200% 100%",animation:"shimmer 1.2s infinite",borderRadius:4,height:12,marginBottom:8,width:`${70+j*5}%`}}/>
                  ))}
                </div>
              ))}
            </div>
          )}
          {Object.entries(config.plans).map(([id,plan])=>{
            const p=getPrice(id,currency,billing,appliedPromo?.code,config);
            const isPro=id==="professional",isEnt=id==="enterprise";
            const feats=(()=>{
              try{
                const pf=config.planFeatures;
                const base=(pf&&Array.isArray(pf[id])&&pf[id].length)?pf[id]:PLAN_FEATURES[id]||[];
                // Strip hardcoded seat/convo/kb rows and prepend live-limits versions
                const stripped=base.filter(([l])=>!/agent seat|conversations.*month|knowledge base|no knowledge/i.test(l));
                const limitRows=getLimitFeatures(id,config.planLimits||config._superConfig?.planLimits);
                return [...limitRows,...stripped];
              }catch(e){return PLAN_FEATURES[id]||[];}
            })();
            const displayPrice=billing==="annual"?p.annualMonthly:p.monthly;
            const finalPrice=p.final;
            const hasDiscount=p.promo&&plan.usd>0;
            return(
              <div key={id} style={{background:isEnt?"#0f172a":S.w,borderRadius:14,padding:22,border:isPro?`2px solid ${B}`:isEnt?`2px solid #334155`:`1px solid ${S.brd}`,position:"relative",boxShadow:isPro?`0 8px 30px ${B}15`:isEnt?"0 8px 30px rgba(0,0,0,.25)":"none",display:"flex",flexDirection:"column",transition:"box-shadow .2s"}}>
                {isPro&&<div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:B,color:"#fff",fontSize:10,fontWeight:700,padding:"4px 14px",borderRadius:20,whiteSpace:"nowrap"}}>MOST POPULAR</div>}
                {isEnt&&<div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:"#7c3aed",color:"#fff",fontSize:10,fontWeight:700,padding:"4px 14px",borderRadius:20,whiteSpace:"nowrap"}}>ENTERPRISE</div>}
                <h3 style={{fontSize:16,fontWeight:700,marginBottom:2,color:isEnt?"#f1f5f9":T.ink}}>{plan.name}</h3>
                <p style={{fontSize:11.5,color:isEnt?T.lt:T.fnt,marginBottom:14}}>{plan.desc}</p>

                {/* Price block */}
                <div style={{marginBottom:14,minHeight:56}}>
                  {isEnt?(
                    <div>
                      <p style={{fontSize:26,fontWeight:800,color:"#f1f5f9",letterSpacing:-.5}}>Custom</p>
                      <p style={{fontSize:11,color:T.lt,marginTop:2}}>Volume-based pricing</p>
                    </div>
                  ):plan.usd===0?(
                    <div>
                      <p style={{fontSize:30,fontWeight:800,letterSpacing:-1,color:T.ink}}>Free</p>
                      <p style={{fontSize:11,color:T.fnt,marginTop:2}}>Forever free</p>
                    </div>
                  ):(
                    <div>
                      {/* strikethrough: annual original OR promo original */}
                      {(billing==="annual"||hasDiscount)&&(
                        <p style={{fontSize:11,color:T.fnt,textDecoration:"line-through",marginBottom:1}}>
                          {cur.symbol}{billing==="annual"?p.monthly:displayPrice}/mo
                        </p>
                      )}
                      <p style={{fontSize:30,fontWeight:800,letterSpacing:-1,color:hasDiscount?G.g:isPro?B:T.ink}}>
                        {cur.symbol}{hasDiscount?finalPrice:(billing==="annual"?p.annualMonthly:p.monthly)}
                        <span style={{fontSize:13,fontWeight:400,color:T.fnt}}>/mo</span>
                      </p>
                      {hasDiscount&&(
                        <div style={{display:"inline-flex",alignItems:"center",gap:4,background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:12,padding:"2px 8px",marginTop:3}}>
                          <Ic n="tag" s={10} c={G.g}/><span style={{fontSize:11,fontWeight:700,color:G.g}}>{p.promo.pct}% off with {p.promo.code}</span>
                        </div>
                      )}
                      {!hasDiscount&&(
                        <p style={{fontSize:11,color:billing==="annual"?G.g:T.fnt,fontWeight:billing==="annual"?600:400,marginTop:2}}>
                          {billing==="annual"?`Billed ${cur.symbol}${p.annualTotal}/yr · Save ${cur.symbol}${p.saving}`:`or ${cur.symbol}${p.annualMonthly}/mo billed annually`}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* SMS row */}
                <div style={{padding:"7px 9px",borderRadius:7,background:plan.smsAlerts?(isEnt?"rgba(22,163,74,.15)":G.gb):(plan.usd===0?G.ab:(isEnt?"rgba(220,38,38,.1)":S.bg)),border:`1px solid ${plan.smsAlerts?(isEnt?"rgba(22,163,74,.3)":G.gbd):(plan.usd===0?G.abd:(isEnt?"rgba(220,38,38,.2)":S.brd))}`,display:"flex",gap:6,alignItems:"center",marginBottom:14}}>
                  <Ic n="sms" s={13} c={plan.smsAlerts?G.g:plan.usd===0?G.a:T.fnt}/>
                  <span style={{fontSize:11.5,fontWeight:600,color:plan.smsAlerts?(isEnt?"#86efac":G.g):plan.usd===0?G.a:T.fnt}}>{plan.smsAlerts?plan.smsLimit:plan.smsNote}</span>
                </div>

                {/* Feature list — only show enabled features for this plan */}
                <div style={{flex:1,marginBottom:16}}>
                  {feats.filter(([,inc])=>inc).map(([label])=>(
                    <div key={label} style={{display:"flex",gap:7,alignItems:"center",marginBottom:6}}>
                      <div style={{width:15,height:15,borderRadius:"50%",background:isEnt?"rgba(22,163,74,.2)":BL,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <Ic n="chk" s={8} c={isEnt?G.g:B}/>
                      </div>
                      <span style={{fontSize:11.5,color:isEnt?"#cbd5e1":T.mid}}>{label}</span>
                    </div>
                  ))}
                </div>

                <button className={`btn ${isEnt?"sm":isPro?"bp":"bo"}`} style={{width:"100%",background:isEnt?"#7c3aed":undefined,color:isEnt?"#fff":undefined,borderColor:isEnt?"#7c3aed":undefined}} onClick={()=>isEnt?openSalesChat():go("purchase",id,appliedPromo?.code)}>
                  {isEnt?"Contact Sales →":plan.usd===0?"Get Started Free":"Start Free Trial"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Currency note */}
        <p style={{fontSize:12,color:T.fnt,textAlign:"center",marginTop:18}}>
          Prices shown in <strong>{config.currencies[currency].label}</strong>. Switch currency above to compare. All plans include a 14-day free trial.
        </p>
      </div>
    </section>

    {/* Help / FAQ */}
    <section id="help" style={{background:S.bg,borderTop:`1px solid ${S.brd}`,padding:"60px 24px"}}>
      <div style={{maxWidth:760,margin:"0 auto"}}>
        <h2 style={{fontSize:28,fontWeight:800,textAlign:"center",marginBottom:6,letterSpacing:-.5}}>How It Works</h2>
        <p style={{fontSize:14,color:T.lt,textAlign:"center",marginBottom:40}}>Everything you need to get live in minutes.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:36}}>
          {[
            ["🚀","Deploy in one line","Paste a single script tag on your website. No coding required. The chat widget appears instantly, fully branded to your colours."],
            ["🤖","AI handles up to 80%","Train the chatbot on your own documentation, FAQs, and product knowledge. It answers common questions 24/7 without involving your team."],
            ["📱","Human handoff via SMS","When a visitor needs a human, agents get an instant SMS with a magic link. One tap opens the live conversation — no login needed."],
            ["📊","Track everything","Real-time dashboards show conversation volume, AI resolution rates, agent performance, CSAT scores, and handoff statistics."],
            ["💳","No long-term contracts","Start with a 14-day free trial on any paid plan. Cancel or downgrade anytime. Upgrade in seconds as your team grows."],
            ["🔒","Enterprise-grade security","Data isolated per tenant. Stripe-powered payments. Role-based access controls. 2FA enforced. SOC 2 compliant infrastructure."],
          ].map(([icon,title,desc],i)=>(
            <div key={i} style={{display:"flex",gap:14,padding:18,background:S.w,borderRadius:12,border:`1px solid ${S.brd}`}}>
              <div style={{fontSize:24,flexShrink:0,marginTop:2}}>{icon}</div>
              <div><p style={{fontSize:13.5,fontWeight:700,marginBottom:5}}>{title}</p><p style={{fontSize:13,color:T.lt,lineHeight:1.65}}>{desc}</p></div>
            </div>
          ))}
        </div>
        <h3 style={{fontSize:20,fontWeight:800,marginBottom:18,letterSpacing:-.3}}>Frequently Asked Questions</h3>
        {[
          ["Do I need a credit card to start?","No. Your 14-day free trial starts immediately with no payment details required. You only add a card when you're ready to upgrade."],
          ["How do I install the chat widget?","Paste one line of HTML before the closing </body> tag of your website. That's it. Full guides for WordPress, Shopify, Webflow, and React are available in your dashboard."],
          ["Can I train the AI on my own content?","Yes. Upload PDFs, Word docs, plain text, JSON, and XML files — or import any web URL. The AI indexes your content and uses it exclusively to answer visitor questions."],
          ["What happens when the AI can't answer?","The bot politely acknowledges it can't help and offers to connect the visitor with a human agent. You control the exact wording and threshold."],
          ["How does SMS agent handoff work?","When a visitor requests a human, offline agents receive an SMS via your messaging provider containing the visitor's name, the page they're on, and a one-tap magic link to join the live chat — no login required. Links expire after 3 minutes."],
          ["Can I have multiple agents?","Starter includes 5 agent seats. Professional includes 10. Enterprise is unlimited. Each agent gets their own login, performance stats, and can be assigned conversations."],
          ["What currencies do you support?","USD, GBP, EUR, CAD, AUD, and ZAR. You can switch currency at any point on the pricing page."],
          ["Is there an API?","Yes. Professional and Enterprise plans include full API access with webhooks for conversation events, handoffs, and CSAT responses."],
        ].map(([q,a],i)=>(
          <details key={i} style={{borderBottom:`1px solid ${S.brd}`,paddingBottom:0}}>
            <summary style={{padding:"14px 0",fontSize:13.5,fontWeight:600,cursor:"pointer",color:T.ink,listStyle:"none",display:"flex",justifyContent:"space-between",alignItems:"center",userSelect:"none"}}>
              {q}<span style={{fontSize:18,color:T.fnt,fontWeight:400,flexShrink:0,marginLeft:12}}>+</span>
            </summary>
            <p style={{fontSize:13.5,color:T.lt,lineHeight:1.7,paddingBottom:16,marginTop:-4}}>{a}</p>
          </details>
        ))}
        <div style={{marginTop:36,padding:28,background:B,borderRadius:14,textAlign:"center"}}>
          <p style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:8}}>Ready to get started?</p>
          <p style={{fontSize:14,color:"rgba(255,255,255,.75)",marginBottom:20}}>Join hundreds of businesses using Hindle to automate customer support.</p>
          <button className="btn" style={{background:"#fff",color:"#2563EB",fontWeight:700,fontSize:14,padding:"11px 28px",borderRadius:8,border:"none",cursor:"pointer"}} onClick={()=>go("purchase")}>Start Free Trial <Ic n="arr" s={14} c={B}/></button>
        </div>
      </div>
    </section>

    <footer style={{background:T.ink,padding:"28px 24px"}}>
      <div style={{maxWidth:1140,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <Logo sm light/>
        <div style={{display:"flex",gap:20}}>{[["Features","features"],["Pricing","pricing"],["Help","help"]].map(([l,id])=><span key={l} onClick={()=>document.getElementById(id)?.scrollIntoView({behavior:"smooth"})} style={{fontSize:12,color:"rgba(255,255,255,.5)",cursor:"pointer"}} onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.5)"}>{l}</span>)}</div>
        <p style={{fontSize:12,color:"rgba(255,255,255,.4)"}}>© 2026 Hindle Consultants Ltd</p>
      </div>
    </footer>
  </div>
  );
};
// ══════════════════════════════════════════════════════════════
// PURCHASE WIZARD
// ══════════════════════════════════════════════════════════════
const PurchaseWizard=({go,initPlan,initPromo,config})=>{
  // Detect existing account from localStorage
  const existingAccount=(()=>{try{const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return c.email?{email:c.email,name:c.name||"",orgId:c.orgId||c.tenantId||null}:null;}catch(e){return null;}})();
  const isExisting=!!existingAccount;

  const [step,setStep]=useState(0);
  const [planId,setPlanId]=useState(initPlan||"starter");
  const [currency,setCurrency]=useState("USD");
  const [billing,setBilling]=useState("monthly");
  const [promoInput,setPromoInput]=useState(initPromo||"");
  const [appliedPromo,setAppliedPromo]=useState(()=>{
    if(!initPromo) return null;
    const found=config.promoCodes.find(p=>p.code===initPromo&&p.active&&p.used<p.limit&&new Date(p.expiry)>new Date());
    return found||null;
  });
  const [promoStatus,setPromoStatus]=useState(()=>initPromo?("ok"):null);
  const [promoMsg,setPromoMsg]=useState(()=>{
    if(!initPromo) return "";
    const found=config.promoCodes.find(p=>p.code===initPromo&&p.active&&p.used<p.limit&&new Date(p.expiry)>new Date());
    return found?`${found.pct}% discount applied — ${found.desc}`:"";
  });
  const [skipTrial,setSkipTrial]=useState(isExisting); // existing clients skip trial
  const [details,setDetails]=useState({
    company:"",
    name:existingAccount?.name||"",
    email:existingAccount?.email||"",
    password:""
  });
  const [processing,setProcessing]=useState(false);

  const sel=config.plans[planId];
  const isFree=sel.usd===0||sel.usd===null;
  const pricing=getPrice(planId,currency,billing,appliedPromo?.code,config);
  const cur=config.currencies[currency];

  // For existing accounts: steps are "Choose Plan" → "Pay & Upgrade"
  // For new accounts: steps are "Choose Plan" → "Your Details" → "Pay & Start" → "All Done!"
  const steps=isExisting
    ?["Choose Plan","Pay & Upgrade","Done!"]
    :["Choose Plan","Your Details",isFree||!skipTrial?"Confirm":"Pay & Start","All Done!"];
  const showPayment=isExisting?step===1:step===2&&!isFree&&skipTrial;
  const showConfirm=(isExisting&&step===2)||(!isExisting&&((step===2&&(isFree||!skipTrial))||step===3));

  const validatePromo=()=>{
    const code=promoInput.trim().toUpperCase();
    if(!code){setPromoStatus("err");setPromoMsg("Please enter a promo code");return;}
    const found=config.promoCodes.find(p=>p.code===code&&p.active&&p.plans.includes(planId)&&p.used<p.limit&&new Date(p.expiry)>new Date());
    if(found){setAppliedPromo(found);setPromoStatus("ok");setPromoMsg(`${found.pct}% discount applied — ${found.desc}`);}
    else{setAppliedPromo(null);setPromoStatus("err");setPromoMsg("Invalid, expired or inapplicable code");}
  };

  return(
    <div style={{minHeight:"100vh",background:S.bg,display:"flex",flexDirection:"column"}}>
      <div style={{background:S.w,borderBottom:`1px solid ${S.brd}`,padding:"0 28px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <Logo sm/>
        <div style={{flex:1,maxWidth:420,margin:"0 32px"}}>
          <div style={{display:"flex",gap:3,marginBottom:5}}>{steps.map((_,i)=><div key={i} style={{flex:1,height:4,borderRadius:2,background:i<=step?B:S.brd,transition:"background .4s"}}/>)}</div>
          <div style={{display:"flex",justifyContent:"space-between"}}>{steps.map((l,i)=><span key={i} style={{fontSize:10,fontWeight:i===step?700:400,color:i<=step?B:T.fnt}}>{l}</span>)}</div>
        </div>
        <button className="btn bg sm" onClick={()=>go("landing")}><Ic n="arrL" s={12}/> Home</button>
      </div>
      <div style={{flex:1,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"36px 24px",overflowY:"auto"}}>
        <div style={{width:"100%",maxWidth:step===0?940:530}}>

          {step===0&&(
            <div className="au">
              <h2 style={{fontSize:26,fontWeight:800,letterSpacing:-.5,marginBottom:4,textAlign:"center"}}>Choose your plan</h2>
              <p style={{fontSize:14,color:T.lt,textAlign:"center",marginBottom:22}}>14-day free trial · No credit card required during trial</p>
              <div style={{display:"flex",justifyContent:"center",gap:14,marginBottom:26,flexWrap:"wrap",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}><Ic n="globe" s={14} c={T.lt}/>
                  <select className="inp" style={{width:"auto",fontSize:12.5,padding:"6px 10px"}} value={currency} onChange={e=>setCurrency(e.target.value)}>
                    {Object.entries(config.currencies).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <Toggle options={[{value:"monthly",label:"Monthly"},{value:"annual",label:`Annual — ${config.annualDiscountPct}% off`}]} value={billing} onChange={setBilling}/>
                {billing==="annual"&&<span style={{fontSize:11.5,background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,borderRadius:20,padding:"3px 10px",fontWeight:600}}>Save up to {cur.symbol}{(config.plans.professional.usd*cur.rate*12*config.annualDiscountPct/100).toFixed(0)}/yr</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
                {Object.entries(config.plans).map(([id,plan])=>{
                  const p=getPrice(id,currency,billing,appliedPromo?.code,config);
                  const isSel=planId===id,isPro=id==="professional",isEnt=id==="enterprise";
                  const feats=(()=>{
              try{
                const pf=config.planFeatures;
                const base=(pf&&Array.isArray(pf[id])&&pf[id].length)?pf[id]:PLAN_FEATURES[id]||[];
                // Strip hardcoded seat/convo/kb rows and prepend live-limits versions
                const stripped=base.filter(([l])=>!/agent seat|conversations.*month|knowledge base|no knowledge/i.test(l));
                const limitRows=getLimitFeatures(id,config.planLimits||config._superConfig?.planLimits);
                return [...limitRows,...stripped];
              }catch(e){return PLAN_FEATURES[id]||[];}
            })();
                  if(isEnt) return(
                    <div key={id} style={{background:"#0f172a",borderRadius:14,padding:18,cursor:"pointer",border:isSel?`2px solid #7c3aed`:`1px solid #334155`,boxShadow:isSel?"0 8px 30px rgba(124,58,237,.3)":"none",transition:"all .2s",position:"relative",display:"flex",flexDirection:"column"}} onClick={()=>setPlanId(id)}>
                      <div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:"#7c3aed",color:"#fff",fontSize:10,fontWeight:700,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>ENTERPRISE</div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                        <div><h3 style={{fontSize:14,fontWeight:700,color:"#f1f5f9"}}>{plan.name}</h3><p style={{fontSize:10.5,color:T.lt}}>{plan.desc}</p></div>
                        <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${isSel?"#7c3aed":"#334155"}`,background:isSel?"#7c3aed":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{isSel&&<Ic n="chk" s={8} c="#fff"/>}</div>
                      </div>
                      <div style={{marginBottom:8}}><p style={{fontSize:18,fontWeight:800,color:"#f1f5f9",letterSpacing:-.5}}>Custom</p><p style={{fontSize:10,color:T.lt}}>Volume pricing</p></div>
                      <div style={{padding:"5px 7px",borderRadius:6,background:"rgba(22,163,74,.15)",border:"1px solid rgba(22,163,74,.3)",display:"flex",gap:5,alignItems:"center",marginBottom:8}}>
                        <Ic n="sms" s={11} c={G.g}/><span style={{fontSize:10.5,fontWeight:600,color:"#86efac"}}>All agents</span>
                      </div>
                      <div style={{flex:1,marginBottom:10}}>
                        {feats.filter(([,inc])=>inc).map(([label])=>(
                          <div key={label} style={{display:"flex",gap:5,alignItems:"center",marginBottom:4}}>
                            <div style={{width:12,height:12,borderRadius:"50%",background:"rgba(22,163,74,.2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              <Ic n="chk" s={7} c={G.g}/>
                            </div>
                            <span style={{fontSize:10.5,color:"#cbd5e1"}}>{label}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={e=>{e.stopPropagation();openSalesChat();}} style={{width:"100%",background:isSel?"#7c3aed":"rgba(124,58,237,.3)",color:"#fff",border:`1px solid #7c3aed`,borderRadius:7,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Contact Sales →</button>
                    </div>
                  );
                  return(
                    <div key={id} onClick={()=>{setPlanId(id);setAppliedPromo(null);setPromoStatus(null);setPromoInput("");}} style={{background:S.w,borderRadius:14,padding:18,cursor:"pointer",border:isSel?`2px solid ${B}`:`1px solid ${S.brd}`,boxShadow:isSel?`0 8px 30px ${B}18`:"none",transition:"all .2s",position:"relative",display:"flex",flexDirection:"column"}}>
                      {isPro&&<div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:B,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>MOST POPULAR</div>}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                        <div><h3 style={{fontSize:14,fontWeight:700}}>{plan.name}</h3><p style={{fontSize:10.5,color:T.fnt}}>{plan.desc}</p></div>
                        <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${isSel?B:S.brd}`,background:isSel?B:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{isSel&&<Ic n="chk" s={8} c="#fff"/>}</div>
                      </div>
                      {plan.usd===0?(
                        <div style={{marginBottom:8}}><p style={{fontSize:22,fontWeight:800,letterSpacing:-1}}>Free</p></div>
                      ):billing==="annual"?(
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:10,color:T.fnt,textDecoration:"line-through"}}>{cur.symbol}{p.monthly}/mo</div>
                          <div style={{fontSize:22,fontWeight:800,letterSpacing:-1}}>{cur.symbol}{p.annualMonthly}<span style={{fontSize:10,fontWeight:400,color:T.fnt}}>/mo</span></div>
                          <div style={{fontSize:10,color:G.g,fontWeight:600}}>Save {cur.symbol}{p.saving}/yr</div>
                        </div>
                      ):(
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:22,fontWeight:800,letterSpacing:-1}}>{cur.symbol}{p.monthly}<span style={{fontSize:10,fontWeight:400,color:T.fnt}}>/mo</span></div>
                          <div style={{fontSize:10,color:T.fnt}}>{cur.symbol}{p.annualMonthly}/mo annual</div>
                        </div>
                      )}
                      <div style={{padding:"5px 7px",borderRadius:6,background:plan.smsAlerts?G.gb:G.rb,border:`1px solid ${plan.smsAlerts?G.gbd:G.rbd}`,display:"flex",gap:5,alignItems:"center",marginBottom:8}}>
                        <Ic n="sms" s={11} c={plan.smsAlerts?G.g:G.r}/><span style={{fontSize:10.5,fontWeight:600,color:plan.smsAlerts?G.g:G.r}}>{plan.smsAlerts?plan.smsLimit:plan.smsNote}</span>
                      </div>
                      <div style={{flex:1,marginBottom:10}}>
                        {feats.filter(([,inc])=>inc).map(([label])=>(
                          <div key={label} style={{display:"flex",gap:5,alignItems:"center",marginBottom:4}}>
                            <div style={{width:12,height:12,borderRadius:"50%",background:BL,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              <Ic n="chk" s={7} c={B}/>
                            </div>
                            <span style={{fontSize:10.5,color:T.mid}}>{label}</span>
                          </div>
                        ))}
                      </div>
                      <button className={`btn ${isSel?"bp":"bo"}`} style={{width:"100%",fontSize:12}}>{plan.usd===0?"Get Started Free":isSel?"✓ Selected":"Choose Plan"}</button>
                    </div>
                  );
                })}
              </div>
              {!isFree&&(
                <div className="card" style={{padding:14,marginBottom:16}}>
                  <p style={{fontSize:11.5,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.6,marginBottom:8}}>Promo Code</p>
                  {appliedPromo?(
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7}}>
                      <Ic n="tag" s={13} c={G.g}/>
                      <div style={{flex:1}}><p style={{fontSize:13,fontWeight:700,color:G.g}}>{appliedPromo.code} — {appliedPromo.pct}% off</p><p style={{fontSize:11.5,color:T.lt}}>{appliedPromo.desc}</p></div>
                      <button className="btn bg sm" onClick={()=>{setAppliedPromo(null);setPromoStatus(null);setPromoMsg("");setPromoInput("");}}><Ic n="x" s={11}/></button>
                    </div>
                  ):(
                    <div>
                      <div style={{display:"flex",gap:8,marginBottom:5}}>
                        <input className={`inp${promoStatus==="err"?" inp-err":promoStatus==="ok"?" inp-ok":""}`} placeholder="Enter promo code…" value={promoInput} onChange={e=>{setPromoInput(e.target.value);setPromoStatus(null);}} onKeyDown={e=>e.key==="Enter"&&validatePromo()} style={{fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:1}}/>
                        <button className="btn bp sm" onClick={validatePromo}>Apply</button>
                      </div>
                      {promoMsg&&<p style={{fontSize:12,color:promoStatus==="ok"?G.g:G.r,fontWeight:500}}>{promoMsg}</p>}
                    </div>
                  )}
                </div>
              )}
              {!isFree&&(
                <div className="card" style={{padding:14,marginBottom:20,border:skipTrial?`1.5px solid ${B}`:undefined}}>
                  <label style={{display:"flex",gap:10,cursor:"pointer",alignItems:"flex-start"}}>
                    <input type="checkbox" checked={skipTrial} onChange={e=>setSkipTrial(e.target.checked)} style={{marginTop:3,accentColor:B,width:14,height:14,flexShrink:0}}/>
                    <div>
                      <p style={{fontSize:13.5,fontWeight:600}}>Skip trial — start paid plan immediately</p>
                      <p style={{fontSize:12,color:T.lt,marginTop:1}}>Your card will be charged today. You can cancel anytime.</p>
                      {skipTrial&&<div style={{marginTop:8,padding:"9px 11px",background:BL,border:`1px solid ${BM}`,borderRadius:7}}>
                        <p style={{fontSize:13,fontWeight:700,color:B}}>Charge today: {cur.symbol}{billing==="annual"?(pricing.final*12):pricing.final} {billing==="annual"?"(annual)":"(monthly)"}{pricing.promo?` · ${pricing.promo.pct}% promo applied`:""}</p>
                        <p style={{fontSize:11.5,color:T.lt,marginTop:2}}>Payment processed via Stripe.</p>
                      </div>}
                    </div>
                  </label>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button className="btn bo" onClick={()=>go("landing")}>Cancel</button>
                <button className="btn bp" onClick={()=>isExisting?setStep(1):setStep(1)}>{isExisting?"Proceed to Payment":"Continue with "+sel.name} <Ic n="arr" s={14} c="#fff"/></button>
              </div>
            </div>
          )}

          {step===1&&(
            <div className="au">
              {isExisting?(
                <>
                  <h2 style={{fontSize:24,fontWeight:800,letterSpacing:-.5,marginBottom:4}}>Upgrade your plan</h2>
                  <p style={{fontSize:13,color:T.lt,marginBottom:20}}><strong>{sel.name}</strong> · {currency} · {billing==="annual"?"Annual":"Monthly"}{appliedPromo?` · ${appliedPromo.code} −${appliedPromo.pct}%`:""}</p>
                  <div className="card" style={{padding:24,marginBottom:18}}>
                    <div style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}><span style={{fontSize:13,color:T.fnt}}>Account</span><span style={{fontSize:13.5,fontWeight:600}}>{existingAccount.email}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}><span style={{fontSize:13,color:T.fnt}}>New Plan</span><span style={{fontSize:13.5,fontWeight:600,color:B}}>{sel.name}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}><span style={{fontSize:13,color:T.fnt}}>Billing</span><span style={{fontSize:13.5,fontWeight:600}}>{billing==="annual"?"Annual":"Monthly"}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between",padding:"9px 0"}}><span style={{fontSize:13,color:T.fnt}}>Amount</span><span style={{fontSize:15,fontWeight:800,color:B}}>{pricing.display}</span></div>
                  </div>
                  {appliedPromo&&<div style={{padding:"10px 14px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:8,marginBottom:16,fontSize:12.5,color:G.g,fontWeight:600}}>🎉 {appliedPromo.code} — {appliedPromo.pct}% off applied</div>}
                  <button className="btn bp" style={{width:"100%",padding:"14px"}} onClick={async()=>{
                    setProcessing(true);
                    try{
                      const r=await fetch(API_URL+"/api/stripe/checkout",{method:"POST",headers:{"Content-Type":"application/json"},
                        body:JSON.stringify({planId,billing,email:existingAccount.email,orgId:existingAccount.orgId,promoCode:appliedPromo?.code})});
                      const d=await r.json();
                      if(d.url){window.location.href=d.url;}
                      else alert("Checkout error: "+(d.error||"Unknown"));
                    }catch(e){alert("Error: "+e.message);}
                    setProcessing(false);
                  }} disabled={processing}>
                    {processing?<><Spinner/> Redirecting…</>:<>Pay {pricing.display} via Stripe <Ic n="arr" s={14} c="#fff"/></>}
                  </button>
                  <button className="btn bo" style={{width:"100%",marginTop:8}} onClick={()=>setStep(0)}><Ic n="arrL" s={13}/> Back</button>
                </>
              ):(
              <>
              <h2 style={{fontSize:24,fontWeight:800,letterSpacing:-.5,marginBottom:4}}>Create your account</h2>
              <p style={{fontSize:13,color:T.lt,marginBottom:20}}><strong>{sel.name}</strong> · {currency} · {billing==="annual"?"Annual":"Monthly"}{skipTrial?" · Charged today":" · 14-day free trial"}{appliedPromo?` · ${appliedPromo.code} −${appliedPromo.pct}%`:""}</p>
              <div className="card" style={{padding:24,display:"flex",flexDirection:"column",gap:12,marginBottom:18}}>
                {[["Company / Organisation","company","Your Company","text"],["Your Full Name","name","Your Name","text"],["Work Email","email","you@company.com","email"],["Password","password","At least 8 characters","password"]].map(([lbl,key,ph,type])=>(
                  <div key={key}><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>{lbl}</label><input className="inp" type={type} placeholder={ph} value={details[key]} onChange={e=>setDetails(d=>({...d,[key]:e.target.value}))}/></div>
                ))}
                <label style={{display:"flex",gap:8,cursor:"pointer",alignItems:"flex-start"}}>
                  <input type="checkbox" style={{marginTop:3,accentColor:B,width:14,height:14}}/>
                  <span style={{fontSize:12,color:T.mid}}>I agree to the <span style={{color:B}}>Terms of Service</span> and <span style={{color:B}}>Privacy Policy</span></span>
                </label>
              </div>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <button className="btn bo" onClick={()=>setStep(0)}><Ic n="arrL" s={13}/> Back</button>
                <button className="btn bp" onClick={()=>setStep(2)}>Continue <Ic n="arr" s={14} c="#fff"/></button>
              </div>
              </>
              )}
            </div>
          )}

          {showPayment&&(
            <div className="au">
              <h2 style={{fontSize:24,fontWeight:800,letterSpacing:-.5,marginBottom:4}}>Secure Payment</h2>
              <p style={{fontSize:13,color:T.lt,marginBottom:18}}>Your plan starts immediately. No trial period.</p>
              <div style={{background:S.bg,border:`1px solid ${S.brd}`,borderRadius:9,padding:"13px 15px",marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontWeight:600,fontSize:13}}>{sel.name} — {billing==="annual"?"Annual":"Monthly"}</span><span style={{fontWeight:700,color:B}}>{cur.symbol}{pricing.monthly}/mo</span></div>
                {billing==="annual"&&<p style={{fontSize:11.5,color:T.lt,marginTop:3}}>Billed as {cur.symbol}{pricing.final*12} today</p>}
                {pricing.promo&&<p style={{fontSize:12,color:G.g,fontWeight:600,marginTop:2}}>Promo {pricing.promo.code}: −{pricing.promo.pct}%</p>}
              </div>
              <div className="stripe-frame" style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:13,fontWeight:700}}>Card Details</p>
                  <span className="stripe-badge"><Ic n="lock" s={11} c="#fff"/> Secured by Stripe</span>
                </div>
                {[["Card Number","1234 5678 9012 3456"],["Expiry","MM/YY"],["CVC","•••"]].map(([l,ph])=>(
                  <div key={l} style={{background:"rgba(255,255,255,.85)",border:"1px solid #c7d2fe",borderRadius:7,padding:"9px 12px",marginBottom:8}}>
                    <p style={{fontSize:10,color:"#6366f1",fontWeight:700,marginBottom:3,textTransform:"uppercase",letterSpacing:.5}}>{l}</p>
                    <input placeholder={ph} style={{width:"100%",fontFamily:"monospace",fontSize:14,border:"none",outline:"none",background:"transparent",color:"#374151"}}/>
                  </div>
                ))}
                <p style={{fontSize:11,color:"#6366f1",marginTop:6}}><Ic n="shld" s={11} c="#6366f1"/> Hindle stores only last 4 digits + expiry. All card data handled exclusively by Stripe.</p>
              </div>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <button className="btn bo" onClick={()=>setStep(1)}><Ic n="arrL" s={13}/> Back</button>
                <button className="btn bp" disabled={processing} onClick={async()=>{
                  setProcessing(true);
                  try{
                    const r=await fetch(API_URL+"/api/stripe/checkout",{method:"POST",headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({planId,billing,email:details.email,promoCode:appliedPromo?.code||"",
                        orgId:(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||"";}return "";}catch(e){return "";}})(),
                        successUrl:window.location.origin+window.location.pathname,
                        cancelUrl:window.location.origin+window.location.pathname+"?payment=cancelled"})});
                    const d=await r.json();
                    if(d.url){window.location.href=d.url;}
                    else{alert("Payment setup failed: "+(d.error||"Unknown error"));setProcessing(false);}
                  }catch(e){alert("Payment error: "+e.message);setProcessing(false);}
                }}>
                  {processing?<><Spinner/> Redirecting to Stripe…</>:<>Pay {cur.symbol}{billing==="annual"?(pricing.final*12):pricing.final} via Stripe <Ic n="arr" s={14} c="#fff"/></>}
                </button>
              </div>
            </div>
          )}

          {showConfirm&&(
            <div className="au" style={{textAlign:"center"}}>
              <div style={{width:70,height:70,borderRadius:"50%",background:G.gb,border:`2px solid ${G.gbd}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",animation:"pop .4s ease"}}><Ic n="chk" s={30} c={G.g}/></div>
              <h2 style={{fontSize:26,fontWeight:800,letterSpacing:-.5,marginBottom:8}}>You're all set! 🎉</h2>
              <p style={{fontSize:14.5,color:T.lt,marginBottom:16}}>{isFree?"Your free account is ready.":skipTrial?`Your ${sel.name} plan is now active.`:`Your 14-day free trial of ${sel.name} has started.`}</p>
              {!isFree&&!skipTrial&&(
                <div style={{background:G.ab,border:`1px solid ${G.abd}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"inline-block",textAlign:"left"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><p style={{fontSize:12.5,color:G.a,fontWeight:700}}>⏱ Trial Reminder Schedule</p></div>
                  {[["Day 12","Reminder email + SMS #1"],["Day 13","Reminder email + SMS #2"],["Day 14","Final reminder — last chance"],["Day 15","Access suspended until payment"]].map(([d,m])=>(
                    <div key={d} style={{display:"flex",gap:8,marginBottom:4}}><span style={{fontSize:11,fontWeight:700,color:G.a,minWidth:42}}>{d}</span><span style={{fontSize:12,color:T.mid}}>{m}</span></div>
                  ))}
                </div>
              )}
              {sel.smsAlerts&&(
                <div style={{background:BL,border:`1px solid ${BM}`,borderRadius:10,padding:"12px 16px",marginBottom:16,textAlign:"left",display:"inline-block"}}>
                  <p style={{fontSize:12.5,fontWeight:700,color:B,marginBottom:4}}>📱 SMS Agent Alerts included</p>
                  <p style={{fontSize:12,color:T.lt}}>{sel.smsLimit} — configure mobile numbers in Agent Management after setup.</p>
                </div>
              )}
              <div className="card" style={{padding:16,textAlign:"left",marginBottom:18}}>
                {["Setup Wizard walks you through configuration","Configure branding, widget and AI bot","Add agent mobile numbers for SMS alerts","Go live — chatbot ready in minutes"].map((s,i)=>(
                  <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",marginBottom:8}}>
                    <div style={{width:18,height:18,borderRadius:"50%",background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}><span style={{fontSize:9,fontWeight:700,color:B}}>{i+1}</span></div>
                    <span style={{fontSize:13,color:T.mid}}>{s}</span>
                  </div>
                ))}
              </div>
              <button className="btn bp lg" style={{width:"100%"}} onClick={()=>go("setup")}>Launch Setup Wizard <Ic n="rocket" s={16} c="#fff"/></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
// ══════════════════════════════════════════════════════════════
// SIGN IN
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// SUPER ADMIN FIRST-TIME SETUP
// Shown on sign-in page — lets admin set password on fresh install
// ══════════════════════════════════════════════════════════════
const SignIn=({go,setConfig})=>{
  const [email,setEmail]=useState(()=>{try{return new URLSearchParams(window.location.search).get("email")||"";}catch(e){return "";}});
  const [pass,setPass]=useState("");
  const [err,setErr]=useState(()=>{try{const m=sessionStorage.getItem("hindle_signin_reason");if(m){sessionStorage.removeItem("hindle_signin_reason");return m;}}catch(e){}return "";});
  const [info]=useState(()=>{try{const m=sessionStorage.getItem("hindle_signin_info");if(m){sessionStorage.removeItem("hindle_signin_info");return m;}}catch(e){}return "";});
  const [loading,setLoading]=useState(false);
  const [twoFaStep,setTwoFaStep]=useState(false); // true = show code entry
  const [twoFaCode,setTwoFaCode]=useState("");
  const [twoFaEmail,setTwoFaEmail]=useState(""); // store email for verify step
  const [forgotStep,setForgotStep]=useState(false);
  const [forgotEmail,setForgotEmail]=useState("");
  const [forgotSent,setForgotSent]=useState(false);
  const [forgotLoading,setForgotLoading]=useState(false);
  const [forgotErr,setForgotErr]=useState("");

  const handleForgot=async()=>{
    if(!forgotEmail.trim()){setForgotErr("Please enter your email address.");return;}
    setForgotLoading(true);setForgotErr("");
    try{
      const r=await fetch(API_URL+"/api/auth/forgot-password",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({email:forgotEmail.trim().toLowerCase()})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.error||"Request failed");
      setForgotSent(true);
    }catch(e){
      setForgotErr(e.message||"Could not send reset email. Please contact support.");
    }
    setForgotLoading(false);
  };

  const handleSignIn=async()=>{
    if(!email.trim()||!pass.trim()){setErr("Please enter your email and password.");return;}
    setErr("");setLoading(true);
    const emailLower=email.trim().toLowerCase();

    // Restore super admin config from DB (called after successful auth)
    const restoreSuperConfig=async()=>{
      try{
        const pR=await fetch(API_URL+"/api/tenant-config/platform");
        if(pR.ok){
          const pD=await pR.json();
          if(pD?.clicksend?.username){
            const existing=JSON.parse(localStorage.getItem("hindle_config")||"{}");
            localStorage.setItem("hindle_config",JSON.stringify({...existing,clicksend:{...INIT_CONFIG.clicksend,...pD.clicksend}}));
          }
        }
      }catch(e){}
      try{
        const aR=await fetch(API_URL+"/api/admin-settings");
        if(aR.ok){
          const aD=await aR.json();
          if(aD.profile&&Object.keys(aD.profile).length)
            localStorage.setItem("hindle_admin_profile",JSON.stringify(aD.profile));
          if(aD.platform&&Object.keys(aD.platform).length)
            localStorage.setItem("hindle_platform_config",JSON.stringify(aD.platform));
          if(aD.github&&Object.keys(aD.github).length)
            localStorage.setItem("hindle_github_config",JSON.stringify(aD.github));
          if(aD.superConfig&&Object.keys(aD.superConfig).length){
            try{
              const sc=aD.superConfig;
              const existing=JSON.parse(localStorage.getItem("hindle_config")||"{}");
              const merged={...INIT_CONFIG,...existing,
                annualDiscountPct:sc.annualDiscountPct??INIT_CONFIG.annualDiscountPct,
                plans:{...INIT_CONFIG.plans,...(existing.plans||{}),
                  starter:{...INIT_CONFIG.plans.starter,...((existing.plans||{}).starter||{}),usd:sc.plans?.starter?.usd??INIT_CONFIG.plans.starter.usd},
                  professional:{...INIT_CONFIG.plans.professional,...((existing.plans||{}).professional||{}),usd:sc.plans?.professional?.usd??INIT_CONFIG.plans.professional.usd},
                },
                promoCodes:sc.promoCodes||existing.promoCodes||INIT_CONFIG.promoCodes,
                planFeatures:sc.planFeatures||existing.planFeatures||null,
                planLimits:sc.planLimits||existing.planLimits||null,
                _superConfig:sc,
                clicksend:sc.clicksend
                  ?{...INIT_CONFIG.clicksend,...(existing.clicksend||{}),...sc.clicksend}
                  :(existing.clicksend||INIT_CONFIG.clicksend),
              };
              localStorage.setItem("hindle_config",JSON.stringify(merged));
              if(setConfig) setConfig(merged);
            }catch(e){}
          }
        }
      }catch(e){}
    };
    
    // Tenant admin — call API
    try{
      const r=await fetch(API_URL+"/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:email.trim(),password:pass})});
      const d=await r.json();
      if(!r.ok||!d.ok){setLoading(false);setErr(d.error||"No account found for that email address.");return;}
      if(d.token){try{localStorage.setItem("hindle_token",d.token);}catch(e){}}
      if(d.role==="super_admin"){ await restoreSuperConfig(); setLoading(false); go("app","super_admin"); return; }
      // Store tenant session + load ALL config from DB into localStorage
      try{
        const base={orgId:d.org_id,tenantId:d.org_id,role:d.role,email:d.email,name:d.name,agentId:d.id||null};        // Also store profile so sidebar shows correct name and agentId is available for claim/PW change
      try{localStorage.setItem("hindle_admin_profile",JSON.stringify({id:d.id||null,name:d.name,email:d.email,role:d.role,org_id:d.org_id||null}));}catch(e){}        try{
          const cfgR=await fetch(API_URL+"/api/tenant-config/"+d.org_id);
          if(cfgR.ok){
            const cfgD=await cfgR.json();
            const merged={...INIT_CONFIG,...cfgD,...base,
              clicksend:{...INIT_CONFIG.clicksend,...(cfgD.clicksend||{})},
              plans:{...INIT_CONFIG.plans,...Object.fromEntries(Object.entries(cfgD.plans||{}).map(([k,v])=>[k,{...INIT_CONFIG.plans[k],...v}]))},
              promoCodes:cfgD.promoCodes||INIT_CONFIG.promoCodes
            };
            localStorage.setItem("hindle_config",JSON.stringify(merged));
            // Populate all tenant-specific localStorage keys from DB so settings survive refresh
            if(cfgD.org)  localStorage.setItem("hindle_tenant_org",JSON.stringify(cfgD.org));
            if(cfgD.brand) localStorage.setItem("hindle_tenant_brand",JSON.stringify(cfgD.brand));
            if(cfgD.billing2) localStorage.setItem("hindle_tenant_billing_details",JSON.stringify(cfgD.billing2));
            if(cfgD.notif) localStorage.setItem("hindle_tenant_notif",JSON.stringify(cfgD.notif));
            if(cfgD.quickReplies) localStorage.setItem("hindle_quick_replies",JSON.stringify(cfgD.quickReplies));
            // Chatbot config
            const chatbotKeys=["model","tone","lang","greeting","fallback","triggers","confidence","maxTurns","collectEmail","offHours","routing","preChatEnabled","preChatFields","autoOpenDelay","autoOpenSkipMobile","kbDocs","widgetColor","widgetPos","widgetLabel","widget_name"];
            const chatbotCfg={};
            chatbotKeys.forEach(k=>{if(cfgD[k]!==undefined)chatbotCfg[k]=cfgD[k];});
            if(cfgD.brand){chatbotCfg.widgetColor=cfgD.brand.primary;chatbotCfg.widgetPos=cfgD.brand.bubble;chatbotCfg.widget_name=cfgD.brand.widget_name||"";chatbotCfg.widgetLabel=cfgD.brand.widget_name||chatbotCfg.widgetLabel||"Chat with us";}
            if(Object.keys(chatbotCfg).length) localStorage.setItem("hindle_chatbot_config",JSON.stringify(chatbotCfg));
            if(cfgD.kbDocs) localStorage.setItem("hindle_chatbot_kb",JSON.stringify(cfgD.kbDocs));
          } else {
            localStorage.setItem("hindle_config",JSON.stringify(base));
          }
        }catch(e){ localStorage.setItem("hindle_config",JSON.stringify(base)); }
      }catch(e){}
      // Update config state so Dashboard.loadDashboardData has orgId without needing hard refresh
      try{
        const stored=localStorage.getItem("hindle_config");
        if(stored&&setConfig) setConfig(prev=>({...INIT_CONFIG,...prev,...JSON.parse(stored),clicksend:{...INIT_CONFIG.clicksend,...(JSON.parse(stored).clicksend||{})}}));
      }catch(e){}
      setLoading(false);go("app",d.role);
    }catch(e){setLoading(false);setErr("Connection error. Please try again.");}
  };

  const handle2faVerify=async()=>{
    if(!twoFaCode.trim()){setErr("Please enter the 6-digit code.");return;}
    setErr("");setLoading(true);
    try{
      const r=await fetch(API_URL+"/api/auth/2fa/verify",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:twoFaEmail,code:twoFaCode.trim()})});
      const d=await r.json();
      if(!r.ok){setLoading(false);setErr(d.error||"Invalid code.");return;}
      // Code verified — restore all settings then enter dashboard
      try{
        const pR=await fetch(API_URL+"/api/tenant-config/platform");
        if(pR.ok){const pD=await pR.json();if(pD?.clicksend?.username){const existing=JSON.parse(localStorage.getItem("hindle_config")||"{}");localStorage.setItem("hindle_config",JSON.stringify({...existing,clicksend:{...INIT_CONFIG.clicksend,...pD.clicksend}}));}}
      }catch(e){}
      try{
        const aR=await fetch(API_URL+"/api/admin-settings");
        if(aR.ok){
          const aD=await aR.json();
          if(aD.profile&&Object.keys(aD.profile).length) localStorage.setItem("hindle_admin_profile",JSON.stringify(aD.profile));
          if(aD.platform&&Object.keys(aD.platform).length) localStorage.setItem("hindle_platform_config",JSON.stringify(aD.platform));
          if(aD.github&&Object.keys(aD.github).length) localStorage.setItem("hindle_github_config",JSON.stringify(aD.github));
          if(aD.adminPassword) localStorage.setItem("hindle_admin_pass",aD.adminPassword);
          if(aD.superConfig&&Object.keys(aD.superConfig).length){
            try{
              const sc=aD.superConfig;
              const existing=JSON.parse(localStorage.getItem("hindle_config")||"{}");
              const merged={...INIT_CONFIG,...existing,annualDiscountPct:sc.annualDiscountPct??INIT_CONFIG.annualDiscountPct,plans:{...INIT_CONFIG.plans,...(existing.plans||{}),starter:{...INIT_CONFIG.plans.starter,...((existing.plans||{}).starter||{}),usd:sc.plans?.starter?.usd??INIT_CONFIG.plans.starter.usd},professional:{...INIT_CONFIG.plans.professional,...((existing.plans||{}).professional||{}),usd:sc.plans?.professional?.usd??INIT_CONFIG.plans.professional.usd}},promoCodes:sc.promoCodes||existing.promoCodes||INIT_CONFIG.promoCodes,planFeatures:sc.planFeatures||existing.planFeatures||null};
              localStorage.setItem("hindle_config",JSON.stringify(merged));
              if(setConfig) setConfig(merged);
            }catch(e){}
          }
        }
      }catch(e){}
      setLoading(false);go("app","super_admin");
    }catch(e){setLoading(false);setErr("Connection error. Please try again.");}
  };

  return(
  <div style={{minHeight:"100vh",background:S.bg,display:"flex",flexDirection:"column"}}>
    <nav style={{background:S.w,borderBottom:`1px solid ${S.brd}`,padding:"0 24px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <Logo sm/><div style={{display:"flex",alignItems:"center",gap:10}}><ThemeSwitch/><button className="btn bg sm" onClick={()=>go("landing")}><Ic n="arrL" s={13}/> Back</button></div>
    </nav>
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div className="card au" style={{padding:36}}>
          {/* Logo badge */}
          <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
            <div style={{width:52,height:52,borderRadius:14,background:BL,border:`1.5px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Ic n="lock" s={24} c={B}/>
            </div>
          </div>

          {twoFaStep?(
            <>
              <h2 style={{fontSize:22,fontWeight:800,marginBottom:4,letterSpacing:-.3,textAlign:"center"}}>Check your email</h2>
              <p style={{fontSize:13,color:T.lt,marginBottom:6,textAlign:"center"}}>A 6-digit code was sent to</p>
              <p style={{fontSize:13,fontWeight:700,color:B,marginBottom:24,textAlign:"center"}}>{twoFaEmail}</p>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Verification Code</label>
                <input className="inp" type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                  value={twoFaCode} onChange={e=>{setTwoFaCode(e.target.value.replace(/\D/g,""));setErr("");}}
                  onKeyDown={e=>e.key==="Enter"&&handle2faVerify()}
                  placeholder="000000"
                  style={{fontSize:28,fontFamily:"monospace",letterSpacing:8,textAlign:"center",fontWeight:700}}/>
              </div>
              {err&&(<div style={{padding:"9px 12px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,marginBottom:12,display:"flex",gap:7,alignItems:"center"}}><Ic n="x" s={12} c={G.r}/><p style={{fontSize:12.5,color:G.r,fontWeight:500}}>{err}</p></div>)}
              <button className="btn bp" style={{width:"100%",marginTop:6,padding:"11px 0",fontSize:14,fontWeight:700}} onClick={handle2faVerify} disabled={loading}>
                {loading?<><Spinner size={14}/> Verifying…</>:<><Ic n="chk" s={13} c="#fff"/> Verify & Sign In</>}
              </button>
              <div style={{textAlign:"center",marginTop:14}}>
                <button style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,color:T.fnt,textDecoration:"underline"}} onClick={async()=>{
                  setErr("");
                  try{await fetch(API_URL+"/api/auth/2fa/send",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:twoFaEmail})});}catch(e){}
                  setErr("");setTwoFaCode("");
                }}>Resend code</button>
                <span style={{color:T.fnt,fontSize:12,margin:"0 8px"}}>·</span>
                <button style={{background:"transparent",border:"none",cursor:"pointer",fontSize:12,color:T.fnt,textDecoration:"underline"}} onClick={()=>{setTwoFaStep(false);setTwoFaCode("");setErr("");}}>Back</button>
              </div>
            </>
          ):(
            <>
              <h2 style={{fontSize:22,fontWeight:800,marginBottom:4,letterSpacing:-.3,textAlign:"center"}}>Welcome back</h2>
              <p style={{fontSize:13,color:T.lt,marginBottom:info?14:26,textAlign:"center"}}>Sign in to Helpdesk</p>
              {info&&<div style={{background:BL,border:`1px solid ${BM}`,color:T.mid,fontSize:12.5,padding:"9px 12px",borderRadius:9,marginBottom:18,lineHeight:1.45}}>{info}</div>}

              <div style={{marginBottom:14}}>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Email Address</label>
                <input className="inp" type="email" autoComplete="off" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}}
                  onKeyDown={e=>e.key==="Enter"&&handleSignIn()} autoComplete="username"/>
              </div>
          <div style={{marginBottom:8}}>
            <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Password</label>
            <input className="inp" type="password" autoComplete="new-password" value={pass} onChange={e=>{setPass(e.target.value);setErr("");}}
              onKeyDown={e=>e.key==="Enter"&&handleSignIn()} placeholder="Enter password" autoComplete="current-password"/>
          </div>

          {err&&(
            <div style={{padding:"9px 12px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,marginBottom:12,display:"flex",gap:7,alignItems:"center"}}>
              <Ic n="x" s={12} c={G.r}/><p style={{fontSize:12.5,color:G.r,fontWeight:500}}>{err}</p>
            </div>
          )}

          {!forgotStep&&(
            <>
              <button className="btn bp" style={{width:"100%",marginTop:10,padding:"11px 0",fontSize:14,fontWeight:700}} onClick={handleSignIn} disabled={loading}>
                {loading?<><Spinner size={14}/> Signing in…</>:<><Ic n="arr" s={13} c="#fff"/> Sign In</>}
              </button>
              <button onClick={()=>{setForgotStep(true);setForgotEmail(email);setForgotErr("");setForgotSent(false);setErr("");}}
                style={{width:"100%",marginTop:10,background:"transparent",border:"none",cursor:"pointer",fontSize:13,color:B,textDecoration:"underline",padding:"4px 0"}}>
                Forgot password?
              </button>
            </>
          )}

          {/* Forgot password panel */}
          {forgotStep&&(
            <div style={{marginTop:8}}>
              {!forgotSent?(
                <>
                  <p style={{fontSize:13,color:T.mid,marginBottom:10,lineHeight:1.5}}>Enter your account email and we'll send a password reset link.</p>
                  <input className="inp" type="email" placeholder="Your email address" value={forgotEmail}
                    onChange={e=>setForgotEmail(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleForgot()}
                    style={{marginBottom:8}}/>
                  {forgotErr&&<div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,padding:"7px 10px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7}}>
                    <Ic n="x" s={12} c={G.r}/><p style={{fontSize:12.5,color:G.r}}>{forgotErr}</p>
                  </div>}
                  <button className="btn bp" style={{width:"100%",padding:"11px 0",fontSize:14,fontWeight:700}} onClick={handleForgot} disabled={forgotLoading}>
                    {forgotLoading?<><Spinner size={14}/> Sending…</>:<><Ic n="mail" s={13} c="#fff"/> Send Reset Link</>}
                  </button>
                  <button onClick={()=>setForgotStep(false)} style={{width:"100%",marginTop:8,background:"transparent",border:"none",cursor:"pointer",fontSize:13,color:T.fnt,padding:"4px 0"}}>
                    ← Back to sign in
                  </button>
                </>
              ):(
                <div style={{textAlign:"center",padding:"16px 0"}}>
                  <div style={{width:48,height:48,borderRadius:"50%",background:G.gb,border:`2px solid ${G.gbd}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
                    <Ic n="chk" s={22} c={G.g}/>
                  </div>
                  <p style={{fontSize:15,fontWeight:700,color:T.ink,marginBottom:6}}>Check your inbox</p>
                  <p style={{fontSize:13,color:T.fnt,lineHeight:1.6,marginBottom:14}}>If an account exists for <strong>{forgotEmail}</strong>, a password reset link has been sent. Check your spam folder if it doesn't arrive within a few minutes.</p>
                  <button onClick={()=>{setForgotStep(false);setForgotSent(false);}} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:13,color:B,textDecoration:"underline"}}>
                    ← Back to sign in
                  </button>
                </div>
              )}
            </div>
          )}

            </>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};
// ══════════════════════════════════════════════════════════════
// SETUP WIZARD
// ══════════════════════════════════════════════════════════════
const SETUP_STEPS=[
  {id:"profile",     label:"Company Profile",       icon:"bld"},
  {id:"branding",    label:"Branding & Appearance", icon:"edit"},
  {id:"widget",      label:"Widget Setup",           icon:"globe"},
  {id:"chatbot",     label:"AI Chatbot Setup",       icon:"bot"},
  {id:"handoff",     label:"Ask a Human",            icon:"hand"},
  {id:"agents",      label:"Agent Mobile Numbers",   icon:"phone"},
  {id:"notifications",label:"Notifications",         icon:"bell"},
  {id:"smtp",        label:"Email Setup (SMTP)",     icon:"mail"},
  {id:"kb",          label:"Knowledge Base",         icon:"book"},
  {id:"embed",       label:"Widget Embed Code",      icon:"copy"},
  {id:"test",        label:"Test Widget",            icon:"zap"},
  {id:"security",    label:"Security Setup",         icon:"lock"},
  {id:"golive",      label:"Go Live Checklist",      icon:"rocket"},
];

const SetupPanel=({title,desc,onDone,btnLabel="Save & Continue",children})=>(
  <div style={{maxWidth:540}}>
    <h2 style={{fontSize:20,fontWeight:800,marginBottom:4,letterSpacing:-.3}}>{title}</h2>
    <p style={{fontSize:13.5,color:T.lt,marginBottom:20}}>{desc}</p>
    <div className="card" style={{padding:22,marginBottom:16}}>{children}</div>
    <div style={{display:"flex",gap:10}}>
      <button className="btn bp" onClick={onDone}>{btnLabel} <Ic n="arr" s={14} c="#fff"/></button>
      <button className="btn bo">Save for later</button>
    </div>
  </div>
);

const EmbedSnippet=()=>{
  const [embedTab,setEmbedTab]=useState("html");
  const [copied,setCopied]=useState(false);
  // Get tenantId from localStorage
  const tenantId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||"YOUR-TENANT-ID";}}catch(e){}return "YOUR-TENANT-ID";})();
  const copyCode=(code)=>{navigator.clipboard?.writeText(code).catch(()=>{});setCopied(true);setTimeout(()=>setCopied(false),2000);};
  const snippets={
    html:`<!-- Hindle Chat Widget -->
<script
  src="https://chatbot.hindleconsultants.com/widget.js"
  data-tenant="${tenantId}"
  data-color="#2563EB"
  defer>
</script>`,
    wordpress:`// Add to your theme's functions.php
function hindle_widget() {
  echo '<script src="https://chatbot.hindleconsultants.com/widget.js"
    data-tenant="${tenantId}"
    defer></script>';
}
add_action('wp_footer', 'hindle_widget');`,
    shopify:`{%- comment -%} Add to theme.liquid before </body> {%- endcomment -%}
<script
  src="https://chatbot.hindleconsultants.com/widget.js"
  data-tenant="${tenantId}"
  defer>
</script>`,
    react:`// Option A: Next.js App Router (app/layout.tsx)
// import Script from 'next/' + 'script'; // Next.js only
// <Script src="https://chatbot.hindleconsultants.com/widget.js"
//   data-tenant="${tenantId}" strategy="lazyOnload" />

// Option B: Plain React / Vite (useEffect)
// useEffect(() => {
//   const s = document.createElement('script');
//   s.src = 'https://chatbot.hindleconsultants.com/widget.js';
//   s.setAttribute('data-tenant', '{your-tenant-id}');
//   s.defer = true;
//   document.body.appendChild(s);
//   return () => document.body.removeChild(s);
// }, []);`,
    webflow:`<!-- Webflow: Site Settings → Custom Code → Footer Code -->
<script
  src="https://chatbot.hindleconsultants.com/widget.js"
  data-tenant="${tenantId}"
  defer>
</script>`,
  };
  const platforms=[["html","HTML / Any site"],["wordpress","WordPress"],["shopify","Shopify"],["react","React / Next.js"],["webflow","Webflow"]];
  return(
    <div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:12}}>
        {platforms.map(([k,l])=>(
          <button key={k} onClick={()=>setEmbedTab(k)} style={{padding:"5px 12px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",background:embedTab===k?B:"transparent",color:embedTab===k?"#fff":T.fnt,border:embedTab===k?`1px solid ${B}`:`1px solid ${S.brd}`,transition:"all .15s"}}>{l}</button>
        ))}
      </div>
      <div style={{position:"relative",marginBottom:12}}>
        <pre className="mono" style={{background:"#0f172a",color:"#e2e8f0",borderRadius:9,padding:"14px 16px",fontSize:12,overflowX:"auto",whiteSpace:"pre",lineHeight:1.65,margin:0}}>{snippets[embedTab]}</pre>
        <button onClick={()=>copyCode(snippets[embedTab])} style={{position:"absolute",top:8,right:8,background:copied?"rgba(22,163,74,.9)":"rgba(255,255,255,.12)",border:"none",borderRadius:6,padding:"5px 10px",cursor:"pointer",color:"#fff",fontSize:11.5,fontWeight:600,display:"flex",alignItems:"center",gap:5,transition:"all .2s"}}>
          <Ic n={copied?"chk":"copy"} s={11} c="#fff"/>{copied?"Copied!":"Copy"}
        </button>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center",padding:"9px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:10}}>
        <div style={{width:8,height:8,borderRadius:"50%",background:G.a,flexShrink:0}}/>
        <p style={{fontSize:12.5,color:T.mid,flex:1}}>Waiting to detect widget on your site…</p>
        <button className="btn bo sm" style={{fontSize:11}}>Check Now</button>
      </div>
      <p style={{fontSize:11.5,color:T.fnt}}>Need help? See the full install guide in <strong>Dashboard → Install & Embed</strong> after setup.</p>
    </div>
  );
};
const SetupWizard=({go})=>{
  const [done,setDone]=useState(new Set([]));
  const [active,setActive]=useState("profile");
  const [wkbDocs,setWkbDocs]=useState(()=>{try{const s=localStorage.getItem("hindle_chatbot_kb");return s?JSON.parse(s):[];}catch(e){return [];}});
  const [wSmtp,setWSmtp]=useState({host:"",port:"587",user:"",pass:"",fromName:"",fromEmail:""});
  const [wSmtpTesting,setWSmtpTesting]=useState(false);
  const [wSmtpResult,setWSmtpResult]=useState(null);
  const [agentMobiles,setAgentMobiles]=useState(()=>{
    try{const s=localStorage.getItem("hindle_agents");const a=s?JSON.parse(s):[];
    return a.length>0?a:[{id:1,name:"",mobile:"",role:"agent",status:"offline",smsAlerts:true}];}
    catch(e){return [{id:1,name:"",mobile:"",role:"agent",status:"offline",smsAlerts:true}];}
  });
  const [botTone,setBotTone]=useState("friendly");
  const [botModel,setBotModel]=useState("gpt4o");
  const [botGreeting,setBotGreeting]=useState("Hi there! 👋 How can I help you today?");
  const [botFallback,setBotFallback]=useState("I'm not sure about that — let me get someone to help you.");
  const [botTriggers,setBotTriggers]=useState(["speak to a human","talk to an agent","real person","help me","urgent"]);
  const [botLang,setBotLang]=useState("en");
  const markDone=async(id)=>{
    // Save SMTP to DB when smtp step is completed
    if(id==="smtp"&&wSmtp.host&&wSmtp.user){
      try{
        const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
        if(orgId) await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tenantId:orgId,smtp:wSmtp})});
      }catch(e){}
    }
    setDone(d=>new Set([...d,id]));
    const idx=SETUP_STEPS.findIndex(s=>s.id===id);
    const nxt=SETUP_STEPS[idx+1];
    if(nxt)setActive(nxt.id);
  };

  const panels={
    profile:<SetupPanel title="Company Profile" desc="Tell us about your organisation" onDone={()=>markDone("profile")}>
      {[["Company Name",""],["Industry",""],["Website",""],["Support Email",""]].map(([l,v])=>(
        <div key={l} style={{marginBottom:12}}><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>{l}</label><input className="inp" defaultValue={v}/></div>
      ))}
    </SetupPanel>,
    branding:<SetupPanel title="Branding & Appearance" desc="Customise how your widget looks" onDone={()=>markDone("branding")}>
      <div style={{marginBottom:12}}><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Primary Brand Colour</label><div style={{display:"flex",gap:8}}><input type="color" defaultValue="#2563EB" style={{width:38,height:38,border:`1.5px solid ${S.brd}`,borderRadius:7,cursor:"pointer",padding:2}}/><input className="inp" defaultValue="#2563EB"/></div></div>
      <div><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Welcome Message</label><textarea className="inp" defaultValue="Hi there! How can I help you today?" style={{resize:"vertical",minHeight:56}}/></div>
    </SetupPanel>,
    widget:<SetupPanel title="Widget Setup" desc="Configure your chat widget" onDone={()=>markDone("widget")}>
      <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Visitor Fields (collected before chat starts)</p>
      {[["Collect visitor name","visitor_name",true],["Collect visitor company","visitor_company",false],["Collect visitor location","visitor_location",false],["Collect visitor email","visitor_email",true]].map(([label,key,def])=>(
        <label key={key} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,cursor:"pointer"}}>
          <input type="checkbox" defaultChecked={def} style={{accentColor:B,width:14,height:14}}/>
          <span style={{fontSize:13,color:T.mid}}>{label}</span>
        </label>
      ))}
      <div style={{height:1,background:S.brd,margin:"10px 0"}}/>
      <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Widget Features</p>
      {["Show agent avatars","Enable file attachments","Show typing indicators","Show conversation history"].map(f=>(
        <label key={f} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,cursor:"pointer"}}><input type="checkbox" defaultChecked style={{accentColor:B,width:14,height:14}}/><span style={{fontSize:13,color:T.mid}}>{f}</span></label>
      ))}
    </SetupPanel>,
    chatbot:<SetupPanel title="AI Chatbot Setup" desc="Configure your AI assistant's behaviour and personality" onDone={()=>markDone("chatbot")}>
      {/* AI Model */}
      <div style={{marginBottom:16}}>
        <label style={{fontSize:12.5,fontWeight:700,color:T.mid,display:"block",marginBottom:8}}>AI Model</label>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
          {[{id:"gpt4o",label:"GPT-4o",desc:"Best accuracy",badge:"Recommended"},{id:"gpt4omini",label:"GPT-4o Mini",desc:"Faster & cheaper",badge:null},{id:"claude",label:"Claude Sonnet",desc:"Long context",badge:null}].map(m=>(
            <div key={m.id} onClick={()=>setBotModel(m.id)} style={{padding:"10px 12px",borderRadius:8,cursor:"pointer",border:botModel===m.id?`2px solid ${B}`:`1px solid ${S.brd}`,background:botModel===m.id?BL:S.bg,transition:"all .15s",position:"relative"}}>
              {m.badge&&<span style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",background:B,color:"#fff",fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:10,whiteSpace:"nowrap"}}>{m.badge}</span>}
              <p style={{fontSize:12.5,fontWeight:700,color:botModel===m.id?B:T.ink}}>{m.label}</p>
              <p style={{fontSize:11,color:T.fnt}}>{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Tone */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:12.5,fontWeight:700,color:T.mid,display:"block",marginBottom:8}}>Personality & Tone</label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {[["friendly","😊 Friendly"],["professional","💼 Professional"],["concise","⚡ Concise"],["empathetic","🤝 Empathetic"]].map(([val,lbl])=>(
            <button key={val} onClick={()=>setBotTone(val)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",background:botTone===val?B:S.bg,color:botTone===val?"#fff":T.mid,border:botTone===val?`1px solid ${B}`:`1px solid ${S.brd}`,transition:"all .15s"}}>{lbl}</button>
          ))}
        </div>
      </div>
      {/* Language */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:12.5,fontWeight:700,color:T.mid,display:"block",marginBottom:6}}>Language</label>
        <select className="inp" value={botLang} onChange={e=>setBotLang(e.target.value)} style={{maxWidth:220}}>
          {[["en","English"],["es","Spanish"],["fr","French"],["de","German"],["pt","Portuguese"],["zh","Chinese"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {/* Greeting */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:12.5,fontWeight:700,color:T.mid,display:"block",marginBottom:6}}>Opening Greeting</label>
        <textarea className="inp" value={botGreeting} onChange={e=>setBotGreeting(e.target.value)} style={{resize:"vertical",minHeight:52}}/>
        <div style={{padding:"8px 11px",background:BL,border:`1px solid ${BM}`,borderRadius:7,marginTop:6,display:"flex",gap:7,alignItems:"flex-start"}}>
          <Ic n="bot" s={13} c={B}/>
          <p style={{fontSize:12,color:T.mid,fontStyle:"italic"}}>Preview: "{botGreeting}"</p>
        </div>
      </div>
      {/* Fallback */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:12.5,fontWeight:700,color:T.mid,display:"block",marginBottom:6}}>Fallback Response <span style={{fontSize:11,fontWeight:400,color:T.fnt}}>(when bot doesn't know)</span></label>
        <textarea className="inp" value={botFallback} onChange={e=>setBotFallback(e.target.value)} style={{resize:"vertical",minHeight:52}}/>
      </div>
    </SetupPanel>,
    handoff:<SetupPanel title="Ask a Human" desc="Configure escalation to human agents" onDone={()=>markDone("handoff")}>
      <div style={{background:G.ab,border:`1px solid ${G.abd}`,borderRadius:8,padding:"11px 13px",marginBottom:14}}>
        <p style={{fontSize:13,color:T.mid,lineHeight:1.55}}>When a visitor requests a human, the system checks if any agent is online. If none are available, <strong>SMS alerts are sent</strong> to offline agents with a magic link to join instantly — no login required.</p>
      </div>
      {[["Trigger Phrase","speak to a human"],["Routing","Round-robin (distribute evenly)"]].map(([l,v])=>(
        <div key={l} style={{marginBottom:12}}><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>{l}</label><input className="inp" defaultValue={v}/></div>
      ))}
      <div><label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:8}}>SMS Alert Trigger</label>
        {[["Alert if no agents online (recommended)","online"],["Always alert all agents","always"],["Alert only primary agent","primary"]].map(([l,v])=>(
          <label key={v} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,cursor:"pointer"}}><input type="radio" name="sms_mode" defaultChecked={v==="online"} style={{accentColor:B}}/><span style={{fontSize:13,color:T.mid}}>{l}</span></label>
        ))}
      </div>
    </SetupPanel>,
    agents:<SetupPanel title="Agent Mobile Numbers" desc="Add mobile numbers for SMS alert delivery" onDone={()=>markDone("agents")}>
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:`${CS}14`,border:`1px solid ${CS}55`,borderRadius:8,marginBottom:14}}>
        <p style={{fontSize:12,color:T.mid,marginLeft:4}}>Mobile numbers are stored securely and used only for handoff SMS alerts.</p>
      </div>
      {agentMobiles.map((a,i)=>(
        <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
          <Av name={a.name} size={30} col={a.status==="online"?G.g:T.fnt}/>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:13,fontWeight:600,marginBottom:3}}>{a.name}<span style={{fontSize:11,color:T.fnt,fontWeight:400,marginLeft:6}}>{a.role}</span></p>
            <input className="inp" placeholder="+44 7700 900000" value={a.mobile} onChange={e=>setAgentMobiles(ag=>ag.map((x,j)=>j===i?{...x,mobile:e.target.value}:x))} style={{fontSize:12,padding:"5px 9px"}}/>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",flexShrink:0}}>
            <div style={{width:32,height:17,borderRadius:9,background:a.smsAlerts?CS:S.brd,position:"relative",transition:"background .2s",cursor:"pointer"}} onClick={()=>setAgentMobiles(ag=>ag.map((x,j)=>j===i?{...x,smsAlerts:!x.smsAlerts}:x))}>
              <div style={{position:"absolute",top:2,left:a.smsAlerts?16:2,width:13,height:13,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
            </div>
            <span style={{fontSize:11,color:a.smsAlerts?CS:T.fnt}}>{a.smsAlerts?"On":"Off"}</span>
          </label>
        </div>
      ))}
      <p style={{fontSize:11.5,color:T.fnt,marginTop:8}}>Magic links expire 3 minutes after sending and are one-time use.</p>
    </SetupPanel>,
    notifications:<SetupPanel title="Notification Reminders" desc="Choose how your team gets alerted" onDone={()=>markDone("notifications")}>
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:`${CS}14`,border:`1px solid ${CS}55`,borderRadius:8,marginBottom:14}}>
        <p style={{fontSize:12,color:T.mid,marginLeft:4}}>All emails and SMS delivered.</p>
      </div>
      {[["Email Notifications",["New conversation opened","Agent assigned","Conversation resolved","Trial payment reminder"]],["SMS Notifications",["Handoff alert (no agents online)","Trial expiry reminder (Day 14)"]]].map(([group,items])=>(
        <div key={group} style={{marginBottom:14}}>
          <p style={{fontSize:11.5,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>{group}</p>
          {items.map(f=><label key={f} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,cursor:"pointer"}}><input type="checkbox" defaultChecked style={{accentColor:B,width:14,height:14}}/><span style={{fontSize:13,color:T.mid}}>{f}</span></label>)}
        </div>
      ))}
    </SetupPanel>,
    smtp:<SetupPanel title="Email Setup (SMTP)" desc="Send emails from your own domain — no platform branding" onDone={()=>markDone("smtp")} btnLabel="Save & Continue">
      <div style={{padding:"10px 12px",background:BL,border:`1px solid ${BM}`,borderRadius:8,marginBottom:16,display:"flex",gap:8,alignItems:"flex-start"}}>
        <Ic n="mail" s={14} c={B}/>
        <div>
          <p style={{fontSize:12.5,fontWeight:700,color:B,marginBottom:2}}>Why set this up?</p>
          <p style={{fontSize:12,color:T.mid,lineHeight:1.6}}>Emails from handoffs, replies and trial reminders will come from <strong>your</strong> email address, not the platform's. Visitors see your brand, replies go to your inbox.</p>
        </div>
      </div>
      <div style={{display:"grid",gap:10,marginBottom:12}}>
        <div>
          <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>SMTP Host</label>
          <input className="inp" placeholder="smtp.gmail.com  or  mail.yourdomain.com" value={wSmtp.host||""} onChange={e=>setWSmtp(s=>({...s,host:e.target.value}))}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Port</label>
            <select className="inp" value={wSmtp.port||"587"} onChange={e=>setWSmtp(s=>({...s,port:e.target.value}))}>
              <option value="587">587 — TLS (recommended)</option>
              <option value="465">465 — SSL</option>
              <option value="25">25 — Plain</option>
            </select>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Username</label>
            <input className="inp" placeholder="your@email.com" value={wSmtp.user||""} onChange={e=>setWSmtp(s=>({...s,user:e.target.value}))}/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Password / App Password</label>
            <input className="inp" type="password" placeholder="••••••••" value={wSmtp.pass||""} onChange={e=>setWSmtp(s=>({...s,pass:e.target.value}))}/>
          </div>
          <div>
            <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>From Name</label>
            <input className="inp" placeholder="Your Company Support" value={wSmtp.fromName||""} onChange={e=>setWSmtp(s=>({...s,fromName:e.target.value}))}/>
          </div>
        </div>
        <div>
          <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>From Email</label>
          <input className="inp" placeholder="support@yourdomain.com" value={wSmtp.fromEmail||""} onChange={e=>setWSmtp(s=>({...s,fromEmail:e.target.value}))}/>
        </div>
      </div>
      <div style={{background:S.bg,border:`1px solid ${S.brd}`,borderRadius:7,padding:"10px 12px",marginBottom:12}}>
        <p style={{fontSize:11.5,color:T.fnt,lineHeight:1.7}}>
          <strong>Gmail:</strong> Use an App Password (Google Account → Security → App Passwords). Host: smtp.gmail.com Port: 587<br/>
          <strong>Office 365:</strong> Host: smtp.office365.com Port: 587<br/>
          <strong>cPanel / Hosting:</strong> Host: mail.yourdomain.com Port: 465<br/>
          <strong>Skip for now:</strong> Emails will use the platform sender until you configure SMTP.
        </p>
      </div>
      {wSmtpResult&&<div style={{padding:"8px 12px",borderRadius:7,background:wSmtpResult.ok?G.gb:G.rb,border:`1px solid ${wSmtpResult.ok?G.gbd:G.rbd}`,fontSize:12.5,color:wSmtpResult.ok?G.g:G.r,fontWeight:600,marginBottom:10}}>{wSmtpResult.msg}</div>}
      <button className="btn bo sm" disabled={wSmtpTesting||!wSmtp.host} style={{marginBottom:4}} onClick={async()=>{
        const addr=window.prompt("Send test email to:");if(!addr)return;
        setWSmtpTesting(true);setWSmtpResult(null);
        try{
          const r=await fetch(API_URL+"/api/test-smtp",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({smtp:wSmtp,to:addr})});
          const d=await r.json();
          setWSmtpResult(d.ok?{ok:true,msg:"✅ Test email sent to "+addr}:{ok:false,msg:"❌ "+(d.error||"Failed")});
        }catch(e){setWSmtpResult({ok:false,msg:"❌ "+e.message});}
        setWSmtpTesting(false);
      }}>{wSmtpTesting?<><Spinner size={12}/> Testing…</>:<><Ic n="mail" s={12}/> Send Test Email</>}</button>
    </SetupPanel>,
    kb:<SetupPanel title="Knowledge Base" desc="Train your AI with your content" onDone={()=>markDone("kb")} btnLabel="Save & Continue">
      <input type="file" id="wkbInp" multiple accept=".pdf,.docx,.txt,.md,.json,.xml,.csv" style={{display:"none"}} onChange={e=>{
        const files=Array.from(e.target.files||[]);
        const newDocs=files.map(f=>({id:Date.now()+Math.random(),name:f.name,size:(f.size/1024/1024).toFixed(1)+" MB",type:f.name.split(".").pop().toUpperCase(),uploaded:new Date().toLocaleDateString()}));
        const updated=[...wkbDocs,...newDocs];
        setWkbDocs(updated);
        try{localStorage.setItem("hindle_chatbot_kb",JSON.stringify(updated));}catch(e){}
      }}/>
      <div style={{border:`2px dashed ${S.brd}`,borderRadius:9,padding:"22px",textAlign:"center",marginBottom:12,cursor:"pointer",background:S.bg}}
        onClick={()=>document.getElementById("wkbInp").click()}
        onMouseEnter={e=>e.currentTarget.style.borderColor=B}
        onMouseLeave={e=>e.currentTarget.style.borderColor=S.brd}>
        <Ic n="book" s={26} c={T.fnt}/>
        <p style={{fontSize:13,color:T.fnt,marginTop:8}}>Drop files or click to upload</p>
        <p style={{fontSize:11.5,color:T.fnt}}>PDF, DOCX, TXT, JSON, XML, CSV up to 50MB</p>
      </div>
      {wkbDocs.map(f=>(
        <div key={f.id} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 11px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7,marginBottom:6}}>
          <Ic n="book" s={13} c={G.g}/>
          <span style={{flex:1,fontSize:12.5,fontWeight:500}}>{f.name}</span>
          <span style={{fontSize:11,color:T.fnt}}>{f.size}</span>
          <Chip s="active"/>
          <button onClick={()=>{const updated=wkbDocs.filter(d=>d.id!==f.id);setWkbDocs(updated);try{localStorage.setItem("hindle_chatbot_kb",JSON.stringify(updated));}catch(ex){}}} style={{background:"none",border:"none",cursor:"pointer",padding:2}}><Ic n="x" s={12} c={G.r}/></button>
        </div>
      ))}
      {wkbDocs.length===0&&<p style={{fontSize:12,color:T.fnt,textAlign:"center",marginTop:8}}>No files uploaded yet. You can also add articles manually in the Chatbot → KB tab after setup.</p>}
    </SetupPanel>,
    embed:<SetupPanel title="Widget Embed Code" desc="Add one snippet to your website — the chatbot is live instantly" onDone={()=>markDone("embed")} btnLabel="I've Added the Code">
      <EmbedSnippet/>
    </SetupPanel>,
    test:<SetupPanel title="Test Widget" desc="Verify your chatbot is working" onDone={()=>markDone("test")} btnLabel="Mark as Tested">
      <div style={{background:S.bg,border:`1px solid ${S.brd}`,borderRadius:10,overflow:"hidden"}}>
        <div style={{background:B,padding:"11px 14px",display:"flex",alignItems:"center",gap:7}}><Ic n="chat" s={14} c="#fff"/><span style={{color:"#fff",fontWeight:600,fontSize:13}}>Your Company Support</span></div>
        <div style={{padding:14}}><div style={{background:S.w,padding:"8px 12px",borderRadius:9,fontSize:13,border:`1px solid ${S.brd}`}}>Hi there! 👋 How can I help you today?</div></div>
      </div>
    </SetupPanel>,
    security:<SetupPanel title="Two-Factor Authentication" desc="2FA is automatically enabled for your account" onDone={()=>markDone("security")} btnLabel="Continue →">
      <div style={{padding:"14px 16px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:9,display:"flex",gap:10,alignItems:"flex-start",marginBottom:12}}>
        <div style={{width:32,height:32,borderRadius:8,background:G.g,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="lock" s={15} c="#fff"/></div>
        <div>
          <p style={{fontSize:13.5,fontWeight:700,color:G.g,marginBottom:3}}>2FA Active — Your account is protected</p>
          <p style={{fontSize:12.5,color:T.mid,lineHeight:1.6}}>A 6-digit verification code will be sent to your email each time you log in from a new device. This cannot be disabled to keep your account and customer data secure.</p>
        </div>
      </div>
      <div style={{padding:"11px 14px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8}}>
        <p style={{fontSize:12.5,fontWeight:600,marginBottom:3}}>Your verified email</p>
        <p style={{fontSize:13,color:B,fontWeight:700}}>your@email.com</p>
      </div>
    </SetupPanel>,
    golive:<SetupPanel title="Go Live Checklist" desc="Final checks before launching" onDone={()=>go("app")} btnLabel="🚀 Launch Dashboard!">
      {[["Widget installed","Snippet detected on site"],["AI Chatbot configured","Model, tone and greeting set"],["Knowledge base ready","7 documents indexed"],["Agent mobiles configured","4 of 4 agents have numbers"],["SMS alerts enabled","Agents notified on handoff"],["Test passed","All responses correct"],["Notifications set","Email + SMS"]].map(([t,s],i)=>(
        <div key={i} style={{display:"flex",gap:11,alignItems:"center",padding:"11px 13px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:8,marginBottom:7}}>
          <GreenCheck size={20}/><div><p style={{fontSize:13.5,fontWeight:600}}>{t}</p><p style={{fontSize:12,color:T.lt}}>{s}</p></div>
        </div>
      ))}
    </SetupPanel>,
  };

  const completedCount=[...done].length;
  return(
    <div style={{display:"flex",height:"100vh",overflow:"hidden",background:S.bg}}>
      <div style={{width:240,background:S.w,borderRight:`1px solid ${S.brd}`,display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto"}}>
        <div style={{padding:"14px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",gap:9}}>
          <div style={{width:34,height:34,borderRadius:8,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,color:B}}>T</div>
          <div style={{flex:1,minWidth:0}}><p style={{fontSize:13,fontWeight:700}}>Setup Wizard</p><p style={{fontSize:11,color:T.fnt}}>Your Organisation</p></div>
          <div style={{width:26,height:26,borderRadius:"50%",border:`2px solid ${T.ink}`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:11}}>H</div>
        </div>
        <div style={{padding:"8px 10px",flex:1}}>
          {SETUP_STEPS.map(st=>(
            <div key={st.id} className={`nl${active===st.id?" act":""}`} style={{fontSize:12.5,padding:"6px 9px",gap:7,marginBottom:1}} onClick={()=>setActive(st.id)}>
              {done.has(st.id)?<Ic n="chk" s={12} c={G.g}/>:<Ic n={st.icon} s={12}/>}
              <span style={{flex:1}}>{st.label}</span>
              {done.has(st.id)&&<span style={{width:6,height:6,borderRadius:"50%",background:G.g,display:"inline-block"}}/>}
            </div>
          ))}
        </div>
        <div style={{padding:"10px 14px",borderTop:`1px solid ${S.brd}`}}>
          <div className="nl" onClick={()=>go("signin")}><Ic n="logout" s={14}/><span>Sign out</span></div>
        </div>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{background:S.w,borderBottom:`1px solid ${S.brd}`,padding:"13px 22px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}><Logo sm/><div style={{width:1,height:26,background:S.brd}}/><div><h1 style={{fontSize:19,fontWeight:800,letterSpacing:-.3}}>Setup Wizard</h1><p style={{fontSize:12,color:T.lt}}>Get your chatbot live in minutes</p></div></div>
            <button onClick={()=>go("app")} title="Minimise — return to dashboard, resume anytime via Re-run Setup"
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:8,border:`1px solid ${S.brd}`,background:"transparent",color:T.mid,cursor:"pointer",fontSize:12.5,fontWeight:600}}>
              <Ic n="chD" s={12}/> Minimise
            </button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,height:5,background:S.bg,borderRadius:3,overflow:"hidden",border:`1px solid ${S.brd}`}}><div style={{height:"100%",width:`${(completedCount/SETUP_STEPS.length)*100}%`,background:G.g,borderRadius:3,transition:"width .5s"}}/></div>
            <span style={{fontSize:12,fontWeight:600,color:G.g,whiteSpace:"nowrap"}}>{completedCount}/{SETUP_STEPS.length} complete</span>
          </div>
        </div>
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>
          <div style={{width:270,borderRight:`1px solid ${S.brd}`,overflowY:"auto",padding:12,background:S.bg,flexShrink:0}}>
            {SETUP_STEPS.map((st,i)=>{const isDone=done.has(st.id),isActive=active===st.id;return(
              <div key={st.id} className={isDone?"step-done":isActive?"step-active":"step-pending"} style={{marginBottom:7}} onClick={()=>setActive(st.id)}>
                <div style={{width:22,height:22,borderRadius:"50%",background:isDone?G.g:isActive?B:S.bg,border:`2px solid ${isDone?G.g:isActive?B:S.brd}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {isDone?<Ic n="chk" s={11} c="#fff"/>:<span style={{fontSize:10,fontWeight:700,color:isActive?"#fff":T.fnt}}>{i+1}</span>}
                </div>
                <div style={{flex:1,minWidth:0}}><p style={{fontSize:13,fontWeight:700,color:isDone?G.g:isActive?B:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{st.label}</p><p style={{fontSize:10.5,color:isDone?G.g:T.fnt}}>{isDone?"Completed":"Pending"}</p></div>
              </div>
            );})}
          </div>
          <div style={{flex:1,overflowY:"auto",padding:24}} key={active} className="ai">{panels[active]||<p style={{color:T.fnt}}>Select a step</p>}</div>
        </div>
      </div>
    </div>
  );
};
// ══════════════════════════════════════════════════════════════
// DASHBOARD PAGES
// ══════════════════════════════════════════════════════════════

// ── Conversations (with SMS handoff) ──────────────────────────
// ══════════════════════════════════════════════════════════════
// CLIENT INFO PANEL — shown in conversation right column
// Pulls org details, contact info, and billing from DB
// ══════════════════════════════════════════════════════════════
const ClientInfoPanel=({config})=>{
  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    if(!orgId){setLoading(false);return;}
    // Load from tenant-config (org, billing2, contact details)
    fetch(API_URL+"/api/tenant-config/"+orgId)
      .then(r=>r.ok?r.json():null)
      .then(d=>{setData(d||{});})
      .catch(()=>setData({}))
      .finally(()=>setLoading(false));
  },[orgId]);

  if(loading) return <div style={{padding:20,textAlign:"center"}}><Spinner size={16}/></div>;

  // Pull fields from stored config
  const org=data?.org||JSON.parse(localStorage.getItem("hindle_tenant_org")||"{}");
  const b=data?.billing2||JSON.parse(localStorage.getItem("hindle_billing2")||"{}");

  const Section=({title,children})=>(
    <div style={{marginBottom:16}}>
      <p style={{fontSize:10,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:8,paddingBottom:4,borderBottom:`1px solid ${S.brd}`}}>{title}</p>
      {children}
    </div>
  );

  const Row=({label,value})=>value?(
    <div style={{marginBottom:7}}>
      <p style={{fontSize:10,color:T.fnt,fontWeight:600,textTransform:"uppercase",letterSpacing:.4,marginBottom:1}}>{label}</p>
      <p style={{fontSize:12.5,color:T.mid,wordBreak:"break-all"}}>{value}</p>
    </div>
  ):null;

  const plan=(()=>{try{const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return c.plan||"—";}catch(e){return "—";}})();
  const status=(()=>{try{const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return c.status||"trial";}catch(e){return "trial";}})();

  return(
    <div>
      <Section title="Organisation">
        <Row label="Name" value={org.name}/>
        <Row label="Website" value={org.website}/>
        <Row label="Phone" value={org.phone}/>
        <Row label="Industry" value={org.industry}/>
        <Row label="Timezone" value={org.timezone}/>
      </Section>

      <Section title="Contact Details">
        <Row label="Support Email" value={org.support_email}/>
        <Row label="Address" value={[org.address,org.city,org.state,org.postcode,org.country].filter(Boolean).join(", ")}/>
      </Section>

      <Section title="Billing">
        <div style={{marginBottom:8,display:"flex",gap:6,alignItems:"center"}}>
          <Chip s={plan}/>
          <Chip s={status}/>
        </div>
        <Row label="Billing Name" value={b.full_name}/>
        <Row label="Company / Reg No." value={b.company_number}/>
        <Row label="Invoice Email" value={b.invoice_email}/>
        <Row label="Address" value={[b.address_line1,b.address_line2,b.city,b.state,b.postcode,b.country].filter(Boolean).join(", ")}/>
      </Section>

      {!org.name&&!b.full_name&&(
        <div style={{textAlign:"center",padding:"20px 0",color:T.fnt}}>
          <Ic n="bld" s={24} c={S.brd}/>
          <p style={{fontSize:12,marginTop:8}}>No client details on file.</p>
          <p style={{fontSize:11,marginTop:4,color:T.fnt}}>Complete Organisation &amp; Billing in Settings.</p>
        </div>
      )}
    </div>
  );
};

// ─── PHelpdesk ──────────────────────────────────────────────────────────────
// Ticket list + detail view, styled to match PConversations.
// Talks only to hindle_api's /api/helpdesk/* proxy routes — never to the
// helpdesk domain directly. Tenant scoping is handled entirely server-side.
//
// Paste this component into chatbot_1.jsx (anywhere alongside PConversations),
// then add {id:"helpdesk",label:"Tickets",icon:"ticket"} to the tenant nav
// array and a matching case in the Dashboard router.

// ─── PHelpdesk (v2 — full agent parity) ─────────────────────────────────────
// Replaces the previous PHelpdesk component entirely.


const PHelpdesk = ({ config, autoOpenTicketId, onRequestAccess }) => {
  const [enabled, setEnabled]   = useState(null);
  const [loadErr, setLoadErr]   = useState("");
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selId, setSelId]       = useState(null);
  const [detail, setDetail]     = useState(null);
  const [messages, setMessages] = useState([]);
  const [csat, setCsat]         = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [linkedAR, setLinkedAR] = useState([]);
  const [sideConvs, setSideConvs] = useState([]);
  const [openSC, setOpenSC] = useState(null);
  const [scMessages, setScMessages] = useState([]);
  const [scBody, setScBody] = useState("");
  const [showNewSC, setShowNewSC] = useState(false);
  const [newSCSubject, setNewSCSubject] = useState("");
  const [newSCBody, setNewSCBody] = useState("");
  const [showMerge, setShowMerge] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);
  const [mergeErr, setMergeErr] = useState("");
  const [activeModal, setActiveModal] = useState(null);
  const [ssSession, setSsSession] = useState(null);
  const [ssStatus, setSsStatus] = useState("");
  const ssPcRef = useRef(null);
  const ssVideoRef = useRef(null);
  const ssPollRef = useRef(null);
  const [splitMode, setSplitMode] = useState(false);
  const [splitSelected, setSplitSelected] = useState([]);
  const [splitSubject, setSplitSubject] = useState("");
  const [splitting, setSplitting] = useState(false);
  const [splitErr, setSplitErr] = useState("");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject:"", description:"", requester_name:"", requester_email:"", priority:"medium", category_id:"" });
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [newTicketErr, setNewTicketErr] = useState("");
  const [categories, setCategories] = useState([]);
  const [resolutionTypes, setResolutionTypes] = useState([]);
  const [selectedResType, setSelectedResType] = useState("");
  const [agentsList, setAgentsList] = useState([]);
  const [canned, setCanned]     = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [cannedQ, setCannedQ]   = useState("");
  const [tab, setTab]           = useState("reply");
  const [body, setBody]         = useState("");
  const [sending, setSending]   = useState(false);
  const [statusF, setStatusF]   = useState("open");
  const [q, setQ]               = useState("");
  const [listCollapsed, setListCollapsed] = useState(false);
  const [myOnly, setMyOnly]     = useState(false);
  const [pageNum, setPageNum]   = useState(1);
  const PAGE_SIZE = 15;

  const currentAgentId = useMemo(() => {
    try {
      const p = JSON.parse(localStorage.getItem("hindle_admin_profile") || "{}");
      return p.id || p.email || p.name || null;
    } catch (e) { return null; }
  }, []);

  const authHeaders = useCallback(() => {
    let role = "";
    try { role = (JSON.parse(localStorage.getItem("hindle_admin_profile") || "{}").role) || ""; } catch(e){}
    return {
      "Content-Type": "application/json",
      "X-Org-Id": config?.orgId || config?.tenantId || "",
      ...(currentAgentId ? { "X-User-Id": currentAgentId } : {}),
      ...(role ? { "X-User-Role": role } : {}),
    };
  }, [config, currentAgentId]);

  const orgReady = !!(config?.orgId || config?.tenantId);

  const loadTickets = useCallback(() => {
    if (!orgReady) return; // guard against firing before the login/config async state has settled — avoids the "not enabled/not approved" flash on first load
    setLoading(true);
    const qs = new URLSearchParams({ limit: "50", ...(statusF ? { status: statusF } : {}), ...(q ? { q } : {}) });
    fetchRetry(`${API_URL}/api/helpdesk/tickets?${qs}`, { headers: authHeaders() })
      .then(async r => { if (r.status === 401) { handleUnauthorized(); throw new Error("Session expired"); } if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(d => { setEnabled(d.enabled !== false); setTickets(d.data || []); setLoadErr(""); })
      // A failed load is NOT "helpdesk disabled" — keep what's on screen and say so.
      .catch(e => setLoadErr(e.message || "Couldn't load tickets"))
      .finally(() => setLoading(false));
  }, [authHeaders, statusF, q, orgReady]);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  // Refresh when returning to the tab or reconnecting
  useEffect(() => {
    let hiddenAt = 0;
    const onVis = () => { if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; } if (hiddenAt && Date.now() - hiddenAt > 60000) loadTickets(); };
    document.addEventListener("visibilitychange", onVis); window.addEventListener("online", loadTickets);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("online", loadTickets); };
  }, [loadTickets]);

  useEffect(() => {
    if (!orgReady) return;
    fetch(`${API_URL}/api/helpdesk/categories`, { headers: authHeaders() }).then(r=>r.json()).then(d=>setCategories(d.data||[])).catch(()=>{});
    fetch(`${API_URL}/api/helpdesk/resolution-types`, { headers: authHeaders() }).then(r=>r.json()).then(d=>setResolutionTypes(d.data||[])).catch(()=>{});
    fetch(`${API_URL}/api/helpdesk/agents`, { headers: authHeaders() }).then(r=>r.json()).then(d=>setAgentsList(d.data||[])).catch(()=>{});
    fetch(`${API_URL}/api/helpdesk/canned-responses`, { headers: authHeaders() }).then(r=>r.json()).then(d=>setCanned(d.data||[])).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgReady]);

  const openTicket = useCallback((id) => {
    setSelId(id); setDetail(null); setMessages([]); setCsat(null); setAuditLog([]); setLinkedAR([]); setSideConvs([]); setOpenSC(null); setTab("reply"); setBody(""); setSelectedResType(""); setSplitMode(false); setSplitSelected([]); setSplitSubject(""); setSplitErr("");
    fetch(`${API_URL}/api/helpdesk/tickets/${id}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setDetail(d.data || d.ticket || null));
    fetch(`${API_URL}/api/helpdesk/tickets/${id}/messages`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setMessages(d.data || d.messages || []));
    fetch(`${API_URL}/api/helpdesk/tickets/${id}/csat`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setCsat(d.data || null)).catch(()=>{});
    fetch(`${API_URL}/api/helpdesk/tickets/${id}/audit-log`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setAuditLog(d.data || [])).catch(()=>{});
    fetch(`${API_URL}/api/access-requests?ticket_id=${id}`)
      .then(r => r.json()).then(d => setLinkedAR(d.requests || [])).catch(()=>setLinkedAR([]));
    fetch(`${API_URL}/api/helpdesk/tickets/${id}/side-conversations`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setSideConvs(d.data || [])).catch(()=>setSideConvs([]));
  }, [authHeaders]);

  useEffect(() => {
    if (autoOpenTicketId) openTicket(autoOpenTicketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTicketId]);

  const refreshMessages = useCallback(() => {
    if (!selId) return;
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}/messages`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setMessages(d.data || d.messages || []));
  }, [selId, authHeaders]);

  // Refetches just detail.updated_at (and status, since reopen-on-reply can
  // silently change it server-side) after any action that might have moved
  // the ticket forward — reply, note, cause, merge, split. Without this,
  // the NEXT action's expected_updated_at check goes stale and falsely
  // triggers the "someone else changed this" conflict warning every time.
  const refreshDetailTimestamp = useCallback(() => {
    if (!selId) return;
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const t = d.data || d.ticket;
        if (t) setDetail(dt => dt ? { ...dt, updated_at: t.updated_at, status: t.status } : dt);
      });
  }, [selId, authHeaders]);

  const send = useCallback(() => {
    if (!body.trim() || !selId) return;
    setSending(true);

    if (tab === "resolution") {
      fetch(`${API_URL}/api/helpdesk/tickets/${selId}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({ resolution: body, resolution_type_id: selectedResType || null, status: "resolved", expected_updated_at: detail?.updated_at }),
      }).then(async r => {
        const d = await r.json();
        if (r.status === 409) {
          alert(d.error || "This ticket was changed by someone else. Reloading the latest version.");
          if (d.latest) setDetail(dt => ({ ...dt, ...d.latest }));
          loadTickets();
          return;
        }
        if (d.data) setDetail(dt => dt ? { ...dt, status: d.data.status, resolution: d.data.resolution, resolution_type_id: d.data.resolution_type_id, updated_at: d.data.updated_at, resolved_at: d.data.resolved_at } : dt);
        setBody(""); loadTickets();
      }).finally(() => setSending(false));
      return;
    }

    const payload = tab === "cause"
      ? { body, is_cause: true, is_internal: true }
      : tab === "internal"
      ? { body, is_internal: true }
      : { body, is_internal: false };

    fetch(`${API_URL}/api/helpdesk/tickets/${selId}/messages`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(payload),
    })
      .then(() => { setBody(""); setShowCanned(false); refreshMessages(); refreshDetailTimestamp(); })
      .finally(() => setSending(false));
  }, [body, selId, tab, authHeaders, refreshMessages, refreshDetailTimestamp, loadTickets, detail, selectedResType]);

  const updateTicket = useCallback((patch) => {
    if (!selId || !detail) return;
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}`, {
      method: "PUT", headers: authHeaders(),
      body: JSON.stringify({ ...patch, expected_updated_at: detail.updated_at }),
    }).then(async r => {
      const d = await r.json();
      if (r.status === 409) {
        alert(d.error || "This ticket was changed by someone else. Reloading the latest version.");
        if (d.latest) setDetail(dt => ({ ...dt, ...d.latest }));
        loadTickets();
        return;
      }
      if (d.data) setDetail(dt => dt ? { ...dt, status: d.data.status, priority: d.data.priority, assigned_to: d.data.assigned_to, category_id: d.data.category_id, resolution: d.data.resolution, resolution_type_id: d.data.resolution_type_id, cause: d.data.cause, updated_at: d.data.updated_at, resolved_at: d.data.resolved_at, closed_at: d.data.closed_at } : dt);
      loadTickets();
    });
  }, [selId, detail, authHeaders, loadTickets]);
  
  const canDeleteHelpdeskTicket = (() => {
    try {
      const role = JSON.parse(localStorage.getItem("hindle_admin_profile") || "{}").role;
      return role === "tenant_admin" || role === "super_admin";
    } catch (e) { return false; }
  })();

  const deleteHelpdeskTicket = useCallback(() => {
    if (!selId || !detail) return;
    if (!window.confirm("Delete ticket #" + detail.ticket_number + "? This cannot be undone.")) return;
    fetch(API_URL + "/api/helpdesk/tickets/" + selId, { method: "DELETE", headers: authHeaders() })
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { alert(d.error || "Failed to delete ticket"); return; }
        setSelId(null);
        setDetail(null);
        loadTickets();
      });
  }, [selId, detail, authHeaders, loadTickets]);

  const createSideConv = useCallback(() => {
    if (!newSCSubject.trim() || !selId) return;
    let selfName = "Agent";
    try { const p = JSON.parse(localStorage.getItem("hindle_admin_profile") || "{}"); selfName = p.name || "Agent"; } catch(e){}
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}/side-conversations`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ subject: newSCSubject.trim(), first_message: newSCBody.trim() || null, created_by_name: selfName }),
    }).then(r => r.json()).then(d => {
      if (d.data) {
        setSideConvs(sc => [d.data, ...sc]);
        setShowNewSC(false); setNewSCSubject(""); setNewSCBody("");
        openSideConv(d.data.id);
      }
    });
  }, [newSCSubject, newSCBody, selId, authHeaders]);

  const openSideConv = useCallback((id) => {
    setOpenSC(id); setScMessages([]);
    fetch(`${API_URL}/api/helpdesk/side-conversations/${id}/messages`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setScMessages(d.data || []));
  }, [authHeaders]);

  const sendSCMessage = useCallback(() => {
    if (!scBody.trim() || !openSC) return;
    let selfName = "Agent";
    try { const p = JSON.parse(localStorage.getItem("hindle_admin_profile") || "{}"); selfName = p.name || "Agent"; } catch(e){}
    fetch(`${API_URL}/api/helpdesk/side-conversations/${openSC}/messages`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ message: scBody.trim(), sender_name: selfName }),
    }).then(() => {
      setScBody("");
      openSideConv(openSC);
    });
  }, [scBody, openSC, authHeaders, openSideConv]);

  const doMerge = useCallback(() => {
    if (!mergeTarget.trim() || !selId) return;
    setMerging(true); setMergeErr("");
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}/merge`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ merge_into_ticket_number: mergeTarget.trim() }),
    })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Merge failed"); return d; })
      .then(() => { setShowMerge(false); setMergeTarget(""); openTicket(selId); loadTickets(); })
      .catch(e => setMergeErr(e.message))
      .finally(() => setMerging(false));
  }, [mergeTarget, selId, authHeaders, loadTickets, openTicket]);

  const startScreenShare = useCallback(() => {
    if (!detail) return;
    setActiveModal("screenshare");
    setSsStatus("Creating session…");
    setSsSession(null);
    fetch(`${API_URL}/api/screen-share/create`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ org_id: config?.orgId || config?.tenantId, ticket_id: selId, ticket_number: detail.ticket_number }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.session_id) throw new Error(d.error || "Failed to create session");
        setSsSession({ id: d.session_id, customerUrl: d.customer_url });
        setSsStatus("Waiting for customer to join and start sharing…");

        // Poll for the customer's offer
        ssPollRef.current = setInterval(async () => {
          const r = await fetch(`${API_URL}/api/screen-share/${d.session_id}`);
          const dd = await r.json();
          const session = dd.session;
          if (!session) return;

          if (session.offer_sdp && !ssPcRef.current) {
            clearInterval(ssPollRef.current);
            setSsStatus("Customer is sharing — connecting…");

            const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
            ssPcRef.current = pc;
            pc.ontrack = (e) => {
              if (ssVideoRef.current) ssVideoRef.current.srcObject = e.streams[0];
              setSsStatus("Connected — live view below.");
            };
            pc.onicecandidate = (e) => {
              if (e.candidate) {
                fetch(`${API_URL}/api/screen-share/${d.session_id}/ice`, {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ role: "agent", candidate: e.candidate }),
                });
              }
            };

            await pc.setRemoteDescription({ type: "offer", sdp: session.offer_sdp });
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await fetch(`${API_URL}/api/screen-share/${d.session_id}/answer`, {
              method: "PUT", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ sdp: answer.sdp }),
            });

            // Poll for late customer ICE candidates
            ssPollRef.current = setInterval(async () => {
              const r2 = await fetch(`${API_URL}/api/screen-share/${d.session_id}`);
              const dd2 = await r2.json();
              const candidates = dd2.session?.customer_ice || [];
              for (const c of candidates) {
                try { await pc.addIceCandidate(c); } catch (e) {}
              }
              if (dd2.session?.status === "ended") {
                setSsStatus("Customer ended the session.");
                clearInterval(ssPollRef.current);
              }
            }, 3000);
          }
        }, 1500);
      })
      .catch(e => setSsStatus("Error: " + e.message));
  }, [detail, selId, config, authHeaders]);

  const stopScreenShare = useCallback(() => {
    if (ssPollRef.current) clearInterval(ssPollRef.current);
    if (ssPcRef.current) { ssPcRef.current.close(); ssPcRef.current = null; }
    if (ssSession?.id) fetch(`${API_URL}/api/screen-share/${ssSession.id}/end`, { method: "POST" });
    setSsSession(null); setSsStatus(""); setActiveModal(null);
  }, [ssSession]);


  const doSplit = useCallback(() => {
    if (!splitSubject.trim() || splitSelected.length === 0 || !selId) return;
    setSplitting(true); setSplitErr("");
    fetch(`${API_URL}/api/helpdesk/tickets/${selId}/split`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ message_ids: splitSelected, new_subject: splitSubject.trim() }),
    })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Split failed"); return d; })
      .then(d => {
        setSplitMode(false); setSplitSelected([]); setSplitSubject("");
        openTicket(selId); loadTickets();
        alert(`Split into new ticket #${d.new_ticket.ticket_number}`);
      })
      .catch(e => setSplitErr(e.message))
      .finally(() => setSplitting(false));
  }, [splitSubject, splitSelected, selId, authHeaders, loadTickets, openTicket]);

  const createTicket = useCallback(() => {
    if (!newTicket.subject.trim() || !newTicket.requester_email.trim()) {
      setNewTicketErr("Subject and requester email are required.");
      return;
    }
    setCreatingTicket(true); setNewTicketErr("");
    fetch(`${API_URL}/api/helpdesk/tickets`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify(newTicket),
    })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Failed to create ticket"); return d; })
      .then(d => {
        setShowNewTicket(false);
        setNewTicket({ subject:"", description:"", requester_name:"", requester_email:"", priority:"medium", category_id:"" });
        loadTickets();
        if (d.data?.id) openTicket(d.data.id);
      })
      .catch(e => setNewTicketErr(e.message))
      .finally(() => setCreatingTicket(false));
  }, [newTicket, authHeaders, loadTickets, openTicket]);


  const visibleTickets = useMemo(() => {
    let list = tickets;
    if (myOnly && currentAgentId) list = list.filter(t => t.assigned_to === currentAgentId);
    return list;
  }, [tickets, myOnly, currentAgentId]);

  const pagedTickets = useMemo(() => {
    const start = (pageNum - 1) * PAGE_SIZE;
    return visibleTickets.slice(start, start + PAGE_SIZE);
  }, [visibleTickets, pageNum]);

  const totalPages = Math.max(1, Math.ceil(visibleTickets.length / PAGE_SIZE));

  useEffect(() => { setPageNum(1); }, [myOnly, statusF, q]);

  const filteredCanned = canned.filter(c => !cannedQ || c.title?.toLowerCase().includes(cannedQ.toLowerCase()) || c.shortcut?.toLowerCase().includes(cannedQ.toLowerCase()));

  const slaText = useMemo(() => {
    if (!detail?.sla_due_at) return null;
    const ms = new Date(detail.sla_due_at) - new Date();
    if (ms <= 0) return { text: "SLA breached", over: true };
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
    return { text: `SLA: ${h}h ${m}m remaining`, over: false };
  }, [detail]);

  if (enabled === false) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ fontSize: 14, color: T.lt, marginBottom: 4 }}>Helpdesk isn't enabled for this account yet.</p>
        <p style={{ fontSize: 12.5, color: T.fnt }}>Contact your account manager to add ticket support.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {loadErr && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 14px", background: G.ab, borderBottom: `1px solid ${G.abd}`, color: G.a, fontSize: 12.5 }}>
          <span>⚠ {loadErr} — showing the last loaded tickets.</span>
          <button className="btn bo sm" onClick={loadTickets}>Retry</button>
        </div>
      )}
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      {showNewTicket && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 70 }} onClick={() => setShowNewTicket(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: S.w, borderRadius: 14, padding: 22, width: 440, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 14 }}>New Ticket</h3>
            {newTicketErr && <div style={{ background:G.rb, color:G.rt, fontSize: 12, padding: "8px 11px", borderRadius: 8, marginBottom: 10 }}>{newTicketErr}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Subject *</label>
                <input value={newTicket.subject} onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Description</label>
                <textarea value={newTicket.description} onChange={e => setNewTicket(t => ({ ...t, description: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5, resize: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Requester Name</label>
                <input value={newTicket.requester_name} onChange={e => setNewTicket(t => ({ ...t, requester_name: e.target.value }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Requester Email *</label>
                <input value={newTicket.requester_email} onChange={e => setNewTicket(t => ({ ...t, requester_email: e.target.value }))} placeholder="customer@example.com"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Priority</label>
                  <select value={newTicket.priority} onChange={e => setNewTicket(t => ({ ...t, priority: e.target.value }))}
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }}>
                    {["low","medium","high","urgent"].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Category</label>
                  <select value={newTicket.category_id} onChange={e => {
                      if (e.target.value === "__new__") {
                        const name = prompt("New category name:");
                        if (name?.trim()) {
                          fetch(`${API_URL}/api/helpdesk/categories`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ name: name.trim() }) })
                            .then(r => r.json()).then(d => { if (d.data) { setCategories(c => [...c, d.data]); setNewTicket(t => ({ ...t, category_id: d.data.id })); } });
                        }
                        return;
                      }
                      setNewTicket(t => ({ ...t, category_id: e.target.value }));
                    }}
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }}>
                    <option value="">No category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__new__">+ Add new category…</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowNewTicket(false)} className="btn bo sm">Cancel</button>
              <button onClick={createTicket} disabled={creatingTicket} className="btn bp sm">{creatingTicket ? <Spinner size={12}/> : "Create Ticket"}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ width: listCollapsed ? 0 : 340, minWidth: listCollapsed ? 0 : 340, borderRight: listCollapsed ? "none" : `1px solid ${S.brd}`, display: "flex", flexDirection: "column", background: S.w, overflow: "hidden", transition: "width .15s, min-width .15s" }}>
        <div style={{ padding: "12px 14px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.fnt, textTransform: "uppercase", letterSpacing: .5 }}>Tickets</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setShowNewTicket(true)} className="btn bp sm" style={{ fontSize: 11, padding: "4px 10px" }}>
              + New Ticket
            </button>
            <button onClick={() => setListCollapsed(true)} title="Minimise ticket list"
              style={{ background: "transparent", border: `1px solid ${S.brd}`, borderRadius: 6, cursor: "pointer", color: T.fnt, padding: "3px 4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Ic n="chL" s={13}/>
            </button>
          </div>
        </div>
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${S.brd}` }}>
          <input placeholder="Search tickets…" value={q} onChange={e => setQ(e.target.value)}
            style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5, marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {["", "open", "in_progress", "pending", "resolved", "closed"].map(s => (
              <button key={s} onClick={() => setStatusF(s)}
                style={{ padding: "3px 9px", borderRadius: 6, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${statusF === s ? B : S.brd}`, background: statusF === s ? BL : "transparent",
                  color: statusF === s ? B : T.fnt, textTransform: "capitalize" }}>
                {s || "All"}
              </button>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, cursor: "pointer" }}>
            <input type="checkbox" checked={myOnly} onChange={e => setMyOnly(e.target.checked)} />
            <span style={{ color: myOnly ? T.ink : T.fnt }}>My Tickets only</span>
          </label>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && <p style={{ padding: 16, fontSize: 12.5, color: T.fnt }}>Loading…</p>}
          {!loading && pagedTickets.length === 0 && <p style={{ padding: 16, fontSize: 12.5, color: T.fnt }}>No tickets found.</p>}
          {pagedTickets.map(t => {
            const sc = STATUS_COLORS[t.status] || STATUS_COLORS.open;
            return (
              <div key={t.id} onClick={() => openTicket(t.id)}
                style={{ padding: "11px 14px", borderBottom: `1px solid ${S.brd}`, cursor: "pointer", background: selId === t.id ? BL : "transparent" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.fnt }}>#{t.ticket_number}</span>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: PRIORITY_COLORS[t.priority]?.fg || T.fnt, marginTop: 3 }} />
                </div>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 4, lineHeight: 1.3 }}>{t.subject}</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: sc.bg, color: sc.fg, textTransform: "capitalize" }}>
                    {(t.status || "").replace("_", " ")}
                  </span>
                  <span style={{ fontSize: 10.5, color: T.fnt }}>{t.requester_name || t.requester_email || ""}</span>
                </div>
              </div>
            );
          })}
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", borderTop: `1px solid ${S.brd}` }}>
            <button onClick={() => setPageNum(p => Math.max(1, p - 1))} disabled={pageNum === 1}
              style={{ fontSize: 11, padding: "4px 9px", borderRadius: 6, border: `1px solid ${S.brd}`, background: "transparent", cursor: pageNum === 1 ? "default" : "pointer", opacity: pageNum === 1 ? 0.4 : 1 }}>← Prev</button>
            <span style={{ fontSize: 11, color: T.fnt }}>Page {pageNum} of {totalPages}</span>
            <button onClick={() => setPageNum(p => Math.min(totalPages, p + 1))} disabled={pageNum === totalPages}
              style={{ fontSize: 11, padding: "4px 9px", borderRadius: 6, border: `1px solid ${S.brd}`, background: "transparent", cursor: pageNum === totalPages ? "default" : "pointer", opacity: pageNum === totalPages ? 0.4 : 1 }}>Next →</button>
          </div>
        )}
      </div>

      <button onClick={() => setListCollapsed(c => !c)} title={listCollapsed ? "Show ticket list" : "Hide ticket list"}
        style={{ width: 20, flexShrink: 0, border: "none", borderRight: `1px solid ${S.brd}`, background: S.w, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.fnt }}>
        {listCollapsed ? "›" : "‹"}
      </button>

      <div style={{ flex: 1, display: "flex", background: S.bg }}>
        {!selId && <div style={{ margin: "auto", color: T.fnt, fontSize: 13 }}>Select a ticket to view details</div>}
        {selId && !detail && <div style={{ margin: "auto", color: T.fnt, fontSize: 13 }}>Loading…</div>}
        {selId && detail && (
          <>
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.brd}`, background: S.w }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: T.fnt, marginBottom: 2 }}>#{detail.ticket_number}</p>
                  <p style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{detail.subject}</p>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <select value={detail.status} onChange={e => updateTicket({ status: e.target.value })}
                    style={{ padding: "5px 9px", borderRadius: 7, border: "none", fontSize: 12, fontWeight: 700, background: (STATUS_COLORS[detail.status]||STATUS_COLORS.open).bg, color: (STATUS_COLORS[detail.status]||STATUS_COLORS.open).fg }}>
                    {["open", "in_progress", "pending", "resolved", "closed"].map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                  <select value={detail.priority || "medium"} onChange={e => updateTicket({ priority: e.target.value })}
                    style={{ padding: "5px 9px", borderRadius: 7, border: "none", fontSize: 12, fontWeight: 700, background: (PRIORITY_COLORS[detail.priority]||PRIORITY_COLORS.medium).bg, color: (PRIORITY_COLORS[detail.priority]||PRIORITY_COLORS.medium).fg }}>
                    {["low", "medium", "high", "urgent"].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: T.lt }}>{detail.requester_name} · {detail.requester_email}</span>
                {slaText && <span style={{ fontSize: 10.5, fontWeight: 700, color: slaText.over ? "#DC2626" : B }}>{slaText.text}</span>}
                {csat?.score && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#6B21A8" }}>★ CSAT: {csat.score}/{csat.scale || 5}</span>}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <select value={detail.category_id || ""} onChange={e => {
                    if (e.target.value === "__new__") {
                      const name = prompt("New category name:");
                      if (name?.trim()) {
                        fetch(`${API_URL}/api/helpdesk/categories`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ name: name.trim() }) })
                          .then(r => r.json()).then(d => { if (d.data) { setCategories(c => [...c, d.data]); updateTicket({ category_id: d.data.id }); } });
                      }
                      return;
                    }
                    updateTicket({ category_id: e.target.value || null });
                  }}
                  style={{ flex: 1, padding: "5px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 11.5 }}>
                  <option value="">No category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  <option value="__new__">+ Add new category…</option>
                </select>
                <select value={detail.assigned_to || ""} onChange={e => updateTicket({ assigned_to: e.target.value || null })}
                  style={{ flex: 1, padding: "5px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 11.5 }}>
                  <option value="">Unassigned</option>
                  {agentsList.map(a => <option key={a.id} value={a.id}>{a.full_name || a.name}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {!detail.merged_into_id && (
                  <button onClick={() => setShowMerge(true)}
                    style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 6, border: `1px solid ${S.brd}`, background: "transparent", color: T.fnt, cursor: "pointer" }}>
                    🔗 Merge into…
                  </button>
                )}
                {!detail.merged_into_id && (
                  <button onClick={() => { setSplitMode(m => !m); setSplitSelected([]); }}
                    style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 6, border: `1px solid ${splitMode ? B : S.brd}`, background: splitMode ? BL : "transparent", color: splitMode ? B : T.fnt, cursor: "pointer" }}>
                    ✂️ {splitMode ? "Cancel Split" : "Split…"}
                    
              {canDeleteHelpdeskTicket && !detail.merged_into_id && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={deleteHelpdeskTicket}
                    style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 6, border: "1px solid #DC2626", background: "transparent", color: "#DC2626", cursor: "pointer" }}>
                    🗑 Delete Ticket
                  </button>
                </div>
              )}
                  </button>
                )}
              </div>

              {showMerge && (
                <div style={{ marginTop: 8, background: S.bg, border: `1px solid ${S.brd}`, borderRadius: 8, padding: 10 }}>
                  {mergeErr && <p style={{ fontSize: 11, color:G.rt, marginBottom: 6 }}>{mergeErr}</p>}
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} placeholder="Target ticket number, e.g. 10005"
                      style={{ flex: 1, padding: "6px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 12 }} />
                    <button onClick={() => { setShowMerge(false); setMergeErr(""); }} className="btn bo sm" style={{ fontSize: 11 }}>Cancel</button>
                    <button onClick={doMerge} disabled={merging || !mergeTarget.trim()} className="btn bp sm" style={{ fontSize: 11 }}>
                      {merging ? "Merging…" : "Merge"}
                    </button>
                  </div>
                  <p style={{ fontSize: 10.5, color: T.fnt, marginTop: 5 }}>All messages from this ticket will move into the target ticket. This ticket becomes read-only.</p>
                </div>
              )}
            </div>

            {detail.merged_into_id && (
              <div style={{ background:G.ab, borderBottom:`1px solid ${G.abd}`, padding: "10px 18px", fontSize: 12.5, color:G.at, fontWeight: 600 }}>
                🔗 This ticket was merged into #{detail.merged_into_ticket_number}. It is now read-only.
              </div>
            )}

            <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
              {detail.description && (
                <div style={{ background: S.w, border: `1px solid ${S.brd}`, borderRadius: 10, padding: "10px 13px" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: T.fnt, marginBottom: 4 }}>ORIGINAL REQUEST</p>
                  <p style={{ fontSize: 12.5, color: T.mid, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{detail.description}</p>
                </div>
              )}
              {detail.cause && (
                <div style={{ background:G.ab, border:`1px solid ${G.abd}`, borderRadius: 10, padding: "10px 13px" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color:G.at, marginBottom: 4 }}>CAUSE</p>
                  <p style={{ fontSize: 12.5, color:G.at, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{detail.cause}</p>
                </div>
              )}
              {detail.resolution && (
                <div style={{ background:G.gb, border:`1px solid ${G.gbd}`, borderRadius: 10, padding: "10px 13px" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color:G.gt, marginBottom: 4 }}>
                    RESOLUTION{detail.resolution_type_name ? ` — ${detail.resolution_type_name}` : ""}
                  </p>
                  <p style={{ fontSize: 12.5, color: "#064E3B", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{detail.resolution}</p>
                </div>
              )}
              {messages.filter(m => !m.is_cause).map(m => (
                <div key={m.id} style={{ alignSelf: splitMode ? "stretch" : (m.is_internal ? "stretch" : (m.author_role === "agent" ? "flex-end" : "flex-start")), maxWidth: splitMode ? "100%" : (m.is_internal ? "100%" : "78%"), display: "flex", alignItems: "flex-start", gap: 8 }}>
                  {splitMode && (
                    <input type="checkbox" checked={splitSelected.includes(m.id)}
                      onChange={e => setSplitSelected(sel => e.target.checked ? [...sel, m.id] : sel.filter(id => id !== m.id))}
                      style={{ marginTop: 10, flexShrink: 0, cursor: "pointer" }} />
                  )}
                  <div style={{ flex: splitMode ? 1 : "initial" }}>
                    <div style={{
                      background: m.is_internal ? G.ab : (m.author_role === "agent" ? B : S.w),
                      color: m.is_internal ? G.at : (m.author_role === "agent" ? "#fff" : T.ink),
                      border: (!m.is_internal && m.author_role !== "agent") ? `1px solid ${S.brd}` : "none",
                      borderRadius: 12, padding: "9px 13px", fontSize: 12.5, lineHeight: 1.5 }}>
                      {m.is_internal && <span style={{ fontSize: 9.5, fontWeight: 700, display: "block", marginBottom: 3 }}>🔒 INTERNAL NOTE</span>}
                      {m.body || m.message}
                    </div>
                    <p style={{ fontSize: 10, color: T.fnt, marginTop: 3, textAlign: splitMode ? "left" : ((!m.is_internal && m.author_role === "agent") ? "right" : "left") }}>
                      {m.author_name || "—"}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {splitMode && (
              <div style={{ borderTop: `1px solid ${S.brd}`, background:G.ab, padding: "10px 18px" }}>
                {splitErr && <p style={{ fontSize: 11, color:G.rt, marginBottom: 6 }}>{splitErr}</p>}
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color:G.at, flexShrink: 0 }}>{splitSelected.length} selected</span>
                  <input value={splitSubject} onChange={e => setSplitSubject(e.target.value)} placeholder="Subject for new ticket…"
                    style={{ flex: 1, padding: "6px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 12 }} />
                  <button onClick={doSplit} disabled={splitting || splitSelected.length === 0 || !splitSubject.trim()} className="btn bp sm" style={{ fontSize: 11 }}>
                    {splitting ? "Splitting…" : "Split into New Ticket"}
                  </button>
                </div>
              </div>
            )}

          </div>

          <div style={{ width: 66, borderLeft: `1px solid ${S.brd}`, background: S.w, display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0", gap: 4, flexShrink: 0 }}>
            {[
              ["reply","💬","Reply"],
              ["internal","🔒","Note"],
              ["cause","🔍","Cause"],
              ["resolution","✅","Resolve"],
              ["audit","🕒","Audit"],
              ["sideconv","🧵","Side"],
              ["screenshare","📺","Screen"],
              ["access","🔒","Access"],
            ].map(([key,icon,label]) => (
              <button key={key} onClick={() => { if(key==="screenshare"){startScreenShare();}else{setActiveModal(key); if(["reply","internal","cause","resolution"].includes(key)) setTab(key);} }}
                title={label}
                style={{ width: 56, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "8px 2px", borderRadius: 8, border: "none",
                  background: activeModal===key ? BL : "transparent", cursor: "pointer", color: activeModal===key ? B : T.mid }}>
                <span style={{ fontSize: 16 }}>{icon}</span>
                <span style={{ fontSize: 9, fontWeight: 600 }}>{label}</span>
                {key==="audit" && auditLog.length>0 && <span style={{fontSize:8,fontWeight:700,color:T.fnt}}>{auditLog.length}</span>}
                {key==="sideconv" && sideConvs.length>0 && <span style={{fontSize:8,fontWeight:700,color:T.fnt}}>{sideConvs.length}</span>}
                {key==="access" && linkedAR.length>0 && <span style={{fontSize:8,fontWeight:700,color:T.fnt}}>{linkedAR.length}</span>}
              </button>
            ))}
          </div>

          {activeModal && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", zIndex: 60 }} onClick={() => activeModal === "screenshare" ? stopScreenShare() : setActiveModal(null)}>
              <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 0, right: 0, bottom: 0, background: S.w, width: 460, maxWidth: "92vw", display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,.18)", animation: "hcSlideIn .18s ease-out" }}>
                <style>{`@keyframes hcSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
                <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
                    {activeModal==="reply"?"Reply":activeModal==="internal"?"Internal Note":activeModal==="cause"?"Cause":activeModal==="resolution"?"Resolution":activeModal==="audit"?`Audit Trail (${auditLog.length})`:activeModal==="screenshare"?"Screen Share":activeModal==="access"?`Access Requests (${linkedAR.length})`:`Side Conversations (${sideConvs.length})`}
                  </h3>
                  <button onClick={() => activeModal === "screenshare" ? stopScreenShare() : setActiveModal(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.fnt }}><Ic n="x" s={16}/></button>
                </div>
                <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>

                  {activeModal === "access" && (
                    <div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                        <button onClick={() => onRequestAccess?.({ reason: `Investigating ticket #${detail.ticket_number}: ${detail.subject}`, ticket_id: selId, ticket_number: detail.ticket_number })}
                          className="btn bp sm" style={{ fontSize: 11 }}>
                          + Request Access
                        </button>
                      </div>
                      {linkedAR.length === 0 && <p style={{ fontSize: 12.5, color: T.fnt }}>No access requests linked to this ticket.</p>}
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {linkedAR.map(ar => {
                          const sc = AR_STATUS_COLORS[ar.status] || AR_STATUS_COLORS.expired;
                          return (
                            <div key={ar.id} style={{ background: S.bg, border: `1px solid ${S.brd}`, borderRadius: 8, padding: "8px 12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>{ar.reason}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: sc.bg, color: sc.fg, textTransform: "capitalize" }}>{ar.status}</span>
                              </div>
                              <p style={{ fontSize: 11, color: T.fnt, marginTop: 3 }}>{ar.created_at ? new Date(ar.created_at).toLocaleString() : ""}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {activeModal === "screenshare" && (
                    <div>
                      <p style={{ fontSize: 12.5, color: T.lt, marginBottom: 10 }}>{ssStatus}</p>
                      {ssSession && (
                        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                          <input readOnly value={ssSession.customerUrl}
                            style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 11.5, color: T.lt }} />
                          <button onClick={() => navigator.clipboard.writeText(ssSession.customerUrl)} className="btn bo sm" style={{ fontSize: 11 }}>Copy Link</button>
                        </div>
                      )}
                      <video ref={ssVideoRef} autoPlay playsInline
                        style={{ width: "100%", borderRadius: 10, background: "#0F172A", minHeight: 240 }} />
                      <p style={{ fontSize: 10.5, color: T.fnt, marginTop: 8 }}>Send the link above to the customer via SMS or email. View-only — you cannot control their screen.</p>
                      <button onClick={stopScreenShare} className="btn bo sm" style={{ fontSize: 11, marginTop: 10 }}>End Session</button>
                    </div>
                  )}

                  {["reply","internal","cause","resolution"].includes(activeModal) && !detail.merged_into_id && (
                    <div>
                      {activeModal === "resolution" && (
                        <div style={{ marginBottom: 8 }}>
                          <select value={selectedResType} onChange={e => {
                              if (e.target.value === "__new__") {
                                const name = prompt("New resolution type name:");
                                if (name?.trim()) {
                                  fetch(`${API_URL}/api/helpdesk/resolution-types`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ name: name.trim() }) })
                                    .then(r => r.json()).then(d => { if (d.data) { setResolutionTypes(rt => [...rt, d.data]); setSelectedResType(d.data.id); } });
                                }
                                return;
                              }
                              setSelectedResType(e.target.value);
                            }}
                            style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }}>
                            <option value="">Resolution type (optional)</option>
                            {resolutionTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                            <option value="__new__">+ Add new type…</option>
                          </select>
                        </div>
                      )}
                      <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
                        placeholder={activeModal === "reply" ? "Type your reply…" : activeModal === "internal" ? "Internal note (not visible to customer)…" : activeModal === "cause" ? "What caused this issue?…" : "Resolution summary…"}
                        style={{ width: "100%", padding: "8px 11px", borderRadius: 9, border: `1px solid ${S.brd}`, fontSize: 12.5, resize: "none", marginBottom: 8 }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ position: "relative" }}>
                          {activeModal === "reply" && (
                            <button onClick={() => setShowCanned(s => !s)} className="btn bo sm" style={{ fontSize: 11 }}>
                              ⚡ Canned {showCanned ? "▲" : "▼"}
                            </button>
                          )}
                          {showCanned && (
                            <div style={{ position: "absolute", bottom: "100%", left: 0, marginBottom: 6, width: 280, maxHeight: 260, overflowY: "auto", background: S.w, border: `1px solid ${S.brd}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 10 }}>
                              <div style={{ padding: 8, borderBottom: `1px solid ${S.brd}` }}>
                                <input value={cannedQ} onChange={e => setCannedQ(e.target.value)} placeholder="Search responses…"
                                  style={{ width: "100%", padding: "5px 8px", fontSize: 11.5, border: `1px solid ${S.brd}`, borderRadius: 6 }} />
                              </div>
                              {filteredCanned.length === 0 ? (
                                <p style={{ padding: 12, fontSize: 11.5, color: T.fnt, textAlign: "center" }}>No canned responses</p>
                              ) : filteredCanned.map(c => (
                                <button key={c.id} onClick={() => { setBody(b => (b ? b + "\n\n" : "") + c.body); setShowCanned(false); setCannedQ(""); }}
                                  style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", fontSize: 11.5, border: "none", borderBottom: `1px solid ${S.brd}`, background: "transparent", cursor: "pointer" }}>
                                  <span style={{ fontWeight: 600, color: T.ink }}>{c.title}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={send} disabled={sending || !body.trim()} className="btn bp sm">
                          {sending ? <Spinner size={12} /> : activeModal === "reply" ? "Send Reply" : activeModal === "internal" ? "Save Note" : activeModal === "cause" ? "Save Cause" : "Resolve & Save"}
                        </button>
                      </div>
                    </div>
                  )}

                  {["reply","internal","cause","resolution"].includes(activeModal) && detail.merged_into_id && (
                    <p style={{ fontSize: 12.5, color: T.fnt }}>This ticket was merged into #{detail.merged_into_ticket_number} and is now read-only.</p>
                  )}

                  {activeModal === "audit" && (
                    auditLog.length === 0 ? <p style={{fontSize:12.5,color:T.fnt}}>No audit events yet.</p> :
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {auditLog.map(e => {
                        const resolveVal = (field, val) => {
                          if (!val) return "—";
                          if (field === "category_id") return categories.find(c => c.id === val)?.name || val;
                          if (field === "assigned_to") return agentsList.find(a => a.id === val)?.full_name || val;
                          return val;
                        };
                        return (
                        <div key={e.id} style={{ fontSize: 12, color: T.lt, display: "flex", justifyContent: "space-between", gap: 8, paddingBottom:8, borderBottom:`1px solid ${S.brd}` }}>
                          <span>
                            <strong style={{ color: T.ink }}>{e.actor_name || "System"}</strong>{" "}
                            {e.action === "field_changed"
                              ? <>changed <strong>{e.field_name}</strong> from "{resolveVal(e.field_name, e.old_value)}" to "{resolveVal(e.field_name, e.new_value)}"</>
                              : e.action === "reply" ? "sent a reply"
                              : e.action === "internal_note" ? "added an internal note"
                              : e.action === "cause_added" ? "recorded the cause"
                              : e.action === "conflict_blocked" ? "attempted a change (blocked — outdated data)"
                              : e.action}
                          </span>
                          <span style={{ flexShrink: 0, color: T.fnt }}>{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {activeModal === "sideconv" && (
                    <div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                        <button onClick={() => setShowNewSC(s => !s)} className="btn bo sm" style={{ fontSize: 11 }}>+ New Thread</button>
                      </div>
                      {showNewSC && (
                        <div style={{ background: S.bg, border: `1px solid ${S.brd}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                          <input value={newSCSubject} onChange={e => setNewSCSubject(e.target.value)} placeholder="Subject (e.g. 'Need billing input from Sam')"
                            style={{ width: "100%", padding: "6px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 12, marginBottom: 6 }} />
                          <textarea value={newSCBody} onChange={e => setNewSCBody(e.target.value)} rows={2} placeholder="Opening message (optional)…"
                            style={{ width: "100%", padding: "6px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 12, resize: "none", marginBottom: 6 }} />
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                            <button onClick={() => setShowNewSC(false)} className="btn bo sm" style={{ fontSize: 11 }}>Cancel</button>
                            <button onClick={createSideConv} disabled={!newSCSubject.trim()} className="btn bp sm" style={{ fontSize: 11 }}>Start Thread</button>
                          </div>
                        </div>
                      )}
                      {sideConvs.length > 0 && !openSC && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {sideConvs.map(sc => (
                            <div key={sc.id} onClick={() => openSideConv(sc.id)}
                              style={{ background: S.bg, border: `1px solid ${S.brd}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>{sc.subject}</span>
                              <span style={{ fontSize: 10.5, color: T.fnt }}>{sc.message_count} msg{sc.message_count === 1 ? "" : "s"} · {sc.created_by_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sideConvs.length === 0 && !showNewSC && <p style={{fontSize:12.5,color:T.fnt}}>No side conversations yet.</p>}
                      {openSC && (
                        <div style={{ background: S.bg, border: `1px solid ${S.brd}`, borderRadius: 10, padding: 10, marginTop: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{sideConvs.find(sc => sc.id === openSC)?.subject}</span>
                            <button onClick={() => setOpenSC(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.fnt }}><Ic n="x" s={13}/></button>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto", marginBottom: 8 }}>
                            {scMessages.map(m => (
                              <div key={m.id} style={{ fontSize: 11.5 }}>
                                <strong style={{ color: T.ink }}>{m.sender_name}:</strong> <span style={{ color: T.mid }}>{m.message}</span>
                              </div>
                            ))}
                            {scMessages.length === 0 && <p style={{ fontSize: 11.5, color: T.fnt }}>No messages yet.</p>}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <input value={scBody} onChange={e => setScBody(e.target.value)} placeholder="Reply in this thread…"
                              onKeyDown={e => { if (e.key === "Enter") sendSCMessage(); }}
                              style={{ flex: 1, padding: "6px 9px", borderRadius: 7, border: `1px solid ${S.brd}`, fontSize: 12 }} />
                            <button onClick={sendSCMessage} disabled={!scBody.trim()} className="btn bp sm" style={{ fontSize: 11 }}>Send</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
    </div>
  );
};



const PAccessRequests = ({ config, prefill, clearPrefill }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusF, setStatusF]   = useState("");
  const [selId, setSelId]       = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [revoking, setRevoking] = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const tenantName = config?.name || config?.orgName || (()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.org?.name || null;}}catch(e){}return null;})() || "Hindle Consultants";
  const [newReq, setNewReq]     = useState({ requester_name:"", requester_email:"", reason:"", scope:"", requested_duration_minutes:60, delivery_method:"both", approver_name:"", approver_email:"", approver_mobile:"", on_behalf_of:tenantName, ticket_id:null, ticket_number:null });

  const orgId = config?.orgId || config?.tenantId || (()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})() || "";

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ ...(orgId ? { org_id: orgId } : {}), ...(statusF ? { status: statusF } : {}) });
    fetch(`${API_URL}/api/access-requests?${qs}`)
      .then(r => r.json())
      .then(d => setRequests(d.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [orgId, statusF]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prefill) {
      setNewReq(r => ({ ...r, reason: prefill.reason || r.reason, ticket_id: prefill.ticket_id || null, ticket_number: prefill.ticket_number || null }));
      setShowNew(true);
      clearPrefill?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const viewAudit = useCallback((id) => {
    setSelId(id); setAuditLog([]);
    fetch(`${API_URL}/api/access-audit-log?access_request_id=${id}`)
      .then(r => r.json()).then(d => setAuditLog(d.log || []));
  }, []);

  const revoke = useCallback((id) => {
    setRevoking(true);
    fetch(`${API_URL}/api/access-requests/${id}/revoke`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ revoked_by: "dashboard" }),
    }).then(load).finally(() => setRevoking(false));
  }, [load]);

  const deleteRequest = useCallback((id) => {
    fetch(`${API_URL}/api/access-requests/${id}`, { method: "DELETE" }).then(load);
  }, [load]);

  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState(null); // { ok, text }
  const resendRequest = useCallback((id) => {
    setResendingId(id); setResendMsg(null);
    fetch(`${API_URL}/api/access-requests/${id}/resend`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resent_by: "dashboard" }),
    })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok || d.ok === false) throw new Error(d.error || "Resend failed"); return d; })
      .then(d => {
        const via = [d.sent?.email && "email", d.sent?.sms && "SMS"].filter(Boolean).join(" + ");
        setResendMsg({ ok: true, text: via ? `New approval link sent via ${via}. Valid for 1 hour.` : "Request reset, but no email/SMS could be delivered — check the approver contact details and ClickSend settings." });
        load();
        if (selId === id) viewAudit(id);
      })
      .catch(e => setResendMsg({ ok: false, text: e.message }))
      .finally(() => setResendingId(null));
  }, [load, selId, viewAudit]);

  const createRequest = useCallback(() => {
    if (!newReq.requester_name.trim() || !newReq.reason.trim()) { setCreateErr("Name and reason are required."); return; }
    if (!newReq.scope.trim()) { setCreateErr("Scope is required."); return; }
    if (!newReq.requested_duration_minutes) { setCreateErr("Duration is required."); return; }
    if (!orgId) { setCreateErr("Missing organisation — please reload the page and try again."); return; }
    setCreating(true); setCreateErr("");
    const payload = { org_id: orgId, ...newReq };
    console.log("[AccessRequest] sending payload:", payload);
    fetch(`${API_URL}/api/access-requests`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async r => { const d = await r.json(); if (!r.ok || d.ok === false) throw new Error(d.error || "Failed to create request"); return d; })
      .then((d) => {
        // If this access request is linked to a ticket, add an internal
        // note to that ticket's message history so agents see it happened
        // right there in the conversation, not just on the Access Requests page.
        if (newReq.ticket_id) {
          fetch(`${API_URL}/api/helpdesk/tickets/${newReq.ticket_id}/messages`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Org-Id": orgId || "" },
            body: JSON.stringify({
              body: `🔒 Access request created by ${newReq.requester_name}. Reason: ${newReq.reason}${newReq.scope ? ` — Scope: ${newReq.scope}` : ""}`,
              is_internal: true,
            }),
          }).catch(() => {});
        }
        setShowNew(false);
        setNewReq({ requester_name:"", requester_email:"", reason:"", scope:"", requested_duration_minutes:60, delivery_method:"both", approver_name:"", approver_email:"", approver_mobile:"", on_behalf_of:tenantName, ticket_id:null, ticket_number:null });
        load();
      })
      .catch(e => setCreateErr(e.message))
      .finally(() => setCreating(false));
  }, [newReq, orgId, load, tenantName]);

  const selected = requests.find(r => r.id === selId);

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>Access Requests</h2>
          <p style={{ fontSize: 12.5, color: T.lt }}>Support access grants — approved via emailed token link, revocable here.</p>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {["", "pending", "approved", "revoked", "expired", "denied"].map(s => (
            <button key={s} onClick={() => setStatusF(s)}
              style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${statusF === s ? B : S.brd}`, background: statusF === s ? BL : "transparent",
                color: statusF === s ? B : T.fnt, textTransform: "capitalize" }}>
              {s || "All"}
            </button>
          ))}
          <button onClick={() => setShowNew(true)} className="btn bp sm" style={{ marginLeft: 8 }}>+ New Request</button>
        </div>
      </div>

      {showNew && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={() => setShowNew(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: S.w, borderRadius: 14, padding: 22, width: 420, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 4 }}>New Access Request</h3>
            {newReq.ticket_number && (
              <p style={{ fontSize: 11.5, color: B, background: BL, display: "inline-block", padding: "3px 9px", borderRadius: 6, marginBottom: 12, fontWeight: 600 }}>
                🎫 Linked to Ticket #{newReq.ticket_number}
              </p>
            )}
            {createErr && <div style={{ background:G.rb, color:G.rt, fontSize: 12, padding: "8px 11px", borderRadius: 8, marginBottom: 10 }}>{createErr}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Requester Name *</label>
                <input value={newReq.requester_name} onChange={e => setNewReq(r => ({ ...r, requester_name: e.target.value }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>On Behalf Of</label>
                <input value={newReq.on_behalf_of} onChange={e => setNewReq(r => ({ ...r, on_behalf_of: e.target.value }))} placeholder="Hindle Consultants"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div style={{ borderTop: `1px solid ${S.brd}`, margin: "4px 0", paddingTop: 10 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.ink, marginBottom: 8 }}>Requesting access from</p>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Approver Name</label>
                <input value={newReq.approver_name} onChange={e => setNewReq(r => ({ ...r, approver_name: e.target.value }))} placeholder="e.g. Sam Pennance"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5, marginBottom: 8 }} />
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Approver Email</label>
                <input value={newReq.approver_email} onChange={e => setNewReq(r => ({ ...r, approver_email: e.target.value }))} placeholder="sam@client.com"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5, marginBottom: 8 }} />
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Approver Mobile</label>
                <input value={newReq.approver_mobile} onChange={e => setNewReq(r => ({ ...r, approver_mobile: e.target.value }))} placeholder="+61 4xx xxx xxx"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Reason *</label>
                <textarea value={newReq.reason} onChange={e => setNewReq(r => ({ ...r, reason: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5, resize: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 3 }}>Scope *</label>
                <input value={newReq.scope} onChange={e => setNewReq(r => ({ ...r, scope: e.target.value }))} placeholder="e.g. tenant_config, billing, full"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${S.brd}`, fontSize: 12.5 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 5 }}>Duration</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[["1 hour",60],["3 hours",180],["1 day",1440],["2 days",2880],["3 days",4320]].map(([l,v]) => (
                    <button key={v} onClick={() => setNewReq(r => ({ ...r, requested_duration_minutes: v }))}
                      style={{ padding: "6px 11px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                        border: `1px solid ${newReq.requested_duration_minutes === v ? B : S.brd}`, background: newReq.requested_duration_minutes === v ? BL : "transparent",
                        color: newReq.requested_duration_minutes === v ? B : T.fnt }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.lt, display: "block", marginBottom: 5 }}>Notify approver via</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["sms","SMS"],["email","Email"],["both","Both"]].map(([v,l]) => (
                    <button key={v} onClick={() => setNewReq(r => ({ ...r, delivery_method: v }))}
                      style={{ flex: 1, padding: "7px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: `1px solid ${newReq.delivery_method === v ? B : S.brd}`, background: newReq.delivery_method === v ? BL : "transparent",
                        color: newReq.delivery_method === v ? B : T.fnt }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowNew(false)} className="btn bo sm">Cancel</button>
              <button onClick={createRequest} disabled={creating} className="btn bp sm">{creating ? <Spinner size={12}/> : "Send Request"}</button>
            </div>
          </div>
        </div>
      )}

      {resendMsg && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: resendMsg.ok ? G.gb : G.rb, color: resendMsg.ok ? G.gt : G.rt, fontSize: 12.5, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
          <span>{resendMsg.text}</span>
          <button onClick={() => setResendMsg(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit" }}><Ic n="x" s={12}/></button>
        </div>
      )}
      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1, background: S.w, border: `1px solid ${S.brd}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr style={{ background: S.bg }}>
              {["Requester", "Org", "Ticket", "Reason", "Scope", "Status", "Requested", ""].map(h =>
                <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 10.5, fontWeight: 700, color: T.fnt, textTransform: "uppercase" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: T.fnt }}>Loading…</td></tr>}
              {!loading && requests.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: T.fnt }}>No access requests found.</td></tr>}
              {requests.map(r => {
                const sc = AR_STATUS_COLORS[r.status] || AR_STATUS_COLORS.expired;
                return (
                  <tr key={r.id} onClick={() => viewAudit(r.id)} style={{ borderTop: `1px solid ${S.brd}`, cursor: "pointer", background: selId === r.id ? BL : "transparent" }}>
                    <td style={{ padding: "9px 12px", fontWeight: 600, color: T.ink }}>{r.requester_name || "—"}</td>
                    <td style={{ padding: "9px 12px", color: T.lt }}>{r.org_name || "—"}</td>
                    <td style={{ padding: "9px 12px", color: T.lt }}>{r.ticket_number ? `#${r.ticket_number}` : "—"}</td>
                    <td style={{ padding: "9px 12px", color: T.lt, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.reason || "—"}</td>
                    <td style={{ padding: "9px 12px", color: T.lt }}>{r.scope || "—"}</td>
                    <td style={{ padding: "9px 12px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: sc.bg, color: sc.fg, textTransform: "capitalize" }}>{r.status}</span>
                    </td>
                    <td style={{ padding: "9px 12px", color: T.fnt, fontSize: 11 }}>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(r.status === "pending" || r.status === "expired") && (
                          <button onClick={(e) => { e.stopPropagation(); resendRequest(r.id); }} disabled={resendingId === r.id}
                            title={r.expires_at ? `Current link ${new Date(r.expires_at) < new Date() ? "expired" : "expires"} ${new Date(r.expires_at).toLocaleString()}` : "Send a new approval link"}
                            style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 6, border: `1px solid ${B}`, background: BL, color: B, cursor: "pointer", fontWeight: 600 }}>
                            {resendingId === r.id ? "Sending…" : "Resend"}
                          </button>
                        )}
                        {r.status === "approved" && (
                          <button onClick={(e) => { e.stopPropagation(); revoke(r.id); }} disabled={revoking}
                            style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 6, border:`1px solid ${G.rbd}`, background:G.rb, color:G.rt, cursor: "pointer", fontWeight: 600 }}>
                            Revoke
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete this access request permanently?`)) deleteRequest(r.id); }}
                          style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 6, border: `1px solid ${S.brd}`, background: "transparent", color: T.fnt, cursor: "pointer", fontWeight: 600 }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selId && (
          <div style={{ width: 320, background: S.w, border: `1px solid ${S.brd}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Audit Trail</h3>
              <button onClick={() => setSelId(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.fnt }}><Ic n="x" s={14}/></button>
            </div>
            {selected && (
              <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${S.brd}`, fontSize: 11.5, color: T.lt }}>
                <p><strong style={{ color: T.ink }}>{selected.requester_name}</strong> — {selected.org_name}</p>
                <p style={{ marginTop: 4 }}>{selected.reason}</p>
              </div>
            )}
            {auditLog.length === 0 && <p style={{ fontSize: 11.5, color: T.fnt }}>No events logged yet.</p>}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {auditLog.map((e, i) => (
                <div key={i} style={{ fontSize: 11, borderLeft: `2px solid ${S.brd}`, paddingLeft: 8 }}>
                  <p style={{ fontWeight: 600, color: T.ink }}>{e.action}</p>
                  <p style={{ color: T.fnt }}>{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PHelpdeskDashboard = ({ config }) => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hover, setHover] = useState(null);
  const load = useCallback(() => {
    setLoading(true); setErr("");
    fetchRetry(`${API_URL}/api/helpdesk/reports/summary?days=${days}`)
      .then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || `Error ${r.status}`); setD(j.data || null); })
      .catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const fmtH = h => h == null ? "—" : h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${h}h` : `${(h / 24).toFixed(1)}d`;
  const Kpi = ({ label, value, sub, col, icon }) => (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: T.lt, textTransform: "uppercase", letterSpacing: .6 }}>{label}</p>
        {icon && <span style={{ width: 28, height: 28, borderRadius: 8, background: `${col || B}18`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={icon} s={14} c={col || B} /></span>}
      </div>
      <p style={{ fontSize: 26, fontWeight: 700, color: T.ink, marginTop: 4, letterSpacing: -.5 }}>{value}</p>
      {sub && <p style={{ fontSize: 11.5, color: T.fnt, marginTop: 2 }}>{sub}</p>}
    </div>
  );
  const Bars = ({ title, data, order, colorFor }) => {
    const total = order.reduce((a, k) => a + (data?.[k] || 0), 0) || 1;
    return (
      <div className="card" style={{ padding: "14px 16px" }}>
        <h3 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 12 }}>{title}</h3>
        {order.map(k => { const v = data?.[k] || 0; const c = colorFor(k); return (
          <div key={k} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 4 }}><span style={{ color: T.mid, textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</span><span style={{ fontWeight: 600, color: T.ink }}>{v}</span></div>
            <div style={{ height: 8, borderRadius: 4, background: S.sub, overflow: "hidden" }}><div style={{ width: `${(v / total) * 100}%`, height: "100%", background: c, borderRadius: 4, transition: "width .3s" }} /></div>
          </div>); })}
      </div>
    );
  };
  const Trend = ({ daily }) => {
    const W = 760, H = 180, P = 24;
    const max = Math.max(1, ...daily.map(x => Math.max(x.created, x.resolved)));
    const bw = (W - P * 2) / Math.max(1, daily.length);
    const y = v => H - P - (v / max) * (H - P * 2);
    return (
      <div className="card" style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ fontSize: 13.5, fontWeight: 700 }}>Created vs resolved</h3>
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: T.lt }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: B }} />Created</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: G.g }} />Resolved</span>
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} onMouseLeave={() => setHover(null)}>
            {[0, .5, 1].map(f => <line key={f} x1={P} x2={W - P} y1={y(max * f)} y2={y(max * f)} stroke={S.brd} strokeDasharray={f ? "3 4" : "0"} />)}
            <text x={P - 6} y={y(max) + 4} fontSize="10" fill={T.fnt} textAnchor="end">{max}</text>
            {daily.map((x, i) => { const cx = P + i * bw; const w = Math.max(1, bw / 2 - 1.5); return (
              <g key={x.day} onMouseEnter={() => setHover(i)}>
                <rect x={cx} y={P} width={bw} height={H - P * 2} fill={hover === i ? `${B}10` : "transparent"} />
                <rect x={cx + 1} y={y(x.created)} width={w} height={H - P - y(x.created)} rx="2" fill={B} />
                <rect x={cx + 1 + w + 1} y={y(x.resolved)} width={w} height={H - P - y(x.resolved)} rx="2" fill={G.g} />
              </g>); })}
            {daily.length > 0 && [0, Math.floor(daily.length / 2), daily.length - 1].map(i => <text key={i} x={P + i * bw + bw / 2} y={H - 6} fontSize="10" fill={T.fnt} textAnchor="middle">{new Date(daily[i].day + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })}</text>)}
          </svg>
          {hover != null && daily[hover] && (
            <div style={{ position: "absolute", top: 0, left: `${Math.min(80, (hover / daily.length) * 100)}%`, background: S.w, border: `1px solid ${S.brd}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, boxShadow: S.shadowLg, pointerEvents: "none" }}>
              <b>{new Date(daily[hover].day + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</b><br />
              Created {daily[hover].created} · Resolved {daily[hover].resolved}
            </div>)}
        </div>
      </div>
    );
  };
  return (
    <div style={{ padding: 20, overflowY: "auto", height: "100%" }}>
      <div style={{ maxWidth: 1100 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
          <p style={{ fontSize: 13, color: T.lt }}>Ticket volume, speed and team performance.</p>
          <div className="toggle">{[7, 30, 90].map(n => <button key={n} className={`toggle-opt${days === n ? " active" : ""}`} onClick={() => setDays(n)}>{n} days</button>)}</div>
        </div>
        {err && <div style={{ padding: "8px 12px", background: G.rb, border: `1px solid ${G.rbd}`, borderRadius: 8, fontSize: 12.5, color: G.rt, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>{err}</span><button className="btn bo sm" onClick={load}>Retry</button></div>}
        {loading && !d ? <div style={{ padding: 40, textAlign: "center", color: T.fnt }}>Loading…</div> : d && (<>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 12 }}>
            <Kpi label="Open now" value={d.open_total} sub={`${d.unassigned} unassigned`} icon="ticket" />
            <Kpi label={`Created (${d.days}d)`} value={d.created} icon="plus" col={G.p} />
            <Kpi label={`Resolved (${d.days}d)`} value={d.resolved} icon="chk" col={G.g} />
            <Kpi label="First response" value={fmtH(d.avg_first_response_h)} sub="average" icon="zap" col={G.a} />
            <Kpi label="Resolution time" value={fmtH(d.avg_resolution_h)} sub="average" icon="cal" col={CS} />
            <Kpi label="CSAT" value={d.csat_avg != null ? `${d.csat_avg}/5` : "—"} sub={`${d.csat_n} rating${d.csat_n === 1 ? "" : "s"}`} icon="star" col={G.a} />
            <Kpi label="Open > 7 days" value={d.older_7d} sub="still open" icon="warn" col={d.older_7d ? G.r : T.fnt} />
          </div>
          <div style={{ marginBottom: 12 }}><Trend daily={d.daily || []} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 12, marginBottom: 12 }}>
            <Bars title="By status" data={d.by_status} order={["open", "in_progress", "pending", "resolved", "closed"]} colorFor={k => (STATUS_COLORS[k] || STATUS_COLORS.open).fg} />
            <Bars title="Open by priority" data={d.open_by_priority} order={["urgent", "high", "medium", "low"]} colorFor={k => (PRIORITY_COLORS[k] || PRIORITY_COLORS.medium).fg} />
          </div>
          <div className="card" style={{ overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.brd}` }}><h3 style={{ fontSize: 13.5, fontWeight: 700 }}>Agent performance</h3><p style={{ fontSize: 12, color: T.lt, marginTop: 2 }}>Last {d.days} days, by assigned agent.</p></div>
            {(d.agents || []).length === 0 ? <p style={{ padding: 18, color: T.fnt, fontSize: 13 }}>No agents yet.</p> : (
              <table className="tbl"><thead><tr><th>Agent</th><th>Open</th><th>Resolved</th><th>Avg resolution</th></tr></thead>
                <tbody>{d.agents.map(a => (<tr key={a.id}>
                  <td><div style={{ display: "flex", alignItems: "center", gap: 9 }}><Av name={a.name || "?"} size={26} /><span style={{ fontWeight: 600 }}>{a.name}</span></div></td>
                  <td>{a.open}</td><td>{a.resolved}</td><td>{fmtH(a.avg_resolution_h)}</td></tr>))}</tbody></table>)}
          </div>
        </>)}
      </div>
    </div>
  );
};

const PConversations=({agents,alertLog,setAlertLog,conversations,setConversations,updateConversation,addAlert,plan,autoOpenConvId,refreshConversations,config})=>{
  const [sel,setSel]=useState(null);
  const [msgs,setMsgs]=useState([]);
  const [inp,setInp]=useState("");
  const [noteMode,setNoteMode]=useState(false);
  const [priority,setPriority]=useState(null);
  const [suggestions,setSuggestions]=useState([]);
  const [sugLoading,setSugLoading]=useState(false);
  const [sugOpen,setSugOpen]=useState(false);
  const [sugError,setSugError]=useState(null);
  const [summary,setSummary]=useState(null);
  const [summaryLoading,setSummaryLoading]=useState(false);
  const [sentiment,setSentiment]=useState(null); // {sentiment, score}
  const [snoozeOpen,setSnoozeOpen]=useState(false);
  const [emailReplyOpen,setEmailReplyOpen]=useState(false);
  const [emailDraft,setEmailDraft]=useState({to:"",subject:"",body:""}); // null|"urgent"|"high"|"normal"|"low"
  const [flt,setFlt]=useState("active"); // active = all non-resolved
  // Agents default to Me=true; tenant admins default to false (see all)
  const _initShowMine=(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");return p.role==="agent";}catch(e){return false;}})();
  const [showMine,setShowMine]=useState(_initShowMine);
  // Check if this agent is restricted to only their own convs (cannot toggle to see all)
  const _agentRestricted=(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");if(p.role!=="agent")return false;const agentId=p.id||null;if(!agentId)return false;const found=(agents||[]).find(a=>a.id===agentId);return found?.restrict_to_mine===true;}catch(e){return false;}})();
  const [convSearch,setConvSearch]=useState("");
  const [lstOpen,setLstOpen]=useState(true);
  const [rTab,setRTab]=useState("visitor");
  const [rOpen,setROpen]=useState(true);
  const [handoffState,setHandoffState]=useState(null);
  const [sentTo,setSentTo]=useState([]);
  const [qSearch,setQSearch]=useState("");
  const [kbSearch,setKbSearch]=useState("");
  const [kbExpandedId,setKbExpandedId]=useState(null);
  const [kbHoverId,setKbHoverId]=useState(null);
  const [visitorTyping,setVisitorTyping]=useState(false);
  const [transferOpen,setTransferOpen]=useState(false);
  const typingTimer=React.useRef(null);
  const agentTypingTimer=React.useRef(null);
  const lastMsgCountRef=React.useRef(0);
  const soundEnabledRef=React.useRef(true);
  const [quickReplies,setQuickReplies]=useState(()=>{try{const s=localStorage.getItem("hindle_quick_replies");return s?JSON.parse(s):["Thanks for reaching out!","Let me check that for you.","Can you share more details?","One moment please.","I'll escalate this to our team.","Is there anything else I can help with?"];}catch(e){return [];}});
  const [kbDocs,setKbDocs]=useState(()=>{try{const s=localStorage.getItem("hindle_chatbot_kb");return s?JSON.parse(s):[];}catch(e){return [];}});
  const [kbLoading,setKbLoading]=useState(false);
  // Fetch KB from DB on mount — overrides localStorage with live data
  useEffect(()=>{
    const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(!orgId)return;
    setKbLoading(true);
    fetch(API_URL+"/api/kb?org_id="+orgId)
      .then(r=>r.ok?r.json():null)
      .then(rows=>{
        if(rows&&Array.isArray(rows)){
          const mapped=rows.map(r=>({
            id:r.id,name:r.name,
            category:r.category||"",sub_category:r.sub_category||"",tertiary:r.tertiary||"",
            content:r.content||"",size:r.size_kb?r.size_kb+" KB":"",
            chunks:r.chunks||1,status:r.status||"indexed",updated:r.updated_at||""
          }));
          setKbDocs(mapped);
        }
      })
      .catch(()=>{})
      .finally(()=>setKbLoading(false));
  },[]);
  const msgContainerRef=useRef(null);
  // Wrap updateConversation so sel is always kept in sync when the selected conversation changes
  const updateConv=useCallback(async(id,data)=>{
    const c=await updateConversation(id,data);
    setSel(prev=>prev?.id===id?{...prev,...c}:prev);
    return c;
  },[updateConversation]);
  const [assignOpen,setAssignOpen]=useState(false);

  // ── Sound alert helper ────────────────────────────────────────
  const playNewMessageSound=useCallback(()=>{
    if(!soundEnabledRef.current)return;
    try{
      const ctx=new(window.AudioContext||window.webkitAudioContext)();
      const osc=ctx.createOscillator();
      const gain=ctx.createGain();
      osc.connect(gain);gain.connect(ctx.destination);
      osc.frequency.value=880;osc.type="sine";
      gain.gain.setValueAtTime(0.3,ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.4);
      osc.start(ctx.currentTime);osc.stop(ctx.currentTime+0.4);
    }catch(_){}
  },[]);

  // ── Browser notification helper ───────────────────────────────
  const showBrowserNotif=useCallback((title,body)=>{
    if(!("Notification"in window))return;
    if(Notification.permission==="granted"){
      new Notification(title,{body,icon:"/favicon.ico"});
    }else if(Notification.permission!=="denied"){
      Notification.requestPermission().then(p=>{
        if(p==="granted")new Notification(title,{body,icon:"/favicon.ico"});
      });
    }
  },[]);

  // Load quickReplies from DB on mount
  useEffect(()=>{
    const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(!orgId) return;
    fetch(API_URL+"/api/tenant-config/"+orgId)
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d?.quickReplies&&Array.isArray(d.quickReplies)&&d.quickReplies.length){
          setQuickReplies(d.quickReplies);
          try{localStorage.setItem("hindle_quick_replies",JSON.stringify(d.quickReplies));}catch(e){}
        }
      }).catch(()=>{});
  },[]);

  // Auto-open from magic link
  useEffect(()=>{
    if(!autoOpenConvId) return;
    if(conversations.length){const m=conversations.find(c=>c.id===autoOpenConvId);if(m){setSel(m);return;}}
    api.get(`/api/conversations/${autoOpenConvId}`)
      .then(conv=>{if(conv?.id){setConversations(p=>{if(p.find(c=>c.id===conv.id))return p;return[conv,...p];});setSel(conv);}})
      .catch(()=>{});
  },[autoOpenConvId,conversations.length]);

  // Load on mount + periodic refresh
  useEffect(()=>{refreshConversations&&refreshConversations();},[]);
  useEffect(()=>{const t=setInterval(()=>{refreshConversations&&refreshConversations();},15000);return()=>clearInterval(t);},[refreshConversations]);

  // Load messages when convo selected — also re-fetch full conv from DB for fresh visitor details
  useEffect(()=>{
    if(!sel?.id) return;
    setHandoffState(null);setSentTo([]);setMsgs([]);
    // Refresh the conversation record itself to get latest visitor_location, claimed_by etc
    api.get(`/api/conversations/${sel.id}`)
      .then(fresh=>{if(fresh?.id) setSel(fresh);})
      .catch(()=>{});
    api.get(`/api/conversations/${sel.id}/messages`)
      .then(data=>setMsgs(data||[]))
      .catch(()=>setMsgs([]));
  },[sel?.id]);

  // Poll every 8s — smart diff, only appends new messages (no full-replace jank)
  useEffect(()=>{
    if(!sel?.id) return;
    const t=setInterval(()=>{
      api.get(`/api/conversations/${sel.id}/messages`)
        .then(data=>{
          if(!data) return;
          setMsgs(prev=>{
            const ids=new Set(prev.map(m=>m.id));
            const fresh=data.filter(m=>!ids.has(m.id));
            return fresh.length?[...prev,...fresh]:prev;
          });
        })
        .catch(()=>{});
    },8000);
    return()=>clearInterval(t);
  },[sel?.id]);

  // Scroll to bottom — fires on any msgs change including content updates
  const scrollToBottom=useCallback(()=>{
    if(msgContainerRef.current){
      msgContainerRef.current.scrollTop=msgContainerRef.current.scrollHeight;
    }
    setTimeout(()=>{
      if(msgContainerRef.current)
        msgContainerRef.current.scrollTop=msgContainerRef.current.scrollHeight;
    },60);
  },[]);
  useEffect(()=>{
    scrollToBottom();
  },[msgs]);

  // Auto-select first active (non-resolved/finished) conversation only
  useEffect(()=>{
    if(!sel){
      const first=conversations.find(c=>c.status!=="resolved"&&c.status!=="finished");
      if(first) setSel(first);
    }
  },[conversations,sel]);

  const send=async()=>{
    if(!inp.trim()||!sel?.id) return;
    if(agentTypingTimer.current){clearTimeout(agentTypingTimer.current);agentTypingTimer.current=null;}
    fetch(API_URL+"/api/typing",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({conversationId:sel.id,role:"agent",typing:false})}).catch(()=>{});
    const msgType=noteMode?"note":"agent";
    const optimistic={id:`m${Date.now()}`,type:msgType,sender:"You",content:inp,created_at:new Date().toISOString()};
    setMsgs(p=>[...p,optimistic]);
    setInp("");
    try{
      const saved=await api.post(`/api/conversations/${sel.id}/messages`,{type:msgType,sender:"You",content:optimistic.content});
      setMsgs(p=>p.map(m=>m.id===optimistic.id?saved:m));
    }catch(e){console.error("Failed to save message",e);}
  };

  // Sound + notification + sentiment when new visitor/bot messages arrive
  useEffect(()=>{
    const visitorMsgs=msgs.filter(m=>m.type==="visitor"||m.type==="bot");
    if(visitorMsgs.length>lastMsgCountRef.current&&lastMsgCountRef.current>0){
      playNewMessageSound();
      if(document.hidden&&sel){
        showBrowserNotif("New message",sel.visitor_name||"Visitor");
      }
      // Analyse sentiment of latest visitor message
      const latest=msgs.filter(m=>m.type==="visitor").slice(-1)[0];
      if(latest?.content){
        fetch(API_URL+"/api/ai/sentiment",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({text:latest.content})})
          .then(r=>r.ok?r.json():null).then(d=>{if(d)setSentiment(d);}).catch(()=>{});
      }
    }
    lastMsgCountRef.current=visitorMsgs.length;
  },[msgs]);

  // Load AI suggested replies when conversation is selected or messages change
  const loadSuggestions=useCallback(()=>{
    setSugOpen(true);
    setSugError(null);
    if(!sel?.id||msgs.length<2){
      setSugError("Need at least 2 messages in the conversation to suggest replies.");
      return;
    }
    setSugLoading(true);
    const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    fetch(API_URL+"/api/ai/suggest-replies",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({messages:msgs.slice(-8),orgId})})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d?.suggestions&&d.suggestions.length>0){setSuggestions(d.suggestions);}
        else{setSuggestions([]);setSugError("No suggestions available right now.");}
        setSugLoading(false);
      })
      .catch(()=>{setSugLoading(false);setSugError("Couldn't load suggestions — try again.");});
  },[sel?.id,msgs.length,config]);

  const loadSummary=()=>{
    if(!sel?.id)return;
    setSummaryLoading(true);setSummary(null);
    fetch(API_URL+"/api/ai/summarise",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({conversationId:sel.id})})
      .then(r=>r.ok?r.json():null)
      .then(d=>{if(d?.summary)setSummary(d.summary);setSummaryLoading(false);})
      .catch(()=>setSummaryLoading(false));
  };

  // Broadcast agent typing state
  const onInpChange=(val)=>{
    setInp(val);
    if(!sel?.id)return;
    fetch(API_URL+"/api/typing",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({conversationId:sel.id,role:"agent",typing:true})}).catch(()=>{});
    if(agentTypingTimer.current)clearTimeout(agentTypingTimer.current);
    agentTypingTimer.current=setTimeout(()=>{
      fetch(API_URL+"/api/typing",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({conversationId:sel.id,role:"agent",typing:false})}).catch(()=>{});
    },3000);
  };

  // Poll for visitor typing indicator
  useEffect(()=>{
    if(!sel?.id)return;
    const t=setInterval(()=>{
      fetch(API_URL+"/api/typing/"+sel.id).then(r=>r.ok?r.json():null).then(d=>{
        if(d)setVisitorTyping(!!d.visitor);
      }).catch(()=>{});
    },2000);
    return()=>clearInterval(t);
  },[sel?.id]);

  const triggerHandoff=async()=>{
    if(plan==="free"){alert("SMS agent alerts are not available on the Free plan.");return;}
    if(!sel?.id) return;
    const tenantId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(!tenantId){alert("No tenant configured.");return;}
    setHandoffState("sending");
    try{
      const r=await fetch(API_URL+"/api/handoff",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        tenantId,
        conversationId:sel.id,
        visitorName:sel.visitor_name||sel.visitor||"Visitor",
        visitorEmail:sel.visitor_email||null,
        page:sel.page||"/chat",
      })});
      const d=await r.json();
      if(!r.ok) throw new Error(d.error||"Handoff failed");
      setSentTo(d.smsTargets?Array(d.smsTargets).fill(1):[]);
      setHandoffState("sent");
      const sysMsg={id:`sys${Date.now()}`,type:"system",content:d.smsSent?`🔔 SMS alert sent to ${d.smsTargets} agent${d.smsTargets!==1?"s":""}. Magic links expire in 3 min.`:`🔔 Handoff logged — ${d.smsError||"SMS not configured"}.`,created_at:new Date().toISOString()};
      setMsgs(p=>[...p,sysMsg]);
      setTimeout(()=>refreshConversations&&refreshConversations(),1000);
    }catch(e){setHandoffState(null);console.error("Handoff failed",e);}
  };

  const insertText=(text)=>setInp(p=>p?p+"\n"+text:text);

  const saveQuickReplies=(list)=>{
    setQuickReplies(list);
    try{localStorage.setItem("hindle_quick_replies",JSON.stringify(list));}catch(e){}
    // Persist to DB so quick replies survive new browser/logout
    const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(orgId) fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tenantId:orgId,quickReplies:list})}).catch(()=>{});
  };

  const importCsv=(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=(ev)=>{
      const lines=(ev.target.result||"").split("\n").map(l=>l.trim().replace(/^"|"$/g,"")).filter(Boolean);
      saveQuickReplies([...new Set([...quickReplies,...lines])]);
    };
    reader.readAsText(file);
    e.target.value="";
  };

  const myEmail=(config?.email||"").toLowerCase();
  const myId=config?.agentId||null;
  const filtered=(()=>{
    let base=conversations;
    // Filter by status
    if(flt==="active") base=base.filter(c=>c.status!=="resolved"&&c.status!=="finished");
    else if(flt==="claimed") base=base.filter(c=>c.claimed_by_id||c.status==="claimed");
    else if(flt!=="all") base=base.filter(c=>c.status===flt);
    // Filter by mine
    // Filter by mine — forced on if agent is restricted
    if(showMine||_agentRestricted) base=base.filter(c=>(c.claimed_by_id&&(c.claimed_by_id===myId||(c.claimed_by_name||"").toLowerCase()===myEmail))||c.assigned_agent_id===myId);
    // Filter by search
    if(convSearch.trim()) base=base.filter(c=>(c.visitor_name||"").toLowerCase().includes(convSearch.toLowerCase())||(c.visitor_email||"").toLowerCase().includes(convSearch.toLowerCase())||(c.subject||"").toLowerCase().includes(convSearch.toLowerCase()));
    return base;
  })();

  // Re-fetch from server whenever filter changes to get full history
  useEffect(()=>{
    if(refreshConversations) refreshConversations();
  },[flt]);

  const fltQ=qSearch?quickReplies.filter(r=>r.toLowerCase().includes(qSearch.toLowerCase())):quickReplies;
  const fltKb=kbSearch?kbDocs.filter(d=>(d.name||d.title||"").toLowerCase().includes(kbSearch.toLowerCase())||(d.content||"").toLowerCase().includes(kbSearch.toLowerCase())):kbDocs;

  if(conversations.length===0||!sel) return(
    <div style={{display:"flex",height:"100%",overflow:"hidden"}}>
      {/* ── Convo list ── */}
      <div style={{width:lstOpen?264:0,minWidth:lstOpen?264:0,borderRight:lstOpen?`1px solid ${S.brd}`:"none",display:"flex",flexDirection:"column",flexShrink:0,background:S.w,overflow:"hidden",transition:"width .22s,min-width .22s"}}>
        <div style={{padding:"9px 10px",borderBottom:`1px solid ${S.brd}`,flexShrink:0}}>
          <div style={{position:"relative",marginBottom:6}}><input className="inp" placeholder="Search…" style={{fontSize:12,padding:"6px 9px 6px 28px"}}/><div style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}}>  <Ic n="search" s={12} c={T.fnt}/></div></div>
          <div style={{display:"flex",gap:3,overflowX:"auto",paddingBottom:2,scrollbarWidth:"none"}}>
            {[["active","Active"],["open","Open"],["claimed","Claimed"],["handoff","Handoff"],["parked","Parked"],["resolved","Resolved"],["all","All"]].filter(([f])=>!_agentRestricted||f!=="all").map(([f,l])=>(
              <button key={f} onClick={()=>setFlt(f)} style={{flexShrink:0,padding:"3px 9px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",
                background:flt===f?B:"transparent",color:flt===f?"#fff":T.fnt,
                border:flt===f?`1px solid ${B}`:`1px solid ${S.brd}`,whiteSpace:"nowrap"}}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {filtered.length===0?(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",gap:8,color:T.fnt,padding:20}}>
              <Ic n="chat" s={22} c={T.fnt}/>
              <p style={{fontSize:12,textAlign:"center",lineHeight:1.5}}>No {flt==="all"?"":flt+" "}conversations yet</p>
            </div>
          ):(
            filtered.map(c=>(
              <div key={c.id} onClick={()=>setSel(c)} style={{padding:"10px 12px",borderBottom:`1px solid ${S.bg}`,cursor:"pointer",background:S.w}} onMouseEnter={e=>e.currentTarget.style.background=S.bg} onMouseLeave={e=>e.currentTarget.style.background=S.w}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                  <span style={{fontSize:12.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{c.visitor_name||c.visitor||"Visitor"}</span>
                  <span style={{fontSize:10.5,color:T.fnt,flexShrink:0,marginLeft:4}}>{c.time||new Date(c.updated_at||c.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
                </div>
                <div style={{display:"flex",gap:5,alignItems:"center"}}>
                  <Chip s={c.status}/>
                  {c.priority==="urgent"&&<span style={{fontSize:9,fontWeight:700,color:"#fff",background:G.r,borderRadius:3,padding:"1px 5px"}}>URGENT</span>}
                  {c.priority==="high"&&<span style={{fontSize:9,fontWeight:700,color:"#fff",background:"#c2410c",borderRadius:3,padding:"1px 5px"}}>HIGH</span>}
                  <span style={{fontSize:11,color:T.fnt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.subject||c.page||"Chat"}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {!lstOpen&&<button onClick={()=>setLstOpen(true)} style={{width:20,flexShrink:0,background:S.bg,border:"none",borderRight:`1px solid ${S.brd}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,color:T.fnt}} onMouseEnter={e=>{e.currentTarget.style.background=BL;e.currentTarget.style.color=B;}} onMouseLeave={e=>{e.currentTarget.style.background=S.bg;e.currentTarget.style.color=T.fnt;}}><Ic n="chR" s={10}/><span style={{fontSize:7,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,writingMode:"vertical-rl",transform:"rotate(180deg)"}}>Convos</span></button>}
      {/* ── Empty chat thread ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:S.w,minWidth:0}}>
        <div style={{padding:"9px 13px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,gap:8}}>
          <div style={{minWidth:0,flex:1}}>
            <h3 style={{fontSize:13.5,fontWeight:700,color:T.fnt}}>No conversation selected</h3>
            <p style={{fontSize:10.5,color:T.fnt}}>Select a conversation from the list, or wait for a visitor to start chatting</p>
          </div>
          <button className="btn bg sm" onClick={()=>setLstOpen(o=>!o)}><Ic n={lstOpen?"chL":"chR"} s={13}/></button>
        </div>
        <div style={{padding:"8px 12px",background:S.w,borderBottom:`1px solid ${S.brd}`,flexShrink:0}}>
          <div style={{display:"flex",gap:6,alignItems:"flex-end",border:`1.5px solid ${S.brd}`,borderRadius:9,padding:"6px 8px",background:S.bg,opacity:.45}}>
            <textarea disabled placeholder="Select a conversation to start messaging…" style={{flex:1,background:"transparent",border:"none",outline:"none",color:T.fnt,fontSize:13,resize:"none",fontFamily:"inherit",lineHeight:1.45,maxHeight:80,minHeight:20,cursor:"not-allowed"}} rows={2}/>
            <button disabled className="btn bp sm" style={{padding:"7px 12px",alignSelf:"flex-end",opacity:.5}}><Ic n="send" s={13} c="#fff"/></button>
          </div>
        </div>
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,background:S.bg,color:T.fnt,padding:40}}>
          <div style={{width:56,height:56,borderRadius:16,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n="chat" s={26} c={B}/></div>
          <p style={{fontSize:15,fontWeight:700,color:T.ink}}>Conversations</p>
          <p style={{fontSize:13,color:T.fnt,textAlign:"center",maxWidth:340,lineHeight:1.6}}>All visitor chats appear in the list on the left. Use the filter tabs to view open, claimed, parked, or resolved conversations. Historical chats are always available under "all".</p>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",marginTop:4}}>
            {[["open","Open — waiting for agent",B],["claimed","Claimed — agent active","#16a34a"],["parked","Parked — available to claim",G.a],["resolved","Resolved — closed chats",T.fnt]].map(([s,desc,col])=>(
              <div key={s} onClick={()=>setFlt(s)} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${S.brd}`,background:S.w,cursor:"pointer",minWidth:160,textAlign:"center"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=col}
                onMouseLeave={e=>e.currentTarget.style.borderColor=S.brd}>
                <Chip s={s}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:5,lineHeight:1.4}}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* ── Right panel placeholder ── */}
      <div style={{width:220,flexShrink:0,borderLeft:`1px solid ${S.brd}`,background:S.w,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"9px 12px",borderBottom:`1px solid ${S.brd}`,display:"flex",gap:6}}>
          {["visitor","quick","kb"].map(t=><button key={t} onClick={()=>setRTab(t)} style={{flex:1,padding:"4px 0",borderRadius:5,fontSize:11,fontWeight:600,cursor:"pointer",background:rTab===t?B:"transparent",color:rTab===t?"#fff":T.fnt,border:rTab===t?`1px solid ${B}`:"1px solid transparent",textTransform:"capitalize"}}>{t==="kb"?"KB":t}</button>)}
        </div>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <p style={{fontSize:12,color:T.fnt,textAlign:"center"}}>Select a conversation to view visitor info</p>
        </div>
      </div>
    </div>
  );

  return(
    <div style={{display:"flex",height:"100%",overflow:"hidden"}}>

      {/* ── Convo list ── */}
      <div style={{width:lstOpen?264:0,minWidth:lstOpen?264:0,borderRight:lstOpen?`1px solid ${S.brd}`:"none",display:"flex",flexDirection:"column",flexShrink:0,background:S.w,overflow:"hidden",transition:"width .22s,min-width .22s"}}>
        <div style={{padding:"9px 10px",borderBottom:`1px solid ${S.brd}`,flexShrink:0}}>
          <div style={{display:"flex",gap:5,alignItems:"center",marginBottom:6}}>
            <div style={{position:"relative",flex:1}}><input className="inp" placeholder="Search…" style={{fontSize:12,padding:"6px 9px 6px 28px"}} value={convSearch} onChange={e=>setConvSearch(e.target.value)}/><div style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}}><Ic n="search" s={12} c={T.fnt}/></div></div>
            {!_agentRestricted
              ?<button onClick={()=>setShowMine(v=>!v)} style={{flexShrink:0,padding:"4px 9px",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",background:showMine?G.g:S.bg,color:showMine?"#fff":T.fnt,border:`1px solid ${showMine?G.g:S.brd}`,whiteSpace:"nowrap"}}>{showMine?"👤 Me":"👥 All"}</button>
              :<span style={{flexShrink:0,padding:"4px 9px",borderRadius:6,fontSize:11,fontWeight:600,background:G.rb,color:G.r,border:`1px solid ${G.rbd}`,whiteSpace:"nowrap"}}>My convos</span>
            }
          </div>
          <div style={{display:"flex",gap:3,overflowX:"auto",paddingBottom:2,scrollbarWidth:"none"}}>
            {[["active","Active"],["open","Open"],["claimed","Claimed"],["handoff","Handoff"],["parked","Parked"],["resolved","Resolved"],["all","All"]].filter(([f])=>!_agentRestricted||f!=="all").map(([f,l])=>(
              <button key={f} onClick={()=>setFlt(f)} style={{flexShrink:0,padding:"3px 9px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",
                background:flt===f?B:"transparent",color:flt===f?"#fff":T.fnt,
                border:flt===f?`1px solid ${B}`:`1px solid ${S.brd}`,whiteSpace:"nowrap"}}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {filtered.map(c=>(
            <div key={c.id} onClick={()=>setSel(c)} style={{padding:"10px 12px",cursor:"pointer",borderBottom:`1px solid ${S.bg}`,background:sel?.id===c.id?BL:S.w,borderLeft:sel?.id===c.id?`3px solid ${B}`:"3px solid transparent"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}><span style={{fontSize:12.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{c.visitor_name||c.visitor||"Visitor"}</span><span style={{fontSize:10.5,color:T.fnt,flexShrink:0,marginLeft:4}}>{c.time||new Date(c.updated_at||c.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span></div>
              <p style={{fontSize:11.5,color:T.lt,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.page||c.subject||"/"}</p>
              <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                <Chip s={c.claimed_by_id?"claimed":c.status}/>
                {c.claimed_by_name&&<span style={{fontSize:10,color:"#16a34a",fontWeight:600}}>👤 {c.claimed_by_name}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {!lstOpen&&<button onClick={()=>setLstOpen(true)} style={{width:20,flexShrink:0,background:S.bg,border:"none",borderRight:`1px solid ${S.brd}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,color:T.fnt}} onMouseEnter={e=>{e.currentTarget.style.background=BL;e.currentTarget.style.color=B;}} onMouseLeave={e=>{e.currentTarget.style.background=S.bg;e.currentTarget.style.color=T.fnt;}}><Ic n="chR" s={10}/><span style={{fontSize:7,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,writingMode:"vertical-rl",transform:"rotate(180deg)"}}>Convos</span></button>}

      {/* ── Chat thread ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:S.w,minWidth:0}}>
        {/* Header */}
        <div style={{padding:"9px 13px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,gap:8}}>
          <div style={{minWidth:0,flex:1}}>
            <h3 style={{fontSize:13.5,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sel.visitor_name||sel.visitor||"Visitor"}</h3>
            <p style={{fontSize:10.5,color:T.fnt}}>{sel.page||sel.visitor_email||"No page info"}</p>
          </div>
          <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0}}>
            <Chip s={sel.status}/>
            {/* Claim / Unclaim */}
            {(()=>{
              const myEmail=(config?.email||"").toLowerCase();
              const claimed=sel.claimed_by_id;
              const mine=claimed&&sel.claimed_by_name&&myEmail&&(sel.claimed_by_id===config?.agentId||(sel.claimed_by_name||"").toLowerCase()===myEmail);
              if(!claimed) return <button className="btn bp sm" onClick={()=>updateConv(sel.id,{claimed_by_id:config?.agentId||myEmail,claimed_by_name:config?.name||myEmail,status:sel.status==="open"||sel.status==="handoff"?"claimed":sel.status})}><Ic n="user" s={11} c="#fff"/> Claim</button>;
              if(mine) return <button className="btn bo sm" style={{color:G.g,borderColor:G.gbd}} onClick={()=>updateConv(sel.id,{claimed_by_id:null,claimed_by_name:null})}><Ic n="chk" s={11} c={G.g}/> Claimed — Release</button>;
              return <span style={{fontSize:11.5,color:G.a,fontWeight:600,padding:"4px 8px",background:G.ab,border:`1px solid ${G.abd}`,borderRadius:6}}>👤 {sel.claimed_by_name}</span>;
            })()}
            {/* Sentiment badge */}
            {sentiment&&<span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,
              background:sentiment.sentiment==="frustrated"?G.rb:sentiment.sentiment==="negative"?G.ab:sentiment.sentiment==="positive"?G.gb:S.sub,
              color:sentiment.sentiment==="frustrated"?G.r:sentiment.sentiment==="negative"?"#c2410c":sentiment.sentiment==="positive"?G.g:T.fnt}}>
              {sentiment.sentiment==="frustrated"?"😤":sentiment.sentiment==="negative"?"😞":sentiment.sentiment==="positive"?"😊":"😐"} {sentiment.sentiment}
            </span>}
            {/* Summary button */}
            <button className="btn bo sm" style={{fontSize:11}} onClick={loadSummary} disabled={summaryLoading} title="AI conversation summary">
              {summaryLoading?<Spinner size={10}/>:<><Ic n="book" s={11}/> Summary</>}
            </button>
            {/* Snooze */}
            <div style={{position:"relative"}}>
              <button className="btn bo sm" style={{fontSize:11}} onClick={()=>setSnoozeOpen(v=>!v)} title="Snooze conversation"><Ic n="cal" s={11}/> Snooze</button>
              {snoozeOpen&&(
                <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,background:S.w,border:`1px solid ${S.brd}`,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.12)",zIndex:200,padding:10,minWidth:180}}>
                  <p style={{fontSize:11,fontWeight:700,color:T.fnt,marginBottom:6}}>Snooze until</p>
                  {[["30 min",30],["1 hour",60],["2 hours",120],["Tomorrow",1440]].map(([label,mins])=>(
                    <div key={label} onClick={async()=>{
                      const until=new Date(Date.now()+mins*60000).toISOString();
                      await fetch(API_URL+"/api/conversations/"+sel.id+"/snooze",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({until})});
                      updateConv(sel.id,{status:"parked"});
                      setSnoozeOpen(false);setSel(null);
                    }} style={{padding:"6px 8px",borderRadius:6,cursor:"pointer",fontSize:12.5,display:"flex",justifyContent:"space-between"}}
                      onMouseEnter={e=>e.currentTarget.style.background=S.bg}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Email reply */}
            {sel.visitor_email&&<button className="btn bo sm" style={{fontSize:11}} onClick={()=>setEmailReplyOpen(v=>!v)} title="Reply by email"><Ic n="mail" s={11}/> Email</button>}
            {sel.status!=="resolved"&&sel.status!=="finished"&&sel.status!=="parked"&&<button className="btn bo sm" onClick={async()=>{await updateConv(sel.id,{status:"resolved",claimed_by_id:sel.claimed_by_id,claimed_by_name:sel.claimed_by_name});setSel(null);}}><Ic n="chk" s={11}/> Resolve</button>}
            {sel.status!=="resolved"&&sel.status!=="finished"&&(<button className="btn bg sm" onClick={async()=>{const newStatus=sel.status==="parked"?"open":"parked";await updateConv(sel.id,{status:newStatus,claimed_by_id:newStatus==="parked"?null:sel.claimed_by_id,claimed_by_name:newStatus==="parked"?null:sel.claimed_by_name});if(newStatus==="parked")setSel(null);}} style={{background:sel.status==="parked"?G.ab:"transparent",color:sel.status==="parked"?G.a:T.mid,border:`1px solid ${sel.status==="parked"?G.abd:S.brd}`}}><Ic n="pause" s={11} c={sel.status==="parked"?G.a:T.mid}/>{sel.status==="parked"?" Unpark":" Park"}</button>)}
            {assignOpen&&(
              <div style={{position:"relative"}}>
                <select className="inp" style={{fontSize:11.5,padding:"4px 8px",minWidth:130}} autoFocus value={sel.assigned_agent_id||""} onChange={e=>{updateConv(sel.id,{assigned_agent_id:e.target.value||null});setAssignOpen(false);}} onBlur={()=>setTimeout(()=>setAssignOpen(false),150)}>
                  <option value="">— Unassigned —</option>
                  {agents.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            {!assignOpen&&(<button className="btn bg sm" onClick={()=>setAssignOpen(true)} title="Move to agent"><Ic n="user" s={11}/> Move</button>)}
            <button className="btn bg sm" onClick={()=>setLstOpen(o=>!o)}><Ic n={lstOpen?"chL":"chR"} s={13}/></button>
          </div>
        </div>

        {/* SMS sent banner */}
        {handoffState==="sent"&&sentTo.length>0&&(
          <div style={{padding:"7px 14px",background:`${CS}14`,borderBottom:`1px solid ${CS}55`,display:"flex",alignItems:"flex-start",gap:8,flexShrink:0}}>
            <div style={{flex:1}}>
              <p style={{fontSize:12,fontWeight:600,color:CS,marginBottom:2}}>SMS alert sent to {sentTo.length} agent{sentTo.length!==1?"s":""}</p>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{sentTo.map(a=><span key={a.id} style={{fontSize:11,color:T.mid,background:S.w,border:`1px solid ${S.brd}`,borderRadius:5,padding:"1px 7px"}}>{a.name} · {a.mobile}</span>)}</div>
            </div>
          </div>
        )}

        {/* ── Messages (oldest at top, newest at bottom) ── */}
        <div ref={msgContainerRef} style={{flex:1,overflowY:"auto",padding:"12px 14px",background:S.bg,display:"flex",flexDirection:"column",minHeight:0}}>
          <div style={{flex:1}}/>
          {/* spacer pushes messages to bottom; overflow scroll works correctly */}
          {msgs.map(msg=>{
            const isA=msg.type==="agent",isB=msg.type==="bot",isSys=msg.type==="system",isNote=msg.type==="note";
            if(isSys) return <div key={msg.id} style={{textAlign:"center",margin:"8px 0"}}><span style={{fontSize:11.5,color:T.fnt,background:S.w,border:`1px solid ${S.brd}`,borderRadius:20,padding:"3px 12px"}}>{msg.content}</span></div>;
            if(isNote) return(
              <div key={msg.id} style={{margin:"6px 0",padding:"8px 12px",background:G.ab,border:"1px dashed #fbbf24",borderRadius:9,position:"relative"}}>
                <p style={{fontSize:9.5,fontWeight:700,color:G.at,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>🔒 Internal Note · {msg.sender} · {new Date(msg.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</p>
                <p style={{fontSize:12.5,color:"#78350f",lineHeight:1.5}}>{msg.content}</p>
              </div>
            );
            return(
              <div key={msg.id} style={{display:"flex",flexDirection:"column",alignItems:isA?"flex-end":"flex-start",marginBottom:12}}>
                <div style={{display:"flex",alignItems:"flex-end",gap:6,flexDirection:isA?"row-reverse":"row"}}>
                  <Av name={msg.sender} size={24} col={isA?B:isB?G.a:G.g}/>
                  <div style={{maxWidth:"70%"}}>
                    <p style={{fontSize:10,color:T.fnt,marginBottom:2,textAlign:isA?"right":"left"}}>{msg.sender}{isB&&<span style={{color:G.a,fontWeight:600}}> · AI</span>} · {new Date(msg.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</p>
                    <div style={{padding:"8px 12px",borderRadius:10,fontSize:13,lineHeight:1.5,background:isA?B:S.w,color:isA?"#fff":T.ink,border:isA?"none":`1px solid ${S.brd}`}}>
                      {msg.file_url?(<a href={msg.file_url} target="_blank" rel="noreferrer" style={{color:isA?"#dbeafe":B,textDecoration:"underline"}}><Ic n="copy" s={12}/> {msg.content||"Attachment"}</a>):msg.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {visitorTyping&&(
            <div style={{display:"flex",alignItems:"flex-end",gap:6,marginBottom:8}}>
              <Av name="Visitor" size={24} col={G.g}/>
              <div style={{padding:"8px 14px",borderRadius:10,background:S.w,border:`1px solid ${S.brd}`,display:"flex",gap:4,alignItems:"center"}}>
                {[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:T.fnt,opacity:.6,animation:`typingDot 1.2s ${i*0.2}s ease-in-out infinite`}}/>)}
              </div>
              <span style={{fontSize:10.5,color:T.fnt}}>typing…</span>
            </div>
          )}
        </div>

        {/* ── AI Summary panel ── */}
        {summary&&(
          <div style={{padding:"10px 14px",background:G.gb,borderTop:`1px solid ${G.gbd}`,flexShrink:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <p style={{fontSize:11,fontWeight:700,color:G.g,textTransform:"uppercase",letterSpacing:.5}}>✨ AI Summary</p>
              <button onClick={()=>setSummary(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:2}}><Ic n="x" s={12}/></button>
            </div>
            <div style={{fontSize:12.5,color:T.ink,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{summary}</div>
          </div>
        )}

        {/* ── Email Reply panel ── */}
        {emailReplyOpen&&(
          <div style={{padding:"10px 14px",background:BL,borderTop:`1px solid ${BM}`,flexShrink:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <p style={{fontSize:11.5,fontWeight:700,color:B}}>✉️ Email Reply to {sel?.visitor_name||"visitor"}</p>
              <button onClick={()=>setEmailReplyOpen(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt}}><Ic n="x" s={13}/></button>
            </div>
            <input className="inp" style={{marginBottom:6,fontSize:12}} placeholder="To email" value={emailDraft.to||sel?.visitor_email||""} onChange={e=>setEmailDraft(d=>({...d,to:e.target.value}))}/>
            <input className="inp" style={{marginBottom:6,fontSize:12}} placeholder="Subject" value={emailDraft.subject} onChange={e=>setEmailDraft(d=>({...d,subject:e.target.value}))}/>
            <textarea className="inp" rows={3} style={{marginBottom:8,fontSize:12,resize:"vertical"}} placeholder="Message body…" value={emailDraft.body} onChange={e=>setEmailDraft(d=>({...d,body:e.target.value}))}/>
            <div style={{display:"flex",gap:6}}>
              <button className="btn bp sm" onClick={async()=>{
                if(!emailDraft.body.trim())return;
                const to=emailDraft.to||sel?.visitor_email;
                if(!to){alert("No email address available.");return;}
                const agentName=(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");return p.name||"Agent";}catch(e){return "Agent";}})();
                const r=await fetch(API_URL+"/api/conversations/"+sel.id+"/email-reply",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to,subject:emailDraft.subject||"Re: Your support request",body:emailDraft.body,agentName})});
                const d=await r.json();
                if(d.ok){setEmailReplyOpen(false);setEmailDraft({to:"",subject:"",body:""});refreshConversations&&refreshConversations();}
                else alert("Failed to send: "+(d.error||"Unknown error"));
              }}><Ic n="send" s={12} c="#fff"/> Send Email</button>
              <button className="btn bo sm" onClick={()=>setEmailReplyOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── AI Suggested replies ── */}
        {sugOpen&&(
          <div style={{padding:"8px 12px",borderTop:`1px solid ${S.brd}`,background:S.bg,flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <p style={{fontSize:10.5,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5}}>✨ AI Suggestions</p>
              <button className="btn bo sm" style={{fontSize:10,padding:"2px 8px"}} onClick={loadSuggestions} disabled={sugLoading}>{sugLoading?<Spinner size={10}/>:"Refresh"}</button>
              <button onClick={()=>{setSugOpen(false);setSuggestions([]);setSugError(null);}} style={{marginLeft:"auto",background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:2}}><Ic n="x" s={11}/></button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {sugLoading&&<p style={{fontSize:12,color:T.fnt}}>Generating suggestions…</p>}
              {!sugLoading&&sugError&&<p style={{fontSize:12,color:T.fnt}}>{sugError}</p>}
              {suggestions.map((s,i)=>(
                <div key={i} onClick={()=>{setInp(s);setNoteMode(false);}} style={{padding:"6px 10px",borderRadius:7,border:`1px solid ${S.brd}`,background:S.w,cursor:"pointer",fontSize:12.5,lineHeight:1.4,color:T.ink,transition:"all .12s"}}
                  onMouseEnter={e=>{e.currentTarget.style.background=BL;e.currentTarget.style.borderColor=B;}}
                  onMouseLeave={e=>{e.currentTarget.style.background=S.w;e.currentTarget.style.borderColor=S.brd;}}>
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Input bar ── */}
        <div style={{padding:"8px 12px",background:S.w,borderTop:`1px solid ${S.brd}`,flexShrink:0}}>
          {/* Mode + priority + transfer toolbar */}
          <div style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
            {/* Note / Reply toggle */}
            <button onClick={()=>setNoteMode(v=>!v)}
              style={{padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${noteMode?"#fbbf24":S.brd}`,background:noteMode?G.ab:"transparent",color:noteMode?G.at:T.fnt}}>
              {noteMode?"🔒 Note":"💬 Reply"}
            </button>
            {/* Priority picker */}
            <select value={priority||""} onChange={e=>{const v=e.target.value||null;setPriority(v);if(sel?.id)updateConv(sel.id,{priority:v});}}
              style={{padding:"3px 8px",borderRadius:6,fontSize:11,border:`1px solid ${S.brd}`,background:priority==="urgent"?G.rb:priority==="high"?G.ab:S.w,color:priority==="urgent"?G.r:priority==="high"?G.a:T.fnt,cursor:"pointer"}}>
              <option value="">Priority</option>
              <option value="urgent">🔴 Urgent</option>
              <option value="high">🟠 High</option>
              <option value="normal">🟡 Normal</option>
              <option value="low">🔵 Low</option>
            </select>
            {/* Transfer */}
            <div style={{position:"relative"}}>
              <button onClick={()=>setTransferOpen(v=>!v)}
                style={{padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${S.brd}`,background:"transparent",color:T.fnt}}>
                <Ic n="rep" s={10}/> Transfer
              </button>
              {transferOpen&&(
                <div style={{position:"absolute",bottom:"calc(100% + 4px)",left:0,background:S.w,border:`1px solid ${S.brd}`,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.12)",zIndex:200,minWidth:160,padding:6}}>
                  <p style={{fontSize:10.5,color:T.fnt,padding:"4px 6px",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Transfer to agent</p>
                  {agents.filter(a=>a.active!==false).map(a=>(
                    <div key={a.id} onClick={async()=>{
                      await updateConv(sel.id,{assigned_agent_id:a.id,claimed_by_id:a.id,claimed_by_name:a.name,status:"claimed"});
                      setTransferOpen(false);
                      setMsgs(p=>[...p,{id:`sys${Date.now()}`,type:"system",content:`Transferred to ${a.name}`,created_at:new Date().toISOString()}]);
                    }} style={{padding:"7px 10px",borderRadius:6,cursor:"pointer",fontSize:12.5,display:"flex",alignItems:"center",gap:8}}
                      onMouseEnter={e=>e.currentTarget.style.background=S.bg}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <Av name={a.name} size={20} col={B}/>{a.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Sound toggle */}
            <button onClick={()=>{soundEnabledRef.current=!soundEnabledRef.current;}}
              style={{marginLeft:"auto",padding:"3px 8px",borderRadius:6,fontSize:11,cursor:"pointer",border:`1px solid ${S.brd}`,background:"transparent",color:T.fnt}}
              title="Toggle sound alerts">
              🔔
            </button>
            {/* AI Suggestions */}
            <button onClick={()=>sugOpen?(setSugOpen(false),setSuggestions([]),setSugError(null)):loadSuggestions()} disabled={sugLoading}
              style={{padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${sugOpen?B:S.brd}`,background:sugOpen?BL:"transparent",color:sugOpen?B:T.fnt}}>
              {sugLoading?<><Spinner size={10}/> …</>:<>✨</>}
            </button>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"flex-end",border:`1.5px solid ${noteMode?"#fbbf24":S.brd}`,borderRadius:9,padding:"6px 8px",background:noteMode?G.ab:S.bg}}>
            {/* File upload */}
            <label title="Attach file" style={{cursor:"pointer",display:"flex",alignItems:"center",padding:"4px",borderRadius:6,flexShrink:0,alignSelf:"flex-end",marginBottom:2}}
              onMouseEnter={e=>e.currentTarget.style.background=BL} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Ic n="clip" s={14} c={T.fnt}/>
              <input type="file" style={{display:"none"}} onChange={async e=>{
                const file=e.target.files[0];if(!file)return;
                const reader=new FileReader();
                reader.onload=async()=>{
                  const dataUrl=reader.result;
                  const optimistic={id:`m${Date.now()}`,type:noteMode?"note":"agent",sender:"You",content:file.name,file_url:dataUrl,created_at:new Date().toISOString()};
                  setMsgs(p=>[...p,optimistic]);
                  try{
                    await api.post(`/api/conversations/${sel.id}/messages`,{type:optimistic.type,sender:"You",content:file.name,file_url:dataUrl});
                  }catch(_){}
                };
                reader.readAsDataURL(file);
                e.target.value="";
              }}/>
            </label>
            <textarea value={inp} onChange={e=>onInpChange(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder={noteMode?"Add internal note… (not visible to visitor)":"Type a message… (Enter to send, Shift+Enter for new line)"} style={{flex:1,background:"transparent",border:"none",outline:"none",color:noteMode?"#78350f":T.ink,fontSize:13,resize:"none",fontFamily:"inherit",lineHeight:1.45,maxHeight:80,minHeight:20}} rows={2}/>
            <button onClick={send} className={noteMode?"btn bo sm":"btn bp sm"} style={{padding:"7px 12px",alignSelf:"flex-end",background:noteMode?"#f59e0b":undefined,borderColor:noteMode?"#f59e0b":undefined}}><Ic n="send" s={13} c="#fff"/></button>
          </div>
        </div>
      </div>

      {/* ── Right panel toggle ── */}
      {!rOpen&&<button onClick={()=>setROpen(true)} style={{width:20,flexShrink:0,background:S.bg,border:"none",borderLeft:`1px solid ${S.brd}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,color:T.fnt}} onMouseEnter={e=>{e.currentTarget.style.background=BL;e.currentTarget.style.color=B;}} onMouseLeave={e=>{e.currentTarget.style.background=S.bg;e.currentTarget.style.color=T.fnt;}}><Ic n="chL" s={10}/><span style={{fontSize:7,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,writingMode:"vertical-rl"}}>Info</span></button>}

      {/* ── Right tabbed panel ── */}
      <div style={{width:rOpen?248:0,minWidth:rOpen?248:0,borderLeft:rOpen?`1px solid ${S.brd}`:"none",display:"flex",flexDirection:"column",flexShrink:0,background:S.w,overflow:"hidden",transition:"width .22s,min-width .22s"}}>
        {/* Tab bar */}
        <div style={{display:"flex",borderBottom:`1px solid ${S.brd}`,flexShrink:0,background:S.w}}>
          {[["visitor","Visitor","eye"],["quick","Quick","zap"],["kb","KB","book"]].map(([t,l,ic])=>(
            <button key={t} onClick={()=>setRTab(t)} style={{flex:1,padding:"9px 4px",fontSize:11.5,fontWeight:600,cursor:"pointer",background:"transparent",color:rTab===t?B:T.fnt,border:"none",borderBottom:rTab===t?`2px solid ${B}`:"2px solid transparent",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"all .15s"}}>
              <Ic n={ic} s={13} c={rTab===t?B:T.fnt}/>{l}
            </button>
          ))}
          <button onClick={()=>setROpen(false)} style={{padding:"9px 8px",background:"transparent",border:"none",borderBottom:"2px solid transparent",cursor:"pointer",color:T.fnt,display:"flex",alignItems:"center"}}><Ic n="chR" s={11}/></button>
        </div>

        {/* ── Visitor tab ── */}
        {rTab==="visitor"&&(
          <div style={{flex:1,overflowY:"auto",padding:14}}>
            <div style={{textAlign:"center",marginBottom:14}}>
              <Av name={sel.visitor_name||sel.visitor||"Visitor"} size={40} col={B}/>
              <p style={{fontSize:13.5,fontWeight:700,marginTop:7}}>{sel.visitor_name||sel.visitor||"Visitor"}</p>
              <p style={{fontSize:11,color:T.fnt}}>{sel.visitor_email||"—"}</p>
            </div>
            <div style={{height:1,background:S.brd,marginBottom:12}}/>
            {/* Phone */}
            <div style={{marginBottom:9}}>
              <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:1}}>Phone</p>
              {sel.visitor_phone
                ?<a href={`tel:${sel.visitor_phone}`} style={{fontSize:12.5,color:B,textDecoration:"none",fontWeight:500}}>{sel.visitor_phone}</a>
                :<p style={{fontSize:12.5,color:T.fnt}}>—</p>}
            </div>
            {/* Company */}
            <div style={{marginBottom:9}}>
              <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:1}}>Company</p>
              <p style={{fontSize:12.5,color:T.mid}}>{sel.visitor_company||"—"}</p>
            </div>
            {/* Location with map link */}
            <div style={{marginBottom:9}}>
              <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:1}}>Location</p>
              {(sel.visitor_location||sel.location)
                ?<div>
                    <p style={{fontSize:12.5,color:T.mid,marginBottom:3}}>{sel.visitor_location||sel.location}</p>
                    <a href={`https://www.google.com/maps/search/${encodeURIComponent(sel.visitor_location||sel.location)}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{fontSize:11,color:B,textDecoration:"none",display:"flex",alignItems:"center",gap:3}}>
                      <Ic n="globe" s={11} c={B}/> View on map
                    </a>
                  </div>
                :<p style={{fontSize:12.5,color:T.fnt}}>—</p>}
            </div>
            {/* Page */}
            <div style={{marginBottom:9}}>
              <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:1}}>Page</p>
              <p style={{fontSize:12.5,color:T.mid,wordBreak:"break-all"}}>{sel.page||"/"}</p>
            </div>
            {/* Status */}
            <div style={{marginBottom:9}}>
              <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:1}}>Status</p>
              <p style={{fontSize:12.5,color:T.mid,textTransform:"capitalize"}}>{sel.status||"—"}</p>
            </div>
            {sel.claimed_by_name&&(
              <div style={{marginBottom:9,padding:"8px 10px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7}}>
                <p style={{fontSize:10,color:G.g,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>Claimed By</p>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <Av name={sel.claimed_by_name} size={22} col={G.g}/>
                  <p style={{fontSize:12.5,color:G.g,fontWeight:600}}>{sel.claimed_by_name}</p>
                </div>
              </div>
            )}
            <div style={{height:1,background:S.brd,margin:"10px 0"}}/>
            <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Assigned Agent</p>
            {(()=>{
              const assignedAgent=agents.find(a=>a.id===sel.assigned_agent_id);
              return(
                <div>
                  {assignedAgent?(
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:BL,border:`1px solid ${BM}`,borderRadius:8,marginBottom:8}}>
                      <Av name={assignedAgent.name} size={26} col={B}/>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:12.5,fontWeight:700,color:B,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{assignedAgent.name}</p>
                        <p style={{fontSize:11,color:T.fnt}}>{assignedAgent.email||assignedAgent.role||"Agent"}</p>
                      </div>
                      <button className="btn bo sm" style={{fontSize:10,padding:"2px 6px",flexShrink:0}}
                        onClick={()=>updateConv(sel.id,{assigned_agent_id:null,claimed_by_id:null,claimed_by_name:null,status:"open"})}>
                        Unassign
                      </button>
                    </div>
                  ):(
                    <p style={{fontSize:12,color:T.fnt,marginBottom:8}}>No agent assigned</p>
                  )}
                  <select className="inp" style={{fontSize:12}} value={sel.assigned_agent_id||""}
                    onChange={e=>{
                      const agentId=e.target.value||null;
                      const agent=agents.find(a=>a.id===agentId);
                      updateConv(sel.id,{
                        assigned_agent_id:agentId,
                        claimed_by_id:agentId,
                        claimed_by_name:agent?.name||null,
                        status:agentId?"claimed":"open"
                      });
                    }}>
                    <option value="">— Unassigned —</option>
                    {agents.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Quick replies tab ── */}
        {rTab==="quick"&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"10px 12px",borderBottom:`1px solid ${S.brd}`,flexShrink:0,display:"flex",flexDirection:"column",gap:6}}>
              <div style={{position:"relative"}}><input className="inp" placeholder="Search replies…" value={qSearch} onChange={e=>setQSearch(e.target.value)} style={{fontSize:12,padding:"6px 9px 6px 28px"}}/><div style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}}><Ic n="search" s={12} c={T.fnt}/></div></div>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"5px 8px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:6}}>
                <Ic n="copy" s={12} c={T.fnt}/>
                <span style={{fontSize:11.5,color:T.mid}}>Import from CSV</span>
                <input type="file" accept=".csv" style={{display:"none"}} onChange={importCsv}/>
              </label>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"8px 10px",display:"flex",flexDirection:"column",gap:5}}>
              {/* Pinned system actions */}
              <div style={{marginBottom:4}}>
                <p style={{fontSize:10,color:T.fnt,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:5}}>Quick Actions</p>
                <div style={{display:"flex",alignItems:"center",gap:6,padding:"7px 10px",borderRadius:7,border:`1px solid ${G.pbd}`,background:G.pb,cursor:"pointer"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=G.p;e.currentTarget.style.background="#ddd6fe";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=G.pbd;e.currentTarget.style.background=G.pb;}}
                  onClick={()=>insertText("Could I get your email address so I can follow up with you if needed?")}>
                  <Ic n="mail" s={11} c={G.p} style={{flexShrink:0}}/>
                  <span style={{fontSize:12.5,color:G.p,flex:1,fontWeight:600}}>Ask for email address</span>
                </div>
              </div>
              <div style={{height:1,background:S.brd,marginBottom:2}}/>
              {fltQ.map((r,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 10px",borderRadius:7,border:`1px solid ${S.brd}`,background:S.w,cursor:"pointer",transition:"all .12s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=B;e.currentTarget.style.background=BL;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=S.brd;e.currentTarget.style.background=S.w;}}
                  onClick={()=>insertText(r)}>
                  <Ic n="send" s={11} c={T.fnt} style={{flexShrink:0}}/>
                  <span style={{fontSize:12.5,color:T.mid,flex:1,lineHeight:1.4}}>{r}</span>
                  <button style={{background:"none",border:"none",cursor:"pointer",padding:2,color:G.r,opacity:.5,flexShrink:0}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.5}
                    onClick={ev=>{ev.stopPropagation();saveQuickReplies(quickReplies.filter((_,j)=>j!==i));}}>
                    <Ic n="x" s={10} c={G.r}/>
                  </button>
                </div>
              ))}
              {fltQ.length===0&&<p style={{fontSize:12,color:T.fnt,textAlign:"center",marginTop:16}}>No replies found</p>}
            </div>
          </div>
        )}

        {/* ── KB tab ── */}
        {rTab==="kb"&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"10px 12px",borderBottom:`1px solid ${S.brd}`,flexShrink:0}}>
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:kbSearch?0:0}}>
                <div style={{position:"relative",flex:1}}>
                  <input className="inp" placeholder="Search knowledge base…" value={kbSearch} onChange={e=>setKbSearch(e.target.value)} style={{fontSize:12,padding:"6px 9px 6px 28px",width:"100%"}}/>
                  <div style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}}><Ic n="search" s={12} c={T.fnt}/></div>
                </div>
                <button title="Refresh KB" onClick={()=>{
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  if(!orgId)return;
                  setKbLoading(true);
                  fetch(API_URL+"/api/kb?org_id="+orgId)
                    .then(r=>r.ok?r.json():null)
                    .then(rows=>{if(rows&&Array.isArray(rows))setKbDocs(rows.map(r=>({id:r.id,name:r.name,category:r.category||"",sub_category:r.sub_category||"",tertiary:r.tertiary||"",content:r.content||"",size:r.size_kb?r.size_kb+" KB":"",chunks:r.chunks||1,status:r.status||"indexed",updated:r.updated_at||""})));})
                    .catch(()=>{}).finally(()=>setKbLoading(false));
                }} style={{background:"transparent",border:`1px solid ${S.brd}`,borderRadius:6,cursor:"pointer",padding:"5px 7px",display:"flex",alignItems:"center",color:T.fnt,flexShrink:0}}>
                  {kbLoading?<Spinner size={11}/>:<Ic n="reload" s={11}/>}
                </button>
              </div>
              {kbDocs.length>0&&<p style={{fontSize:10,color:T.fnt,marginTop:4}}>{kbDocs.length} article{kbDocs.length!==1?"s":""}{kbSearch?`, ${fltKb.length} match${fltKb.length!==1?"es":""}`:""}</p>}
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"6px 8px"}}>
              {kbLoading&&kbDocs.length===0&&(
                <div style={{textAlign:"center",padding:"24px 12px",color:T.fnt}}>
                  <Spinner size={20}/>
                  <p style={{fontSize:12,marginTop:8}}>Loading knowledge base…</p>
                </div>
              )}
              {!kbLoading&&(()=>{
                if(fltKb.length===0) return(
                  <div style={{textAlign:"center",padding:"24px 12px",color:T.fnt}}>
                    <Ic n="book" s={26} c={T.fnt}/>
                    <p style={{fontSize:12.5,marginTop:8,fontWeight:600}}>{kbSearch?"No matches":"No KB docs yet"}</p>
                    <p style={{fontSize:11.5,marginTop:4,lineHeight:1.5}}>{kbSearch?"Try a different search term":"Add articles in AI Chatbot → Knowledge Base"}</p>
                  </div>
                );
                // Group by category
                const groups={};
                fltKb.forEach(doc=>{
                  const cat=doc.category||"General";
                  if(!groups[cat]) groups[cat]=[];
                  groups[cat].push(doc);
                });
                return Object.entries(groups).map(([cat,docs])=>(
                  <div key={cat} style={{marginBottom:4}}>
                    {/* Category header */}
                    <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 6px",cursor:"pointer",borderRadius:6,userSelect:"none"}}
                      onClick={()=>setKbSearch(s=>s===cat?"":cat)}>
                      <div style={{width:3,height:14,borderRadius:2,background:B,flexShrink:0}}/>
                      <p style={{fontSize:10.5,fontWeight:700,color:B,textTransform:"uppercase",letterSpacing:.6,flex:1}}>{cat}</p>
                      <span style={{fontSize:10,color:T.fnt}}>{docs.length}</span>
                    </div>
                    {/* Articles */}
                    {docs.map((doc,i)=>{
                      const expanded=kbExpandedId===doc.id;
                      const hovered=kbHoverId===doc.id;
                      return(
                        <div key={doc.id||i} style={{position:"relative",marginBottom:3,borderRadius:7,border:`1px solid ${expanded?B:S.brd}`,background:expanded?BL:S.w,overflow:"hidden",transition:"all .15s"}}
                          onMouseEnter={()=>setKbHoverId(doc.id)} onMouseLeave={()=>setKbHoverId(null)}>
                          {/* Article header row */}
                          <div style={{display:"flex",alignItems:"center",gap:7,padding:"9px",cursor:"pointer"}}
                            onClick={()=>setKbExpandedId(expanded?null:doc.id)}>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:12,fontWeight:600,color:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name||doc.title||"Untitled"}</p>
                              {(doc.sub_category||doc.subcat||doc.tertiary)&&(
                                <p style={{fontSize:10,color:T.fnt,marginTop:1}}>{[doc.sub_category||doc.subcat,doc.tertiary].filter(Boolean).join(" › ")}</p>
                              )}
                              {!expanded&&doc.content&&(
                                <p style={{fontSize:10.5,color:T.lt,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {String(doc.content).replace(/[*_#`]/g,"").slice(0,90)}
                                </p>
                              )}
                            </div>
                            <button className="btn bp sm" style={{padding:"2px 8px",fontSize:10.5,flexShrink:0}}
                              onClick={e=>{e.stopPropagation();insertText(doc.content||doc.name||"");}}>
                              Insert
                            </button>
                          </div>

                          {/* Hover preview — bigger insight without needing to click */}
                          {hovered&&!expanded&&doc.content&&(
                            <div style={{position:"fixed",zIndex:200,pointerEvents:"none"}} ref={el=>{
                                if(!el)return;
                                const parent=el.parentElement?.getBoundingClientRect();
                                if(parent){
                                  el.style.top=Math.max(8,parent.top)+"px";
                                  el.style.left=(parent.left-374)+"px";
                                }
                              }}>
                              <div style={{width:360,maxHeight:320,overflowY:"auto",background:"#0F172A",color:"#fff",borderRadius:10,padding:"12px 14px",boxShadow:"0 12px 32px rgba(0,0,0,.35)"}}>
                                <p style={{fontSize:11.5,fontWeight:700,marginBottom:6,color:"#93C5FD"}}>{doc.name||doc.title}</p>
                                <p style={{fontSize:11.5,lineHeight:1.6,whiteSpace:"pre-wrap",color:"#E2E8F0"}}>
                                  {String(doc.content).replace(/[*_#`]/g,"").slice(0,600)}
                                  {String(doc.content).length>600?"…":""}
                                </p>
                              </div>
                            </div>
                          )}
                          {/* Expanded content */}
                          {expanded&&doc.content&&(
                            <div style={{padding:"0 10px 10px",borderTop:`1px solid ${BM}`,cursor:"pointer"}} onClick={()=>setKbExpandedId(null)}>
                              <p style={{fontSize:12,color:T.mid,lineHeight:1.6,whiteSpace:"pre-wrap",marginTop:8,maxHeight:320,overflowY:"auto"}}>{doc.content}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Small helper for adding new quick reply inline
const AddQuickReply=({onAdd})=>{
  const [v,setV]=useState("");
  return(
    <div style={{display:"flex",gap:5}}>
      <input className="inp" value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&v.trim()){onAdd(v);setV("");}}} placeholder="Add reply…" style={{fontSize:12,flex:1}}/>
      <button className="btn bp sm" onClick={()=>{if(v.trim()){onAdd(v);setV("");}}} style={{padding:"5px 9px"}}><Ic n="plus" s={12} c="#fff"/></button>
    </div>
  );
};

// ── Dashboard Home ────────────────────────────────────────────
const OnboardingChecklist=({config,onNav})=>{
  const [data,setData]=useState(null);
  const [collapsed,setCollapsed]=useState(()=>{try{return localStorage.getItem("hindle_setup_collapsed")==="true";}catch(e){return false;}});
  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
  useEffect(()=>{
    if(!orgId)return;
    fetch(API_URL+"/api/onboarding/"+orgId).then(r=>r.ok?r.json():null).then(d=>{if(d)setData(d);}).catch(()=>{});
  },[orgId]);
  const toggleCollapsed=()=>{
    setCollapsed(c=>{const next=!c;try{localStorage.setItem("hindle_setup_collapsed",String(next));}catch(e){}return next;});
  };
  if(!data||data.pct===100)return null;
  return(
    <div className="card" style={{padding:16,marginBottom:14,border:`1.5px solid ${BM}`,background:BL}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:collapsed?0:10}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <p style={{fontSize:13.5,fontWeight:700,color:B}}>🚀 Get set up — {data.pct}% complete</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:13,fontWeight:800,color:B}}>{data.steps.filter(s=>s.done).length}/{data.steps.length}</span>
          <button onClick={toggleCollapsed}
            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,border:`1px solid ${BM}`,background:S.w,color:B,cursor:"pointer",fontSize:11.5,fontWeight:700}}>
            <span style={{fontSize:11,transform:collapsed?"rotate(-90deg)":"rotate(0deg)",transition:"transform .15s",display:"inline-block"}}>▾</span>
            {collapsed?"Show":"Hide"}
          </button>
        </div>
      </div>
      {!collapsed&&<p style={{fontSize:11.5,color:T.mid,marginTop:-4,marginBottom:10}}>Complete these steps to get the most out of Hindle. <span style={{color:B,cursor:"pointer",textDecoration:"underline",fontWeight:600}} onClick={()=>onNav&&onNav("_setup")}>Re-run Setup</span></p>}
      {!collapsed&&<>
      <div style={{height:6,background:S.brd,borderRadius:3,marginBottom:12,marginTop:10,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${data.pct}%`,background:B,borderRadius:3,transition:"width .4s"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
        {data.steps.map(s=>{
          const pageMap={configure_chatbot:"chatbot",add_knowledge_base:"chatbot",create_agent:"agents",install_widget:"install",set_branding:"chatbot"};
          const target=pageMap[s.id];
          return(
          <div key={s.id} onClick={!s.done&&target&&onNav?()=>onNav(target):undefined}
            style={{display:"flex",alignItems:"center",gap:7,padding:"5px 7px",borderRadius:6,background:s.done?"transparent":S.bg,cursor:!s.done&&target?"pointer":"default"}}
            onMouseEnter={e=>{if(!s.done&&target)e.currentTarget.style.background=BL;}}
            onMouseLeave={e=>{e.currentTarget.style.background=s.done?"transparent":S.bg;}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:s.done?G.g:S.brd,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {s.done?<Ic n="chk" s={10} c="#fff"/>:<div style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
            </div>
            <span style={{fontSize:11.5,color:s.done?T.fnt:T.ink,fontWeight:s.done?400:500,textDecoration:s.done?"line-through":"none"}}>{s.label}{!s.done&&target&&<span style={{color:B,fontSize:10,marginLeft:4}}>→</span>}</span>
          </div>
          );
        })}
      </div>
      </>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SUPER ADMIN DASHBOARD
// ══════════════════════════════════════════════════════════════
const PSuperDash=({alertLog=[],tenants=[]})=>{
  const paid=tenants.filter(t=>t.paid||t.status==="paid").length;
  const trial=tenants.filter(t=>!t.paid&&t.status!=="expired"&&t.status!=="paid").length;
  const expired=tenants.filter(t=>t.status==="expired").length;
  const smsClicked=alertLog.filter(a=>a.status==="clicked").length;
  const recent=alertLog.slice(0,8);
  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:17,fontWeight:800,marginBottom:3}}>Platform Overview</h2>
        <p style={{fontSize:12.5,color:T.fnt}}>Welcome back. Here's your platform at a glance.</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <Metric label="Total Tenants" value={tenants.length} icon="bld" col={B} d={0}/>
        <Metric label="Active Paid" value={paid} icon="chk" col={G.g} d={.05}/>
        <Metric label="In Trial" value={trial} icon="cal" col={G.a} d={.1}/>
        <Metric label="Expired" value={expired} icon="warn" col={G.r} d={.15}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:14}}>
        {/* Recent tenants */}
        <div className="card">
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`}}>
            <h3 style={{fontSize:13.5,fontWeight:700}}>Recent Tenants</h3>
          </div>
          {tenants.length===0?<p style={{padding:20,fontSize:13,color:T.fnt,textAlign:"center"}}>No tenants yet</p>:tenants.slice(0,8).map(t=>(
            <div key={t.id} style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${S.bg}`}}>
              <Av name={t.name} size={30} col={t.paid?G.g:t.status==="expired"?G.r:G.a}/>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</p>
                <p style={{fontSize:11.5,color:T.fnt}}>{t.email}</p>
              </div>
              <div style={{display:"flex",gap:5}}>
                <Chip s={t.plan}/>
                <Chip s={t.paid?"paid":t.status||"trial"}/>
              </div>
            </div>
          ))}
        </div>
        {/* Recent SMS alerts */}
        <div className="card">
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{fontSize:13.5,fontWeight:700}}>SMS Alert Log</h3>
            <span style={{fontSize:11.5,color:T.fnt}}>{smsClicked} clicked</span>
          </div>
          {recent.length===0?<p style={{padding:20,fontSize:13,color:T.fnt,textAlign:"center"}}>No alerts yet</p>:recent.map((a,i)=>(
            <div key={i} style={{padding:"9px 16px",display:"flex",alignItems:"center",gap:9,borderBottom:`1px solid ${S.bg}`}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:a.status==="clicked"?G.g:a.status==="expired"?G.r:G.a,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.mobile||a.to||"—"}</p>
                <p style={{fontSize:11,color:T.fnt}}>{a.created_at?new Date(a.created_at).toLocaleString("en-AU",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}):""}</p>
              </div>
              <Chip s={a.status||"pending"}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PDash=({alertLog,agents,conversations,setPage,config,role})=>{
  const [modal,setModal]=useState(null);
  const [csatData,setCsatData]=useState(null);
  const [liveConvos,setLiveConvos]=useState(conversations||[]);
  const [liveAgents,setLiveAgents]=useState(agents||[]);
  const [lastRefresh,setLastRefresh]=useState(new Date());
  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();

  useEffect(()=>{
    if(!orgId)return;
    fetch(API_URL+"/api/analytics/"+orgId+"?days=30").then(r=>r.ok?r.json():null).then(d=>{if(d)setCsatData(d);}).catch(()=>{});
  },[orgId]);

  // Real-time refresh every 15 seconds
  useEffect(()=>{setLiveConvos(conversations||[]);setLiveAgents(agents||[]);},[conversations,agents]);
  useEffect(()=>{
    if(!orgId)return;
    const tick=async()=>{
      try{
        const r=await fetch(API_URL+"/api/conversations?org_id="+orgId+"&limit=50");
        if(r.ok){const d=await r.json();setLiveConvos(d);setLastRefresh(new Date());}
      }catch(_){}
    };
    const t=setInterval(tick,15000);
    return()=>clearInterval(t);
  },[orgId]);

  const openConvos=liveConvos||[];
  return(
  <div style={{padding:20,overflowY:"auto",height:"100%"}}>
    {/* Metric modal */}
    {modal&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={()=>setModal(null)}>
        <div className="card au" style={{width:"100%",maxWidth:560,maxHeight:"80vh",overflow:"hidden",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:S.bg}}>
            <h3 style={{fontSize:14,fontWeight:700}}>{modal==="convos"?"Open Conversations":modal==="agents"?"Agent Status":modal==="sms"?"SMS Alerts":"CSAT"}</h3>
            <button onClick={()=>setModal(null)} style={{background:"transparent",border:"none",cursor:"pointer",padding:4}}><Ic n="x" s={14} c={T.fnt}/></button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:14}}>
            {modal==="convos"&&(openConvos.length===0?<p style={{fontSize:13,color:T.fnt,textAlign:"center",padding:20}}>No conversations yet</p>:openConvos.slice(0,20).map(c=>(
              <div key={c.id} style={{padding:"9px 12px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${S.bg}`,cursor:"pointer"}} onClick={()=>{setModal(null);setPage&&setPage("convos");}}>
                <Av name={c.visitor_name||c.visitor||"Visitor"} size={28} col={B}/>
                <div style={{flex:1,minWidth:0}}><p style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.visitor_name||c.visitor||"Visitor"}</p><p style={{fontSize:11.5,color:T.fnt}}>{c.page||"/"}</p></div>
                <Chip s={c.status}/>
              </div>
            )))}
            {modal==="agents"&&agents.map(a=>(
              <div key={a.id} style={{padding:"9px 12px",display:"flex",alignItems:"center",gap:9,borderBottom:`1px solid ${S.bg}`}}>
                <Av name={a.name} size={28} col={a.status==="online"?G.g:T.fnt}/>
                <div style={{flex:1}}><p style={{fontSize:12.5,fontWeight:600}}>{a.name}</p><p style={{fontSize:11,color:T.fnt}}>{a.email}</p></div>
                <div style={{display:"flex",gap:5,alignItems:"center"}}><Chip s={a.status}/>{a.sms_alerts&&<Ic n="sms" s={12} c={CS}/>}</div>
              </div>
            ))}
            {modal==="sms"&&(alertLog.length===0?<p style={{fontSize:13,color:T.fnt,textAlign:"center",padding:20}}>No SMS alerts yet</p>:alertLog.slice(0,20).map((a,i)=>(
              <div key={i} style={{padding:"9px 12px",display:"flex",alignItems:"center",gap:9,borderBottom:`1px solid ${S.bg}`}}>
                <div style={{width:30,height:30,borderRadius:7,background:`${CS}14`,border:`1px solid ${CS}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="sms" s={13} c={CS}/></div>
                <div style={{flex:1,minWidth:0}}><p style={{fontSize:12.5,fontWeight:600}}>{a.agent_name||"—"} → {a.visitor_name||"Visitor"}</p><p style={{fontSize:11.5,color:T.fnt}}>{a.mobile||"—"} · {a.created_at?new Date(a.created_at).toLocaleString():"—"}</p></div>
                <Chip s={a.status||"pending"}/>
              </div>
            )))}
            {modal==="csat"&&(csatData?.csatTotal>0?(
              <div style={{padding:10}}>
                <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14}}>
                  <div style={{textAlign:"center"}}>
                    <p style={{fontSize:40,fontWeight:800,color:csatData.csatScore>=70?G.g:csatData.csatScore>=50?G.a:G.r}}>{csatData.csatScore}%</p>
                    <p style={{fontSize:11.5,color:T.fnt}}>satisfaction</p>
                  </div>
                  <div style={{flex:1}}>
                    {[["👍 Positive",csatData.csatPositive||0,G.g],["👎 Negative",(csatData.csatTotal||0)-(csatData.csatPositive||0),G.r]].map(([l,v,c])=>(
                      <div key={l} style={{marginBottom:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{fontSize:12}}>{l}</span><span style={{fontSize:12,fontWeight:700,color:c}}>{v}</span></div>
                        <div style={{height:8,background:S.bg,borderRadius:4,overflow:"hidden"}}><div style={{height:"100%",width:`${csatData.csatTotal===0?0:Math.round(v/csatData.csatTotal*100)}%`,background:c,borderRadius:4}}/></div>
                      </div>
                    ))}
                    <p style={{fontSize:11.5,color:T.fnt,marginTop:8}}>{csatData.csatTotal} total rating{csatData.csatTotal!==1?"s":""} in last 30 days</p>
                  </div>
                </div>
              </div>
            ):<p style={{fontSize:13,color:T.fnt,textAlign:"center",padding:20}}>No ratings yet. Visitors are prompted to rate after a conversation is resolved.</p>
            )}
          </div>
        </div>
      </div>
    )}
    {role!=="agent"&&<OnboardingChecklist config={config} onNav={setPage}/>}
    {/* Live Now bar */}
    <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:14,padding:"8px 14px",background:BL,border:`1px solid ${BM}`,borderRadius:9}}>
      <div style={{width:7,height:7,borderRadius:"50%",background:G.g,animation:"pulse 2s ease-in-out infinite",flexShrink:0}}/>
      <p style={{fontSize:12,fontWeight:600,color:B}}>Live</p>
      <p style={{fontSize:12,color:T.mid}}>
        <b>{openConvos.filter(c=>c.status==="open"||c.status==="handoff").length}</b> open · <b>{openConvos.filter(c=>c.status==="claimed").length}</b> claimed · <b>{liveAgents.filter(a=>a.active!==false).length}</b> agents active
      </p>
      <p style={{fontSize:11,color:T.fnt,marginLeft:"auto"}}>Updated {lastRefresh.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</p>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
      <div style={{cursor:"pointer"}} onClick={()=>{setModal("convos");}}><Metric label="Open Conversations" value={openConvos.filter(c=>c.status==="open"||c.status==="handoff").length} sub={{t:"click to view"}} icon="chat" d={0}/></div>
      <div style={{cursor:"pointer"}} onClick={()=>setModal("agents")}><Metric label="Agents Active" value={liveAgents.filter(a=>a.active!==false).length} sub={{t:`${liveAgents.filter(a=>a.active!==false&&(a.claimed_count||0)>0).length} in conversation · click to view`}} icon="users" col={G.g} d={.05}/></div>
      <div style={{cursor:"pointer"}} onClick={()=>setModal("sms")}><Metric label="SMS Alerts Sent" value={alertLog.length} sub={{t:"click to view"}} icon="sms" col={CS} d={.1}/></div>
      <div style={{cursor:"pointer"}} onClick={()=>setModal("csat")}><Metric label="CSAT Score" value={csatData?.csatScore!=null?`${csatData.csatScore}%`:"—"} sub={{t:csatData?.csatTotal>0?`${csatData.csatTotal} ratings`:"no ratings yet"}} icon="star" col={G.a} d={.15}/></div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:14,marginBottom:14}}>
      <div className="card">
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between"}}><h3 style={{fontSize:13.5,fontWeight:700}}>Recent Conversations</h3><button className="btn bp sm"><Ic n="plus" s={12} c="#fff"/> New</button></div>
        {(conversations||[]).slice(0,6).map(c=>(
          <div key={c.id} style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${S.bg}`,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=S.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <Av name={c.visitor_name||c.visitor||"Visitor"} size={30} col={c.status==="open"?B:G.g}/>
            <div style={{flex:1,minWidth:0}}><p style={{fontSize:13,fontWeight:600}}>{c.visitor_name||c.visitor||"Visitor"}</p><p style={{fontSize:11.5,color:T.lt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.subject||c.page||"/"}</p></div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}><Chip s={c.status}/><span style={{fontSize:10.5,color:T.fnt}}>{c.updated_at?new Date(c.updated_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):""}</span></div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`}}><h3 style={{fontSize:13.5,fontWeight:700}}>Agent Status</h3></div>
        {agents.map(a=>(
          <div key={a.id} style={{padding:"9px 16px",display:"flex",alignItems:"center",gap:9,borderBottom:`1px solid ${S.bg}`}}>
            <Av name={a.name} size={28} col={a.status==="online"?G.g:T.fnt}/>
            <div style={{flex:1}}><p style={{fontSize:12.5,fontWeight:600}}>{a.name}</p><p style={{fontSize:11,color:T.fnt}}>{a.convos} active</p></div>
            <div style={{display:"flex",gap:4,alignItems:"center"}}>
              <Chip s={a.status}/>
              {(a.sms_alerts||a.smsAlerts)&&<span title="SMS alerts enabled"><Ic n="sms" s={13} c={CS}/></span>}
            </div>
          </div>
        ))}
      </div>
    </div>
    {alertLog.length>0&&(
      <div className="card">
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",gap:8}}><h3 style={{fontSize:13.5,fontWeight:700}}>Recent SMS Alerts</h3></div>
        {alertLog.slice(0,4).map((a,i)=>(
          <div key={i} style={{padding:"9px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${S.bg}`}}>
            <div style={{width:30,height:30,borderRadius:7,background:`${CS}14`,border:`1px solid ${CS}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="sms" s={13} c={CS}/></div>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:12.5,fontWeight:600}}>{a.agent_name||a.agentName||"—"} → {a.visitor_name||a.visitor||"Visitor"}</p>
              <p style={{fontSize:11.5,color:T.fnt}}>{a.mobile||"—"} · {a.created_at?new Date(a.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):""}</p>
            </div>
            <Chip s={a.status==="expired"?"expired_link":a.status||"pending"}/>
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

// Agent context menu
const AgentMenu=({agent,updateAgent,deleteAgent})=>{
  const [open,setOpen]=useState(false);
  const ref=React.useRef(null);
  useEffect(()=>{
    if(!open) return;
    const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[open]);
  return(
    <div ref={ref} style={{position:"relative"}}>
      <button className="btn bg sm" onClick={()=>setOpen(o=>!o)}><Ic n="more" s={13}/></button>
      {open&&(
        <div style={{position:"absolute",right:0,top:"100%",zIndex:50,background:S.w,border:`1px solid ${S.brd}`,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.12)",minWidth:160,overflow:"hidden"}}>
          {[
            {label:"Edit Name/Email",icon:"edit",action:()=>{const n=prompt("Name:",agent.name);if(n)updateAgent(agent.id,{name:n});}},
            {label:"Edit Mobile",icon:"sms",action:()=>{const m=prompt("Mobile number:",agent.mobile||"");if(m!==null)updateAgent(agent.id,{mobile:m});}},
            {label:"Change Password",icon:"lock",action:async()=>{const p=prompt("New password for "+agent.name+":");if(!p||!p.trim())return;try{await fetch(API_URL+"/api/agents/"+agent.id+"/password",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({password:p.trim()})});alert("Password updated.");}catch(e){alert("Failed: "+e.message);}}},
            {label:agent.sms_alerts?"Disable SMS Alerts":"Enable SMS Alerts",icon:"bell",action:()=>updateAgent(agent.id,{sms_alerts:!agent.sms_alerts})},
            {label:agent.active===false?"Reactivate":"Deactivate",icon:"lock",action:()=>updateAgent(agent.id,{active:agent.active===false}),danger:agent.active!==false},
            {label:"Delete Agent",icon:"trash",action:()=>{if(window.confirm(`Remove ${agent.name}?`))deleteAgent(agent.id);},danger:true},
          ].map(({label,icon,action,danger})=>(
            <div key={label} onClick={()=>{action();setOpen(false);}} style={{padding:"9px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:9,fontSize:12.5,color:danger?G.r:T.ink,background:"transparent",transition:"background .1s"}}
              onMouseEnter={e=>e.currentTarget.style.background=danger?G.rb:S.bg}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Ic n={icon} s={12} c={danger?G.r:T.fnt}/>{label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Agent Management ──────────────────────────────────────────
const PAgents=({agents,createAgent,updateAgent,deleteAgent,config})=>{
  const [editing,setEditing]=useState(null);
  const [showInvite,setShowInvite]=useState(false);
  const [inviteErr,setInviteErr]=useState("");
  const [inviteForm,setInviteForm]=useState({name:"",email:"",mobile:"",role:"agent"});
  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
        <Metric label="Total Agents" value={agents.length} icon="users" d={0}/>
        <Metric label="Online Now" value={agents.filter(a=>a.status==="online").length} icon="zap" col={G.g} d={.05}/>
        <Metric label="SMS Alerts On" value={agents.filter(a=>a.smsAlerts).length} icon="sms" col={CS} d={.1}/>
        <Metric label="Avg Rating" value="—" icon="star" col={G.a} d={.15}/>
      </div>
      <div className="card" style={{marginBottom:14}}>
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><h3 style={{fontSize:13.5,fontWeight:700}}>Agent Team</h3></div>
          <button className="btn bp sm" onClick={()=>setShowInvite(true)}><Ic n="plus" s={12} c="#fff"/> Invite Agent</button>
        </div>
        <table className="tbl">
          {showInvite&&(
          <tbody><tr><td colSpan={7} style={{padding:0}}>
          <div style={{padding:"14px 16px",background:S.bg,borderBottom:`1px solid ${S.brd}`}}>
            <p style={{fontSize:13,fontWeight:700,marginBottom:10}}>Invite New Agent</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <input className="inp" placeholder="Full name" value={inviteForm.name} onChange={e=>setInviteForm(f=>({...f,name:e.target.value}))}/>
              <input className="inp" placeholder="Email address" value={inviteForm.email} onChange={e=>setInviteForm(f=>({...f,email:e.target.value}))}/>
              <input className="inp" placeholder="+44 7700 900000" value={inviteForm.mobile} onChange={e=>setInviteForm(f=>({...f,mobile:e.target.value}))}/>
              <select className="inp" value={inviteForm.role} onChange={e=>setInviteForm(f=>({...f,role:e.target.value}))}><option value="agent">Agent</option><option value="admin">Admin</option></select>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn bp sm" onClick={async()=>{
                if(!inviteForm.name||!inviteForm.email)return;
                try{
                  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  if(!orgId){setInviteErr("Organisation not found — please sign out and back in.");return;}
                  await createAgent({org_id:orgId,name:inviteForm.name,email:inviteForm.email,mobile:inviteForm.mobile||null,role:inviteForm.role,sms_alerts:true});
                  const tenantId=orgId;
                  const ir=await fetch(API_URL+"/api/invite-agent",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tenantId,name:inviteForm.name,email:inviteForm.email,mobile:inviteForm.mobile||null})});
                  const id=await ir.json();
                  const pw=id.tempPassword||"(see agent record)";
                  if(id.ok){
                    alert(`✅ Agent created & credentials sent via SMS to ${inviteForm.mobile}.\n\nTemp Password: ${pw}\n\nAgent must change password after first login.`);
                  }else{
                    alert(`✅ Agent created.\n\n${inviteForm.mobile?"SMS could not be sent — "+( id.sendErr||"unknown error"):"No mobile provided — SMS not sent."}\n\nShare these credentials manually:\nURL: https://chatbot.hindleconsultants.com\nEmail: ${inviteForm.email}\nTemp Password: ${pw}`);
                  }
                }catch(e){setInviteErr(e&&e.message?e.message:String(e));return;}
                setInviteErr("");setInviteForm({name:"",email:"",mobile:"",role:"agent"});setShowInvite(false);
              }}>Send Invite</button>
              <button className="btn sm" style={{background:S.bg,border:`1px solid ${S.brd}`}} onClick={()=>{setShowInvite(false);setInviteErr("");}}>Cancel</button>
            </div>
            {inviteErr&&<div style={{marginTop:8,padding:"7px 11px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,fontSize:12,color:G.r,fontWeight:600}}>{inviteErr}</div>}
          </div>
          </td></tr></tbody>
          )}
          <thead><tr>{["Agent","Role","Mobile Number","SMS Alerts","See All Convos","Notify For","Status","Rating",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {agents.map((a,i)=>(
              <tr key={a.id}>
                <td><div style={{display:"flex",alignItems:"center",gap:9}}><Av name={a.name} size={30} col={a.status==="online"?G.g:T.fnt}/><div><p style={{fontWeight:600,fontSize:13}}>{a.name}</p><p style={{fontSize:11,color:T.fnt}}>{a.email}</p></div></div></td>
                <td><span style={{fontSize:12,textTransform:"capitalize",color:T.lt}}>{a.role.replace("_"," ")}</span></td>
                <td>
                  {editing===i?(
                    <input className="inp" value={a.mobile||""} onChange={e=>updateAgent(a.id,{mobile:e.target.value})} onBlur={()=>setEditing(null)} autoFocus style={{fontSize:12,padding:"4px 8px"}}/>
                  ):(
                    <div style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}} onClick={()=>setEditing(i)}>
                      <span className="mono" style={{fontSize:12,color:a.mobile?T.ink:T.fnt}}>{a.mobile||"+ Add number"}</span>
                      <Ic n="edit" s={11} c={T.fnt}/>
                    </div>
                  )}
                </td>
                <td>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:34,height:18,borderRadius:9,background:a.sms_alerts?CS:S.brd,position:"relative",transition:"background .2s",cursor:"pointer",flexShrink:0}} onClick={()=>updateAgent(a.id,{sms_alerts:!a.sms_alerts})}>
                      <div style={{position:"absolute",top:2,left:a.sms_alerts?18:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                    </div>
                    <span style={{fontSize:11.5,color:a.sms_alerts?CS:T.fnt}}>{a.sms_alerts?"On":"Off"}</span>
                  </div>
                </td>
                <td>
                  <div style={{display:"flex",flexDirection:"column",gap:2}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:34,height:18,borderRadius:9,background:a.restrict_to_mine?S.brd:G.g,position:"relative",transition:"background .2s",cursor:"pointer",flexShrink:0}} onClick={()=>updateAgent(a.id,{restrict_to_mine:!a.restrict_to_mine})}>
                        <div style={{position:"absolute",top:2,left:a.restrict_to_mine?2:18,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                      </div>
                      <span style={{fontSize:11.5,color:a.restrict_to_mine?T.fnt:G.g}}>{a.restrict_to_mine?"No — mine only":"Yes"}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{display:"flex",flexDirection:"column",gap:3}}>
                    {["chatbot","tickets"].map(scope=>{
                      const active=(a.work_scope||["chatbot","tickets"]).includes(scope);
                      return (
                        <label key={scope} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:11}}>
                          <input type="checkbox" checked={active} onChange={()=>{
                            const cur=a.work_scope||["chatbot","tickets"];
                            const next=active?cur.filter(s=>s!==scope):[...cur,scope];
                            updateAgent(a.id,{work_scope:next.length?next:[scope]});
                          }} style={{cursor:"pointer"}}/>
                          <span style={{color:active?T.ink:T.fnt,textTransform:"capitalize"}}>{scope}</span>
                        </label>
                      );
                    })}
                  </div>
                </td>
                <td><Chip s={a.status}/></td>
                <td><div style={{display:"flex",alignItems:"center",gap:3}}><Ic n="star" s={12} c={G.a}/><span className="mono" style={{fontSize:12.5}}>{a.rating}</span></div></td>
                <td><AgentMenu agent={a} updateAgent={updateAgent} deleteAgent={deleteAgent}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{padding:18,background:`${CS}14`,border:`1px solid ${CS}55`}}>
        <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
          
          <div>
            <p style={{fontSize:13,fontWeight:600,color:T.ink,marginBottom:4}}>How SMS Agent Alerts Work</p>
            <p style={{fontSize:12.5,color:T.mid,lineHeight:1.6}}>When a visitor requests to speak with a human and no agents are online, Hindle automatically sends an SMS to agents with alerts enabled. The SMS contains the visitor's name, page, and a <strong>magic link</strong> — tapping it opens the conversation directly in Hindle, fully authenticated. Links are one-time use and expire after 3 minutes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Analytics ─────────────────────────────────────────────────
const PUsers=({config})=>{
  const [tenants,setTenants]=useState([]);
  const [orgId,setOrgId]=useState("");
  const [users,setUsers]=useState([]);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [q,setQ]=useState("");
  const isSuper=(()=>{try{const p=localStorage.getItem("hindle_admin_profile");return p?JSON.parse(p).role==="super_admin":false;}catch(e){return false;}})();

  useEffect(()=>{
    if(!isSuper)return;
    fetch(API_URL+"/api/tenants").then(r=>r.json())
      .then(d=>setTenants(Array.isArray(d)?d:[])).catch(()=>{});
  },[isSuper]);

  const load=async(id)=>{
    setLoading(true);setErr("");
    try{
      const r=await fetch(API_URL+"/api/helpdesk/users"+(id?"?org_id="+id:""));
      const d=await r.json();
      if(!r.ok) throw new Error(d.error||"Failed to load users");
      setUsers(Array.isArray(d.data)?d.data:[]);
    }catch(e){setErr(e.message||String(e));setUsers([]);}
    setLoading(false);
  };
  useEffect(()=>{load(orgId);},[orgId]);

  const patchUser=async(id,patch)=>{
    setUsers(us=>us.map(u=>u.id===id?{...u,...patch}:u));
    try{
      const r=await fetch(API_URL+"/api/helpdesk/users/"+id,{method:"PATCH",
        headers:{"Content-Type":"application/json"},body:JSON.stringify(patch)});
      if(!r.ok){const d=await r.json();throw new Error(d.error||"Update failed");}
    }catch(e){setErr(e.message||String(e));load(orgId);}
  };

  const delUser=async(u)=>{
    if(!window.confirm(`Delete ${u.full_name||u.email} permanently?`))return;
    try{
      const r=await fetch(API_URL+"/api/helpdesk/users/"+u.id,{method:"DELETE"});
      const d=await r.json();
      if(r.status===409){
        if(!window.confirm(d.error+"\n\nDelete anyway?"))return;
        const r2=await fetch(API_URL+"/api/helpdesk/users/"+u.id+"?force=1",{method:"DELETE"});
        if(!r2.ok){const d2=await r2.json();throw new Error(d2.error||"Delete failed");}
      }else if(!r.ok) throw new Error(d.error||"Delete failed");
      load(orgId);
    }catch(e){setErr(e.message||String(e));}
  };

  const resetPw=async(u)=>{
    const pw=window.prompt(`New password for ${u.full_name||u.email} (min 8 chars).\nThey must change it at next login.`);
    if(!pw)return;
    try{
      const r=await fetch(API_URL+"/api/helpdesk/users/"+u.id+"/password",{method:"POST",
        headers:{"Content-Type":"application/json"},body:JSON.stringify({password:pw})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.error||"Reset failed");
      alert(`✅ Password set for ${u.full_name||u.email}.\n\nShare securely:\nEmail: ${u.email}\nPassword: ${pw}`);
    }catch(e){setErr(e.message||String(e));}
  };

  const orgName=id=>tenants.find(t=>String(t.helpdesk_org_id)===String(id))?.name
    ||tenants.find(t=>String(t.id)===String(id))?.name||"—";

  const shown=users.filter(u=>{
    if(!q.trim())return true;
    const s=q.toLowerCase();
    return (u.full_name||"").toLowerCase().includes(s)||(u.email||"").toLowerCase().includes(s);
  });

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
     <div style={{display:"grid",gridTemplateColumns:isSuper?"repeat(4,1fr)":"repeat(3,1fr)",gap:12,marginBottom:18}}>
        <Metric label="Total Users" value={users.length} icon="users" d={0}/>
        <Metric label="Active" value={users.filter(u=>u.is_active!==false).length} icon="zap" col={G.g} d={.05}/>
        <Metric label="Disabled" value={users.filter(u=>u.is_active===false).length} icon="x" col={G.r} d={.1}/>
        {isSuper&&<Metric label="Tenants" value={tenants.length} icon="bld" col={CS} d={.15}/>}
      </div>
      <div className="card">
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <h3 style={{fontSize:13.5,fontWeight:700}}>User Management</h3>
          <div style={{display:"flex",gap:8}}>
            <input className="inp" placeholder="Search name or email" value={q} onChange={e=>setQ(e.target.value)} style={{fontSize:12.5,maxWidth:200}}/>
            {isSuper&&(
            <select className="inp" value={orgId} onChange={e=>setOrgId(e.target.value)} style={{maxWidth:240,fontSize:12.5}}>
              <option value="">All tenants</option>
              {tenants.filter(t=>t.helpdesk_org_id).map(t=><option key={t.id} value={t.helpdesk_org_id}>{t.name}</option>)}
            </select>
            )}
          </div>
        </div>
        {err&&<div style={{margin:"10px 16px",padding:"7px 11px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,fontSize:12,color:G.r,fontWeight:600}}>{err}</div>}
        {loading?<div style={{padding:24}}><Spinner size={16}/></div>:(
        <table className="tbl">
          <thead><tr>{["User","Tenant","Role","Phone","Status","Actions"].map(h=><th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.length===0&&<tr><td colSpan={6} style={{padding:18,fontSize:12.5,color:T.fnt}}>No users found.</td></tr>}
            {shown.map(u=>(
              <tr key={u.id} style={{opacity:u.is_active===false?.55:1}}>
                <td><div style={{display:"flex",alignItems:"center",gap:9}}><Av name={u.full_name||u.email} size={30} col={u.is_active===false?T.fnt:G.g}/><div><p style={{fontWeight:600,fontSize:13}}>{u.full_name||"—"}{u.is_super_admin&&<span style={{marginLeft:6,fontSize:9.5,fontWeight:700,color:G.r}}>SUPER</span>}</p><p style={{fontSize:11,color:T.fnt}}>{u.email}</p></div></div></td>
                <td><span style={{fontSize:12,color:T.lt}}>{orgName(u.organization_id)}</span></td>
                <td>
                  <select className="inp" value={u.role||"customer"} onChange={e=>patchUser(u.id,{role:e.target.value})} style={{fontSize:11.5,padding:"3px 6px"}}>
                    <option value="customer">Customer</option>
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td><span className="mono" style={{fontSize:12,color:u.phone?T.ink:T.fnt}}>{u.phone||"—"}</span></td>
                <td>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:34,height:18,borderRadius:9,background:u.is_active===false?S.brd:G.g,position:"relative",cursor:"pointer",flexShrink:0}} onClick={()=>patchUser(u.id,{is_active:u.is_active===false})}>
                      <div style={{position:"absolute",top:2,left:u.is_active===false?2:18,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                    </div>
                    <span style={{fontSize:11.5,color:u.is_active===false?T.fnt:G.g}}>{u.is_active===false?"Disabled":"Active"}</span>
                  </div>
                </td>
                <td>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn sm" style={{background:S.bg,border:`1px solid ${S.brd}`,fontSize:11}} onClick={()=>resetPw(u)}>Reset PW</button>
                    <button className="btn sm" style={{background:G.rb,border:`1px solid ${G.rbd}`,color:G.r,fontSize:11}} onClick={()=>delUser(u)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};
// ── Analytics ─────────────────────────────────────────────────
const PAnalytics=({conversations=[],agents=[],alertLog=[],config})=>{
  const [range,setRange]=useState("30");
  const [tab,setTab]=useState("overview");
  const [apiData,setApiData]=useState(null);
  const [loading,setLoading]=useState(true);

  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();

  useEffect(()=>{
    if(!orgId)return;
    setLoading(true);
    fetch(API_URL+"/api/analytics/"+orgId+"?days="+range)
      .then(r=>r.ok?r.json():null)
      .then(d=>{setApiData(d);setLoading(false);})
      .catch(()=>setLoading(false));
  },[orgId,range]);

  // Build chart data from dailyRows or fallback to conversations prop
  const dailyRows=apiData?.dailyRows||[];
  const buildFallback=(convos,days)=>{
    const rows=[];const now=new Date();
    for(let i=days-1;i>=0;i--){
      const d=new Date();d.setDate(now.getDate()-i);
      const day=d.toISOString().slice(0,10);
      const bucket=convos.filter(c=>{const cd=(c.created_at||"").slice(0,10);return cd===day;});
      rows.push({day,total:bucket.length,human:bucket.filter(c=>c.claimed_by_id).length,csat_pos:0,csat_total:0});
    }
    return rows;
  };
  const rows=dailyRows.length>0?dailyRows:buildFallback(conversations,Number(range));
  const labels=rows.map(r=>{const d=new Date(r.day);return d.toLocaleDateString("en-AU",{month:"short",day:"numeric"});});
  const totals=rows.map(r=>r.total||0);
  const humans=rows.map(r=>r.human||0);
  const aiBots=totals.map((t,i)=>Math.max(0,t-humans[i]));
  const maxBar=Math.max(...totals,1)*1.1;
  const csatScore=apiData?.csatScore;
  const csatTotal=apiData?.csatTotal||0;
  const avgFrtSec=apiData?.avgFrt;
  const medFrtSec=apiData?.medFrt;
  const fmtTime=s=>s==null?"—":s<60?`${s}s`:s<3600?`${Math.round(s/60)}m`:">1h";
  const totalConvs=conversations.length;
  const resolvedConvs=conversations.filter(c=>c.status==="resolved"||c.status==="finished").length;
  const handoffConvs=conversations.filter(c=>c.status==="handoff"||c.claimed_by_id).length;
  const aiPct=totalConvs===0?0:Math.round((totalConvs-handoffConvs)/totalConvs*100);

  const Bar=({bot,human,label,max})=>{
    const botH=Math.round((bot||0)/max*100);const humH=Math.round((human||0)/max*100);
    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
        <div style={{display:"flex",flexDirection:"column",justifyContent:"flex-end",height:100,gap:1,width:"100%",alignItems:"center"}}>
          {botH>0&&<div style={{width:"70%",height:botH,background:B,borderRadius:"3px 3px 0 0",opacity:.8}}/>}
          {humH>0&&<div style={{width:"70%",height:humH,background:G.g,borderRadius:botH>0?"0":"3px 3px 0 0",opacity:.9}}/>}
        </div>
        <span style={{fontSize:9.5,color:T.fnt,textAlign:"center",whiteSpace:"nowrap"}}>{label}</span>
      </div>
    );
  };

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <h2 style={{fontSize:17,fontWeight:800,marginBottom:3}}>Analytics</h2>
          <p style={{fontSize:12.5,color:T.fnt}}>Conversation volume, AI resolution rate, CSAT, and response times.</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <select className="inp" value={range} onChange={e=>setRange(e.target.value)} style={{width:"auto",fontSize:12}}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button className="btn bo sm" onClick={()=>{
            if(!orgId){alert("No org ID available.");return;}
            window.open(API_URL+"/api/analytics/"+orgId+"/export?days="+range,"_blank");
          }} title="Export conversations as CSV">
            <Ic n="copy" s={12}/> Export CSV
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <Metric label="Total Conversations" value={totalConvs.toLocaleString()} icon="chat" col={B} d={0}/>
        <Metric label="AI Resolution Rate" value={`${aiPct}%`} icon="bot" col={G.p} d={.05}/>
        <Metric label="CSAT Score" value={csatScore!=null?`${csatScore}%`:"—"} sub={{t:csatTotal>0?`${csatTotal} ratings`:"no ratings yet"}} icon="star" col={G.a} d={.1}/>
        <Metric label="Avg First Response" value={fmtTime(avgFrtSec)} sub={{t:medFrtSec!=null?`median ${fmtTime(medFrtSec)}`:""}} icon="zap" col={G.g} d={.15}/>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14}}>
        {[["overview","Overview"],["csat","CSAT Detail"],["agents","Agents"],["frt","Response Time"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} className={`btn sm ${tab===t?"bp":"bo"}`} style={{fontSize:11.5}}>{l}</button>
        ))}
      </div>

      {loading&&<div style={{textAlign:"center",padding:40}}><Spinner size={20}/><p style={{marginTop:10,fontSize:12,color:T.fnt}}>Loading analytics…</p></div>}

      {!loading&&tab==="overview"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:14}}>
          {/* Volume chart */}
          <div className="card" style={{padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <p style={{fontSize:12.5,fontWeight:700}}>Conversation Volume</p>
              <div style={{display:"flex",gap:10}}>
                {[[B,"AI handled"],[G.g,"Human handled"]].map(([c,l])=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{width:10,height:10,borderRadius:2,background:c}}/>
                    <span style={{fontSize:10.5,color:T.fnt}}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:4,height:120}}>
              {rows.map((r,i)=><Bar key={i} bot={aiBots[i]} human={humans[i]} label={labels[i]} max={maxBar}/>)}
            </div>
          </div>

          {/* Summary breakdown */}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div className="card" style={{padding:14}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Breakdown</p>
              {[
                ["Total chats",totalConvs,B],
                ["Resolved",resolvedConvs,G.g],
                ["Human handoff",handoffConvs,G.a],
                ["AI handled",totalConvs-handoffConvs,G.p],
              ].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:7,alignItems:"center"}}>
                  <span style={{fontSize:12,color:T.mid}}>{l}</span>
                  <span style={{fontSize:13,fontWeight:700,color:c}}>{v}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{padding:14}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Agents</p>
              {agents.length===0?<p style={{fontSize:12,color:T.fnt}}>No agents yet</p>:agents.slice(0,4).map(a=>(
                <div key={a.id} style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                  <div style={{display:"flex",gap:7,alignItems:"center"}}>
                    <Av name={a.name} size={22} col={B}/>
                    <span style={{fontSize:12,fontWeight:500}}>{a.name}</span>
                  </div>
                  <Chip s={a.status||"offline"}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading&&tab==="csat"&&(
        <div style={{maxWidth:580}}>
          <div className="card" style={{padding:16,marginBottom:14}}>
            <p style={{fontSize:12.5,fontWeight:700,marginBottom:12}}>CSAT Summary — last {range} days</p>
            {csatTotal===0?(
              <div style={{textAlign:"center",padding:32}}>
                <Ic n="star" s={28} c={T.fnt}/>
                <p style={{marginTop:10,fontSize:13,color:T.fnt}}>No CSAT ratings yet.</p>
                <p style={{fontSize:12,color:T.fnt,marginTop:4}}>Ratings appear here after visitors rate conversations in the widget.</p>
              </div>
            ):(
              <>
                <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:16}}>
                  <div style={{textAlign:"center"}}>
                    <p style={{fontSize:36,fontWeight:800,color:csatScore>=70?G.g:csatScore>=50?G.a:G.r}}>{csatScore}%</p>
                    <p style={{fontSize:11.5,color:T.fnt}}>satisfaction</p>
                  </div>
                  <div style={{flex:1}}>
                    {[["👍 Positive",apiData?.csatPositive||0,G.g],[`👎 Negative`,csatTotal-(apiData?.csatPositive||0),G.r]].map(([l,v,c])=>(
                      <div key={l} style={{marginBottom:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:12}}>{l}</span>
                          <span style={{fontSize:12,fontWeight:700,color:c}}>{v} ({csatTotal===0?0:Math.round(v/csatTotal*100)}%)</span>
                        </div>
                        <div style={{height:8,background:S.bg,borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${csatTotal===0?0:Math.round(v/csatTotal*100)}%`,background:c,borderRadius:4}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{fontSize:11.5,color:T.fnt,textAlign:"center"}}>{csatTotal} total rating{csatTotal!==1?"s":""}</p>
              </>
            )}
          </div>
          <div style={{padding:"10px 14px",background:BL,border:`1px solid ${BM}`,borderRadius:8,fontSize:12,color:T.mid,lineHeight:1.55}}>
            Visitors are prompted to rate their conversation (👍/👎) after it is resolved. Ratings feed directly into this dashboard.
          </div>
        </div>
      )}

      {!loading&&tab==="frt"&&(
        <div style={{maxWidth:540}}>
          <div className="card" style={{padding:16,marginBottom:14}}>
            <p style={{fontSize:12.5,fontWeight:700,marginBottom:12}}>First Response Time — last {range} days</p>
            {avgFrtSec==null?(
              <div style={{textAlign:"center",padding:32}}>
                <Ic n="zap" s={28} c={T.fnt}/>
                <p style={{marginTop:10,fontSize:13,color:T.fnt}}>No response time data yet.</p>
                <p style={{fontSize:12,color:T.fnt,marginTop:4}}>Measured from conversation start to first agent reply.</p>
              </div>
            ):(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                {[["Average",fmtTime(avgFrtSec),B],["Median",fmtTime(medFrtSec),G.p],["Samples",apiData?.frtCount||0,G.g]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center",padding:"14px 10px",background:S.bg,borderRadius:8,border:`1px solid ${S.brd}`}}>
                    <p style={{fontSize:24,fontWeight:800,color:c}}>{v}</p>
                    <p style={{fontSize:11.5,color:T.fnt,marginTop:3}}>{l}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{padding:"10px 14px",background:BL,border:`1px solid ${BM}`,borderRadius:8,fontSize:12,color:T.mid,lineHeight:1.55}}>
            <strong>Target:</strong> Under 2 minutes is considered good. Under 30 seconds is excellent. Configure SMS magic links in Integrations to ensure agents are notified instantly.
          </div>
        </div>
      )}

      {!loading&&tab==="agents"&&(
        <div>
          {agents.length===0?(
            <div style={{textAlign:"center",padding:60,color:T.fnt}}>
              <Ic n="users" s={28} c={T.fnt}/>
              <p style={{marginTop:10,fontSize:13}}>No agents yet.</p>
            </div>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
              {agents.map(a=>{
                const claimed=conversations.filter(c=>c.claimed_by_id===a.id||c.claimed_by_name===a.name);
                return(
                  <div key={a.id} className="card" style={{padding:16}}>
                    <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12}}>
                      <Av name={a.name} size={36} col={B}/>
                      <div>
                        <p style={{fontSize:13,fontWeight:700}}>{a.name}</p>
                        <div style={{display:"flex",gap:5,alignItems:"center"}}>
                          <Chip s={a.status||"offline"}/>
                          <Chip s={a.role||"agent"}/>
                        </div>
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <div style={{textAlign:"center"}}>
                        <p style={{fontSize:18,fontWeight:700,color:B}}>{claimed.length}</p>
                        <p style={{fontSize:10.5,color:T.fnt}}>claimed</p>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <p style={{fontSize:18,fontWeight:700,color:G.g}}>{claimed.filter(c=>c.status==="resolved").length}</p>
                        <p style={{fontSize:10.5,color:T.fnt}}>resolved</p>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <p style={{fontSize:18,fontWeight:700,color:G.a}}>{a.sms_alerts?"ON":"OFF"}</p>
                        <p style={{fontSize:10.5,color:T.fnt}}>SMS alerts</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TRIAL MONITOR — super admin
// ══════════════════════════════════════════════════════════════
const TrialMonitor=({config,tenants:apiTenants=[]})=>{
  const rows=(apiTenants||[]).map(t=>({
    id:t.id, name:t.name||"—", email:t.email||"—",
    plan:t.plan||"starter", status:t.status||"trial",
    day:t.trial_day||0,
    reminded:Array.isArray(t.trial_reminded)?t.trial_reminded:[],
    paid:["active","paid"].includes(t.status),
    expired:t.status==="expired",
  }));
  const [auditRows,setAuditRows]=useState([]);
  const [auditTenant,setAuditTenant]=useState(null);
  const [sending,setSending]=useState(null);
  const [msg,setMsg]=useState("");

  const loadAudit=async(tenantId)=>{
    setAuditTenant(tenantId);
    try{
      const r=await fetch(API_URL+"/api/audit-log/"+tenantId);
      const d=await r.json();
      setAuditRows(Array.isArray(d)?d:[]);
    }catch(e){setAuditRows([]);}
  };

  const sendReminder=async(t)=>{
    setSending(t.id);setMsg("");
    try{
      // Write audit entry — actual email fired by scheduler; manual trigger via audit POST
      const r=await fetch(API_URL+"/api/audit-log",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({tenantId:t.id,eventType:"trial_reminder_manual",
          description:`Manual reminder triggered by admin for day ${t.day}`,
          meta:{day:t.day,email:t.email}})});
      if(r.ok) setMsg("Logged — scheduler will send on next cycle");
    }catch(e){setMsg("Error: "+e.message);}
    setSending(null);
  };

  const urgCol=(d,paid,expired)=>paid?G.g:expired?G.r:d>=14?G.r:d>=12?G.a:G.g;

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
        <Metric label="In Trial" value={rows.filter(t=>!t.paid&&!t.expired&&t.day<=14).length} icon="cal" col={G.a} d={0}/>
        <Metric label="Ending ≤2 Days" value={rows.filter(t=>!t.paid&&!t.expired&&t.day>=12&&t.day<=14).length} icon="warn" col={G.r} d={.05}/>
        <Metric label="Expired" value={rows.filter(t=>t.expired).length} icon="lock" col={G.r} d={.1}/>
        <Metric label="Converted" value={rows.filter(t=>t.paid).length} icon="chk" col={G.g} d={.15}/>
      </div>

      {msg&&<div style={{padding:"8px 14px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7,marginBottom:12,fontSize:12,color:G.g}}>{msg}</div>}

      <div style={{display:"grid",gridTemplateColumns:auditTenant?"1fr 1fr":"1fr",gap:14}}>
        <div className="card">
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{fontSize:13.5,fontWeight:700}}>Trial Status</h3>
            <span style={{fontSize:11,color:T.fnt}}>Emails auto-sent days 12, 13 & 14 by scheduler</span>
          </div>
          <table className="tbl">
            <thead><tr>{["Organisation","Plan","Day","Progress","Reminded","Status","Actions"].map(h=><th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.length===0&&<tr><td colSpan={7} style={{textAlign:"center",color:T.fnt,padding:24}}>No tenants found</td></tr>}
              {rows.map(t=>{
                const col=urgCol(t.day,t.paid,t.expired);
                return(
                  <tr key={t.id} style={{background:auditTenant===t.id?BL:"transparent"}}>
                    <td><div style={{display:"flex",alignItems:"center",gap:7}}>
                      <Av name={t.name} size={26} col={col}/>
                      <div><p style={{fontWeight:600,fontSize:12.5}}>{t.name}</p><p style={{fontSize:10.5,color:T.fnt}}>{t.email}</p></div>
                    </div></td>
                    <td><Chip s={t.plan}/></td>
                    <td><span className="mono" style={{fontSize:14,fontWeight:800,color:col}}>Day {t.day}</span></td>
                    <td style={{minWidth:100}}>
                      {!t.paid&&!t.expired&&<div>
                        <div style={{height:5,background:S.brd,borderRadius:3,overflow:"hidden",marginBottom:2}}>
                          <div style={{height:"100%",width:`${Math.min(t.day/14*100,100)}%`,background:col}}/>
                        </div>
                        <p style={{fontSize:10,color:col,fontWeight:600}}>{t.day>=14?"LAST DAY":t.day>=12?"ENDING SOON":`${14-t.day}d left`}</p>
                      </div>}
                      {t.paid&&<Chip s="paid"/>}
                      {t.expired&&<Chip s="expired"/>}
                    </td>
                    <td>
                      <div style={{display:"flex",gap:3}}>
                        {[12,13,14].map(d=>{
                          const sent=t.reminded.includes(d);
                          return <div key={d} title={`Day ${d}`} style={{width:18,height:18,borderRadius:"50%",background:sent?B:S.bg,border:`1.5px solid ${sent?B:S.brd}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {sent?<Ic n="chk" s={8} c="#fff"/>:<span style={{fontSize:8,fontWeight:700,color:T.fnt}}>{d}</span>}
                          </div>;
                        })}
                      </div>
                    </td>
                    <td>{t.paid?<Chip s="paid"/>:t.expired?<Chip s="expired"/>:<Chip s="trial"/>}</td>
                    <td>
                      <div style={{display:"flex",gap:4}}>
                        <button className="btn bo sm" style={{fontSize:10}} onClick={()=>loadAudit(t.id)}><Ic n="doc" s={10}/> Log</button>
                        {!t.paid&&<button className="btn bo sm" style={{fontSize:10}} onClick={()=>sendReminder(t)} disabled={sending===t.id}>
                          {sending===t.id?<><Spinner size={9}/> …</>:<><Ic n="sms" s={10}/> Remind</>}
                        </button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Audit log panel */}
        {auditTenant&&(
          <div className="card" style={{maxHeight:480,display:"flex",flexDirection:"column"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <h3 style={{fontSize:13,fontWeight:700}}>Audit Log — {rows.find(r=>r.id===auditTenant)?.name||auditTenant}</h3>
              <button onClick={()=>setAuditTenant(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt}}><Ic n="x" s={14}/></button>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {auditRows.length===0&&<p style={{padding:16,color:T.fnt,fontSize:12}}>No audit events yet.</p>}
              {auditRows.map(a=>(
                <div key={a.id} style={{padding:"9px 14px",borderBottom:`1px solid ${S.brd}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                    <div>
                      <span style={{fontSize:11,fontWeight:700,color:a.event_type.includes("fail")?G.r:a.event_type.includes("sent")||a.event_type.includes("reactivat")||a.event_type.includes("paid")?G.g:G.a,background:a.event_type.includes("fail")?G.rb:a.event_type.includes("sent")?G.gb:G.ab,padding:"1px 7px",borderRadius:10,marginRight:6}}>
                        {a.event_type.replace(/_/g," ")}
                      </span>
                      <span style={{fontSize:11.5,color:T.mid}}>{a.description||"—"}</span>
                    </div>
                    <span style={{fontSize:10,color:T.fnt,whiteSpace:"nowrap",flexShrink:0}}>{new Date(a.created_at).toLocaleString("en-AU",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                  </div>
                  {a.meta&&Object.keys(a.meta).length>0&&<p style={{fontSize:10,color:T.fnt,marginTop:3,fontFamily:"monospace"}}>{JSON.stringify(a.meta)}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SUPER ADMIN — TENANT ACCOUNTS PAGE
// ══════════════════════════════════════════════════════════════
const TENANT_DATA=[];

const PTenants=({tenants=[],setTenants})=>{
  const [search,setSearch]=useState("");
  const [filter,setFilter]=useState("all");
  const [selected,setSelected]=useState(null);
  const [usage,setUsage]=useState(null);
  const [auditRows,setAuditRows]=useState([]);
  const [auditTab,setAuditTab]=useState("overview"); // overview | audit
  const [actioning,setActioning]=useState(false);
  const [actionMsg,setActionMsg]=useState({type:"",text:""});
  const [manageForm,setManageForm]=useState({plan:"starter",note:"",trialDays:"7"});

  const PLAN_LIMITS={
    free:{agents:1,conversations_month:100,kb_docs:0},
    starter:{agents:5,conversations_month:500,kb_docs:5},
    professional:{agents:10,conversations_month:2000,kb_docs:999},
    enterprise:{agents:999,conversations_month:999999,kb_docs:999},
  };

  const rows=tenants.map(t=>({
    id:t.id, name:t.name||"—", email:t.email||"—",
    plan:t.plan||"free", status:t.status||"trial",
    paid:["active","paid"].includes(t.status),
    trial_day:t.trial_day||0,
    trial_start_date:t.trial_start_date||t.created_at,
    created_at:t.created_at,
    tenantId:t.tenant_id||t.id,
  }));

  const filtered=rows.filter(t=>{
    const q=search.toLowerCase();
    const matchS=t.name.toLowerCase().includes(q)||t.email.toLowerCase().includes(q);
    const matchF=filter==="all"||(filter==="trial"&&!t.paid&&t.status!=="expired")||
      (filter==="active"&&t.paid)||(filter==="expired"&&t.status==="expired")||
      (filter==="expiring"&&!t.paid&&t.trial_day>=12&&t.trial_day<=14);
    return matchS&&matchF;
  });

  const sel=selected?rows.find(t=>t.id===selected):null;

  const [tenantInfo,setTenantInfo]=useState(null); // org + billing config for selected tenant

  const loadDetail=async(t)=>{
    setSelected(t.id);
    setManageForm(f=>({...f,plan:t.plan}));
    setAuditTab("overview");
    setActionMsg({type:"",text:""});
    setTenantInfo(null);
    try{
      const [uR,aR,cfgR]=await Promise.all([
        fetch(API_URL+"/api/tenants/"+t.id+"/usage"),
        fetch(API_URL+"/api/audit-log/"+t.id),
        fetch(API_URL+"/api/tenant-config/"+(t.tenantId||t.id)),
      ]);
      const [u,a,cfg]=await Promise.all([uR.json(),aR.json(),cfgR.ok?cfgR.json():{}]);
      setUsage(u);
      setAuditRows(Array.isArray(a)?a:[]);
      setTenantInfo(cfg||{});
    }catch(e){setUsage(null);setAuditRows([]);setTenantInfo({});}
  };

  const doAction=async(action)=>{
    if(!sel) return;
    setActioning(true);setActionMsg({type:"",text:""});
    try{
      const r=await fetch(API_URL+"/api/tenants/"+sel.id+"/manage",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({action,plan:manageForm.plan,note:manageForm.note,trialDays:manageForm.trialDays}),
      });
      const d=await r.json();
      if(d.ok){
        setActionMsg({type:"ok",text:`Action "${action.replace(/_/g," ")}" completed successfully.`});
        // Refresh tenant list
        const tR=await fetch(API_URL+"/api/tenants");
        if(tR.ok){const td=await tR.json();setTenants(td);}
        // Refresh audit log
        const aR=await fetch(API_URL+"/api/audit-log/"+sel.id);
        if(aR.ok){const ad=await aR.json();setAuditRows(Array.isArray(ad)?ad:[]);}
        // Refresh usage
        const uR=await fetch(API_URL+"/api/tenants/"+sel.id+"/usage");
        if(uR.ok){const ud=await uR.json();setUsage(ud);}
      }else{
        setActionMsg({type:"err",text:d.error||"Action failed"});
      }
    }catch(e){setActionMsg({type:"err",text:e.message});}
    setActioning(false);
  };

  const UsageBar=({label,used,limit})=>{
    const pct=limit>=999?0:Math.min(used/limit*100,100);
    const col=pct>90?G.r:pct>70?G.a:G.g;
    return(
      <div style={{marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
          <span style={{fontSize:11.5,color:T.mid,fontWeight:600}}>{label}</span>
          <span style={{fontSize:11.5,color:col,fontWeight:700,fontFamily:"monospace"}}>
            {used} / {limit>=999?"∞":limit}
          </span>
        </div>
        {limit<999&&<div style={{height:5,background:S.brd,borderRadius:3,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pct}%`,background:col,transition:"width .4s"}}/>
        </div>}
      </div>
    );
  };

  const [showCreate,setShowCreate]=useState(false);
  const [createForm,setCreateForm]=useState({name:"",email:"",plan:"starter"});
  const [creating,setCreating]=useState(false);
  const [createErr,setCreateErr]=useState("");

  const doCreate=async()=>{
    if(!createForm.name.trim()||!createForm.email.trim()){setCreateErr("Name and email are required.");return;}
    setCreating(true);setCreateErr("");
    try{
      const r=await fetch(API_URL+"/api/tenants",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({name:createForm.name.trim(),email:createForm.email.trim(),plan:createForm.plan})});
      const d=await r.json();
      if(!r.ok){setCreateErr(d.error||"Failed to create tenant.");setCreating(false);return;}
      // Refresh tenant list
      const tR=await fetch(API_URL+"/api/tenants");
      if(tR.ok)setTenants(await tR.json());
      setShowCreate(false);setCreateForm({name:"",email:"",plan:"starter"});
      // writeAudit is server-side only
    }catch(e){setCreateErr(e.message||"Error");}
    setCreating(false);
  };

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%",display:"flex",flexDirection:"column",gap:14}}>
      {/* Create tenant modal */}
      {showCreate&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>{if(e.target===e.currentTarget)setShowCreate(false);}}>
          <div className="card" style={{width:380,padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:700}}>Create Tenant Account</h3>
              <button onClick={()=>setShowCreate(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt}}><Ic n="x" s={16}/></button>
            </div>
            {[["Organisation Name","name","text","Hindle Consultants Ltd"],["Admin Email","email","email","admin@company.com"]].map(([l,k,t,ph])=>(
              <div key={k} style={{marginBottom:12}}>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>{l}</label>
                <input className="inp" type={t} placeholder={ph} value={createForm[k]} onChange={e=>setCreateForm(f=>({...f,[k]:e.target.value}))}/>
              </div>
            ))}
            <div style={{marginBottom:16}}>
              <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Plan</label>
              <select className="inp" value={createForm.plan} onChange={e=>setCreateForm(f=>({...f,plan:e.target.value}))}>
                {["free","starter","professional","enterprise"].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
            {createErr&&<div style={{padding:"7px 11px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,marginBottom:10,fontSize:12,color:G.r,fontWeight:600}}>{createErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <button className="btn bp" style={{flex:1}} onClick={doCreate} disabled={creating}>
                {creating?<><Spinner size={12}/> Creating…</>:<><Ic n="plus" s={12} c="#fff"/> Create Tenant</>}
              </button>
              <button className="btn bo" onClick={()=>setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Summary metrics + Create Tenant */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,flex:1}}>
          <Metric label="Total Tenants" value={rows.length} icon="bld" col={B} d={0}/>
          <Metric label="Active Paid" value={rows.filter(t=>t.paid).length} icon="chk" col={G.g} d={.05}/>
          <Metric label="In Trial" value={rows.filter(t=>!t.paid&&t.status!=="expired").length} icon="cal" col={G.a} d={.1}/>
          <Metric label="Expired" value={rows.filter(t=>t.status==="expired").length} icon="warn" col={G.r} d={.15}/>
        </div>
        <button className="btn bp" style={{flexShrink:0,whiteSpace:"nowrap"}} onClick={()=>setShowCreate(true)}>
          <Ic n="plus" s={12} c="#fff"/> Create Tenant
        </button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:sel?"1.4fr 1fr":"1fr",gap:14,flex:1,minHeight:0}}>
        {/* Tenant list */}
        <div className="card" style={{display:"flex",flexDirection:"column",minHeight:0}}>
          <div style={{padding:"12px 14px",borderBottom:`1px solid ${S.brd}`,display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
            <input className="inp" placeholder="Search tenants…" value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1,fontSize:12}}/>
            <select className="inp" value={filter} onChange={e=>setFilter(e.target.value)} style={{fontSize:12,width:"auto"}}>
              {[["all","All"],["trial","In Trial"],["expiring","Expiring"],["active","Active"],["expired","Expired"]].map(([v,l])=>
                <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={{overflowY:"auto",flex:1}}>
            <table className="tbl">
              <thead><tr>{["Tenant","Plan","Status","Trial Day","Created"].map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.length===0&&<tr><td colSpan={5} style={{textAlign:"center",color:T.fnt,padding:24}}>No tenants match</td></tr>}
                {filtered.map(t=>(
                  <tr key={t.id} style={{cursor:"pointer",background:selected===t.id?BL:"transparent"}} onClick={()=>loadDetail(t)}>
                    <td>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <Av name={t.name} size={26} col={t.paid?G.g:t.status==="expired"?G.r:G.a}/>
                        <div>
                          <p style={{fontWeight:600,fontSize:12.5}}>{t.name}</p>
                          <p style={{fontSize:10.5,color:T.fnt}}>{t.email}</p>
                        </div>
                      </div>
                    </td>
                    <td><Chip s={t.plan}/></td>
                    <td><Chip s={t.paid?"paid":t.status}/></td>
                    <td>
                      {!t.paid&&t.status!=="expired"&&
                        <span className="mono" style={{fontSize:12.5,fontWeight:700,color:t.trial_day>=12?G.r:t.trial_day>=8?G.a:G.g}}>
                          Day {t.trial_day}
                        </span>}
                      {(t.paid||t.status==="expired")&&<span style={{fontSize:11,color:T.fnt}}>—</span>}
                    </td>
                    <td style={{fontSize:11,color:T.fnt}}>{t.created_at?new Date(t.created_at).toLocaleDateString("en-AU"):""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail panel */}
        {sel&&(
          <div style={{display:"flex",flexDirection:"column",gap:10,overflowY:"auto"}}>
            {/* Header */}
            <div className="card" style={{padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <Av name={sel.name} size={36} col={sel.paid?G.g:sel.status==="expired"?G.r:G.a}/>
                  <div>
                    <p style={{fontSize:14,fontWeight:700}}>{sel.name}</p>
                    <p style={{fontSize:11.5,color:T.fnt}}>{sel.email}</p>
                    <div style={{display:"flex",gap:5,marginTop:3}}><Chip s={sel.plan}/><Chip s={sel.paid?"paid":sel.status}/></div>
                  </div>
                </div>
                <button onClick={()=>{setSelected(null);setUsage(null);}} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:4}}><Ic n="x" s={15}/></button>
              </div>
              {/* Sub-tabs */}
              <div style={{display:"flex",gap:4}}>
                {[["overview","Overview"],["info","Info"],["manage","Manage"],["audit","Audit Log"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setAuditTab(v)} className={`btn sm ${auditTab===v?"bp":"bo"}`} style={{fontSize:11}}>{l}</button>
                ))}
              </div>
            </div>

            {/* Info tab — org + billing + contact details */}
            {auditTab==="info"&&(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {!tenantInfo?(
                  <div style={{padding:24,textAlign:"center"}}><Spinner size={18}/></div>
                ):(()=>{
                  const org=tenantInfo.org||{};
                  const b=tenantInfo.billing2||{};
                  const Row=({label,value})=>value?(
                    <div style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${S.bg}`}}>
                      <span style={{fontSize:12,color:T.fnt,fontWeight:600}}>{label}</span>
                      <span style={{fontSize:12.5,color:T.ink,maxWidth:"60%",textAlign:"right",wordBreak:"break-all"}}>{value}</span>
                    </div>
                  ):null;
                  return(
                    <>
                      <div className="card" style={{padding:16}}>
                        <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Organisation</p>
                        <Row label="Name" value={org.name||sel?.name}/>
                        <Row label="Email" value={org.support_email||sel?.email}/>
                        <Row label="Phone" value={org.phone}/>
                        <Row label="Website" value={org.website}/>
                        <Row label="Industry" value={org.industry}/>
                        <Row label="Timezone" value={org.timezone}/>
                        <Row label="Address" value={[org.address,org.city,org.state,org.postcode,org.country].filter(Boolean).join(", ")}/>
                        {!org.name&&!org.phone&&<p style={{fontSize:12,color:T.fnt,padding:"8px 0"}}>No organisation details on file.</p>}
                      </div>
                      <div className="card" style={{padding:16}}>
                        <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Billing & Contact</p>
                        <Row label="Billing Name" value={b.full_name}/>
                        <Row label="Company / Reg No." value={b.company_number}/>
                        <Row label="Invoice Email" value={b.invoice_email}/>
                        <Row label="Billing Address" value={[b.address_line1,b.address_line2,b.city,b.state,b.postcode,b.country].filter(Boolean).join(", ")}/>
                        {!b.full_name&&!b.invoice_email&&<p style={{fontSize:12,color:T.fnt,padding:"8px 0"}}>No billing details on file.</p>}
                      </div>
                      <div className="card" style={{padding:16}}>
                        <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Account Details</p>
                        <Row label="Plan" value={sel?.plan}/>
                        <Row label="Status" value={sel?.status}/>
                        <Row label="Trial Day" value={sel?.trial_day?`Day ${sel.trial_day} of 14`:null}/>
                        <Row label="Created" value={sel?.created_at?new Date(sel.created_at).toLocaleDateString("en-AU",{year:"numeric",month:"short",day:"numeric"}):null}/>
                        <Row label="Tenant ID" value={sel?.tenantId}/>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Overview tab */}
            {auditTab==="overview"&&(
              <div className="card" style={{padding:14}}>
                <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Usage vs Plan Limits</p>
                {usage?(
                  <>
                    <UsageBar label="Agent Seats" used={usage.usage?.agents||0} limit={usage.limits?.agents||1}/>
                    <UsageBar label="Conversations / month" used={usage.usage?.conversations_month||0} limit={usage.limits?.conversations_month||100}/>
                    <UsageBar label="KB Documents" used={usage.usage?.kb_docs||0} limit={usage.limits?.kb_docs||0}/>
                    <div style={{marginTop:12,padding:"9px 11px",background:S.bg,borderRadius:7,border:`1px solid ${S.brd}`}}>
                      {[
                        ["Plan",<Chip s={sel.plan}/>],
                        ["Status",<Chip s={sel.paid?"paid":sel.status}/>],
                        ["Trial Day",sel.trial_day>0?`Day ${sel.trial_day}`:"Not started"],
                        ["Trial Started",sel.trial_start_date?new Date(sel.trial_start_date).toLocaleDateString("en-AU"):"—"],
                        ["Member Since",sel.created_at?new Date(sel.created_at).toLocaleDateString("en-AU"):"—"],
                      ].map(([l,v])=>(
                        <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                          <span style={{fontSize:11.5,color:T.fnt}}>{l}</span>
                          <span style={{fontSize:12,fontWeight:600}}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ):<div style={{textAlign:"center",padding:20,color:T.fnt,fontSize:12}}><Spinner size={16}/> Loading…</div>}
              </div>
            )}

            {/* Manage tab */}
            {auditTab==="manage"&&(
              <div className="card" style={{padding:14}}>
                <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Plan Management</p>

                {actionMsg.text&&(
                  <div style={{padding:"8px 12px",borderRadius:7,marginBottom:10,fontSize:12,fontWeight:600,
                    background:actionMsg.type==="ok"?G.gb:G.rb,color:actionMsg.type==="ok"?G.g:G.r,
                    border:`1px solid ${actionMsg.type==="ok"?G.gbd:G.rbd}`}}>
                    {actionMsg.text}
                  </div>
                )}

                {/* Change Plan */}
                <div style={{padding:"11px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,marginBottom:8}}>Change Plan</p>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8}}>
                    <select className="inp" value={manageForm.plan} onChange={e=>setManageForm(f=>({...f,plan:e.target.value}))} style={{flex:1,fontSize:12}}>
                      {["free","starter","professional","enterprise"].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                    </select>
                    <button className="btn bp sm" onClick={()=>doAction("change_plan")} disabled={actioning} style={{whiteSpace:"nowrap"}}>
                      {actioning?<Spinner size={11}/>:"Apply Plan"}
                    </button>
                  </div>
                  <div style={{padding:"7px 9px",background:BL,borderRadius:6,border:`1px solid ${BM}`}}>
                    {[["Agents",PLAN_LIMITS[manageForm.plan]?.agents],["Convos/mo",PLAN_LIMITS[manageForm.plan]?.conversations_month],["KB Docs",PLAN_LIMITS[manageForm.plan]?.kb_docs>=999?"∞":PLAN_LIMITS[manageForm.plan]?.kb_docs]].map(([l,v])=>(
                      <span key={l} style={{fontSize:11,color:T.lt,marginRight:14}}><strong style={{color:B}}>{v}</strong> {l}</span>
                    ))}
                  </div>
                </div>

                {/* Trial controls */}
                <div style={{padding:"11px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,marginBottom:8}}>Trial Controls</p>
                  <div style={{display:"flex",gap:6,marginBottom:6}}>
                    <div style={{flex:1}}>
                      <label style={{fontSize:11,color:T.fnt,display:"block",marginBottom:3}}>Extend by (days)</label>
                      <input className="inp" type="number" min="1" max="30" value={manageForm.trialDays} onChange={e=>setManageForm(f=>({...f,trialDays:e.target.value}))} style={{fontSize:12}}/>
                    </div>
                    <button className="btn bo sm" onClick={()=>doAction("extend_trial")} disabled={actioning} style={{alignSelf:"flex-end",whiteSpace:"nowrap"}}>Extend</button>
                    <button className="btn bo sm" onClick={()=>doAction("reset_trial")} disabled={actioning} style={{alignSelf:"flex-end",whiteSpace:"nowrap"}}>Reset</button>
                  </div>
                </div>

                {/* Suspend / Reactivate */}
                <div style={{padding:"11px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,marginBottom:8}}>Account Status</p>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn sm" onClick={()=>doAction("suspend")} disabled={actioning||sel.status==="suspended"}
                      style={{flex:1,background:G.rb,color:G.r,border:`1px solid ${G.rbd}`,fontSize:11}}>
                      {actioning?<Spinner size={10}/>:"Suspend"}
                    </button>
                    <button className="btn sm" onClick={()=>doAction("reactivate")} disabled={actioning||sel.status==="active"}
                      style={{flex:1,background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,fontSize:11}}>
                      {actioning?<Spinner size={10}/>:"Reactivate"}
                    </button>
                  </div>
                </div>

                {/* Custom limit overrides */}
                <div style={{padding:"11px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <p style={{fontSize:12,fontWeight:700}}>Discretionary Overrides</p>
                    <span style={{fontSize:10,color:T.fnt}}>Blank = use plan default</span>
                  </div>
                  {usage&&(
                    <><div style={{display:"flex",gap:6,marginBottom:8,padding:"6px 8px",background:BL,border:`1px solid ${BM}`,borderRadius:6}}>
                      <span style={{fontSize:10.5,color:T.lt}}>Plan limits ({sel.plan}):</span>
                      {[["Agents",usage.limits?.agents],["Convos/mo",usage.limits?.conversations_month>=999999?"∞":usage.limits?.conversations_month],["KB Docs",usage.limits?.kb_docs>=999?"∞":usage.limits?.kb_docs]].map(([l,v])=>(
                        <span key={l} style={{fontSize:10.5,color:T.mid}}><strong style={{color:B}}>{v}</strong> {l}</span>
                      ))}
                    </div>
                    {/* Signed-up snapshot */}
                    {usage?.plan_snapshot&&(
                      <div style={{marginBottom:8,padding:"7px 10px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:6}}>
                        <p style={{fontSize:10.5,fontWeight:700,color:G.g,marginBottom:3}}>
                          🔒 Signed-up limits (locked {usage.plan_snapshot.snapped_at?new Date(usage.plan_snapshot.snapped_at).toLocaleDateString("en-AU",{day:"2-digit",month:"short",year:"numeric"}):"on signup"})
                        </p>
                        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                          {[["Agents",usage.plan_snapshot.limits?.agents],["Convos/mo",usage.plan_snapshot.limits?.conversations_month>=999999?"∞":usage.plan_snapshot.limits?.conversations_month],["KB Docs",usage.plan_snapshot.limits?.kb_docs>=999?"∞":usage.plan_snapshot.limits?.kb_docs]].map(([l,v])=>(
                            <span key={l} style={{fontSize:11,color:T.mid}}><strong style={{color:G.g}}>{v}</strong> {l}</span>
                          ))}
                        </div>
                        <p style={{fontSize:10,color:T.fnt,marginTop:3}}>These are the limits this tenant was promised at signup. Custom overrides below take priority.</p>
                      </div>
                    )}
                    {!usage?.plan_snapshot&&(
                      <div style={{marginBottom:8,padding:"6px 10px",background:G.ab,border:`1px solid ${G.abd}`,borderRadius:6}}>
                        <p style={{fontSize:10.5,color:G.a}}>⚠ No signed-up snapshot yet. <button className="btn bo sm" style={{fontSize:10,marginLeft:4}} onClick={async()=>{
                          const r=await fetch(API_URL+"/api/tenants/"+sel.id+"/manage",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"snapshot_plan"})});
                          const d=await r.json();
                          if(d.ok){const uR=await fetch(API_URL+"/api/tenants/"+sel.id+"/usage");if(uR.ok)setUsage(await uR.json());}
                        }}>Snapshot now</button></p>
                      </div>
                    )}
                    </>)}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:8}}>
                    {[
                      ["Agents",   "custom_agents",  usage?.custom_limits?.agents              ?? "", usage?.plan_limits?.agents              ?? usage?.limits?.agents],
                      ["Convos/mo","custom_convos",   usage?.custom_limits?.conversations_month ?? "", usage?.plan_limits?.conversations_month ?? usage?.limits?.conversations_month],
                      ["KB Docs",  "custom_kb",       usage?.custom_limits?.kb_docs             ?? "", usage?.plan_limits?.kb_docs             ?? usage?.limits?.kb_docs],
                    ].map(([label,key,def,planVal])=>(
                      <div key={key}>
                        <label style={{fontSize:10,color:T.fnt,display:"block",marginBottom:2}}>{label}</label>
                        <input className="inp" type="number" min="0"
                          placeholder={planVal!=null&&planVal<999999?String(planVal):"∞"}
                          defaultValue={def||""}
                          id={`override_${key}`}
                          style={{fontSize:12,fontFamily:"monospace"}}/>
                      </div>
                    ))}
                  </div>
                  <button className="btn bo sm" style={{fontSize:11,width:"100%"}} disabled={actioning} onClick={()=>{
                    const a=document.getElementById("override_custom_agents")?.value;
                    const c=document.getElementById("override_custom_convos")?.value;
                    const k=document.getElementById("override_custom_kb")?.value;
                    const body={action:"update_limits",note:manageForm.note};
                    if(a!=="") body.custom_agents=a;
                    if(c!=="") body.custom_convos=c;
                    if(k!=="") body.custom_kb=k;
                    (async()=>{
                      setActioning(true);
                      try{
                        const r=await fetch(API_URL+"/api/tenants/"+sel.id+"/manage",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
                        const d=await r.json();
                        if(d.ok){
                          setActionMsg({type:"ok",text:"Custom limits saved."});
                          const uR=await fetch(API_URL+"/api/tenants/"+sel.id+"/usage");
                          if(uR.ok) setUsage(await uR.json());
                        } else {setActionMsg({type:"err",text:d.error||"Failed"});}
                      }catch(e){setActionMsg({type:"err",text:e.message});}
                      setActioning(false);
                    })();
                  }}>
                    {actioning?<Spinner size={10}/>:"Save Overrides"}
                  </button>
                </div>

                {/* Account notes */}
                <div style={{padding:"11px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,marginBottom:6}}>Account Notes</p>
                  <textarea className="inp" rows={3} placeholder="Internal notes about this account (not visible to tenant)…"
                    defaultValue={usage?.account_notes||""}
                    id="account_notes_field"
                    style={{fontSize:12,resize:"vertical",width:"100%",boxSizing:"border-box"}}/>
                  <button className="btn bo sm" style={{fontSize:11,marginTop:5}} disabled={actioning} onClick={()=>{
                    const notes=document.getElementById("account_notes_field")?.value||"";
                    (async()=>{
                      setActioning(true);
                      try{
                        const r=await fetch(API_URL+"/api/tenants/"+sel.id+"/manage",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"update_notes",notes})});
                        const d=await r.json();
                        setActionMsg({type:d.ok?"ok":"err",text:d.ok?"Notes saved.":d.error||"Failed"});
                        if(d.ok){const uR=await fetch(API_URL+"/api/tenants/"+sel.id+"/usage");if(uR.ok)setUsage(await uR.json());}
                      }catch(e){setActionMsg({type:"err",text:e.message});}
                      setActioning(false);
                    })();
                  }}>Save Notes</button>
                </div>

                {/* Note */}
                <div>
                  <label style={{fontSize:11,color:T.fnt,display:"block",marginBottom:3}}>Admin note (appears in audit log)</label>
                  <input className="inp" placeholder="Reason for this action…" value={manageForm.note} onChange={e=>setManageForm(f=>({...f,note:e.target.value}))} style={{fontSize:12}}/>
                </div>
              </div>
            )}

            {/* Audit log tab */}
            {auditTab==="audit"&&(
              <PAuditLog tenantId={sel.id}/>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// HELPDESK CONFIGURATION — categories, resolution types, canned
// responses, tags, departments, SLA, escalation rules, CSAT.
// Talks to hindle_api /api/helpdesk/* (admin-only writes).
// ══════════════════════════════════════════════════════════════
const CONFIG_SECTIONS=[
  {id:"categories",label:"Categories",icon:"tag",path:"categories",hint:"Group tickets by topic. Agents pick one on each ticket.",
    cols:[["name","Name"],["description","Description"]],
    fields:[{k:"name",l:"Name",req:true},{k:"description",l:"Description",type:"textarea"}],canEdit:true,canDelete:true},
  {id:"resolution-types",label:"Resolution types",icon:"chk",path:"resolution-types",hint:"How a ticket was resolved — chosen when resolving.",
    cols:[["name","Name"]],fields:[{k:"name",l:"Name",req:true}],canEdit:true,canDelete:true},
  {id:"canned-responses",label:"Canned responses",icon:"rep",path:"canned-responses",hint:"Saved replies agents can insert into tickets.",
    cols:[["title","Title"],["category","Category"],["shortcut","Shortcut"],["use_count","Used"]],
    fields:[{k:"title",l:"Title",req:true},{k:"category",l:"Category",ph:"General"},{k:"shortcut",l:"Shortcut",ph:"/refund"},{k:"content",l:"Reply text",type:"textarea",req:true}],canEdit:true,canDelete:true},
  {id:"tags",label:"Tags",icon:"tag",path:"tags",hint:"Free labels for filtering and reporting.",
    cols:[["name","Name"],["color","Colour"]],fields:[{k:"name",l:"Name",req:true},{k:"color",l:"Colour",type:"color",def:"#2563eb"}],canEdit:true,canDelete:true},
  {id:"departments",label:"Departments",icon:"bld",path:"departments",hint:"Teams tickets can be routed to.",
    cols:[["name","Name"],["description","Description"]],fields:[{k:"name",l:"Name",req:true},{k:"description",l:"Description"}],canEdit:false,canDelete:true},
  {id:"sla-policies",label:"SLA policies",icon:"cal",path:"sla-policies",hint:"Target first-response and resolution times per priority.",
    cols:[["name","Name"],["priority","Priority"],["first_response_hours","First response (h)"],["resolution_hours","Resolution (h)"],["is_active","Active"]],
    fields:[{k:"name",l:"Name",req:true},{k:"priority",l:"Priority",type:"select",opts:["urgent","high","medium","low"],req:true,def:"medium"},
      {k:"first_response_hours",l:"First response (hours)",type:"number",def:4},{k:"resolution_hours",l:"Resolution (hours)",type:"number",def:24}],canEdit:true,canDelete:true,toggle:"is_active"},
  {id:"escalation-rules",label:"Escalation rules",icon:"warn",path:"escalation-rules",hint:"When a condition is met, take an action (e.g. no reply in 4h → notify admin).",
    cols:[["condition","When"],["threshold","Threshold"],["action","Then"],["is_active","Active"]],
    fields:[{k:"condition",l:"Condition",type:"select",opts:["no_first_response","no_update","sla_breach","priority_urgent","customer_reply_waiting"],req:true,def:"no_first_response"},
      {k:"threshold",l:"Threshold (hours)",type:"number",def:4},
      {k:"action",l:"Action",type:"select",opts:["notify_admin","notify_assignee","raise_priority","reassign","sms_admin"],req:true,def:"notify_admin"}],canEdit:true,canDelete:true,toggle:"is_active"},
];
const humanise=v=>typeof v==="string"?v.replace(/_/g," "):v;

const ConfigList=({sec})=>{
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");
  const [edit,setEdit]=useState(null); // null | {} (new) | row
  const [form,setForm]=useState({});
  const [saving,setSaving]=useState(false);
  const url=`${API_URL}/api/helpdesk/${sec.path}`;
  const load=useCallback(()=>{
    setLoading(true);setErr("");
    fetchRetry(url).then(async r=>{const d=await r.json().catch(()=>({}));if(!r.ok)throw new Error(d.error||`Error ${r.status}`);setRows(d.data||[]);})
      .catch(e=>setErr(e.message)).finally(()=>setLoading(false));
  },[url]);
  useEffect(()=>{load();},[load]);
  const open=row=>{const f={};sec.fields.forEach(x=>{f[x.k]=row?(row[x.k]??""):(x.def??"");});setForm(f);setEdit(row||{});};
  const save=async()=>{
    for(const f of sec.fields){if(f.req&&!String(form[f.k]??"").trim()){setErr(`${f.l} is required.`);return;}}
    setSaving(true);setErr("");
    const body={...form};sec.fields.forEach(f=>{if(f.type==="number"&&body[f.k]!=="")body[f.k]=Number(body[f.k]);});
    try{
      const isNew=!edit?.id;
      const r=await fetch(isNew?url:`${url}/${edit.id}`,{method:isNew?"POST":"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      const d=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(d.error||"Save failed");
      setEdit(null);load();
    }catch(e){setErr(e.message);}finally{setSaving(false);}
  };
  const del=async(row,force)=>{
    if(!force&&!window.confirm(`Delete "${row.name||row.title||row.condition}"?`))return;
    const r=await fetch(`${url}/${row.id}${force?"?force=1":""}`,{method:"DELETE"});
    const d=await r.json().catch(()=>({}));
    if(r.status===409&&!force){if(window.confirm(d.error||"In use. Delete anyway?"))return del(row,true);return;}
    if(!r.ok){setErr(d.error||"Delete failed");return;}
    load();
  };
  const toggle=async(row)=>{
    const r=await fetch(`${url}/${row.id}`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({[sec.toggle]:!row[sec.toggle]})});
    const d=await r.json().catch(()=>({}));
    if(!r.ok){setErr(d.error||"Update failed");return;}
    load();
  };
  const cell=(row,k)=>{
    const v=row[k];
    if(k===sec.toggle) return(
      <button onClick={()=>toggle(row)} aria-pressed={!!v} title={v?"Active — click to pause":"Paused — click to activate"}
        style={{width:36,height:20,borderRadius:10,border:"none",cursor:"pointer",background:v?G.g:S.brd,position:"relative"}}>
        <span style={{position:"absolute",top:2,left:v?18:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left .15s"}}/>
      </button>);
    if(k==="color") return <span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:14,height:14,borderRadius:4,background:v||"#2563eb",border:`1px solid ${S.brd}`}}/>{v}</span>;
    if(k==="priority"){const c=PRIORITY_COLORS[v]||PRIORITY_COLORS.medium;return <span className="badge" style={{background:c.bg,color:c.fg,textTransform:"capitalize"}}>{v}</span>;}
    if(v===null||v===undefined||v==="") return <span style={{color:T.fnt}}>—</span>;
    return <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"block",maxWidth:320}}>{humanise(String(v))}</span>;
  };
  return(
    <div className="card" style={{overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
        <div><h3 style={{fontSize:14.5,fontWeight:700}}>{sec.label}</h3><p style={{fontSize:12.5,color:T.lt,marginTop:2}}>{sec.hint}</p></div>
        <button className="btn bp sm" onClick={()=>open(null)}><Ic n="plus" s={12} c="#fff"/> Add</button>
      </div>
      {err&&<div style={{margin:"10px 18px 0",padding:"8px 12px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:8,fontSize:12.5,color:G.rt}}>{err}</div>}
      {edit&&(
        <div style={{padding:"14px 18px",background:S.sub,borderBottom:`1px solid ${S.brd}`}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            {sec.fields.map(f=>(
              <div key={f.k} style={{gridColumn:f.type==="textarea"?"1 / -1":undefined}}>
                <label style={{fontSize:11.5,fontWeight:600,color:T.lt,display:"block",marginBottom:4}}>{f.l}{f.req?" *":""}</label>
                {f.type==="textarea"?<textarea className="inp" rows={3} value={form[f.k]} placeholder={f.ph} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))}/>
                :f.type==="select"?<select className="inp" value={form[f.k]} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))}>{f.opts.map(o=><option key={o} value={o}>{humanise(o)}</option>)}</select>
                :f.type==="color"?<div style={{display:"flex",gap:8}}><input type="color" value={form[f.k]||"#2563eb"} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))} style={{width:40,height:36,border:`1px solid ${S.brd}`,borderRadius:8,background:S.w,padding:2}}/><input className="inp" value={form[f.k]} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))}/></div>
                :<input className="inp" type={f.type==="number"?"number":"text"} value={form[f.k]} placeholder={f.ph} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))}/>}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginTop:12}}>
            <button className="btn bp sm" disabled={saving} onClick={save}>{saving?"Saving…":edit?.id?"Save changes":"Add"}</button>
            <button className="btn bo sm" onClick={()=>{setEdit(null);setErr("");}}>Cancel</button>
          </div>
        </div>
      )}
      {loading?<div style={{padding:20,color:T.fnt,fontSize:13}}>Loading…</div>:rows.length===0?(
        <div style={{padding:"28px 18px",textAlign:"center",color:T.fnt,fontSize:13}}>Nothing here yet. Click <b>Add</b> to create the first one.</div>
      ):(
        <div style={{overflowX:"auto"}}>
          <table className="tbl">
            <thead><tr>{sec.cols.map(([k,l])=><th key={k}>{l}</th>)}<th style={{width:120}}></th></tr></thead>
            <tbody>{rows.map(r=>(
              <tr key={r.id}>
                {sec.cols.map(([k])=><td key={k}>{cell(r,k)}</td>)}
                <td style={{textAlign:"right",whiteSpace:"nowrap"}}>
                  {sec.canEdit&&<button className="iconbtn" title="Edit" onClick={()=>open(r)}><Ic n="edit" s={14}/></button>}
                  {sec.canDelete&&<button className="iconbtn" title="Delete" onClick={()=>del(r)} style={{color:G.r}}><Ic n="trash" s={14}/></button>}
                </td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CsatSettings=()=>{
  const [d,setD]=useState(null);const [msg,setMsg]=useState(null);const [saving,setSaving]=useState(false);
  useEffect(()=>{fetchRetry(`${API_URL}/api/helpdesk/csat-settings`).then(r=>r.json()).then(j=>setD(j.data||{enabled:false,delay_hours:2})).catch(()=>setD({enabled:false,delay_hours:2}));},[]);
  const save=async(patch)=>{
    const next={...d,...patch};setD(next);setSaving(true);setMsg(null);
    try{const r=await fetch(`${API_URL}/api/helpdesk/csat-settings`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({enabled:!!next.enabled,delay_hours:Number(next.delay_hours)||2})});
      const j=await r.json().catch(()=>({}));if(!r.ok)throw new Error(j.error||"Save failed");setMsg({ok:true,t:"Saved"});}
    catch(e){setMsg({ok:false,t:e.message});}finally{setSaving(false);}
  };
  if(!d) return <div className="card" style={{padding:20,color:T.fnt}}>Loading…</div>;
  return(
    <div className="card" style={{padding:"16px 18px"}}>
      <h3 style={{fontSize:14.5,fontWeight:700}}>Customer satisfaction (CSAT)</h3>
      <p style={{fontSize:12.5,color:T.lt,marginTop:2,marginBottom:14}}>Email customers a one-click rating after their ticket is resolved.</p>
      <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}>
          <input type="checkbox" checked={!!d.enabled} disabled={saving} onChange={e=>save({enabled:e.target.checked})}/> Send CSAT surveys
        </label>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13}}>
          Send after <input className="inp" type="number" min={0} max={168} value={d.delay_hours} onChange={e=>setD(x=>({...x,delay_hours:e.target.value}))} onBlur={()=>save({})} style={{width:70}}/> hours
        </label>
        {msg&&<span style={{fontSize:12,color:msg.ok?G.g:G.r}}>{msg.t}</span>}
      </div>
    </div>
  );
};

const PortalBranding=()=>{
  const [d,setD]=useState(null);const [f,setF]=useState({logo_url:"",primary_color:"#2563EB",accent_color:"#7C3AED"});
  const [msg,setMsg]=useState(null);const [saving,setSaving]=useState(false);
  useEffect(()=>{fetchRetry(`${API_URL}/api/helpdesk/portal-branding`).then(r=>r.json()).then(j=>{if(j.data){setD(j.data);setF({logo_url:j.data.logo_url||"",primary_color:j.data.primary_color||"#2563EB",accent_color:j.data.accent_color||"#7C3AED"});}else setMsg({ok:false,t:"Helpdesk portal isn't enabled for this account."});}).catch(e=>setMsg({ok:false,t:e.message}));},[]);
  const save=async()=>{setSaving(true);setMsg(null);
    try{const r=await fetch(`${API_URL}/api/helpdesk/portal-branding`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(f)});
      const j=await r.json().catch(()=>({}));if(!r.ok)throw new Error(j.error||"Save failed");setD(x=>({...x,...j.data}));setMsg({ok:true,t:"Saved — the customer portal uses these now."});}
    catch(e){setMsg({ok:false,t:e.message});}finally{setSaving(false);}};
  const Color=({k,l})=>(<div><label style={{fontSize:11.5,fontWeight:600,color:T.lt,display:"block",marginBottom:4}}>{l}</label>
    <div style={{display:"flex",gap:8}}><input type="color" value={f[k]} onChange={e=>setF(x=>({...x,[k]:e.target.value.toUpperCase()}))} style={{width:40,height:36,border:`1px solid ${S.brd}`,borderRadius:8,background:S.w,padding:2}}/>
    <input className="inp" value={f[k]} onChange={e=>setF(x=>({...x,[k]:e.target.value}))}/></div></div>);
  return(
    <div className="card" style={{padding:"16px 18px"}}>
      <h3 style={{fontSize:14.5,fontWeight:700}}>Customer portal branding</h3>
      <p style={{fontSize:12.5,color:T.lt,marginTop:2,marginBottom:14}}>How the customer helpdesk portal looks to your clients.{d?.portal_url&&<> Portal: <a href={d.portal_url} target="_blank" rel="noopener noreferrer">{d.portal_url}</a></>}</p>
      {msg&&<div style={{padding:"8px 12px",borderRadius:8,marginBottom:12,fontSize:12.5,background:msg.ok?G.gb:G.rb,color:msg.ok?G.gt:G.rt,border:`1px solid ${msg.ok?G.gbd:G.rbd}`}}>{msg.t}</div>}
      {d&&(<div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 300px",gap:20}}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div><label style={{fontSize:11.5,fontWeight:600,color:T.lt,display:"block",marginBottom:4}}>Logo address (https)</label>
            <input className="inp" placeholder="https://yourcompany.com/logo.png" value={f.logo_url} onChange={e=>setF(x=>({...x,logo_url:e.target.value}))}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}><Color k="primary_color" l="Primary colour"/><Color k="accent_color" l="Accent colour"/></div>
          <div><button className="btn bp sm" disabled={saving} onClick={save}>{saving?"Saving…":"Save branding"}</button></div>
        </div>
        <div style={{border:`1px solid ${S.brd}`,borderRadius:12,overflow:"hidden",background:"#fff"}}>
          <div style={{padding:"12px 14px",borderBottom:"1px solid #E5E7EB",display:"flex",alignItems:"center",gap:10}}>
            {f.logo_url?<img src={f.logo_url} alt="" style={{height:26,maxWidth:140,objectFit:"contain"}} onError={e=>{e.currentTarget.style.display="none";}}/>:<span style={{fontWeight:700,color:"#111"}}>{d.name}</span>}
          </div>
          <div style={{padding:16}}>
            <p style={{fontSize:13,fontWeight:700,color:"#111",marginBottom:8}}>How can we help?</p>
            <div style={{height:30,border:"1px solid #E5E7EB",borderRadius:7,marginBottom:10}}/>
            <div style={{display:"flex",gap:8}}>
              <span style={{background:f.primary_color,color:"#fff",fontSize:12,fontWeight:600,padding:"7px 12px",borderRadius:7}}>Lodge a ticket</span>
              <span style={{border:`1px solid ${f.accent_color}`,color:f.accent_color,fontSize:12,fontWeight:600,padding:"7px 12px",borderRadius:7}}>My tickets</span>
            </div>
          </div>
          <p style={{fontSize:10.5,color:"#6B7280",padding:"0 16px 10px"}}>Preview</p>
        </div>
      </div>)}
    </div>
  );
};

const InviteCodes=()=>{
  const [rows,setRows]=useState([]);const [signup,setSignup]=useState("");const [warn,setWarn]=useState("");
  const [err,setErr]=useState("");const [form,setForm]=useState({invite_code:"",invite_type:"client",uses_remaining:"",expires_at:"",notes:""});const [adding,setAdding]=useState(false);
  const load=useCallback(()=>{fetchRetry(`${API_URL}/api/helpdesk/invites`).then(async r=>{const j=await r.json().catch(()=>({}));if(!r.ok)throw new Error(j.error||"Couldn't load");setRows(j.data||[]);setSignup(j.signup_url||"");setWarn(j.warning||"");}).catch(e=>setErr(e.message));},[]);
  useEffect(()=>{load();},[load]);
  const add=async()=>{setErr("");
    try{const r=await fetch(`${API_URL}/api/helpdesk/invites`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(form)});
      const j=await r.json().catch(()=>({}));if(!r.ok)throw new Error(j.error||"Couldn't create");setAdding(false);setForm({invite_code:"",invite_type:"client",uses_remaining:"",expires_at:"",notes:""});load();}
    catch(e){setErr(e.message);}};
  const upd=async(id,patch)=>{const r=await fetch(`${API_URL}/api/helpdesk/invites/${id}`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(patch)});if(!r.ok){const j=await r.json().catch(()=>({}));setErr(j.error||"Update failed");}load();};
  const del=async(id)=>{if(!window.confirm("Delete this invite code?"))return;await fetch(`${API_URL}/api/helpdesk/invites/${id}`,{method:"DELETE"});load();};
  const link=code=>`${signup}?code=${encodeURIComponent(code)}`;
  const copy=t=>{try{navigator.clipboard.writeText(t);}catch(e){}};
  return(
    <div className="card" style={{overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div><h3 style={{fontSize:14.5,fontWeight:700}}>Invite codes</h3><p style={{fontSize:12.5,color:T.lt,marginTop:2}}>Let customers join your portal with a code or link.</p></div>
        <button className="btn bp sm" onClick={()=>setAdding(a=>!a)}><Ic n="plus" s={12} c="#fff"/> New code</button>
      </div>
      {(err||warn)&&<div style={{margin:"10px 18px 0",padding:"8px 12px",background:err?G.rb:G.ab,border:`1px solid ${err?G.rbd:G.abd}`,borderRadius:8,fontSize:12.5,color:err?G.rt:G.at}}>{err||warn}</div>}
      {adding&&(<div style={{padding:"14px 18px",background:S.sub,borderBottom:`1px solid ${S.brd}`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10,alignItems:"end"}}>
        {[["invite_code","Code (blank = random)","text"],["uses_remaining","Max uses (blank = unlimited)","number"],["expires_at","Expires","date"],["notes","Notes","text"]].map(([k,l,t])=>(
          <div key={k}><label style={{fontSize:11.5,fontWeight:600,color:T.lt,display:"block",marginBottom:4}}>{l}</label><input className="inp" type={t} value={form[k]} onChange={e=>setForm(x=>({...x,[k]:e.target.value}))}/></div>))}
        <div style={{display:"flex",gap:8}}><button className="btn bp sm" onClick={add}>Create</button><button className="btn bo sm" onClick={()=>setAdding(false)}>Cancel</button></div>
      </div>)}
      {rows.length===0?<div style={{padding:"28px 18px",textAlign:"center",color:T.fnt,fontSize:13}}>No invite codes yet.</div>:(
        <div style={{overflowX:"auto"}}><table className="tbl">
          <thead><tr><th>Code</th><th>Used</th><th>Left</th><th>Expires</th><th>Notes</th><th>Active</th><th></th></tr></thead>
          <tbody>{rows.map(r=>(<tr key={r.id}>
            <td><span className="mono" style={{fontWeight:600}}>{r.invite_code}</span></td>
            <td>{r.times_used||0}</td><td>{r.uses_remaining??"∞"}</td>
            <td>{r.expires_at?new Date(r.expires_at).toLocaleDateString():"—"}</td>
            <td style={{color:T.lt}}>{r.notes||"—"}</td>
            <td><button onClick={()=>upd(r.id,{is_active:!r.is_active})} aria-pressed={!!r.is_active} style={{width:36,height:20,borderRadius:10,border:"none",cursor:"pointer",background:r.is_active?G.g:S.brd,position:"relative"}}><span style={{position:"absolute",top:2,left:r.is_active?18:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left .15s"}}/></button></td>
            <td style={{textAlign:"right",whiteSpace:"nowrap"}}>
              {signup&&<button className="iconbtn" title="Copy signup link" onClick={()=>copy(link(r.invite_code))}><Ic n="link" s={14}/></button>}
              <button className="iconbtn" title="Delete" onClick={()=>del(r.id)} style={{color:G.r}}><Ic n="trash" s={14}/></button>
            </td></tr>))}</tbody>
        </table></div>)}
    </div>
  );
};

const PHelpdeskConfig=()=>{
  const [tab,setTab]=useState("categories");
  const tabs=[...CONFIG_SECTIONS.map(s=>({id:s.id,label:s.label,icon:s.icon,group:"Ticket setup"})),{id:"csat",label:"CSAT",icon:"star",group:"Ticket setup"},
    {id:"branding",label:"Portal branding",icon:"globe",group:"Customer portal"},{id:"invites",label:"Invite codes",icon:"key",group:"Customer portal"}];
  const sec=CONFIG_SECTIONS.find(s=>s.id===tab);
  return(
    <div style={{display:"flex",height:"100%",overflow:"hidden"}}>
      <div style={{width:220,flexShrink:0,borderRight:`1px solid ${S.brd}`,padding:12,overflowY:"auto",background:S.w}}>
        {tabs.map((t,i)=>(
          <React.Fragment key={t.id}>
            {t.group!==tabs[i-1]?.group&&<p style={{fontSize:11,fontWeight:600,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,padding:i?"16px 10px 8px":"4px 10px 8px"}}>{t.group}</p>}
            <div className={`nl${tab===t.id?" act":""}`} onClick={()=>setTab(t.id)} style={{marginBottom:2}}>
              <Ic n={t.icon} s={15} c={tab===t.id?B:T.lt}/><span>{t.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:20}}>
        <div style={{maxWidth:980}}>
          {tab==="csat"?<CsatSettings/>:tab==="branding"?<PortalBranding/>:tab==="invites"?<InviteCodes/>:<ConfigList key={sec.id} sec={sec}/>}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SINGLE SIGN-ON (SAML) — customer portal
// Tenant admins manage their own tenant; super admins pick a tenant.
// Backed by hindle_api /api/sso/*; the helpdesk portal runs the SAML flow.
// ══════════════════════════════════════════════════════════════
const PSso=({tenants})=>{
  const isSuper=!!tenants;
  const [tenantId,setTenantId]=useState("");
  const [d,setD]=useState(null);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState(null);
  const [mode,setMode]=useState("url");
  const [mdUrl,setMdUrl]=useState("");
  const [mdXml,setMdXml]=useState("");
  const [domains,setDomains]=useState("");
  const [showCert,setShowCert]=useState(false);
  const linked=isSuper?(tenants||[]).filter(t=>t.helpdesk_org_id):[];
  const q=isSuper&&tenantId?`?tenant_id=${encodeURIComponent(tenantId)}`:"";

  const apply=(data)=>{setD(data);setMdUrl(data.idp_metadata_url||"");setDomains((data.allowed_domains||[]).join(", "));};
  const load=useCallback(()=>{
    if(isSuper&&!tenantId){setD(null);return;}
    setLoading(true);setMsg(null);
    fetch(`${API_URL}/api/sso/settings${q}`)
      .then(async r=>({ok:r.ok,j:await r.json().catch(()=>({}))}))
      .then(({ok,j})=>{if(!ok){setD(null);setMsg({ok:false,text:j.error||"Could not load single sign-on settings"});return;}apply(j.data);})
      .catch(()=>setMsg({ok:false,text:"Network error"}))
      .finally(()=>setLoading(false));
  },[q,isSuper,tenantId]);
  useEffect(()=>{load();},[load]);

  const save=async(patch,okText,at)=>{
    setSaving(true);setMsg(at?{ok:true,text:"Saving…",at}:null);
    try{
      const r=await fetch(`${API_URL}/api/sso/settings`,{method:"PUT",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({...(isSuper?{tenant_id:tenantId}:{}),...patch})});
      const j=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(j.error||"Save failed");
      apply(j.data);setMsg({ok:true,text:okText,at});
    }catch(e){setMsg({ok:false,text:e.message,at});}
    finally{setSaving(false);}
  };
  const test=async()=>{
    setMsg(null);
    if(!d?.configured){setMsg({ok:false,text:"Import the client's identity provider (step 2) before testing.",at:"test"});return;}
    // Save any domains typed but not yet saved, so the test uses what's on screen.
    const typed=domains.split(/[\s,;]+/).map(x=>x.trim().toLowerCase().replace(/^@/,"")).filter(Boolean).sort().join(",");
    const saved=[...(d.allowed_domains||[])].sort().join(",");
    if(typed!==saved){
      try{
        const r=await fetch(`${API_URL}/api/sso/settings`,{method:"PUT",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({...(isSuper?{tenant_id:tenantId}:{}),allowed_domains:domains})});
        const j=await r.json().catch(()=>({}));
        if(!r.ok)throw new Error(j.error||"Could not save domains");
        apply(j.data);
        if(!(j.data.allowed_domains||[]).length){setMsg({ok:false,text:"Add at least one allowed email domain (step 3) before testing.",at:"test"});return;}
      }catch(e){setMsg({ok:false,text:"Domains not saved: "+e.message,at:"domains"});return;}
    } else if(!(d.allowed_domains||[]).length){setMsg({ok:false,text:"Add at least one allowed email domain (step 3) before testing.",at:"test"});return;}
    // Open the tab synchronously inside the click so pop-up blockers allow it,
    // then point it at the one-time test link once the server returns it.
    const w=window.open("about:blank","_blank");
    try{
      const r=await fetch(`${API_URL}/api/sso/test-link`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify(isSuper?{tenant_id:tenantId}:{})});
      const j=await r.json().catch(()=>({}));
      if(!r.ok||!j.url)throw new Error(j.error||"Could not start test");
      if(w&&!w.closed){try{w.opener=null;}catch(e){}w.location.href=j.url;}
      else window.location.href=j.url;
      setMsg({ok:true,text:"Test opened in a new tab. Sign in there with a client account, then come back and click Refresh.",at:"test"});
    }catch(e){
      if(w&&!w.closed)w.close();
      setMsg({ok:false,text:e.message,at:"test"});
    }
  };
  const copy=t=>{try{navigator.clipboard.writeText(t);setMsg({ok:true,text:"Copied"});}catch(e){}};

  const Row=({label,value})=>(
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
      <span style={{width:130,flexShrink:0,fontSize:12,color:T.lt}}>{label}</span>
      <code style={{flex:1,fontSize:11.5,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:6,padding:"5px 8px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{value}</code>
      <button className="btn bo sm" style={{fontSize:11}} onClick={()=>copy(value)}>Copy</button>
    </div>
  );
  const Switch=({label,hint,checked,disabled,onChange})=>(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",border:`1px solid ${S.brd}`,borderRadius:9,padding:"10px 12px",marginBottom:8,opacity:disabled?.6:1}}>
      <div><div style={{fontSize:13,fontWeight:600,color:T.ink}}>{label}</div><div style={{fontSize:11.5,color:T.lt,marginTop:2}}>{hint}</div></div>
      <button disabled={disabled} onClick={()=>onChange(!checked)} aria-pressed={checked}
        style={{width:42,height:24,borderRadius:12,border:"none",cursor:disabled?"default":"pointer",background:checked?B:S.brd,position:"relative",flexShrink:0}}>
        <span style={{position:"absolute",top:3,left:checked?21:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .15s"}}/>
      </button>
    </div>
  );
  const H=({n,t})=><h4 style={{fontSize:13,fontWeight:700,color:T.ink,margin:"18px 0 8px"}}>{n}. {t}</h4>;
  const Note=({at})=>msg&&msg.at===at?<div style={{padding:"7px 11px",borderRadius:7,marginTop:8,fontSize:12,background:msg.ok?G.gb:G.rb,color:msg.ok?G.gt:G.rt,border:`1px solid ${msg.ok?"#A7F3D0":"#FECACA"}`}}>{msg.text}</div>:null;
  const tested=!!d?.last_test_ok_at;
  const canEnable=!!(d&&d.configured&&(d.allowed_domains||[]).length&&tested);

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div className="card" style={{maxWidth:820,padding:20}}>
        <h3 style={{fontSize:15,fontWeight:700,color:T.ink,marginBottom:4}}>Single Sign-On (SAML) — customer helpdesk portal</h3>
        <p style={{fontSize:12.5,color:T.lt,marginBottom:12}}>Lets the client's customers sign in to the helpdesk portal (to lodge and update tickets) with their company account. Agents and admins keep their normal sign-in. Off until you switch it on.</p>

        {isSuper&&(
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:600,color:T.lt,display:"block",marginBottom:4}}>Tenant</label>
            <select className="inp" value={tenantId} onChange={e=>setTenantId(e.target.value)} style={{maxWidth:340,fontSize:12.5}}>
              <option value="">Choose a tenant…</option>
              {linked.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {linked.length===0&&<p style={{fontSize:11.5,color:T.fnt,marginTop:4}}>No tenants are linked to a helpdesk portal yet.</p>}
          </div>
        )}

        {msg&&!msg.at&&<div style={{padding:"8px 12px",borderRadius:8,marginBottom:10,fontSize:12.5,background:msg.ok?G.gb:G.rb,color:msg.ok?G.gt:G.rt,border:`1px solid ${msg.ok?"#A7F3D0":"#FECACA"}`}}>{msg.text}</div>}
        {loading&&<div style={{padding:16,color:T.fnt,fontSize:12.5}}>Loading…</div>}

        {d&&!loading&&(<>
          <p style={{fontSize:12,color:T.lt}}>Portal: <b style={{color:T.ink}}>{d.portal.name}</b> — <a href={d.portal.login_url} target="_blank" rel="noopener noreferrer" style={{color:B}}>{d.portal.login_url}</a></p>

          <H n={1} t="Give these to the client's IT team"/>
          <Row label="Metadata URL" value={d.sp.metadata_url}/>
          <Row label="Entity ID" value={d.sp.entity_id}/>
          <Row label="Reply (ACS) URL" value={d.sp.acs_url}/>
          <button className="btn bo sm" style={{fontSize:11}} onClick={()=>setShowCert(v=>!v)}>{showCert?"Hide":"Show"} signing certificate</button>
          {showCert&&<textarea readOnly value={d.sp.cert||""} rows={6} className="inp" style={{width:"100%",fontFamily:"monospace",fontSize:11,marginTop:6}}/>}
          <p style={{fontSize:11.5,color:T.fnt,marginTop:6}}>Ask them to send the user's email as the NameID (emailAddress format) or as an <code>email</code> attribute, and to sign the assertion.</p>

          <H n={2} t="Identity provider"/>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <button className={`btn sm ${mode==="url"?"bp":"bo"}`} onClick={()=>setMode("url")}>Metadata URL</button>
            <button className={`btn sm ${mode==="xml"?"bp":"bo"}`} onClick={()=>setMode("xml")}>Paste metadata XML</button>
          </div>
          {mode==="url"
            ?<input className="inp" placeholder="https://idp.client.com/metadata" value={mdUrl} onChange={e=>setMdUrl(e.target.value)} style={{width:"100%",fontSize:12.5}}/>
            :<textarea className="inp" placeholder="<EntityDescriptor …>" rows={6} value={mdXml} onChange={e=>setMdXml(e.target.value)} style={{width:"100%",fontFamily:"monospace",fontSize:11}}/>}
          <button className="btn bp sm" style={{marginTop:8}} disabled={saving||(mode==="url"?!mdUrl:!mdXml)}
            onClick={()=>save(mode==="url"?{idp_metadata_url:mdUrl}:{idp_metadata_xml:mdXml},"Identity provider imported. Add domains, then run a test sign-in.","idp")}>{saving&&msg?.at==="idp"?"Importing…":"Import"}</button>
          <Note at="idp"/>
          {d.idp_entity_id&&(
            <div style={{fontSize:11.5,color:T.lt,marginTop:8,lineHeight:1.6}}>
              <div><b>Entity ID:</b> {d.idp_entity_id}</div>
              <div><b>Sign-in URL:</b> {d.idp_sso_url}</div>
              {d.idp_slo_url&&<div><b>Sign-out URL:</b> {d.idp_slo_url}</div>}
              <div><b>Certificate:</b> {d.idp_cert_set?"loaded":"missing"}</div>
            </div>
          )}

          <H n={3} t="Allowed email domains"/>
          <div style={{display:"flex",gap:8}}>
            <input className="inp" placeholder="client.com, client.com.au" value={domains} onChange={e=>setDomains(e.target.value)} style={{flex:1,fontSize:12.5}}/>
            <button className="btn bo sm" disabled={saving} onClick={()=>save({allowed_domains:domains},"Domains saved.","domains")}>Save domains</button>
          </div>
          <div style={{fontSize:11.5,color:T.fnt,marginTop:6}}>Saved: {(d.allowed_domains||[]).length?(d.allowed_domains||[]).join(", "):<b style={{color:G.r}}>none</b>}</div>
          <Note at="domains"/>

          <H n={4} t="Test, then switch on"/>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <button className="btn bo sm" onClick={test}>Test sign-in</button>
            <button className="btn bo sm" onClick={load}>Refresh</button>
            <span style={{fontSize:12,color:tested?G.g:T.fnt}}>{tested?`Last test passed ${new Date(d.last_test_ok_at).toLocaleString()} (${d.last_test_email})`:"No passing test with the current settings"}</span>
          </div>
          <Note at="test"/><div style={{height:8}}/>
          <Note at="switch"/>
          <Switch label="Single sign-on" hint={canEnable||d.enabled?"Shows “Sign in with your company” on the portal.":"Import the identity provider, add domains and pass a test first."}
            checked={!!d.enabled} disabled={saving||(!d.enabled&&!canEnable)} onChange={v=>save({enabled:v},v?"Single sign-on is on.":"Single sign-on is off.","switch")}/>
          <Switch label="SSO only for customers" hint="Hides the password box for customers. Staff can still use a password."
            checked={!!d.sso_only} disabled={saving||!d.enabled} onChange={v=>save({sso_only:v},"Saved.","switch")}/>
          <Switch label="Create customer accounts on first sign-in" hint="Off: only customers who already have a portal account can use SSO."
            checked={!!d.auto_create} disabled={saving} onChange={v=>save({auto_create:v},"Saved.","switch")}/>
        </>)}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SIDEBAR
// ══════════════════════════════════════════════════════════════

// Light / System / Dark segmented switch
const PAGE_TITLES={dash:"Home",convos:"Live Chats",helpdesk:"Tickets","helpdesk-dash":"Ticket Reports","access-requests":"Access Requests",
  analytics:"Chat Analytics",faq:"Knowledge & FAQs",chatbot:"AI Assistant",agents:"Agents",users:"Helpdesk Users",install:"Chat Widget",
  integrations:"Integrations",crm:"CRM",sso:"Single Sign-On","helpdesk-config":"Ticket Setup",settings:"Settings",alertlog:"SMS Alert Log",trials:"Trial Monitor",
  pricing:"Pricing & Promos",tenants:"Tenants",audit:"Audit Log"};
const ThemeSwitch=()=>{
  const [pref,setPref]=useState(getThemePref());
  useEffect(()=>{const fn=()=>setPref(getThemePref());themeListeners.add(fn);return()=>{themeListeners.delete(fn);};},[]);
  return(
    <div className="theme-seg" role="radiogroup" aria-label="Theme">
      {[["light","sun","Light"],["system","monitor","Match system"],["dark","moon","Dark"]].map(([v,ic,l])=>(
        <button key={v} role="radio" aria-checked={pref===v} title={l} className={pref===v?"on":""} onClick={()=>setThemePref(v)}><Ic n={ic} s={14}/></button>
      ))}
    </div>
  );
};

const Sidebar=({role,active,onNav,collapsed,onToggle,goSignIn,convCount=0,config})=>{
  const [menuOpen,setMenuOpen]=useState(false);
  const [showChangePw,setShowChangePw]=useState(false);
  const [pwForm,setPwForm]=useState({cur:"",next:"",confirm:""});
  const [pwErr,setPwErr]=useState("");
  const [pwOk,setPwOk]=useState(false);
  const menuRef=React.useRef(null);
  useEffect(()=>{
    if(!menuOpen)return;
    const h=e=>{if(menuRef.current&&!menuRef.current.contains(e.target))setMenuOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[menuOpen]);
  const userName=(()=>{try{const p=localStorage.getItem("hindle_admin_profile");const cfg=localStorage.getItem("hindle_config");const cfgP=cfg?JSON.parse(cfg):null;return p?JSON.parse(p).name||(cfgP?.name||"User"):(cfgP?.name||"User");}catch(e){return "User";}})();
  const userEmail=(()=>{try{const p=localStorage.getItem("hindle_admin_profile");return p?JSON.parse(p).email||"":(config?.email||"");}catch(e){return config?.email||"";}})();
  const agentId=(()=>{try{const p=localStorage.getItem("hindle_admin_profile");return p?JSON.parse(p).id||null:null;}catch(e){return null;}})();

  // One streamlined menu: chat + helpdesk side by side. Page ids unchanged.
  const tenantNav=[
    {id:"dash",label:"Home",icon:"home",section:"Workspace"},
    {id:"convos",label:"Live Chats",icon:"chat",badge:convCount||null,section:"Workspace"},
    {id:"helpdesk",label:"Tickets",icon:"ticket",section:"Workspace"},
    {id:"access-requests",label:"Access Requests",icon:"lock",section:"Workspace"},
    {id:"helpdesk-dash",label:"Ticket Reports",icon:"bar",section:"Insights"},
    {id:"analytics",label:"Chat Analytics",icon:"trend",section:"Insights"},
    {id:"faq",label:"Knowledge & FAQs",icon:"book",section:"Knowledge"},
    {id:"chatbot",label:"AI Assistant",icon:"bot",section:"Knowledge"},
    {id:"agents",label:"Agents",icon:"hdst",section:"Team"},
    {id:"users",label:"Helpdesk Users",icon:"users",section:"Team"},
    {id:"helpdesk-config",label:"Ticket Setup",icon:"wiz",section:"Setup"},
    {id:"install",label:"Chat Widget",icon:"copy",section:"Setup"},
    {id:"integrations",label:"Integrations",icon:"intg",section:"Setup"},
    {id:"crm",label:"CRM",icon:"link",section:"Setup"},
    {id:"sso",label:"Single Sign-On",icon:"shld",section:"Setup"},
    {id:"settings",label:"Settings",icon:"cog",section:"Setup"},
  ];
  const superNav=[
    {id:"dash",label:"Platform Overview",icon:"home",section:"Platform"},
    {id:"tenants",label:"Tenants",icon:"bld",section:"Platform"},
    {id:"trials",label:"Trial Monitor",icon:"cal",section:"Platform"},
    {id:"users",label:"Users",icon:"users",section:"People"},
    {id:"audit",label:"Audit Log",icon:"book",section:"People"},
    {id:"pricing",label:"Pricing & Promos",icon:"tag",section:"Configuration"},
    {id:"integrations",label:"SMS & Email",icon:"intg",section:"Configuration"},
    {id:"sso",label:"Portal SSO",icon:"shld",section:"Configuration"},
    {id:"settings",label:"Platform Settings",icon:"cog",section:"Configuration"},
  ];
  const agentNav=[
    {id:"dash",label:"Home",icon:"home",section:"Workspace"},
    {id:"convos",label:"My Chats",icon:"chat",badge:convCount||null,section:"Workspace"},
    {id:"helpdesk",label:"Tickets",icon:"ticket",section:"Workspace"},
    {id:"analytics",label:"My Performance",icon:"trend",section:"Workspace"},
  ];
  const nav=role==="super_admin"?superNav:role==="agent"?agentNav:tenantNav;
  const orgName=(()=>{try{const o=JSON.parse(localStorage.getItem("hindle_tenant_org")||"{}");return o.name||"";}catch(e){return "";}})();
  const roleLabel=role==="super_admin"?"Super Admin":role==="agent"?"Agent":"Admin";

  return(
    <div style={{width:collapsed?60:236,minWidth:collapsed?60:236,height:"100%",background:S.side,borderRight:`1px solid ${S.brd}`,display:"flex",flexDirection:"column",transition:"width .22s cubic-bezier(.2,.8,.2,1),min-width .22s cubic-bezier(.2,.8,.2,1)",overflow:"hidden",flexShrink:0}}>
      {/* Brand + collapse */}
      <div style={{height:56,padding:collapsed?"0":"0 12px 0 14px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",gap:10,justifyContent:collapsed?"center":"space-between",flexShrink:0}}>
        {collapsed
          ?<button onClick={onToggle} title="Expand menu" style={{width:34,height:34,borderRadius:10,background:B,border:"none",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}><Ic n="hdst" s={17} c="#fff"/></button>
          :<>
            <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
              <div style={{width:32,height:32,borderRadius:10,background:`linear-gradient(135deg,${B},${G.p})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 4px 12px ${B}40`}}><Ic n="hdst" s={17} c="#fff"/></div>
              <div style={{minWidth:0}}>
                <p style={{fontSize:15,fontWeight:700,color:T.ink,letterSpacing:-.2,lineHeight:1.1}}>Helpdesk</p>
                <p style={{fontSize:11,color:T.fnt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{role==="super_admin"?"Platform admin":(orgName||"Hindle Consultants")}</p>
              </div>
            </div>
            <button onClick={onToggle} title="Collapse menu" className="iconbtn"><Ic n="chL" s={15}/></button>
          </>}
      </div>

      {/* Nav */}
      <nav style={{flex:1,overflowY:"auto",padding:collapsed?"10px 8px":"10px 10px 10px 10px"}}>
        {nav.map((item,idx)=>{
          const isActive=active===item.id;
          const showHeader=item.section&&item.section!==nav[idx-1]?.section;
          return(
            <React.Fragment key={item.id}>
              {showHeader&&(collapsed
                ?(idx>0&&<div style={{height:1,background:S.brd,margin:"8px 6px"}}/>)
                :<div style={{padding:idx===0?"4px 10px 6px":"16px 10px 6px",fontSize:11,fontWeight:600,color:T.fnt,textTransform:"uppercase",letterSpacing:.7}}>{item.section}</div>)}
              <div title={collapsed?item.label:undefined} onClick={()=>onNav(item.id)}
                className={`nl${isActive?" act":""}`}
                style={{justifyContent:collapsed?"center":"flex-start",padding:collapsed?"9px 0":undefined,marginBottom:2}}>
                <span style={{display:"flex",flexShrink:0,position:"relative"}}>
                  <Ic n={item.icon} s={17} c={isActive?B:T.lt}/>
                  {collapsed&&item.badge>0&&<span style={{position:"absolute",top:-4,right:-6,minWidth:14,height:14,borderRadius:7,background:G.r,color:"#fff",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px"}}>{item.badge}</span>}
                </span>
                {!collapsed&&<span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.label}</span>}
                {!collapsed&&item.badge!=null&&item.badge>0&&<span style={{background:isActive?B:`${B}22`,color:isActive?"#fff":B,borderRadius:999,padding:"1px 7px",fontSize:11,fontWeight:700,flexShrink:0}}>{item.badge}</span>}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Theme */}
      <div style={{padding:collapsed?"8px 0":"8px 12px",borderTop:`1px solid ${S.brd}`,display:"flex",alignItems:"center",justifyContent:collapsed?"center":"space-between",gap:8}}>
        {!collapsed&&<span style={{fontSize:12,color:T.lt,fontWeight:500}}>Theme</span>}
        {collapsed
          ?<button className="iconbtn" title={THEME_MODE==="dark"?"Switch to light":"Switch to dark"} onClick={()=>setThemePref(THEME_MODE==="dark"?"light":"dark")}><Ic n={THEME_MODE==="dark"?"sun":"moon"} s={15}/></button>
          :<ThemeSwitch/>}
      </div>

      {/* User section */}
      <div style={{borderTop:`1px solid ${S.brd}`,padding:collapsed?"8px 0":"8px",flexShrink:0,position:"relative"}} ref={menuRef}>
        {/* Change password modal */}
        {showChangePw&&!collapsed&&(
          <div style={{position:"absolute",bottom:"100%",left:8,right:8,background:S.w,border:`1px solid ${S.brd}`,borderRadius:10,padding:14,boxShadow:"0 4px 24px rgba(0,0,0,.12)",zIndex:200}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <p style={{fontSize:12.5,fontWeight:700}}>Change Password</p>
              <button onClick={()=>{setShowChangePw(false);setPwErr("");setPwOk(false);setPwForm({cur:"",next:"",confirm:""}); }} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt}}><Ic n="x" s={14}/></button>
            </div>
            {[["Current","cur"],["New","next"],["Confirm","confirm"]].map(([l,k])=>(
              <div key={k} style={{marginBottom:8}}>
                <label style={{fontSize:11,color:T.fnt,display:"block",marginBottom:3}}>{l} Password</label>
                <input className="inp" type="password" value={pwForm[k]} onChange={e=>setPwForm(f=>({...f,[k]:e.target.value}))} style={{fontSize:12}}/>
              </div>
            ))}
            {pwErr&&<p style={{fontSize:11.5,color:G.r,marginBottom:6}}>{pwErr}</p>}
            {pwOk&&<p style={{fontSize:11.5,color:G.g,marginBottom:6}}>✓ Password updated!</p>}
            <button className="btn bp sm" style={{width:"100%"}} onClick={async()=>{
              setPwErr("");setPwOk(false);
              if(!pwForm.cur||!pwForm.next||!pwForm.confirm){setPwErr("All fields required.");return;}
              if(pwForm.next!==pwForm.confirm){setPwErr("Passwords don't match.");return;}
              if(pwForm.next.length<6){setPwErr("Min 6 characters.");return;}
              try{
                const tId=agentId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                if(tId){await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tenantId:tId,admin_password:pwForm.next})});}
                setPwOk(true);setPwForm({cur:"",next:"",confirm:""});
                setTimeout(()=>{setShowChangePw(false);setPwOk(false);},1500);
              }catch(e){setPwErr("Failed to save.");}
            }}>Update Password</button>
          </div>
        )}

        {/* User menu popover */}
        {menuOpen&&!collapsed&&(
          <div style={{position:"absolute",bottom:"100%",left:8,right:8,background:S.w,border:`1px solid ${S.brd}`,borderRadius:10,padding:6,boxShadow:"0 4px 24px rgba(0,0,0,.12)",zIndex:200}}>
            <div style={{padding:"8px 10px",borderBottom:`1px solid ${S.brd}`,marginBottom:4}}>
              <p style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userName}</p>
              <p style={{fontSize:11,color:T.fnt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userEmail}</p>
            </div>
            {[
              {label:"Change Password",icon:"lock",action:()=>{setMenuOpen(false);setShowChangePw(true);}},
              {label:"Sign Out",icon:"logout",action:goSignIn,danger:true},
            ].map(item=>(
              <div key={item.label} onClick={item.action} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 10px",borderRadius:7,cursor:"pointer",color:item.danger?G.r:T.ink}} onMouseEnter={e=>e.currentTarget.style.background=item.danger?G.rb:S.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <Ic n={item.icon} s={13} c={item.danger?G.r:T.mid}/>
                <span style={{fontSize:12.5,fontWeight:500}}>{item.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* User row */}
        <div style={{display:"flex",alignItems:"center",gap:collapsed?0:9,padding:collapsed?"5px 0":"5px 6px",borderRadius:7,cursor:"pointer",justifyContent:collapsed?"center":"flex-start"}}
          onMouseEnter={e=>e.currentTarget.style.background=S.bg}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}
          onClick={()=>collapsed?goSignIn():setMenuOpen(m=>!m)}>
          <Av name={userName} size={28} col={role==="super_admin"?G.r:B}/>
          {!collapsed&&<div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:12.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userName}</p>
            <p style={{fontSize:11,color:T.fnt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{roleLabel} · <span className="mono" style={{opacity:.7}}>{BUILD}</span></p>
          </div>}
          {!collapsed&&<Ic n={menuOpen?"chU":"chD"} s={12} c={T.fnt}/>}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SETTINGS — TENANT ADMIN
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// FAQ MANAGER
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// CRM INTEGRATIONS PAGE
// ══════════════════════════════════════════════════════════════
const CRM_LIST=[
  {id:"salesforce",  name:"Salesforce",        cat:"CRM",           logo:"☁️",  col:"#00A1E0",
   desc:"World's leading CRM. Push conversations, leads and contacts from Hindle directly into Salesforce.",
   docs:"https://developer.salesforce.com/docs/atlas.en-us.api_rest.meta/api_rest/",
   webhook:"POST /services/data/v59.0/sobjects/Contact",
   auth:"OAuth 2.0 — Connected App",
   fields:["FirstName","LastName","Email","Phone","Company","LeadSource"],
   guide:"1. Create a Connected App in Salesforce Setup. 2. Get Consumer Key + Secret. 3. Use OAuth 2.0 client_credentials flow. 4. POST to /sobjects/Contact with visitor data."},
  {id:"hubspot",     name:"HubSpot",           cat:"CRM",           logo:"🟠",  col:"#FF7A59",
   desc:"Create contacts and deals in HubSpot when a visitor starts a conversation or submits a handoff.",
   docs:"https://developers.hubspot.com/docs/api/crm/contacts",
   webhook:"POST /crm/v3/objects/contacts",
   auth:"Private App Token (Bearer)",
   fields:["email","firstname","lastname","phone","company","hs_lead_status"],
   guide:"1. In HubSpot → Settings → Integrations → Private Apps. 2. Create app, copy token. 3. POST to /crm/v3/objects/contacts with properties object."},
  {id:"pipedrive",   name:"Pipedrive",         cat:"CRM",           logo:"🟢",  col:"#1A1F36",
   desc:"Add persons and deals to Pipedrive pipelines. Map conversation data to pipeline stages.",
   docs:"https://developers.pipedrive.com/docs/api/v1",
   webhook:"POST /v1/persons",
   auth:"API Key (query param ?api_token=)",
   fields:["name","email","phone","org_name"],
   guide:"1. Pipedrive Settings → Personal → API. 2. Copy API token. 3. POST to /v1/persons?api_token=TOKEN with person data."},
  {id:"zoho",        name:"Zoho CRM",          cat:"CRM",           logo:"🔵",  col:"#E42527",
   desc:"Create Leads or Contacts in Zoho CRM from visitor conversations.",
   docs:"https://www.zoho.com/crm/developer/docs/api/v6/",
   webhook:"POST /crm/v6/Leads",
   auth:"OAuth 2.0 — Self Client",
   fields:["First_Name","Last_Name","Email","Phone","Company","Lead_Source"],
   guide:"1. Zoho Developer Console → Self Client. 2. Generate access token. 3. POST to /crm/v6/Leads with data array."},
  {id:"freshsales",  name:"Freshsales",        cat:"CRM",           logo:"🌿",  col:"#25C16F",
   desc:"Add contacts and notes to Freshsales from Hindle conversations.",
   docs:"https://developer.freshworks.com/crm/api/",
   webhook:"POST /api/contacts",
   auth:"API Key (header token TOKEN:X)",
   fields:["first_name","last_name","email","mobile_number","job_title"],
   guide:"1. Freshsales Settings → API Settings → Copy API Key. 2. POST to /api/contacts with Authorization: Token token=YOUR_KEY."},
  {id:"monday",      name:"Monday.com",        cat:"Project",       logo:"🟡",  col:"#FF3D57",
   desc:"Create items in Monday boards when handoffs occur or conversations resolve.",
   docs:"https://developer.monday.com/api-reference/docs",
   webhook:"POST /v2 (GraphQL)",
   auth:"Personal API Token (Bearer)",
   fields:["board_id","group_id","item_name","column_values"],
   guide:"1. Monday → Profile → Developers → My Access Tokens. 2. Copy token. 3. Use GraphQL mutation create_item with your board ID."},
  {id:"zendesk",     name:"Zendesk",           cat:"Support",       logo:"💚",  col:"#03363D",
   desc:"Create Zendesk tickets automatically from Hindle handoffs and resolved conversations.",
   docs:"https://developer.zendesk.com/api-reference/ticketing/tickets/tickets/",
   webhook:"POST /api/v2/tickets.json",
   auth:"Basic Auth (email/token) or OAuth",
   fields:["subject","comment","requester_email","requester_name","priority","tags"],
   guide:"1. Zendesk Admin → Apps & Integrations → Zendesk API → Enable API token. 2. Basic auth: base64(email/token:TOKEN). 3. POST to /api/v2/tickets.json."},
  {id:"intercom",    name:"Intercom",          cat:"Support",       logo:"💙",  col:"#1F8DED",
   desc:"Create or update Intercom contacts and conversations from Hindle visitor data.",
   docs:"https://developers.intercom.com/docs/references/rest-api/api.intercom.io/",
   webhook:"POST /contacts",
   auth:"Bearer Token (Access Token)",
   fields:["email","name","phone","company"],
   guide:"1. Intercom → Settings → Integrations → Developer Hub. 2. Create app, get Access Token. 3. POST to /contacts with Intercom-Version header."},
  {id:"activecampaign",name:"ActiveCampaign",  cat:"Marketing",     logo:"📧",  col:"#356AE6",
   desc:"Add contacts to ActiveCampaign lists and trigger automations from Hindle conversations.",
   docs:"https://developers.activecampaign.com/reference/create-a-contact",
   webhook:"POST /:accountname.api-us1.com/api/3/contacts",
   auth:"API Key (header Api-Token)",
   fields:["email","firstName","lastName","phone","fieldValues"],
   guide:"1. ActiveCampaign → Settings → Developer → API URL + Key. 2. POST to your account URL /api/3/contacts with Api-Token header."},
  {id:"mailchimp",   name:"Mailchimp",         cat:"Marketing",     logo:"🐵",  col:"#FFE01B",
   desc:"Subscribe visitors to Mailchimp audiences when they provide an email.",
   docs:"https://mailchimp.com/developer/marketing/api/list-members/",
   webhook:"POST /3.0/lists/{list_id}/members",
   auth:"Basic Auth (anystring:APIKEY)",
   fields:["email_address","status","merge_fields.FNAME","merge_fields.LNAME","merge_fields.PHONE"],
   guide:"1. Mailchimp → Account → Extras → API Keys. 2. Get your List ID from Audience → Settings. 3. Basic auth: base64(anystring:API_KEY-dc). Server prefix (dc) is in your API key."},
  {id:"dynamics",    name:"Microsoft Dynamics 365",cat:"CRM",       logo:"🔷",  col:"#0078D4",
   desc:"Create Leads or Contacts in Dynamics 365 from visitor conversations.",
   docs:"https://learn.microsoft.com/en-us/power-apps/developer/data-platform/webapi/create-entity-web-api",
   webhook:"POST /api/data/v9.2/leads",
   auth:"OAuth 2.0 — Azure App Registration",
   fields:["firstname","lastname","emailaddress1","telephone1","companyname","leadsourcecode"],
   guide:"1. Azure Portal → App Registrations → New registration. 2. Add Dynamics 365 API permissions. 3. Client credentials flow to get Bearer token. 4. POST to /api/data/v9.2/leads."},
  {id:"webhook",     name:"Custom Webhook",    cat:"Custom",        logo:"⚡",  col:"#6366F1",
   desc:"Send conversation data to any endpoint — your own CRM, internal system, or automation platform.",
   docs:"",
   webhook:"POST your-endpoint.com/webhook",
   auth:"Custom (Bearer, API key, HMAC)",
   fields:["visitor_name","visitor_email","visitor_phone","visitor_company","visitor_location","status","message","page","timestamp"],
   guide:"Configure any endpoint to receive a POST with conversation data as JSON. Secure with a shared secret in an Authorization header."},
];

const PCrmIntegrations=()=>{
  const [search,setSearch]=useState("");
  const [catFilter,setCatFilter]=useState("All");
  const [expanded,setExpanded]=useState(null);
  const [copied,setCopied]=useState(null);

  const cats=["All",...[...new Set(CRM_LIST.map(c=>c.cat))]];
  const filtered=CRM_LIST.filter(c=>{
    if(catFilter!=="All"&&c.cat!==catFilter)return false;
    if(search){const s=search.toLowerCase();return c.name.toLowerCase().includes(s)||c.desc.toLowerCase().includes(s)||c.cat.toLowerCase().includes(s);}
    return true;
  });

  const copy=(text,id)=>{navigator.clipboard?.writeText(text).catch(()=>{});setCopied(id);setTimeout(()=>setCopied(null),2000);};

  return(
    <div style={{padding:24,overflowY:"auto",height:"100%"}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:17,fontWeight:800,marginBottom:4}}>CRM & Integration Directory</h2>
        <p style={{fontSize:13,color:T.fnt}}>API reference for connecting Hindle to your CRM, helpdesk, or marketing platform. Use Hindle's webhook or build a direct integration using your platform's API.</p>
      </div>

      {/* Search + filter */}
      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:18,flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,minWidth:200}}>
          <input className="inp" placeholder="Search integrations…" value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:30,fontSize:12.5}}/>
          <div style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)"}}><Ic n="search" s={12} c={T.fnt}/></div>
        </div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setCatFilter(c)} style={{padding:"4px 12px",borderRadius:20,fontSize:11.5,fontWeight:600,cursor:"pointer",
              background:catFilter===c?B:"transparent",color:catFilter===c?"#fff":T.fnt,
              border:catFilter===c?`1px solid ${B}`:`1px solid ${S.brd}`}}>{c}</button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(crm=>{
          const isOpen=expanded===crm.id;
          return(
            <div key={crm.id} className="card" style={{overflow:"hidden",border:isOpen?`1.5px solid ${crm.col}`:`1px solid ${S.brd}`,transition:"border .15s"}}>
              {/* Header row */}
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:"pointer"}}
                onClick={()=>setExpanded(isOpen?null:crm.id)}>
                <div style={{width:36,height:36,borderRadius:9,background:crm.col+"18",border:`1px solid ${crm.col}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{crm.logo}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                    <p style={{fontSize:13.5,fontWeight:700,color:T.ink}}>{crm.name}</p>
                    <span style={{fontSize:10,fontWeight:700,color:crm.col,background:crm.col+"15",border:`1px solid ${crm.col}30`,borderRadius:10,padding:"1px 7px"}}>{crm.cat}</span>
                  </div>
                  <p style={{fontSize:12,color:T.fnt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{crm.desc}</p>
                </div>
                <Ic n={isOpen?"chD":"chR"} s={14} c={T.fnt}/>
              </div>

              {/* Expanded detail */}
              {isOpen&&(
                <div style={{borderTop:`1px solid ${S.brd}`,padding:16,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                  {/* Left: API details */}
                  <div>
                    <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.6,marginBottom:8}}>API Details</p>
                    <div style={{marginBottom:8}}>
                      <p style={{fontSize:11,color:T.fnt,marginBottom:2}}>Endpoint</p>
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <code style={{flex:1,fontSize:11.5,background:"#0f172a",color:"#e2e8f0",borderRadius:6,padding:"5px 10px",fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{crm.webhook}</code>
                        <button onClick={()=>copy(crm.webhook,crm.id+"ep")} style={{flexShrink:0,background:copied===crm.id+"ep"?G.g:S.bg,border:`1px solid ${S.brd}`,borderRadius:5,padding:"4px 8px",cursor:"pointer",fontSize:11,color:copied===crm.id+"ep"?"#fff":T.fnt}}>
                          {copied===crm.id+"ep"?"✓":"Copy"}
                        </button>
                      </div>
                    </div>
                    <div style={{marginBottom:8}}>
                      <p style={{fontSize:11,color:T.fnt,marginBottom:2}}>Authentication</p>
                      <p style={{fontSize:12.5,color:T.mid,fontWeight:600}}>{crm.auth}</p>
                    </div>
                    <div>
                      <p style={{fontSize:11,color:T.fnt,marginBottom:4}}>Key Fields</p>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {crm.fields.map(f=>(
                          <code key={f} style={{fontSize:10.5,background:BL,color:B,border:`1px solid ${BM}`,borderRadius:4,padding:"2px 6px",fontFamily:"monospace"}}>{f}</code>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: Quick start guide */}
                  <div>
                    <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.6,marginBottom:8}}>Quick Start</p>
                    <p style={{fontSize:12.5,color:T.mid,lineHeight:1.7,marginBottom:10}}>{crm.guide}</p>
                    {crm.docs&&(
                      <a href={crm.docs} target="_blank" rel="noopener noreferrer"
                        style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,color:B,textDecoration:"none",fontWeight:600,padding:"5px 10px",border:`1px solid ${BM}`,borderRadius:6,background:BL}}>
                        <Ic n="globe" s={12} c={B}/> Official API Docs
                      </a>
                    )}
                  </div>

                  {/* Sample payload */}
                  <div style={{gridColumn:"1/-1"}}>
                    <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.6,marginBottom:6}}>Sample Hindle Visitor Payload</p>
                    <div style={{position:"relative"}}>
                      <pre style={{background:"#0f172a",color:"#e2e8f0",borderRadius:8,padding:"12px 14px",fontSize:11.5,fontFamily:"monospace",overflowX:"auto",margin:0,lineHeight:1.6}}>{`{
  "visitor_name":    "Jane Smith",
  "visitor_email":   "jane@company.com",
  "visitor_phone":   "+61400000000",
  "visitor_company": "Acme Corp",
  "visitor_location":"Sydney, NSW, Australia",
  "status":          "handoff",
  "page":            "/contact",
  "timestamp":       "${new Date().toISOString()}"
}`}</pre>
                      <button onClick={()=>copy(`{\n  "visitor_name":"Jane Smith",\n  "visitor_email":"jane@company.com"\n}`,crm.id+"pl")}
                        style={{position:"absolute",top:8,right:8,background:copied===crm.id+"pl"?"rgba(22,163,74,.9)":"rgba(255,255,255,.12)",border:"none",borderRadius:5,padding:"4px 9px",cursor:"pointer",color:"#fff",fontSize:11,fontWeight:600}}>
                        {copied===crm.id+"pl"?"✓ Copied":"Copy"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length===0&&(
        <div style={{textAlign:"center",padding:"40px 20px",color:T.fnt}}>
          <p style={{fontSize:14,fontWeight:600,marginBottom:4}}>No integrations found</p>
          <p style={{fontSize:12}}>Try a different search term or category filter.</p>
        </div>
      )}
    </div>
  );
};

const PInstall=()=>{
  const [platform,setPlatform]=useState("html");
  const [copied,setCopied]=useState(null);
  const [detected,setDetected]=useState(false);
  const [checking,setChecking]=useState(false);
  const [advOpen,setAdvOpen]=useState(false);
  // Read directly from localStorage on every render — no useState so changes in Branding flow through instantly
  const _hCfg=(()=>{try{const s=localStorage.getItem("hindle_chatbot_config");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const [widgetColor,setWidgetColor]=useState(_hCfg.widgetColor||_hCfg.brand?.primary||"#2563EB");
  const [widgetPos,setWidgetPos]=useState(_hCfg.widgetPos||_hCfg.brand?.bubble||"bottom-right");
  const [widgetLabel,setWidgetLabel]=useState(_hCfg.widgetLabel||_hCfg.widget_name||"Chat with us");
  const [hideMobile,setHideMobile]=useState(false);
  // Re-read config every time this panel becomes visible (page navigation within SPA)
  const _installCfg=(()=>{try{const s=localStorage.getItem("hindle_chatbot_config");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const _derivedColor=_installCfg.widgetColor||_installCfg.brand?.primary||widgetColor;
  const _derivedPos=_installCfg.widgetPos||_installCfg.brand?.bubble||widgetPos;
  const _derivedLabel=_installCfg.widgetLabel||_installCfg.widget_name||widgetLabel;

  const copyText=(text,key)=>{navigator.clipboard?.writeText(text).catch(()=>{});setCopied(key);setTimeout(()=>setCopied(null),2000);};
  const _hConfigEarly=(()=>{try{const s=localStorage.getItem("hindle_config");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const _orgIdEarly=_hConfigEarly.orgId||_hConfigEarly.tenantId||null;
  const [lastSeen,setLastSeen]=useState(null);
  const checkDetection=()=>{
    if(!_orgIdEarly){setChecking(false);return;}
    setChecking(true);
    fetch(API_URL+"/api/widget-ping/"+_orgIdEarly)
      .then(r=>r.json())
      .then(d=>{
        setChecking(false);
        setDetected(!!d.detected);
        setLastSeen(d.last_seen||null);
        try{localStorage.setItem("hindle_widget_detected",d.detected?"true":"false");}catch(e){}
      })
      .catch(()=>{setChecking(false);});
  };
  // Restore last-known detection state on load, then immediately re-verify for real
  useEffect(()=>{
    try{if(localStorage.getItem("hindle_widget_detected")==="true")setDetected(true);}catch(e){}
    checkDetection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  // Re-check every 30s if not yet detected — genuine poll, not simulated
  useEffect(()=>{
    if(detected) return;
    const t=setInterval(checkDetection,30000);
    return()=>clearInterval(t);
  },[detected]);

  const _orgData=(()=>{try{const s=localStorage.getItem("hindle_tenant_org");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const _hConfig=(()=>{try{const s=localStorage.getItem("hindle_config");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const tenantId=_hConfig.tenantId||_orgData.slug||_orgData.name?.toLowerCase().replace(/[^a-z0-9]/g,"-")||"your-tenant-id";
  const baseAttrs=`src="https://chatbot.hindleconsultants.com/widget.js"\n  data-tenant="${tenantId}"\n  data-color="${_derivedColor}"\n  data-position="${_derivedPos}"\n  data-label="${_derivedLabel}"${hideMobile?'\n  data-hide-mobile="true"':''}`;

  const PLATFORMS=[
    {id:"html",     label:"HTML",         icon:"🌐"},
    {id:"wordpress",label:"WordPress",    icon:"🔵"},
    {id:"shopify",  label:"Shopify",      icon:"🟢"},
    {id:"react",    label:"React / Next", icon:"⚛️"},
    {id:"webflow",  label:"Webflow",      icon:"⬜"},
    {id:"wix",      label:"Wix",          icon:"🟣"},
    {id:"gtm",      label:"Google Tag Manager",icon:"🏷️"},
  ];

  const snippets={
    html:{
      title:"Any HTML website",
      steps:["Open your website's main HTML file (usually index.html or a shared template/layout file)","Paste the snippet just before the closing </body> tag","Save and deploy — the widget appears immediately"],
      code:`<!-- Hindle Chat Widget -->
<script
  ${baseAttrs}
  defer>
</script>`,
    },
    wordpress:{
      title:"WordPress (functions.php or plugin)",
      steps:["In your WordPress admin go to Appearance → Theme File Editor","Open functions.php for your active theme","Paste the PHP snippet at the bottom of the file and save","Alternatively install the free 'Insert Headers and Footers' plugin and paste the <script> tag there"],
      code:`// functions.php — add at the bottom
function hindle_widget() { ?>
  <script
    ${baseAttrs}
    defer>
  </script>
<?php }
add_action('wp_footer', 'hindle_widget');`,
    },
    shopify:{
      title:"Shopify",
      steps:["In your Shopify admin go to Online Store → Themes","Click Actions → Edit Code on your active theme","Open Layout → theme.liquid","Paste the snippet just before </body> and save"],
      code:`{%- comment -%}
  Hindle Chat Widget — paste before </body> in theme.liquid
{%- endcomment -%}
<script
  ${baseAttrs}
  defer>
</script>`,
    },
    react:{
      title:"React / Next.js",
      steps:["For Next.js App Router add to app/layout.tsx","For Next.js Pages Router add to pages/_app.tsx","For plain React (CRA / Vite) use a useEffect hook in your root component"],
      code:`// ── Option A: Next.js App Router (app/layout.tsx) ──
// import Script from 'next/' + 'script'; // Next.js only
// Add inside your <body> return:
// <Script src="https://chatbot.hindleconsultants.com/widget.js"
//   data-tenant="${tenantId}"
//   strategy="lazyOnload" />

// ── Option B: Plain React (CRA / Vite) ──
// import { useEffect } from 'react';
//
// export default function App() {
//   useEffect(() => {
//     const s = document.createElement('script');
//     s.src = 'https://chatbot.hindleconsultants.com/widget.js';
//     s.setAttribute('data-tenant', '${tenantId}');
    s.setAttribute('data-color', '${widgetColor}');
    s.setAttribute('data-position', '${widgetPos}');
//     s.defer = true;
//     document.body.appendChild(s);
//     return () => document.body.removeChild(s);
//   }, []);
//   // ...
// }`,
    },
    webflow:{
      title:"Webflow",
      steps:["Open your Webflow project and go to Site Settings","Click the Custom Code tab","Paste the snippet in the Footer Code section","Publish your site — the widget appears on all pages"],
      code:`<!-- Webflow: Site Settings → Custom Code → Footer Code -->
<script
  ${baseAttrs}
  defer>
</script>`,
    },
    wix:{
      title:"Wix",
      steps:["In your Wix Editor click Add → Embed → Custom Embed","Choose Embed a Widget","Paste the full script tag and click Apply","Publish your site"],
      code:`<!-- Wix Custom Embed (HTML widget) -->
<script
  ${baseAttrs}
  defer>
</script>`,
    },
    gtm:{
      title:"Google Tag Manager",
      steps:["In GTM create a new Tag → Custom HTML","Paste the script tag into the HTML field","Set Triggering to All Pages (or your chosen trigger)","Submit and publish the container"],
      code:`<!-- GTM Custom HTML Tag -->
<script
  ${baseAttrs}
  defer>
</script>`,
    },
  };

  const snip=snippets[platform];

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      {/* Status metrics */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <Metric label="Widget Status" value={detected?"Live":"Not Detected"} icon="globe" col={detected?G.g:G.a} d={0}/>
        <Metric label="Tenant ID"     value={tenantId}                       icon="key"  col={B}                d={.05}/>
        <Metric label="CDN Region"    value="EU-West"                        icon="zap"  col={G.p}             d={.1}/>
        <Metric label="Script Size"   value="12 KB"                          icon="copy" col={T.mid}           d={.15}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 360px",gap:16,alignItems:"start"}}>

        {/* Left — main install guide */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Platform picker */}
          <div className="card" style={{padding:0,overflow:"hidden"}}>
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <h3 style={{fontSize:13.5,fontWeight:700}}>Install Guide</h3>
              <p style={{fontSize:12,color:T.lt}}>Choose your platform</p>
            </div>
            <div style={{display:"flex",gap:0,borderBottom:`1px solid ${S.brd}`,overflowX:"auto"}}>
              {PLATFORMS.map(p=>(
                <button key={p.id} onClick={()=>setPlatform(p.id)} style={{padding:"10px 16px",fontSize:12.5,fontWeight:600,cursor:"pointer",background:platform===p.id?BL:"transparent",color:platform===p.id?B:T.mid,border:"none",borderBottom:platform===p.id?`2px solid ${B}`:"2px solid transparent",marginBottom:-1,whiteSpace:"nowrap",transition:"all .15s",flexShrink:0}}>
                  {p.icon} {p.label}
                </button>
              ))}
            </div>

            <div style={{padding:20}}>
              <h4 style={{fontSize:13.5,fontWeight:700,marginBottom:16}}>{snip.title}</h4>

              {/* Steps */}
              <ol style={{paddingLeft:0,listStyle:"none",marginBottom:18}}>
                {snip.steps.map((s,i)=>(
                  <li key={i} style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12}}>
                    <div style={{width:22,height:22,borderRadius:"50%",background:B,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0,marginTop:1}}>{i+1}</div>
                    <p style={{fontSize:13,color:T.mid,lineHeight:1.6}}>{s}</p>
                  </li>
                ))}
              </ol>

              {/* Code block */}
              <div style={{position:"relative"}}>
                <pre className="mono" style={{background:"#0f172a",color:"#e2e8f0",borderRadius:10,padding:"16px 18px",fontSize:12,overflowX:"auto",whiteSpace:"pre",lineHeight:1.7,margin:0}}>{snip.code}</pre>
                <button onClick={()=>copyText(snip.code,"main")} style={{position:"absolute",top:10,right:10,background:copied==="main"?"rgba(22,163,74,.85)":"rgba(255,255,255,.12)",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6,transition:"all .2s"}}>
                  <Ic n={copied==="main"?"chk":"copy"} s={12} c="#fff"/>{copied==="main"?"Copied!":"Copy Code"}
                </button>
              </div>
            </div>
          </div>

          {/* CSP / CORS note */}
          <div className="card" style={{padding:16}}>
            <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{width:30,height:30,borderRadius:7,background:G.ab,border:`1px solid ${G.abd}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="lock" s={14} c={G.a}/></div>
              <div>
                <p style={{fontSize:13,fontWeight:700,marginBottom:4}}>Content Security Policy (CSP)</p>
                <p style={{fontSize:12.5,color:T.lt,lineHeight:1.6,marginBottom:8}}>If your site uses a strict CSP header, add these entries to allow the widget:</p>
                <pre className="mono" style={{background:"#0f172a",color:"#e2e8f0",borderRadius:8,padding:"10px 13px",fontSize:11.5,overflowX:"auto",whiteSpace:"pre",lineHeight:1.65}}{...{}}>{`script-src 'self' https://chatbot.hindleconsultants.com;
connect-src 'self' https://hindleapi-production.up.railway.app wss://hindleapi-production.up.railway.app;
frame-src   'self' https://chatbot.hindleconsultants.com;`}</pre>
              </div>
            </div>
          </div>
        </div>

        {/* Right — detection + quick ref */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Detection */}
          <div className="card" style={{padding:18}}>
            <h3 style={{fontSize:13,fontWeight:700,marginBottom:4}}>Widget Detection</h3>
            <p style={{fontSize:12,color:T.lt,marginBottom:14}}>After installing the snippet, run a check to confirm it's live on your domain.</p>
            <div style={{padding:"11px 13px",background:detected?G.gb:G.ab,border:`1px solid ${detected?G.gbd:G.abd}`,borderRadius:9,display:"flex",gap:9,alignItems:"center",marginBottom:12}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:detected?G.g:G.a,flexShrink:0,animation:detected?"none":"blink 1.5s infinite"}}/>
              <div style={{flex:1}}>
                <p style={{fontSize:13,fontWeight:700,color:detected?G.g:G.a}}>{detected?"✓ Widget detected on hindleconsultants.com":"Waiting to detect…"}</p>
                <p style={{fontSize:11,color:T.fnt}}>{detected?"Last seen: just now · chatbot.hindleconsultants.com/widget.js loaded":"Install the snippet then click Check Now"}</p>
              </div>
            </div>
            <button className={`btn ${detected?"bg-g":"bp"}`} onClick={checkDetection} disabled={checking} style={{width:"100%"}}>
              {checking?<><Spinner/> Checking…</>:detected?<><Ic n="chk" s={13} c={G.g}/> Detected</>:<><Ic n="zap" s={13} c="#fff"/> Check Now</>}
            </button>
          </div>

          {/* Tenant credentials */}
          <div className="card" style={{padding:18}}>
            <h3 style={{fontSize:13,fontWeight:700,marginBottom:12}}>Your Install Credentials</h3>
            {[["Tenant ID",tenantId,"key"],["CDN URL","chatbot.hindleconsultants.com/widget.js","copy"],["API Domain","hindleapi-production.up.railway.app","globe"],["WS Domain","hindleapi-production.up.railway.app","zap"]].map(([l,v,ic])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${S.bg}`}}>
                <div style={{display:"flex",gap:7,alignItems:"center"}}>
                  <Ic n={ic} s={12} c={T.fnt}/>
                  <span style={{fontSize:12,color:T.fnt}}>{l}</span>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <code className="mono" style={{fontSize:12,color:T.ink}}>{v}</code>
                  <button onClick={()=>copyText(v,l)} style={{background:"transparent",border:"none",cursor:"pointer",color:copied===l?G.g:T.fnt,padding:2,display:"flex"}}>
                    <Ic n={copied===l?"chk":"copy"} s={11} c={copied===l?G.g:T.fnt}/>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Data attributes quick ref */}
          <div className="card" style={{padding:18}}>
            <h3 style={{fontSize:13,fontWeight:700,marginBottom:10}}>data-* Quick Reference</h3>
            {[["data-tenant","Your unique tenant ID","Required"],["data-color","Widget accent colour (hex)","#2563EB"],["data-position","Launcher position","bottom-right"],["data-label","Launcher button text","Chat with us"],["data-hide-mobile","Hide widget on mobile","false"],["data-open-delay","Auto-open delay (ms)","0"],["data-z-index","CSS z-index override","9999"]].map(([attr,desc,def])=>(
              <div key={attr} style={{marginBottom:9,paddingBottom:9,borderBottom:`1px solid ${S.bg}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                  <code className="mono" style={{fontSize:11.5,color:B,fontWeight:600}}>{attr}</code>
                  <span style={{fontSize:10.5,color:T.fnt,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:4,padding:"1px 6px"}}>{def}</span>
                </div>
                <p style={{fontSize:11.5,color:T.lt}}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// CHATBOT MANAGEMENT PAGE
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// KB MAPPER MODAL
// Universal field-mapping UI for all KB import types
// ══════════════════════════════════════════════════════════════
const KBMapperModal=({onClose,onImport,parsedData,fileName,mode})=>{
  // mode: "json_multi" | "single_meta" (for PDF/DOCX/URL single-article category assignment)
  const {keys=[],sample=[],items=[],items_count=1,suggested={}}=parsedData||{};
  const [mapping,setMapping]=useState({
    title:    suggested.title    ||keys[0]||"",
    content:  suggested.content  ||keys[1]||"",
    category:     suggested.category     ||"",
    sub_category: suggested.sub_category ||"",
    tertiary:     suggested.tertiary     ||"",
  });
  // Re-sync mapping when parsedData/suggested arrives (handles async parse)
  useEffect(()=>{
    setMapping({
      title:    suggested.title    ||keys[0]||"",
      content:  suggested.content  ||keys[1]||"",
      category:     suggested.category     ||"",
      sub_category: suggested.sub_category ||"",
      tertiary:     suggested.tertiary     ||"",
    });
  },[parsedData]);
  // For single-article mode, category/sub/tertiary are free-text
  const [singleCat,setSingleCat]=useState({category:"",sub_category:"",tertiary:""});
  const [importing,setImporting]=useState(false);
  const [previewOpen,setPreviewOpen]=useState(true);

  const preview=mode==="json_multi"?items.slice(0,3).map(item=>({
    title:  String(item[mapping.title]    ||"").slice(0,80)||"(no title)",
    content:String(item[mapping.content]  ||"").slice(0,120)||"(no content)",
    category:    mapping.category     ?String(item[mapping.category]    ||""):"",
    sub_category:mapping.sub_category ?String(item[mapping.sub_category]||""):"",
    tertiary:    mapping.tertiary     ?String(item[mapping.tertiary]    ||""):"",
  })):[];

  const Field=({label,val,onChange,opts,placeholder,free})=>(
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11.5,fontWeight:700,color:T.mid,display:"block",marginBottom:4}}>{label}</label>
      {free?(
        <input className="inp" placeholder={placeholder||""} value={val} onChange={e=>onChange(e.target.value)} style={{fontSize:12.5}}/>
      ):(
        <select className="inp" value={val} onChange={e=>onChange(e.target.value)} style={{fontSize:12.5}}>
          <option value="">— not mapped —</option>
          {opts.map(k=><option key={k} value={k}>{k}</option>)}
        </select>
      )}
    </div>
  );

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div className="card au" style={{width:"100%",maxWidth:700,maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <h3 style={{fontSize:14,fontWeight:800}}>
              {mode==="json_multi"?`Map Fields — ${fileName} (${items_count} articles)`:`Categorise — ${fileName}`}
            </h3>
            <p style={{fontSize:12,color:T.fnt,marginTop:2}}>
              {mode==="json_multi"
                ?"Tell us which JSON fields map to title, content, and categories."
                :"Optionally assign this document to a category before saving."}
            </p>
          </div>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",padding:4}}><Ic n="x" s={16} c={T.fnt}/></button>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:18,display:"grid",gridTemplateColumns:mode==="json_multi"?"1fr 1fr":"1fr",gap:18}}>
          {/* Left: field mapping */}
          <div>
            <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>
              {mode==="json_multi"?"Field Mapping":"Category Assignment"}
            </p>

            {mode==="json_multi"&&(
              <>
                <Field label="Title / Question *" val={mapping.title} onChange={v=>setMapping(m=>({...m,title:v}))} opts={keys}/>
                <Field label="Content / Answer *" val={mapping.content} onChange={v=>setMapping(m=>({...m,content:v}))} opts={keys}/>
                <div style={{height:1,background:S.brd,margin:"12px 0"}}/>
              </>
            )}

            <Field label="Category" val={mode==="json_multi"?mapping.category:singleCat.category}
              onChange={v=>mode==="json_multi"?setMapping(m=>({...m,category:v})):setSingleCat(c=>({...c,category:v}))}
              opts={keys} free={mode==="single_meta"} placeholder="e.g. Billing, Product, Support"/>
            <Field label="Sub-category" val={mode==="json_multi"?mapping.sub_category:singleCat.sub_category}
              onChange={v=>mode==="json_multi"?setMapping(m=>({...m,sub_category:v})):setSingleCat(c=>({...c,sub_category:v}))}
              opts={keys} free={mode==="single_meta"} placeholder="e.g. Refunds, Plans, Technical"/>
            <Field label="Tertiary (optional)" val={mode==="json_multi"?mapping.tertiary:singleCat.tertiary}
              onChange={v=>mode==="json_multi"?setMapping(m=>({...m,tertiary:v})):setSingleCat(c=>({...c,tertiary:v}))}
              opts={keys} free={mode==="single_meta"} placeholder="e.g. International, Domestic"/>

            {mode==="json_multi"&&(
              <p style={{fontSize:11,color:T.fnt,marginTop:8,lineHeight:1.6}}>
                * Required. Fields left unmapped as "—" will be left blank on imported articles.
              </p>
            )}
          </div>

          {/* Right: preview (json_multi only) */}
          {mode==="json_multi"&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7}}>Preview (first 3)</p>
                <button onClick={()=>setPreviewOpen(v=>!v)} style={{fontSize:11,color:B,background:"transparent",border:"none",cursor:"pointer"}}>{previewOpen?"Hide":"Show"}</button>
              </div>
              {previewOpen&&preview.map((p,i)=>(
                <div key={i} style={{marginBottom:10,padding:"10px 12px",background:S.bg,borderRadius:8,border:`1px solid ${S.brd}`}}>
                  <p style={{fontSize:12.5,fontWeight:700,color:T.ink,marginBottom:3}}>{p.title}</p>
                  {(p.category||p.sub_category)&&<p style={{fontSize:10.5,color:B,marginBottom:3}}>{[p.category,p.sub_category,p.tertiary].filter(Boolean).join(" › ")}</p>}
                  <p style={{fontSize:11.5,color:T.fnt,lineHeight:1.5}}>{p.content}{p.content.length>=120?"…":""}</p>
                </div>
              ))}
              {!previewOpen&&<p style={{fontSize:12,color:T.fnt}}>Click Show to preview mapped articles.</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"12px 18px",borderTop:`1px solid ${S.brd}`,display:"flex",gap:8,justifyContent:"flex-end",flexShrink:0,background:S.bg}}>
          <button className="btn bo sm" onClick={onClose}>Cancel</button>
          {mode==="json_multi"&&(!mapping.title||!mapping.content)&&(
            <p style={{fontSize:11.5,color:G.r,fontWeight:600,alignSelf:"center"}}>Select Title and Content fields to continue</p>
          )}
            <button className="btn bp sm" disabled={importing||(mode==="json_multi"&&(!mapping.title||!mapping.content))}
            onClick={async()=>{
              setImporting(true);
              try{
                await onImport(
                  mode==="json_multi"?{mapping,items}:{category:singleCat}
                );
              }catch(ex){
                alert("Import error: "+ex.message);
              }finally{
                setImporting(false);
              }
            }}>
            {importing?<><Spinner size={12}/> Importing…</>:
              mode==="json_multi"?<>Import {items_count} article{items_count!==1?"s":""}</>:<>Save & Add</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const PChatbot=()=>{
  const [tab,setTab]=useState("config");
  const [saved,setSaved]=useState(false);
  const [showManualAdd,setShowManualAdd]=useState(false);
  const [manualDoc,setManualDoc]=useState({title:"",content:""});
  const [kbMapper,setKbMapper]=useState(null); // {parsedData, fileName, mode, pendingDoc}

  // ── Load persisted config from localStorage ──
  const loadCfg=()=>{
    try{const s=localStorage.getItem("hindle_chatbot_config");return s?JSON.parse(s):null;}catch(e){return null;}
  };
  const loadKb=()=>{
    try{const s=localStorage.getItem("hindle_chatbot_kb");return s?JSON.parse(s):[];}catch(e){return[];}
  };
  const _cfg=loadCfg()||{};

  // Config state
  const [model,setModel]=useState(_cfg.model||"gpt4o");
  const [tone,setTone]=useState(_cfg.tone||"friendly");
  const [lang,setLang]=useState(_cfg.lang||"en");
  const [greeting,setGreeting]=useState(_cfg.greeting||"Hi there! 👋 How can I help you today?");
  const [fallback,setFallback]=useState(_cfg.fallback||"I'm not sure about that — let me get someone to help you.");
  const [triggers,setTriggers]=useState(_cfg.triggers||["speak to a human","talk to an agent","real person","help me","urgent"]);
  const [trigInput,setTrigInput]=useState("");
  const [confidence,setConfidence]=useState(_cfg.confidence??75);
  const [maxTurns,setMaxTurns]=useState(_cfg.maxTurns??8);
  const [collectEmail,setCollectEmail]=useState(_cfg.collectEmail??true);
  const [offHours,setOffHours]=useState(_cfg.offHours||"leave_message");
  const [routing,setRouting]=useState(_cfg.routing||"online_only");
  const [additionalInstructions,setAdditionalInstructions]=useState(_cfg.additionalInstructions||"");
  const [handoffInactivityTimeout,setHandoffInactivityTimeout]=useState(_cfg.handoffInactivityTimeout??120);
  const [autoOpenDelay,setAutoOpenDelay]=useState(_cfg.autoOpenDelay??0);
  const [autoOpenSkipMobile,setAutoOpenSkipMobile]=useState(_cfg.autoOpenSkipMobile??true);
  const [preChatEnabled,setPreChatEnabled]=useState(_cfg.preChatEnabled??false);
  const [widgetColor,setWidgetColor]=useState(_cfg.widgetColor||"#2563EB");
  const [widgetLabel,setWidgetLabel]=useState(_cfg.widgetLabel||"Chat with us");
  const [widgetPosition,setWidgetPosition]=useState(_cfg.widgetPos||"bottom-right");
  const [preChatFields,setPreChatFields]=useState((_cfg.preChatFields&&_cfg.preChatFields.length)?_cfg.preChatFields:[
    {id:"name",  label:"Name",    type:"text",  required:true,  enabled:true},
    {id:"email", label:"Email",   type:"email", required:true,  enabled:true},
    {id:"phone", label:"Phone",   type:"tel",   required:false, enabled:false},
    {id:"company",label:"Company",type:"text",  required:false, enabled:false},
    {id:"message",label:"Message",type:"textarea",required:false,enabled:false},
  ]);

  // Knowledge base state
  const [kbDocs,setKbDocs]=useState(loadKb);

  const [testMsgs,setTestMsgs]=useState([{role:"bot",text:greeting||"Hi there! 👋 How can I help you today?"}]);
  const [testInp,setTestInp]=useState("");
  const [testLoading,setTestLoading]=useState(false);
  const testMsgRef=React.useRef(null);
  const sendTestMsg=async()=>{
    const text=testInp.trim();
    if(!text||testLoading)return;
    setTestInp("");
    const userMsg={role:"visitor",text};
    setTestMsgs(p=>[...p,userMsg]);
    setTestLoading(true);
    setTimeout(()=>{if(testMsgRef.current)testMsgRef.current.scrollTop=testMsgRef.current.scrollHeight;},30);
    try{
      const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
      const kbText=kbDocs.filter(d=>d.content).map(d=>`[${d.name}]\n${d.content}`).join("\n---\n");
      const sysPrompt=`You are a helpful AI support assistant. Tone: ${tone}. Fallback: "${fallback}".${kbText?"\n\nKnowledge base:\n"+kbText:""}\n${additionalInstructions||""}`;
      const history=testMsgs.slice(-10).map(m=>({role:m.role==="visitor"?"user":"assistant",content:m.text}));
      const r=await fetch(API_URL+"/api/chat",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({tenantId:orgId,messages:[...history,{role:"user",content:text}],system:sysPrompt,confidenceThreshold:confidence/100})});
      const d=await r.json();
      const reply=d.reply||d.error||"No response";
      setTestMsgs(p=>[...p,{role:"bot",text:reply}]);
      if(d.suggest_human)setTestMsgs(p=>[...p,{role:"system",text:"⚠️ Low confidence — human handoff would be suggested"}]);
    }catch(e){setTestMsgs(p=>[...p,{role:"bot",text:"Error: "+e.message}]);}
    setTestLoading(false);
    setTimeout(()=>{if(testMsgRef.current)testMsgRef.current.scrollTop=testMsgRef.current.scrollHeight;},60);
  };

  // ── Load KB from DB on mount ──
  useEffect(()=>{
    const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(!orgId)return;
    fetch(API_URL+"/api/kb?org_id="+orgId)
      .then(r=>r.ok?r.json():null)
      .then(rows=>{
        if(rows&&rows.length){
          setKbDocs(rows.map(r=>({id:r.id,name:r.name,size:r.size_kb?r.size_kb+" KB":"manual",chunks:r.chunks||1,status:r.status||"indexed",updated:r.updated_at||"",category:r.category||"",sub_category:r.sub_category||"",tertiary:r.tertiary||"",content:r.content||""})));
        }
      }).catch(()=>{});
  },[]);

  // ── Auto-save config whenever any setting changes ──
  useEffect(()=>{
    try{
      const _existing=JSON.parse(localStorage.getItem("hindle_chatbot_config")||"{}");
      localStorage.setItem("hindle_chatbot_config",JSON.stringify({..._existing,model,tone,lang,greeting,fallback,triggers,confidence,maxTurns,collectEmail,offHours,routing,preChatEnabled,preChatFields,autoOpenDelay,autoOpenSkipMobile,additionalInstructions,handoffInactivityTimeout,widgetColor,widgetLabel,widgetPos:widgetPosition}));
    }
    catch(e){}
  },[model,tone,lang,greeting,fallback,triggers,confidence,maxTurns,collectEmail,offHours,routing,preChatEnabled,preChatFields,autoOpenDelay,autoOpenSkipMobile,additionalInstructions,handoffInactivityTimeout,widgetColor,widgetLabel,widgetPosition]);

  // ── Auto-save KB whenever docs change ──
  useEffect(()=>{
    try{localStorage.setItem("hindle_chatbot_kb",JSON.stringify(kbDocs));}
    catch(e){}
  },[kbDocs]);

  const saveConfig=async()=>{
    setSaved(true);
    // Persist to DB so widget can load it
    try{
      let orgId=null;
      try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
      if(orgId){
        // Read current brand config
        let brand={};
        try{const b=localStorage.getItem("hindle_chatbot_config");if(b){const p=JSON.parse(b);brand={widgetColor:p.widgetColor,widgetPosition:p.widgetPosition,widgetLabel:p.widgetLabel,companyName:p.companyName,logo:p.logo};}}catch(e){}
        const payload={
          tenantId:orgId,
          greeting,fallback,tone,model,lang,
          maxTurns,confidence,collectEmail,offHours,routing,
          triggers,kbDocs,preChatEnabled,preChatFields,autoOpenDelay,autoOpenSkipMobile,widgetColor,widgetLabel,widgetPos:widgetPosition,
          additionalInstructions,handoffInactivityTimeout,
          ...brand,
        };
        await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
      }
    }catch(e){}
    setTimeout(()=>setSaved(false),2500);
  };
  const addTrigger=()=>{if(trigInput.trim()){setTriggers(t=>[...t,trigInput.trim()]);setTrigInput("");}};
  const removeTrigger=(i)=>setTriggers(t=>t.filter((_,j)=>j!==i));
  const removeDoc=(id)=>{
    setKbDocs(d=>d.filter(k=>k.id!==id));
    const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
    if(orgId) fetch(API_URL+"/api/kb/"+id+"?org_id="+orgId,{method:"DELETE"}).catch(()=>{});
  };

  const MODELS=[
    {id:"gpt4o",    label:"GPT-4o",         desc:"Best accuracy · Recommended",badge:true},
    {id:"gpt4omini",label:"GPT-4o Mini",    desc:"Cost efficient",              badge:false},
    {id:"claude",   label:"Claude Sonnet",  desc:"Long context",               badge:false},
  ];
  const TONES=[["friendly","😊 Friendly"],["professional","💼 Professional"],["concise","⚡ Concise"],["empathetic","🤝 Empathetic"]];
  const LANGS=[["en","English"],["es","Spanish"],["fr","French"],["de","German"],["pt","Portuguese"],["zh","Chinese"]];

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>

      {/* Single save bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,padding:"10px 14px",background:saved?G.gb:S.w,border:`1px solid ${saved?G.gbd:S.brd}`,borderRadius:10,transition:"all .3s"}}>
        <div>
          <p style={{fontSize:13,fontWeight:700,color:saved?G.g:T.ink}}>{saved?"✓ All chatbot settings saved and pushed to live widget!":"Chatbot Configuration"}</p>
          <p style={{fontSize:11.5,color:T.fnt}}>All tabs are saved together — configure across tabs then click Save once.</p>
        </div>
        <button className={`btn ${saved?"bg-g":"bp"}`} onClick={saveConfig} style={{minWidth:160}}>
          {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save All Settings</>}
        </button>
      </div>

      {/* Metrics */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <Metric label="Conversations (7d)" value="312"  sub={{u:true,t:"12% vs last week"}} icon="chat" d={0}/>
        <Metric label="AI Resolved"         value="81%" sub={{u:true,t:"vs 76% last week"}} icon="bot"  col={G.g} d={.05}/>
        <Metric label="Avg Turns"           value="4.2" sub={{t:"per session"}}             icon="zap"  col={G.p} d={.1}/>
        <Metric label="Handoff Rate"        value="19%" sub={{t:"escalated to agent"}}      icon="hand" col={G.a} d={.15}/>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:3,marginBottom:16,borderBottom:`1px solid ${S.brd}`,paddingBottom:0}}>
        {[["config","⚙️ Configuration"],["widget","🎨 Widget"],["kb","📚 Knowledge Base"],["test","🧪 Test Chatbot"],["handoff","🤝 Handoff Rules"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer",background:"transparent",color:tab===t?B:T.fnt,border:"none",borderBottom:tab===t?`2px solid ${B}`:"2px solid transparent",marginBottom:-1,transition:"all .15s",borderRadius:"6px 6px 0 0"}}>{l}</button>
        ))}
      </div>

      {/* ── CONFIG TAB ── */}
      {tab==="config"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {/* Left column */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            {/* Model */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>AI Model</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {MODELS.map(m=>(
                  <div key={m.id} onClick={()=>setModel(m.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 13px",borderRadius:9,cursor:"pointer",border:model===m.id?`2px solid ${B}`:`1px solid ${S.brd}`,background:model===m.id?BL:S.bg,transition:"all .15s",position:"relative"}}>
                    {m.badge&&<span style={{position:"absolute",top:-8,right:10,background:B,color:"#fff",fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:10}}>RECOMMENDED</span>}
                    <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${model===m.id?B:S.brd}`,background:model===m.id?B:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {model===m.id&&<div style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
                    </div>
                    <div><p style={{fontSize:13,fontWeight:700,color:model===m.id?B:T.ink}}>{m.label}</p><p style={{fontSize:11,color:T.fnt}}>{m.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tone & Language */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Personality & Language</p>
              <p style={{fontSize:12,fontWeight:600,color:T.mid,marginBottom:8}}>Tone</p>
              <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:14}}>
                {TONES.map(([val,lbl])=>(
                  <button key={val} onClick={()=>setTone(val)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",background:tone===val?B:S.bg,color:tone===val?"#fff":T.mid,border:tone===val?`1px solid ${B}`:`1px solid ${S.brd}`,transition:"all .15s"}}>{lbl}</button>
                ))}
              </div>
              <p style={{fontSize:12,fontWeight:600,color:T.mid,marginBottom:6}}>Language</p>
              <select className="inp" value={lang} onChange={e=>setLang(e.target.value)} style={{maxWidth:200}}>
                {LANGS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
              <div style={{marginTop:16}}>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:6}}>Additional Bot Instructions</label>
                <textarea className="inp" rows={5} placeholder={"Examples:\n- Always mention our 30-day money-back guarantee\n- Never discuss competitor products\n- Refer users to support@company.com for billing issues\n- Respond only in formal English"} value={additionalInstructions} onChange={e=>setAdditionalInstructions(e.target.value)} style={{resize:"vertical",lineHeight:1.5,fontSize:12.5}}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:4}}>These instructions are appended to the AI system prompt on every conversation. Use them to shape specific behaviour, topics to avoid, or references to always include.</p>
              </div>
            </div>

            {/* Behaviour */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Behaviour</p>
              <div style={{marginBottom:14}}>
                <label style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:12.5,fontWeight:600,color:T.mid}}>Confidence Threshold</span>
                  <span className="mono" style={{fontSize:13,fontWeight:700,color:B}}>{confidence}%</span>
                </label>
                <input type="range" min={50} max={95} step={5} value={confidence} onChange={e=>setConfidence(Number(e.target.value))} style={{width:"100%",accentColor:B}}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:3}}>Below this confidence the bot says it's unsure and offers to escalate.</p>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:12.5,fontWeight:600,color:T.mid}}>AI silence after handoff</span>
                  <span className="mono" style={{fontSize:13,fontWeight:700,color:B}}>{handoffInactivityTimeout}s</span>
                </label>
                <input type="range" min={30} max={600} step={30} value={handoffInactivityTimeout} onChange={e=>setHandoffInactivityTimeout(Number(e.target.value))} style={{width:"100%",accentColor:B}}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:3}}>After a human handoff, the AI stays silent for this long. If the agent does not respond within this period, the AI resumes. Set to 600s (10 min) to keep AI silent indefinitely.</p>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:12.5,fontWeight:600,color:T.mid}}>Max turns before handoff offer</span>
                  <span className="mono" style={{fontSize:13,fontWeight:700,color:B}}>{maxTurns}</span>
                </label>
                <input type="range" min={2} max={20} step={1} value={maxTurns} onChange={e=>setMaxTurns(Number(e.target.value))} style={{width:"100%",accentColor:B}}/>
              </div>
              <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer"}}>
                <input type="checkbox" checked={collectEmail} onChange={e=>setCollectEmail(e.target.checked)} style={{accentColor:B,width:14,height:14}}/>
                <div><p style={{fontSize:12.5,fontWeight:600}}>Collect visitor email before chatting</p><p style={{fontSize:11,color:T.fnt}}>Asks for email in the opening message</p></div>
              </label>
            </div>
          </div>

          {/* Right column */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            {/* Greeting & Fallback */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Messages</p>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:6}}>Opening Greeting</label>
                <textarea className="inp" value={greeting} onChange={e=>setGreeting(e.target.value)} style={{resize:"vertical",minHeight:54}}/>
                <div style={{padding:"8px 11px",background:BL,border:`1px solid ${BM}`,borderRadius:7,marginTop:6,display:"flex",gap:7,alignItems:"flex-start"}}>
                  <Ic n="bot" s={13} c={B} style={{flexShrink:0,marginTop:1}}/>
                  <p style={{fontSize:12,color:T.mid,fontStyle:"italic"}}>"{greeting}"</p>
                </div>
              </div>
              <div>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Fallback Response <span style={{fontSize:11,fontWeight:400,color:T.fnt}}>(when bot is unsure)</span></label>
                <textarea className="inp" value={fallback} onChange={e=>setFallback(e.target.value)} style={{resize:"vertical",minHeight:54}}/>
              </div>
            </div>

            {/* Auto-open delay */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Auto-Open Behaviour</p>
              <p style={{fontSize:12,fontWeight:600,color:T.mid,marginBottom:8}}>Open widget after</p>
              <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:14}}>
                {[[0,"Disabled"],[3,"3s"],[5,"5s"],[10,"10s"],[20,"20s"],[30,"30s"],[60,"60s"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setAutoOpenDelay(v)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",background:autoOpenDelay===v?B:S.bg,color:autoOpenDelay===v?"#fff":T.mid,border:autoOpenDelay===v?`1px solid ${B}`:`1px solid ${S.brd}`,transition:"all .15s"}}>{l}</button>
                ))}
              </div>
              <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",opacity:autoOpenDelay===0?.45:1,pointerEvents:autoOpenDelay===0?"none":"auto"}}>
                <input type="checkbox" checked={autoOpenSkipMobile} onChange={e=>setAutoOpenSkipMobile(e.target.checked)} style={{accentColor:B,width:14,height:14}}/>
                <div><p style={{fontSize:12.5,fontWeight:600}}>Skip auto-open on mobile</p><p style={{fontSize:11,color:T.fnt}}>Widget stays minimised on phones and tablets</p></div>
              </label>
            </div>

            {/* Off-hours */}
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Out-of-Hours Behaviour</p>
              {[["leave_message","Take a message — email visitor when agent available"],["bot_only","Keep bot running — no handoff offer"],["show_hours","Show business hours and close chat"]].map(([val,lbl])=>(
                <label key={val} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:10,cursor:"pointer"}}>
                  <input type="radio" name="offhours" value={val} checked={offHours===val} onChange={()=>setOffHours(val)} style={{accentColor:B,marginTop:2,flexShrink:0}}/>
                  <span style={{fontSize:12.5,color:offHours===val?T.ink:T.mid,fontWeight:offHours===val?600:400}}>{lbl}</span>
                </label>
              ))}
            </div>

            <button className={`btn ${saved?"bg-g":"bp"}`} onClick={saveConfig} style={{width:"100%"}}>
              {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save All Settings</>}
            </button>
          </div>
        </div>
      )}

      {/* ── WIDGET TAB ── */}
      {tab==="widget"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Auto-open */}
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Auto-Open Behaviour</p>
            <p style={{fontSize:12.5,color:T.mid,marginBottom:8}}>Automatically open the chat widget after a delay</p>
            <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:14}}>
              {[[0,"Disabled"],[3,"3s"],[5,"5s"],[10,"10s"],[20,"20s"],[30,"30s"],[60,"60s"]].map(([v,l])=>(
                <button key={v} onClick={()=>setAutoOpenDelay(v)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",background:autoOpenDelay===v?B:S.bg,color:autoOpenDelay===v?"#fff":T.mid,border:autoOpenDelay===v?`1px solid ${B}`:`1px solid ${S.brd}`,transition:"all .15s"}}>{l}</button>
              ))}
            </div>
            <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",opacity:autoOpenDelay===0?.45:1,pointerEvents:autoOpenDelay===0?"none":"auto"}}>
              <input type="checkbox" checked={autoOpenSkipMobile} onChange={e=>setAutoOpenSkipMobile(e.target.checked)} style={{accentColor:B,width:14,height:14}}/>
              <div><p style={{fontSize:12.5,fontWeight:600}}>Skip auto-open on mobile</p><p style={{fontSize:11,color:T.fnt}}>Widget stays minimised on phones and tablets</p></div>
            </label>
          </div>

          {/* Widget appearance */}
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Appearance</p>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Widget Colour</label>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input type="color" value={widgetColor||"#2563EB"} onChange={e=>setWidgetColor(e.target.value)}
                    style={{width:36,height:36,borderRadius:8,border:`1px solid ${S.brd}`,cursor:"pointer",padding:2}}/>
                  <input className="inp" value={widgetColor||"#2563EB"} onChange={e=>setWidgetColor(e.target.value)}
                    style={{width:110,fontSize:13,fontFamily:"monospace"}}/>
                  <div style={{width:36,height:36,borderRadius:"50%",background:widgetColor||"#2563EB",border:`1px solid ${S.brd}`}}/>
                </div>
              </div>
              <div>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Widget Label</label>
                <input className="inp" placeholder="Chat with us" value={widgetLabel||""} onChange={e=>setWidgetLabel(e.target.value)} style={{maxWidth:280}}/>
              </div>
              <div>
                <label style={{fontSize:12.5,fontWeight:600,color:T.mid,display:"block",marginBottom:5}}>Position</label>
                <div style={{display:"flex",gap:8}}>
                  {[["bottom-right","Bottom Right"],["bottom-left","Bottom Left"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setWidgetPosition(v)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",background:widgetPosition===v?B:S.bg,color:widgetPosition===v?"#fff":T.mid,border:widgetPosition===v?`1px solid ${B}`:`1px solid ${S.brd}`}}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pre-chat form */}
          <div className="card" style={{padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div>
                <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7}}>Pre-Chat Form</p>
                <p style={{fontSize:12,color:T.fnt,marginTop:2}}>Collect visitor details before the chat starts</p>
              </div>
              <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer"}}>
                <input type="checkbox" checked={preChatEnabled} onChange={e=>setPreChatEnabled(e.target.checked)} style={{accentColor:B,width:15,height:15}}/>
                <span style={{fontSize:12.5,fontWeight:600}}>{preChatEnabled?"Enabled":"Disabled"}</span>
              </label>
            </div>
            {preChatEnabled&&(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {preChatFields.map((f,i)=>(
                  <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:S.bg,borderRadius:8,border:`1px solid ${S.brd}`}}>
                    <input type="checkbox" checked={f.enabled} onChange={e=>setPreChatFields(fs=>fs.map((x,j)=>j===i?{...x,enabled:e.target.checked}:x))} style={{accentColor:B,width:14,height:14}}/>
                    <span style={{fontSize:13,flex:1}}>{f.label}</span>
                    <label style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:T.fnt}}>
                      <input type="checkbox" checked={f.required} onChange={e=>setPreChatFields(fs=>fs.map((x,j)=>j===i?{...x,required:e.target.checked}:x))} style={{accentColor:G.r,width:12,height:12}}/>
                      Required
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className="btn bp" onClick={()=>{setSaved(true);setTimeout(()=>setSaved(false),2500);}} style={{alignSelf:"flex-start",padding:"9px 20px"}}>
            {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Widget Settings</>}
          </button>
        </div>
      )}

      {/* ── KNOWLEDGE BASE TAB ── */}
      {tab==="kb"&&(
        <div>

          {/* ── Action bar ── */}
          <div className="card" style={{marginBottom:14}}>
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div>
                <h3 style={{fontSize:13.5,fontWeight:700}}>Knowledge Base</h3>
                <p style={{fontSize:12,color:T.lt,marginTop:1}}>
                  {kbDocs.filter(d=>d.status==="indexed").length===0
                    ? "No documents indexed yet — upload your first file or import a URL"
                    : `${kbDocs.filter(d=>d.status==="indexed").reduce((s,d)=>s+d.chunks,0)} chunks across ${kbDocs.filter(d=>d.status==="indexed").length} document${kbDocs.filter(d=>d.status==="indexed").length!==1?"s":""}`
                  }
                </p>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn bo sm" onClick={async()=>{
                  const url=window.prompt("Enter URL to import (e.g. https://yoursite.com/faq)");
                  if(!url||(url.startsWith("http://")||url.startsWith("https://"))===false){if(url)alert("Please enter a valid URL starting with https://");return;}
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  if(!orgId){alert("No org ID — please save your chatbot settings first.");return;}
                  const slug=url.split("/").filter(Boolean).pop()||"page";
                  const tempId=`d${Date.now()}`;
                  setKbDocs(d=>[{id:tempId,name:slug+".url",size:"web",chunks:0,status:"indexing",updated:"Just now",category:"",subcategory:""},...d]);
                  try{
                    const r=await fetch(API_URL+"/api/kb/import-url",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({org_id:orgId,url})});
                    const saved=await r.json();
                    if(r.ok&&saved.id){
                      setKbDocs(d=>d.map(x=>x.id===tempId?{...x,...saved,status:"indexed"}:x));
                    }else{
                      setKbDocs(d=>d.filter(x=>x.id!==tempId));
                      alert("Import failed: "+(saved.error||"Unknown error"));
                    }
                  }catch(e){setKbDocs(d=>d.filter(x=>x.id!==tempId));alert("Import failed: "+e.message);}
                }}><Ic n="link" s={12}/> Import URL</button>
                {/* Scrape Website button - auto-discovers all pages */}
                <button className="btn bo sm" onClick={async()=>{
                  const siteUrl=window.prompt("Enter website base URL to auto-scrape all pages:\n\nExamples:\nhttps://kuhlekt.com\nhttps://knowledge.hindleconsultants.com/kb/kuhlekt","https://kuhlekt.com");
                  if(!siteUrl) return;
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  if(!orgId){alert("Not logged in");return;}
                  const cat=window.prompt("Category for imported articles:","Website");
                  if(!window.confirm(`Auto-discover and scrape all pages from:\n${siteUrl}\n\nThis will crawl up to 60 pages and import them into the Knowledge Base. Continue?`)) return;
                  const btn=document.activeElement;
                  if(btn) btn.textContent="⏳ Scraping...";
                  try{
                    const r=await fetch(API_URL+"/api/kb/scrape-discover",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({org_id:orgId,base_url:siteUrl,category:cat||"Website"})});
                    const d=await r.json();
                    if(r.ok){
                      alert(`✅ Scraped ${d.imported} of ${d.pagesVisited} pages discovered.\nRefreshing KB...`);
                      const kr=await fetch(API_URL+"/api/kb?org_id="+orgId);
                      if(kr.ok){const kd=await kr.json();setKbDocs(kd);}
                    }else{alert("Scrape failed: "+(d.error||"Unknown error"));}
                  }catch(e){alert("Error: "+e.message);}
                  if(btn) btn.textContent="🌐 Scrape Website";
                }}><Ic n="globe" s={12}/> Scrape Website</button>
                {/* Import from Kuhlekt KB API */}
                <button className="btn bo sm" onClick={async()=>{
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  if(!orgId){alert("Not logged in");return;}
                  const apiUrl=window.prompt("KB API URL:","https://knowledge.hindleconsultants.com/api/public/search");
                  if(!apiUrl)return;
                  const apiKey=window.prompt("API Key:","kb_live_kh2026_kuhlekt");
                  if(!window.confirm("Import all articles from the KB API into this knowledge base?"))return;
                  try{
                    const r=await fetch(API_URL+"/api/kb/import-from-kb-api",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({org_id:orgId,kb_api_url:apiUrl,kb_api_key:apiKey})});
                    const d=await r.json();
                    if(r.ok&&d.ok){
                      alert(`✅ Imported ${d.imported} articles from KB API. Refreshing...`);
                      const kr=await fetch(API_URL+"/api/kb?org_id="+orgId);
                      if(kr.ok){const kd=await kr.json();setKbDocs(kd);}
                    }else{alert("Import failed: "+(d.error||JSON.stringify(d)));}
                  }catch(e){alert("Error: "+e.message);}
                }}><Ic n="book" s={12}/> Import KB API</button>
                {/* Upload Doc — PDF/DOCX/TXT/MD go to server; JSON triggers mapper */}
                <button className="btn bp sm" onClick={()=>{
                  const inp=document.createElement("input");
                  inp.type="file";
                  inp.accept=".pdf,.docx,.doc,.txt,.md,.xml,.csv";
                  inp.onchange=async e=>{
                    const file=e.target.files[0];
                    if(!file) return;
                    const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(ex){}return null;})();
                    const ext=file.name.split(".").pop().toLowerCase();
                    const needsServer=["pdf","docx","doc"].includes(ext);
                    const tempId=`d${Date.now()}`;
                    if(needsServer){
                      setKbDocs(d=>[{id:tempId,name:file.name,size:`${(file.size/1024).toFixed(0)} KB`,chunks:0,status:"processing",updated:"Just now",category:"",subcategory:"",category:"",sub_category:""},...d]);
                      try{
                        if(!orgId){setKbDocs(d=>d.filter(x=>x.id!==tempId));alert("Save your chatbot settings first.");return;}
                        const fd=new FormData();fd.append("file",file);
                        const r=await fetch(API_URL+"/api/kb/upload-file?org_id="+orgId,{method:"POST",headers:{"X-Org-Id":orgId},body:fd});
                        const saved=await r.json();
                        if(r.ok&&saved.id){
                          // Show mapper for category assignment
                          setKbDocs(d=>d.filter(x=>x.id!==tempId));
                          setKbMapper({
                            parsedData:{keys:[],sample:[],items:[saved],items_count:1,suggested:{}},
                            fileName:file.name,mode:"single_meta",
                            pendingDoc:saved
                          });
                        }else{setKbDocs(d=>d.filter(x=>x.id!==tempId));alert("Upload failed: "+(saved.error||"Unknown"));}
                      }catch(ex){setKbDocs(d=>d.filter(x=>x.id!==tempId));alert("Upload failed: "+ex.message);}
                    }else{
                      setKbDocs(d=>[{id:tempId,name:file.name,size:`${(file.size/1024).toFixed(0)} KB`,chunks:0,status:"processing",updated:"Just now",category:"",subcategory:""},...d]);
                      const reader=new FileReader();
                      reader.onload=async ev=>{
                        let text=ev.target.result||"";
                        if(ext==="xml"){text=text.replace(/<[^>]+>/g," ").replace(/\s{2,}/g," ").trim();}
                        text=text.slice(0,80000);
                        if(!orgId){setKbDocs(d=>d.map(x=>x.id===tempId?{...x,status:"indexed",content:text}:x));return;}
                        try{
                          const r=await fetch(API_URL+"/api/kb",{method:"POST",headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({org_id:orgId,name:file.name,content:text,size_kb:Math.round(file.size/1024),chunks:Math.ceil(text.length/500),status:"indexed"})});
                          const saved=await r.json();
                          if(r.ok&&saved.id){
                            setKbDocs(d=>d.filter(x=>x.id!==tempId));
                            setKbMapper({parsedData:{keys:[],sample:[],items:[saved],items_count:1,suggested:{}},fileName:file.name,mode:"single_meta",pendingDoc:saved});
                          }else setKbDocs(d=>d.filter(x=>x.id!==tempId));
                        }catch(ex){setKbDocs(d=>d.filter(x=>x.id!==tempId));}
                      };
                      reader.readAsText(file);
                    }
                  };
                  inp.click();
                }}><Ic n="plus" s={12} c="#fff"/> Upload Doc</button>
                {/* JSON Import — parse client-side, no large server upload */}
                <button className="btn bo sm" onClick={()=>{
                  const inp=document.createElement("input");
                  inp.type="file";inp.accept=".json";
                  inp.onchange=e=>{
                    const file=e.target.files[0];if(!file)return;
                    const reader=new FileReader();
                    reader.onload=ev=>{
                      try{
                        const raw=JSON.parse(ev.target.result);
                        let items=[];
                        // Detect shape client-side (same logic as server)
                        if(Array.isArray(raw)){
                          items=raw;
                        }else if(typeof raw==="object"){
                          const keys=Object.keys(raw);
                          const firstVal=raw[keys[0]];
                          if(Array.isArray(firstVal)){
                            keys.forEach(cat=>{(Array.isArray(raw[cat])?raw[cat]:[]).forEach(item=>items.push({...item,_detected_category:cat}));});
                          }else if(typeof firstVal==="string"){
                            items=Object.entries(raw).map(([k,v])=>({title:k,content:v}));
                          }else{items=[raw];}
                        }
                        if(!items.length){alert("No items found in JSON file.");return;}
                        // Build key list from first 20 items
                        const keySet=new Set();
                        items.slice(0,20).forEach(item=>Object.keys(item).forEach(k=>keySet.add(k)));
                        const keys=[...keySet];
                        // Auto-suggest
                        const suggest=candidates=>keys.find(k=>candidates.some(c=>k.toLowerCase().includes(c)))||"";
                        const suggested={
                          title:suggest(["title","name","question","q","heading","subject","topic"]),
                          content:suggest(["content","answer","a","body","description","text","detail","response"]),
                          category:suggest(["category","cat","group","section","type","module","_detected_category"]),
                          sub_category:suggest(["sub","subcategory","sub_category","subtopic","tag"]),
                          tertiary:suggest(["tertiary","third","level3","tier"]),
                        };
                        setKbMapper({
                          parsedData:{keys,sample:items.slice(0,3),items,items_count:items.length,suggested},
                          fileName:file.name,mode:"json_multi"
                        });
                      }catch(ex){alert("Invalid JSON: "+ex.message);}
                    };
                    reader.readAsText(file);
                  };
                  inp.click();
                }}><Ic n="book" s={12}/> Import JSON</button>
                <button className="btn bo sm" onClick={()=>setShowManualAdd(v=>!v)}
                  style={{background:showManualAdd?BL:"",borderColor:showManualAdd?B:"",color:showManualAdd?B:""}}>
                  <Ic n="edit" s={12} c={showManualAdd?B:T.mid}/> Manual Add
                </button>
                <button className="btn bo sm" onClick={async()=>{
                  const csv=window.prompt("Bulk CSV — format: title,content (one per line)\nHow do I reset my password?,Go to Settings and click Change Password.\nWhat are your hours?,Monday-Friday 9am-5pm.");
                  if(!csv)return;
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  const lines=csv.split("\n").filter(l=>l.trim()&&!l.toLowerCase().startsWith("title,"));
                  const parsed=lines.map((line,i)=>{
                    const comma=line.indexOf(",");if(comma===-1)return null;
                    const t=line.slice(0,comma).trim();const c=line.slice(comma+1).trim();
                    if(!t||!c)return null;
                    return{id:`d${Date.now()}_${i}`,name:t.slice(0,80),size:"manual",chunks:1,status:"indexed",updated:"Just now",category:"",sub_category:"",tertiary:"",content:c};
                  }).filter(Boolean);
                  if(!parsed.length){alert("No valid rows. Format: title,content");return;}
                  setKbMapper({parsedData:{keys:["title","content"],sample:parsed.slice(0,3),items:parsed,items_count:parsed.length,suggested:{title:"title",content:"content"}},fileName:"CSV import",mode:"json_multi"});
                }}><Ic n="plus" s={12}/> Bulk CSV</button>
              </div>
            </div>

            {/* KB Mapper modal */}
            {kbMapper&&(
              <KBMapperModal
                parsedData={kbMapper.parsedData}
                fileName={kbMapper.fileName}
                mode={kbMapper.mode}
                onClose={()=>setKbMapper(null)}
                onImport={async(result)=>{
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  try{
                    if(kbMapper.mode==="single_meta"){
                      const doc=kbMapper.pendingDoc;
                      if(doc?.id&&orgId){
                        await fetch(API_URL+"/api/kb/"+doc.id,{method:"PATCH",headers:{"Content-Type":"application/json"},
                          body:JSON.stringify({org_id:orgId,...result.category})}).catch(()=>{});
                      }
                      setKbDocs(d=>[{...doc,...result.category,id:doc.id||`d${Date.now()}`},...d]);
                    }else{
                      const {items,mapping}=result;
                      if(orgId){
                        // Batch into chunks of 100 to avoid payload limits
                        const BATCH=100;
                        const saved=[];
                        for(let i=0;i<items.length;i+=BATCH){
                          const batch=items.slice(i,i+BATCH);
                          const r=await fetch(API_URL+"/api/kb/import-mapped",{method:"POST",headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({org_id:orgId,items:batch,mapping})});
                          if(!r.ok){const e=await r.json().catch(()=>({}));throw new Error(e.error||`HTTP ${r.status}`);}
                          const d=await r.json();
                          if(d.items) saved.push(...d.items);
                        }
                        if(saved.length){
                          setKbDocs(prev=>[...saved.map(x=>({...x,status:"indexed"})),...prev]);
                        }
                      }else{
                        const newDocs=items.map((item,i)=>({id:`d${Date.now()}_${i}`,name:String(item[mapping.title]||"").slice(0,80),content:String(item[mapping.content]||""),category:mapping.category?String(item[mapping.category]||""):"",sub_category:mapping.sub_category?String(item[mapping.sub_category]||""):"",tertiary:mapping.tertiary?String(item[mapping.tertiary]||""):"",size:"manual",chunks:1,status:"indexed",updated:"Just now"}));
                        setKbDocs(d=>[...newDocs,...d]);
                      }
                    }
                  }catch(ex){
                    throw ex; // re-throw so the button's finally block catches it
                  }
                  setKbMapper(null);
                }}
              />
            )}

            {/* Manual Add form */}
            {showManualAdd&&(
              <div style={{margin:"0 0 14px",padding:16,background:BL,border:`1.5px solid ${BM}`,borderRadius:10}}>
                <p style={{fontSize:13,fontWeight:700,color:B,marginBottom:12}}>Add Article Manually</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
                  {[["Category","category","e.g. Billing"],["Sub-category","sub_category","e.g. Refunds"],["Tertiary","tertiary","optional"]].map(([l,k,ph])=>(
                    <div key={k}><label style={{fontSize:11,fontWeight:600,color:T.mid,display:"block",marginBottom:3}}>{l}</label>
                    <input className="inp" style={{fontSize:12}} placeholder={ph} value={manualDoc[k]||""} onChange={e=>setManualDoc(d=>({...d,[k]:e.target.value}))}/></div>
                  ))}
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Title / Question</label>
                  <input className="inp" placeholder="e.g. How do I reset my password?" value={manualDoc.title} onChange={e=>setManualDoc(d=>({...d,title:e.target.value}))}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Content / Answer</label>
                  <textarea className="inp" rows={4} placeholder="Write the full answer here. The AI will use this to answer visitor questions." value={manualDoc.content} onChange={e=>setManualDoc(d=>({...d,content:e.target.value}))} style={{resize:"vertical"}}/>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn bp sm" onClick={async()=>{
                    if(!manualDoc.title.trim()||!manualDoc.content.trim())return;
                    const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                    const newDoc={id:`d${Date.now()}`,name:manualDoc.title.trim().slice(0,80),size:"manual",chunks:1,status:"indexed",updated:"Just now",category:manualDoc.category||"",sub_category:manualDoc.sub_category||"",tertiary:manualDoc.tertiary||"",content:manualDoc.content.trim()};
                    setKbDocs(d=>[newDoc,...d]);
                    setManualDoc({title:"",content:"",category:"",sub_category:"",tertiary:""});
                    setShowManualAdd(false);
                    if(orgId){try{await fetch(API_URL+"/api/kb",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({org_id:orgId,name:newDoc.name,content:newDoc.content,category:newDoc.category,sub_category:newDoc.sub_category,tertiary:newDoc.tertiary,size_kb:0,chunks:1,status:"indexed"})});}catch(e){}}
                  }} disabled={!manualDoc.title.trim()||!manualDoc.content.trim()}>
                    <Ic n="chk" s={12} c="#fff"/> Add Article
                  </button>
                  <button className="btn bo sm" onClick={()=>{setShowManualAdd(false);setManualDoc({title:"",content:"",category:"",sub_category:"",tertiary:""});}}>Cancel</button>
                </div>
              </div>
            )}

            {kbDocs.length===0?(
              <div style={{padding:"48px 24px",textAlign:"center"}}>
                <div style={{width:52,height:52,borderRadius:14,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}><Ic n="book" s={22} c={B}/></div>
                <p style={{fontSize:14,fontWeight:700,color:T.ink,marginBottom:6}}>No documents yet</p>
                <p style={{fontSize:13,color:T.lt,maxWidth:360,margin:"0 auto 18px",lineHeight:1.6}}>Upload PDFs, Word docs, plain text, JSON, XML, JSX or import any URL. The AI will index your content and use it exclusively to answer visitor questions.</p>
                <div style={{display:"flex",gap:8,justifyContent:"center"}}>
                  <button className="btn bp sm" onClick={()=>{
                    const inp=document.createElement("input");inp.type="file";inp.accept=".pdf,.docx,.txt,.md,.json,.xml,.jsx,.csv";
                    inp.onchange=e=>{const file=e.target.files[0];if(!file)return;const ext=file.name.split(".").pop().toLowerCase();const newDoc={id:`d${Date.now()}`,name:file.name,size:`${(file.size/1024).toFixed(0)} KB`,chunks:0,status:"processing",updated:"Just now",category:"",subcategory:"",processing:{strip_html:["html","xml","jsx"].includes(ext),clean_ws:true,split_sections:true,remove_headers:false,extract_tables:["docx","pdf"].includes(ext)}};setKbDocs(d=>[newDoc,...d]);setTimeout(()=>setKbDocs(d=>d.map(x=>x.id===newDoc.id?{...x,status:"indexed",chunks:Math.floor(Math.random()*50)+10}:x)),2800);};inp.click();
                  }}>Upload your first document</button>
                  <button className="btn bo sm" onClick={()=>{const url=window.prompt("Enter URL to import (e.g. https://yoursite.com/faq)");if(url&&(url.startsWith("http://")||url.startsWith("https://"))){const slug=url.split("/").filter(Boolean).pop()||"page";const newDoc={id:`d${Date.now()}`,name:slug+".url",size:"web",chunks:0,status:"indexing",updated:"Just now",category:"",subcategory:"",processing:{strip_html:true,clean_ws:true,split_sections:true}};setKbDocs(d=>[newDoc,...d]);setTimeout(()=>setKbDocs(d=>d.map(x=>x.id===newDoc.id?{...x,status:"indexed",chunks:Math.floor(Math.random()*30)+8}:x)),2200);}}}>Import from URL</button>
                </div>
              </div>
            ):(
              <div style={{overflowX:"auto"}}>
                <table className="tbl">
                  <thead><tr>{["Document","Type","Category","Sub-category","Size","Chunks","Status","Updated",""].map(h=><th key={h} style={{whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
                  <tbody>
                    {kbDocs.map(doc=>{
                      const ext=doc.name.split(".").pop().toLowerCase();
                      const extCol={pdf:G.r,docx:B,txt:T.lt,md:G.p,json:G.a,xml:G.g,jsx:G.p,csv:B,url:B}[ext]||T.fnt;
                      const extBg={pdf:G.rb,docx:BL,txt:S.bg,md:G.pb,json:G.ab,xml:G.gb,jsx:G.pb,csv:BL,url:BL}[ext]||S.bg;
                      return(
                        <tr key={doc.id}>
                          <td style={{minWidth:160}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:30,height:30,borderRadius:7,background:extBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:extCol,flexShrink:0,letterSpacing:.3}}>
                                {ext==="url"?"URL":ext.toUpperCase()}
                              </div>
                              <span style={{fontWeight:600,fontSize:12.5,whiteSpace:"nowrap"}}>{doc.name}</span>
                            </div>
                          </td>
                          <td>
                            <span style={{fontSize:11,background:extBg,color:extCol,border:`1px solid ${extCol}30`,borderRadius:5,padding:"2px 8px",fontWeight:700}}>{ext.toUpperCase()}</span>
                          </td>
                          <td style={{minWidth:220}}>
                            <div style={{display:"flex",gap:4}}>
                              {(()=>{
                                const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                                const allCats=[...new Set(kbDocs.map(d=>d.category).filter(Boolean))];
                                const saveCat=async(id,patch)=>{
                                  setKbDocs(d=>d.map(x=>x.id===id?{...x,...patch}:x));
                                  if(orgId)try{await fetch(API_URL+"/api/kb/"+id,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({org_id:orgId,...patch})});}catch(e){}
                                };
                                return(<>
                                  <input list={`cat-list-${doc.id}`} style={{fontSize:11,border:`1px solid ${S.brd}`,borderRadius:6,padding:"4px 7px",background:S.w,color:T.mid,width:90,minWidth:0}} placeholder="Category…"
                                    value={doc.category||""} onChange={e=>saveCat(doc.id,{category:e.target.value})}/>
                                  <datalist id={`cat-list-${doc.id}`}>{["Products","Policies","Support","Billing","Technical","FAQs","Legal","Marketing","HR","Other",...allCats].filter((v,i,a)=>a.indexOf(v)===i&&v).map(c=><option key={c} value={c}/>)}</datalist>
                                  <input style={{fontSize:11,border:`1px solid ${S.brd}`,borderRadius:6,padding:"4px 7px",background:S.w,color:T.mid,width:80,minWidth:0}} placeholder="Sub-cat…"
                                    value={doc.sub_category||""} onChange={e=>saveCat(doc.id,{sub_category:e.target.value})}/>
                                  <input style={{fontSize:11,border:`1px solid ${S.brd}`,borderRadius:6,padding:"4px 7px",background:S.w,color:T.mid,width:70,minWidth:0}} placeholder="Tertiary…"
                                    value={doc.tertiary||""} onChange={e=>saveCat(doc.id,{tertiary:e.target.value})}/>
                                </>);
                              })()}
                            </div>
                          </td>
                          <td><span style={{fontSize:12,color:T.fnt,whiteSpace:"nowrap"}}>{doc.size}</span></td>
                          <td><span className="mono" style={{fontSize:12}}>{doc.status==="indexed"?doc.chunks:"—"}</span></td>
                          <td>
                            {doc.status==="indexed"?(
                              <span style={{background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,borderRadius:5,padding:"2px 9px",fontSize:11,fontWeight:700}}>Indexed</span>
                            ):doc.status==="processing"?(
                              <div style={{display:"flex",alignItems:"center",gap:5}}><Spinner/><span style={{fontSize:12,color:B}}>Processing…</span></div>
                            ):(
                              <div style={{display:"flex",alignItems:"center",gap:5}}><Spinner/><span style={{fontSize:12,color:G.a}}>Indexing…</span></div>
                            )}
                          </td>
                          <td><span style={{fontSize:12,color:T.fnt,whiteSpace:"nowrap"}}>{doc.updated}</span></td>
                          <td>
                            <div style={{display:"flex",gap:5}}>
                              <button className="btn bo sm" style={{fontSize:10.5,padding:"3px 8px"}} onClick={()=>setKbDocs(d=>d.map(x=>x.id===doc.id?{...x,status:"indexing",chunks:0}:x))}><Ic n="reload" s={10}/> Re-index</button>
                              <button className="btn bd sm" style={{padding:"3px 7px"}} onClick={()=>removeDoc(doc.id)}><Ic n="trash" s={11}/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Bottom two-column grid ── */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

            {/* Accepted formats */}
            <div className="card" style={{padding:16}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Accepted Formats</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["PDF",".pdf","Policies, guides","#FEF2F2","#DC2626"],["Word",".docx","Documentation","#EFF6FF","#2563EB"],["Text",".txt .md","Plain & markdown","#F5F3FF","#7C3AED"],["JSON",".json","Structured data & FAQs","#FFFBEB","#D97706"],["XML",".xml","Data feeds, sitemaps","#F0FDF4","#059669"],["JSX",".jsx","React component docs","#F5F3FF","#7C3AED"],["CSV",".csv","Tabular data","#EFF6FF","#0891B2"],["Web URL","https://…","Auto-crawled pages","#EFF6FF","#2563EB"]].map(([t,ext,hint,bg,col])=>(
                  <div key={t} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"8px 10px",background:bg,borderRadius:8}}>
                    <div style={{width:28,height:28,borderRadius:6,background:"rgba(255,255,255,.7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7.5,fontWeight:800,color:col,flexShrink:0}}>{ext.split(" ")[0].replace(/[.:/]/g,"").toUpperCase()||"URL"}</div>
                    <div><p style={{fontSize:12,fontWeight:700,color:col}}>{t}</p><p style={{fontSize:10.5,color:T.lt,lineHeight:1.4}}>{hint}</p></div>
                  </div>
                ))}
              </div>
            </div>

            {/* File processing options */}
            <div className="card" style={{padding:16}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>File Processing Options</p>
              <p style={{fontSize:12,color:T.lt,marginBottom:12,lineHeight:1.5}}>Applied automatically when files are uploaded. These settings clean and normalise content before indexing.</p>
              {[
                ["strip_html","Strip HTML tags","Removes all HTML/XML markup, preserving visible text only",true],
                ["clean_ws","Clean whitespace & formatting","Normalises multiple spaces, tabs, and inconsistent line breaks",true],
                ["split_sections","Split by headings","Creates separate chunks per heading for more precise retrieval",true],
                ["extract_tables","Extract tables as structured rows","Converts HTML or DOCX tables into readable text rows",true],
                ["remove_headers","Remove repeating headers & footers","Strips page headers and footers repeated across PDF pages",false],
                ["deduplicate","Deduplicate similar paragraphs","Removes near-identical blocks of text within a document",false],
              ].map(([k,l,hint,def])=>(
                <label key={k} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:11,cursor:"pointer"}}>
                  <input type="checkbox" defaultChecked={def} style={{accentColor:B,width:13,height:13,marginTop:2,flexShrink:0}}/>
                  <div><p style={{fontSize:12.5,fontWeight:600,color:T.ink,marginBottom:1}}>{l}</p><p style={{fontSize:11,color:T.fnt,lineHeight:1.45}}>{hint}</p></div>
                </label>
              ))}
              <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${S.brd}`,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["Total docs",kbDocs.length],["Indexed",kbDocs.filter(d=>d.status==="indexed").length],["Total chunks",kbDocs.filter(d=>d.status==="indexed").reduce((s,d)=>s+d.chunks,0)],["Embed model","text-3-large"]].map(([l,v])=>(
                  <div key={l} style={{padding:"9px 11px",background:S.bg,borderRadius:7,border:`1px solid ${S.brd}`}}>
                    <p style={{fontSize:10.5,color:T.fnt,marginBottom:2}}>{l}</p>
                    <p style={{fontSize:15,fontWeight:800,color:T.ink}}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      )}


      {/* ── TEST TAB ── */}
      {tab==="test"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div className="card" style={{display:"flex",flexDirection:"column",height:520}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:G.g,animation:"blink 2s infinite"}}/>
              <h3 style={{fontSize:13,fontWeight:700}}>Live Test — AI Assistant</h3>
              <span style={{marginLeft:"auto",fontSize:11,background:BL,color:B,border:`1px solid ${BM}`,borderRadius:5,padding:"2px 8px",fontWeight:600}}>{MODELS.find(m=>m.id===model)?.label}</span>
              <button className="btn bo sm" style={{fontSize:10,padding:"2px 8px"}} onClick={()=>setTestMsgs([{role:"bot",text:greeting||"Hi there! 👋 How can I help you today?"}])}>Reset</button>
            </div>
            <div ref={testMsgRef} style={{flex:1,overflowY:"auto",padding:14,display:"flex",flexDirection:"column",gap:10}}>
              <div style={{flex:1}}/>
              {testMsgs.map((m,i)=>(
                <div key={i} style={{display:"flex",justifyContent:m.role==="visitor"?"flex-end":m.role==="system"?"center":"flex-start",gap:8,alignItems:"flex-start"}}>
                  {m.role==="system"&&<span style={{fontSize:11,color:G.a,background:G.ab,border:`1px solid ${G.abd}`,borderRadius:20,padding:"3px 12px"}}>{m.text}</span>}
                  {m.role!=="system"&&<>
                    {m.role==="bot"&&<div style={{width:26,height:26,borderRadius:7,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="bot" s={12} c={B}/></div>}
                    <div style={{maxWidth:"78%",padding:"9px 12px",borderRadius:m.role==="visitor"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:m.role==="visitor"?B:S.bg,border:m.role==="visitor"?"none":`1px solid ${S.brd}`,color:m.role==="visitor"?"#fff":T.ink,fontSize:13,lineHeight:1.55,whiteSpace:"pre-wrap"}}>{m.text}</div>
                  </>}
                </div>
              ))}
              {testLoading&&<div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                <div style={{width:26,height:26,borderRadius:7,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="bot" s={12} c={B}/></div>
                <div style={{padding:"9px 14px",borderRadius:"14px 14px 14px 4px",background:S.bg,border:`1px solid ${S.brd}`,display:"flex",gap:4,alignItems:"center"}}>
                  {[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:T.fnt,opacity:.6,animation:`typingDot 1.2s ${i*0.2}s ease-in-out infinite`}}/>)}
                </div>
              </div>}
            </div>
            <div style={{padding:"10px 12px",borderTop:`1px solid ${S.brd}`,display:"flex",gap:8}}>
              <input className="inp" placeholder="Type a test message…" style={{flex:1}} value={testInp} onChange={e=>setTestInp(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();sendTestMsg();}}} disabled={testLoading}/>
              <button className="btn bp sm" onClick={sendTestMsg} disabled={testLoading||!testInp.trim()}><Ic n="arr" s={12} c="#fff"/></button>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card" style={{padding:16}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Current Config Preview</p>
              {[["Model",MODELS.find(m=>m.id===model)?.label],["Tone",TONES.find(t=>t[0]===tone)?.[1]],["Language",LANGS.find(l=>l[0]===lang)?.[1]],["Confidence",`${confidence}%`],["Max turns",maxTurns],["AI silence after handoff",`${handoffInactivityTimeout}s`]].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${S.bg}`}}>
                  <span style={{fontSize:12,color:T.fnt}}>{l}</span><span style={{fontSize:12.5,fontWeight:600,color:T.ink}}>{v}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{padding:16}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Quick Test Prompts</p>
              {["What are your shipping times?","How do I return an item?","I need to speak to a human","What payment methods do you accept?","Can you help me with my order?"].map((q,i)=>(
                <div key={i} onClick={()=>{setTestInp(q);}} style={{padding:"8px 11px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:7,marginBottom:6,cursor:"pointer",fontSize:12.5,color:T.mid,transition:"all .15s"}} onMouseEnter={e=>{e.currentTarget.style.background=BL;e.currentTarget.style.borderColor=BM;e.currentTarget.style.color=B;}} onMouseLeave={e=>{e.currentTarget.style.background=S.bg;e.currentTarget.style.borderColor=S.brd;e.currentTarget.style.color=T.mid;}}>
                  {q}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── HANDOFF RULES TAB ── */}
      {tab==="handoff"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Handoff Trigger Phrases</p>
              <p style={{fontSize:12.5,color:T.lt,marginBottom:10}}>The bot automatically offers human handoff when these phrases are detected.</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                {triggers.map((t,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:4,background:G.ab,border:`1px solid ${G.abd}`,borderRadius:20,padding:"4px 11px"}}>
                    <span style={{color:G.a,fontWeight:500,fontSize:12}}>{t}</span>
                    <button onClick={()=>removeTrigger(i)} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",color:G.a}}><Ic n="x" s={10} c={G.a}/></button>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:8}}>
                <input className="inp" placeholder="Add phrase…" value={trigInput} onChange={e=>setTrigInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTrigger()} style={{flex:1}}/>
                <button className="btn bp sm" onClick={addTrigger}>Add</button>
              </div>
            </div>
            <div className="card" style={{padding:18}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7}}>Pre-Handoff Form</p>
                <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer"}}>
                  <span style={{fontSize:12,color:T.fnt}}>Enable</span>
                  <div onClick={()=>setPreChatEnabled(v=>!v)} style={{width:36,height:20,borderRadius:10,background:preChatEnabled?B:S.brd,cursor:"pointer",position:"relative",transition:"background .2s"}}>
                    <div style={{position:"absolute",top:2,left:preChatEnabled?18:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                  </div>
                </label>
              </div>
              <p style={{fontSize:12,color:T.lt,marginBottom:12}}>Collect visitor details before connecting them to an agent.</p>
              <div style={{display:"flex",flexDirection:"column",gap:8,opacity:preChatEnabled?1:.45,pointerEvents:preChatEnabled?"auto":"none"}}>
                {preChatFields.map((f,i)=>(
                  <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:S.bg,borderRadius:8,border:`1px solid ${S.brd}`}}>
                    <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",flex:1}}>
                      <input type="checkbox" checked={f.enabled} onChange={e=>setPreChatFields(fs=>fs.map((x,j)=>j===i?{...x,enabled:e.target.checked}:x))} style={{accentColor:B,width:14,height:14}}/>
                      <span style={{fontSize:13,fontWeight:600,color:f.enabled?T.ink:T.fnt}}>{f.label}</span>
                    </label>
                    <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
                      <input type="checkbox" checked={f.required} disabled={!f.enabled} onChange={e=>setPreChatFields(fs=>fs.map((x,j)=>j===i?{...x,required:e.target.checked}:x))} style={{accentColor:G.r,width:13,height:13}}/>
                      <span style={{fontSize:11,color:T.fnt}}>Required</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>SMS Alert Routing</p>
              {[["online_only","Send SMS only when agents are offline","Minimise alerts — agents join existing chat"],["always","Always send SMS on handoff request","Agents always notified, regardless of status"],["primary","Send to primary agent only","Single point of contact (Starter plan)"]].map(([v,lbl,hint])=>(
                  <label key={v} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:12,cursor:"pointer"}}>
                    <input type="radio" name="routing" value={v} checked={routing===v} onChange={()=>setRouting(v)} style={{accentColor:B,marginTop:2,flexShrink:0}}/>
                    <div><p style={{fontSize:12.5,fontWeight:600}}>{lbl}</p><p style={{fontSize:11,color:T.fnt}}>{hint}</p></div>
                  </label>
                ))}
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card" style={{padding:18}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7}}>SMS Preview</p>
                
              </div>
              <div style={{background:"#1a1a2e",borderRadius:16,padding:16,maxWidth:280}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <div style={{width:34,height:34,borderRadius:"50%",background:"#2d2d44",display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n="sms" s={16} c={CS}/></div>
                  <div><p style={{color:"#fff",fontSize:13,fontWeight:600}}>HINDLE</p><p style={{color:"#666",fontSize:11}}>SMS Alert</p></div>
                </div>
                <div style={{background:"#2d2d44",borderRadius:12,padding:"11px 13px"}}>
                  <p style={{color:"#e2e8f0",fontSize:12.5,lineHeight:1.65}}>
                    Your Company Support<br/>
                    New handoff from <strong style={{color:"#fff"}}>Visitor Name</strong><br/>
                    Page: /your-page<br/>
                    Tap to join:<br/>
                    <span style={{color:CS}}>https://chat.hindle.ai/m/[token]</span>
                  </p>
                  <p style={{color:T.mid,fontSize:10.5,marginTop:8}}>Expires in 30 min · One-time use</p>
                </div>
              </div>
            </div>
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Handoff Stats (7d)</p>
              {[["Total handoffs","38"],["SMS alerts sent","31"],["Agents responded","29"],["Avg response time","2m 14s"],["Magic links clicked","27 / 31"]].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${S.bg}`}}>
                  <span style={{fontSize:12.5,color:T.fnt}}>{l}</span>
                  <span style={{fontSize:13,fontWeight:700,color:T.ink}}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TrialBanner=({role,tenantTrialDay})=>{
  const [dismissed,setDismissed]=useState(false);
  // Only show for tenant_admin on a trial (day 1-14)
  const day=tenantTrialDay;
  if(dismissed||role==="super_admin"||!day||day<1||day>14) return null;
  const isLast=day>=14;const isClose=day>=12;
  return(
    <div style={{background:isLast?G.r:isClose?G.a:B,color:"#fff",padding:"8px 18px",display:"flex",alignItems:"center",gap:10,flexShrink:0,fontSize:12.5}}>
      <Ic n="bell" s={14} c="#fff"/>
      <p style={{flex:1,fontWeight:500}}><strong>{isLast?"Last day of trial!":isClose?`Trial ends in ${14-day} days`:`${14-day} days remaining in trial`}</strong> — Add a payment method to keep access.</p>
      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        
        <span style={{fontSize:11,opacity:.8}}>Reminders sent</span>
        <button className="btn sm" style={{background:"rgba(255,255,255,.2)",color:"#fff",border:"1px solid rgba(255,255,255,.3)",fontSize:11}}>Add Payment</button>
        <button onClick={()=>setDismissed(true)} style={{background:"transparent",border:"none",cursor:"pointer",color:"rgba(255,255,255,.7)",display:"flex",padding:2}}><Ic n="x" s={13}/></button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// DASHBOARD SHELL
// ══════════════════════════════════════════════════════════════
const Dashboard=({role,goSignIn,goSetup,config,setConfig,tokenConvId,tokenTicketId})=>{
  const [page,setPage]=useState(tokenConvId?"convos":(tokenTicketId?"helpdesk":"dash"));
  const [arPrefill,setArPrefill]=useState(null);
  const [collapsed,setCollapsed]=useState(false);
  const [agents,setAgents]=useState(()=>{try{const s=localStorage.getItem("hindle_agents");return s?JSON.parse(s):[];}catch(e){return [];}});
  useEffect(()=>{try{localStorage.setItem("hindle_agents",JSON.stringify(agents));}catch(e){}},  [agents]);
  const [alertLog,setAlertLog]=useState([]);
  const [conversations,setConversations]=useState([]);
  const [tenants,setTenants]=useState([]);
  const [apiError,setApiError]=useState(null);
  const [apiLoading,setApiLoading]=useState(true);
  const plan=role==="super_admin"?"professional":"starter";

  // ── Fetch all data on mount ──
  const loadRef=useRef(null);
  const lastLoadRef=useRef(0);
  useEffect(()=>{
    let retryTimer=null,cancelled=false,attempt=0;
    const load=async(silent)=>{
      if(!silent) setApiLoading(true);
      try{
        // Get org_id from localStorage for tenant users
        let orgId=null;
        try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
        const isSuper=role==="super_admin";
        console.log("[Dashboard] load — orgId="+orgId+" role="+role);
        const agentQ=orgId&&!isSuper?"/api/agents?org_id="+orgId:"/api/agents";
        const alertQ=isSuper?"/api/alert-log?super=1":orgId?"/api/alert-log?org_id="+orgId:"/api/alert-log";
        const convQ=isSuper?"/api/conversations":orgId?"/api/conversations?org_id="+orgId:"/api/conversations";
        console.log("[Dashboard] convQ="+convQ);
        const [agentsData,alertData,convosData,tenantsData]=await Promise.all([
          api.get(agentQ),
          api.get(alertQ),
          api.get(convQ),
          isSuper?api.get("/api/tenants"):Promise.resolve([]),
        ]);
        console.log("[Dashboard] conversations loaded: "+(convosData?.length||0));
        setAgents(agentsData);
        setAlertLog(alertData);
        setConversations(convosData);
        setTenants(tenantsData);
        setApiError(null);
        attempt=0;
        lastLoadRef.current=Date.now();
      }catch(e){
        // Silent refreshes keep the data already on screen; only the first
        // load shows the error page, and it retries itself.
        if(!silent){
          setApiError(e.message);
          if(!cancelled&&attempt<5){attempt++;retryTimer=setTimeout(()=>load(false),3000*attempt);}
        }
      }finally{
        if(!silent) setApiLoading(false);
      }
    };
    loadRef.current=load;
    load(false);
    // Coming back to the tab or reconnecting → refresh quietly.
    let hiddenAt=0;
    const onVis=()=>{
      if(document.visibilityState==="hidden"){hiddenAt=Date.now();return;}
      if(Date.now()-Math.max(hiddenAt,lastLoadRef.current)>60000) load(true);
    };
    const onOnline=()=>load(true);
    document.addEventListener("visibilitychange",onVis);
    window.addEventListener("online",onOnline);
    window.addEventListener("focus",onVis);
    return()=>{cancelled=true;clearTimeout(retryTimer);document.removeEventListener("visibilitychange",onVis);window.removeEventListener("online",onOnline);window.removeEventListener("focus",onVis);};
  },[]);

  // ── Agent CRUD ──
  const createAgent=async(data)=>{
    const a=await api.post("/api/agents",data);
    setAgents(p=>[...p,a]);
    return a;
  };
  const updateAgent=async(id,data)=>{
    const a=await api.patch(`/api/agents/${id}`,data);
    setAgents(p=>p.map(x=>x.id===id?a:x));
    return a;
  };
  const deleteAgent=async(id)=>{
    await api.del(`/api/agents/${id}`);
    setAgents(p=>p.filter(x=>x.id!==id));
  };

  // ── Conversation CRUD ──
  const updateConversation=async(id,data)=>{
    const c=await api.patch(`/api/conversations/${id}`,data);
    setConversations(p=>p.map(x=>x.id===id?c:x));
    return c;
  };
  const refreshConversations=async()=>{
    try{
      let orgId=null;
      try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
      const isSuper=role==="super_admin";
      const convQ=isSuper?"/api/conversations?super=1":orgId?"/api/conversations?org_id="+orgId:"/api/conversations";
      const data=await api.get(convQ);
      setConversations(data);
    }catch(e){}
  };

  // ── Alert log ──
  const addAlert=async(data)=>{
    const a=await api.post("/api/alert-log",data);
    setAlertLog(p=>[a,...p]);
    return a;
  };

  const pageEl=()=>{
    if(apiLoading) return(
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,color:T.fnt}}>
        <Spinner size={28}/><p style={{fontSize:14,fontWeight:600}}>Connecting to database…</p>
      </div>
    );
    if(apiError) return(
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:40}}>
        <div style={{width:48,height:48,borderRadius:12,background:G.rb,border:`1px solid ${G.rbd}`,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n="x" s={22} c={G.r}/></div>
        <p style={{fontSize:15,fontWeight:700,color:T.ink}}>API Connection Error</p>
        <p style={{fontSize:13,color:T.fnt,textAlign:"center",maxWidth:400}}>{apiError}</p>
        <p style={{fontSize:12,color:T.fnt}}>Retrying automatically…</p>
        <button className="btn bp" onClick={()=>loadRef.current&&loadRef.current(false)}><Ic n="reload" s={13} c="#fff"/> Retry now</button>
      </div>
    );
    if(role==="super_admin"){
      if(page==="dash")         return <PSuperDash alertLog={alertLog} tenants={tenants}/>;
      if(page==="tenants")      return <PTenants tenants={tenants} setTenants={setTenants}/>;
      if(page==="trials")       return <TrialMonitor config={config} tenants={tenants}/>;
      if(page==="users")        return <PUsers config={config}/>;
      if(page==="alertlog")     return <PAlertLog alertLog={alertLog} setAlertLog={setAlertLog}/>;
      if(page==="audit")        return <div style={{padding:24}}><h2 style={{fontSize:18,fontWeight:800,marginBottom:18}}>Audit Log</h2><PAuditLog/></div>;
      if(page==="integrations") return <PIntegrations config={config} setConfig={setConfig} role={role}/>;
      if(page==="sso")          return <PSso tenants={tenants}/>;
      if(page==="settings")     return <PSettingsSuperAdmin/>;
    } else {
      if(page==="dash")         return <PDash alertLog={alertLog} agents={agents} conversations={conversations} setPage={(p)=>{if(p==="_setup"){goSetup&&goSetup();}else setPage(p);}} config={config} role={role}/>;
      if(page==="convos")       return <PConversations agents={agents} alertLog={alertLog} setAlertLog={setAlertLog} conversations={conversations} setConversations={setConversations} updateConversation={updateConversation} addAlert={addAlert} plan={plan} autoOpenConvId={tokenConvId} refreshConversations={refreshConversations} config={config}/>;
      if(page==="helpdesk-dash") return <PHelpdeskDashboard config={config}/>;
      if(page==="helpdesk")     return <PHelpdesk config={config} autoOpenTicketId={tokenTicketId} onRequestAccess={(prefill)=>{setArPrefill(prefill);setPage("access-requests");}}/>;
      if(page==="access-requests") return <PAccessRequests config={config} prefill={arPrefill} clearPrefill={()=>setArPrefill(null)}/>;
      if(page==="agents")       return <PAgents agents={agents} createAgent={createAgent} updateAgent={updateAgent} deleteAgent={deleteAgent}/>;
      if(page==="users")        return <PUsers config={config}/>;
      if(page==="chatbot")      return <PChatbot/>;
      if(page==="install")      return <PInstall/>;
      if(page==="crm")          return <PCrmIntegrations/>;
      if(page==="alertlog")     return <PAlertLog alertLog={alertLog} setAlertLog={setAlertLog}/>;
      if(page==="analytics")    return <PAnalytics/>;
      if(page==="integrations") return <PIntegrations config={config} setConfig={setConfig} role={role}/>;
      if(page==="faq")          return <PFaqManager config={config}/>;
      if(page==="sso")          return role!=="agent"?<PSso/>:null;
      if(page==="helpdesk-config") return role!=="agent"?<PHelpdeskConfig/>:null;
      if(page==="settings")     return <PSettingsTenant/>;
    }
    return null;
  };

  return(
    <div style={{display:"flex",height:"100vh",overflow:"hidden",flexDirection:"column"}}>
      <TrialBanner role={role} tenantTrialDay={(()=>{try{const c=localStorage.getItem("hindle_chatbot_config");return c?JSON.parse(c).trial_day||null:null;}catch(e){return null;}})()}/>
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        <Sidebar role={role} active={page} onNav={setPage} collapsed={collapsed} onToggle={()=>setCollapsed(c=>!c)} goSignIn={goSignIn}/>
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:S.bg}}>
          <div className="topbar">
            {collapsed&&<button onClick={()=>setCollapsed(false)} className="iconbtn" title="Expand menu"><Ic n="chR" s={15}/></button>}
            <div style={{flex:1,minWidth:0}}>
              <h1 style={{fontSize:17,fontWeight:700,letterSpacing:-.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{role==="super_admin"&&page==="dash"?"Platform Overview":(PAGE_TITLES[page]||"Home")}</h1>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {role!=="super_admin"&&role!=="agent"&&<button className="btn bo sm" onClick={goSetup}><Ic n="wiz" s={12}/> Setup wizard</button>}
              <HelpButton page={page}/>
              <button className="iconbtn" title={THEME_MODE==="dark"?"Switch to light mode":"Switch to dark mode"} onClick={()=>setThemePref(THEME_MODE==="dark"?"light":"dark")}><Ic n={THEME_MODE==="dark"?"sun":"moon"} s={16}/></button>
              <div style={{width:1,height:22,background:S.brd,margin:"0 2px"}}/>
              <Av name={(()=>{try{const p=localStorage.getItem("hindle_admin_profile");const cfg=localStorage.getItem("hindle_config");const cfgP=cfg?JSON.parse(cfg):null;return p?JSON.parse(p).name||(cfgP?.name||"User"):(cfgP?.name||"User");}catch(e){return "User";}})() } size={30} col={role==="super_admin"?G.r:B}/>
            </div>
          </div>
          <div style={{flex:1,overflow:"hidden"}} key={page} className="ai">{pageEl()}</div>
        </div>
      </div>
    </div>
  );
};
// ══════════════════════════════════════════════════════════════
// ERROR BOUNDARY — prevents blank screen on crash
// ══════════════════════════════════════════════════════════════
class ErrorBoundary extends React.Component {
  constructor(props){super(props);this.state={err:null};}
  static getDerivedStateFromError(e){return{err:e};}
  componentDidCatch(e,info){console.error("Hindle App Error:",e,info);}
  render(){
    if(this.state.err){
      return(
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"DM Sans,sans-serif",background:S.bg}}>
          <div style={{textAlign:"center",padding:40}}>
            <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
            <h2 style={{fontSize:22,fontWeight:700,marginBottom:8,color:T.ink}}>Something went wrong</h2>
            <p style={{fontSize:14,color:T.lt,marginBottom:24}}>The application encountered an error. Please clear your browser cache and reload.</p>
            <button onClick={()=>{try{clearHindleStorage();}catch(e){}window.location.reload();}} style={{background:"#2563EB",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Clear Cache &amp; Reload</button>
            <details style={{marginTop:20,textAlign:"left",fontSize:12,color:T.fnt}}><summary style={{cursor:"pointer"}}>Error details</summary><pre style={{marginTop:8,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{String(this.state.err)}</pre></details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ══════════════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// MOBILE AGENT UI — phone-optimised view for magic link handoffs
// ══════════════════════════════════════════════════════════════
const PMobileAgent=({convId,orgId,agentName,agentEmail,goDesktop})=>{
  const [conv,setConv]=useState(null);
  const [msgs,setMsgs]=useState([]);
  const [input,setInput]=useState("");
  const [sending,setSending]=useState(false);
  const [loading,setLoading]=useState(true);
  const [claimed,setClaimed]=useState(false);
  const [tab,setTab]=useState("chat"); // chat | info
  const [note,setNote]=useState("");
  const [sendingNote,setSendingNote]=useState(false);
  const [error,setError]=useState(null);
  const [showQuick,setShowQuick]=useState(false);
  const [quickReplies]=useState(()=>{try{const q=localStorage.getItem("hindle_quick_replies");return q?JSON.parse(q):[];}catch(e){return [];}});
  const bottomRef=React.useRef(null);
  const pollRef=React.useRef(null);

  const loadConv=async()=>{
    if(!convId||!orgId) return;
    try{
      const [cRes,mRes]=await Promise.all([
        fetch(API_URL+"/api/conversations/"+convId),
        fetch(API_URL+"/api/conversations/"+convId+"/messages"),
      ]);
      if(cRes.ok) setConv(await cRes.json());
      if(mRes.ok) setMsgs(await mRes.json());
    }catch(e){setError("Connection error");}
    setLoading(false);
  };

  useEffect(()=>{
    loadConv();
    pollRef.current=setInterval(loadConv,6000);
    return()=>clearInterval(pollRef.current);
  },[convId,orgId]);

  useEffect(()=>{
    if(bottomRef.current) bottomRef.current.scrollIntoView({behavior:"smooth"});
  },[msgs]);

  const claim=async()=>{
    try{
      await fetch(API_URL+"/api/conversations/"+convId,{method:"PATCH",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({status:"claimed",claimed_by_name:agentName||"Agent",claimed_by_id:null})});
      setClaimed(true);
      setConv(c=>c?{...c,status:"claimed",claimed_by_name:agentName||"Agent"}:c);
    }catch(e){}
  };

  const send=async()=>{
    if(!input.trim()||sending) return;
    const text=input.trim();
    setInput("");setSending(true);
    try{
      const r=await fetch(API_URL+"/api/conversations/"+convId+"/messages",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({type:"agent",sender:agentName||"Agent",content:text})});
      if(r.ok){
        const m=await r.json();
        setMsgs(p=>[...p,m]);
        if(!claimed) claim();
        // Update conv timestamp
        await fetch(API_URL+"/api/conversations/"+convId,{method:"PATCH",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({updated_at:new Date().toISOString()})}).catch(()=>{});
      }
    }catch(e){}
    setSending(false);
  };

  const sendNote=async()=>{
    if(!note.trim()||sendingNote) return;
    const text=note.trim();
    setNote("");setSendingNote(true);
    try{
      const r=await fetch(API_URL+"/api/conversations/"+convId+"/messages",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({type:"note",sender:agentName||"Agent",content:text})});
      if(r.ok){const m=await r.json();setMsgs(p=>[...p,m]);}
    }catch(e){}
    setSendingNote(false);
  };

  const resolve=async()=>{
    if(!window.confirm("Mark this conversation as resolved?")) return;
    try{
      await fetch(API_URL+"/api/conversations/"+convId,{method:"PATCH",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({status:"resolved"})});
      setConv(c=>c?{...c,status:"resolved"}:c);
    }catch(e){}
  };

  const fmt=(ts)=>{try{return new Date(ts).toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"});}catch(e){return "";}};
  const statusCol={open:G.a,claimed:G.g,handoff:B,parked:T.fnt,resolved:T.fnt}[conv?.status]||T.fnt;

  if(loading) return(
    <div style={{height:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,background:S.bg}}>
      <div style={{width:36,height:36,border:`3px solid ${BM}`,borderTop:`3px solid ${B}`,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
      <p style={{fontSize:14,color:T.fnt}}>Loading conversation…</p>
    </div>
  );

  if(error||!conv) return(
    <div style={{height:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:24,background:S.bg}}>
      <Ic n="x" s={32} c={G.r}/>
      <p style={{fontSize:16,fontWeight:700,color:T.ink}}>Conversation not found</p>
      <p style={{fontSize:13,color:T.fnt,textAlign:"center"}}>{error||"This link may have expired or the conversation was deleted."}</p>
      <button className="btn bo sm" onClick={()=>window.location.href="/"}>Go to Dashboard</button>
    </div>
  );

  const isResolved=conv.status==="resolved";

  return(
    <div style={{height:"100dvh",display:"flex",flexDirection:"column",background:S.bg,fontFamily:"'DM Sans',sans-serif",maxWidth:500,margin:"0 auto",position:"relative"}}>
      {/* Header */}
      <div style={{background:S.w,borderBottom:`1px solid ${S.brd}`,padding:"12px 16px",flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <Av name={conv.visitor_name||"Visitor"} size={36} col={B}/>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:15,fontWeight:700,color:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{conv.visitor_name||"Visitor"}</p>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:statusCol,flexShrink:0}}/>
              <p style={{fontSize:12,color:statusCol,fontWeight:600,textTransform:"capitalize"}}>{conv.status}</p>
              {conv.priority&&conv.priority!=="normal"&&<span style={{fontSize:10,fontWeight:700,color:conv.priority==="urgent"?G.r:G.a,background:conv.priority==="urgent"?G.rb:G.ab,borderRadius:4,padding:"1px 6px",textTransform:"uppercase"}}>{conv.priority}</span>}
            </div>
          </div>
          {/* Claim / Resolve buttons */}
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            {!claimed&&conv.status!=="claimed"&&conv.status!=="resolved"&&(
              <button onClick={claim} style={{background:G.g,color:"#fff",border:"none",borderRadius:8,padding:"7px 12px",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>Claim</button>
            )}
            {!isResolved&&(
              <button onClick={resolve} style={{background:S.bg,color:T.fnt,border:`1px solid ${S.brd}`,borderRadius:8,padding:"7px 10px",fontSize:12.5,fontWeight:600,cursor:"pointer"}}>✓ Resolve</button>
            )}
          </div>
        </div>
        {/* Tab bar */}
        <div style={{display:"flex",gap:4}}>
          {[["chat","💬 Chat"],["info","👤 Visitor"],["note","🔒 Note"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{padding:"5px 12px",borderRadius:6,fontSize:12.5,fontWeight:600,cursor:"pointer",flex:1,
              background:tab===t?B:"transparent",color:tab===t?"#fff":T.fnt,
              border:tab===t?`1px solid ${B}`:`1px solid ${S.brd}`}}>{l}</button>
          ))}
          {goDesktop&&<button onClick={goDesktop} style={{padding:"5px 8px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",background:S.bg,color:T.fnt,border:`1px solid ${S.brd}`,flexShrink:0}} title="Open full dashboard">🖥</button>}
        </div>
      </div>

      {/* Chat tab */}
      {tab==="chat"&&(
        <>
          <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
            {msgs.length===0&&<p style={{fontSize:13,color:T.fnt,textAlign:"center",marginTop:32}}>No messages yet</p>}
            {msgs.map((m,i)=>{
              const isAgent=m.type==="agent";
              const isBot=m.type==="bot";
              const isNote=m.type==="note";
              const isSystem=m.type==="system";
              if(isSystem) return <p key={i} style={{fontSize:11,color:T.fnt,textAlign:"center",padding:"2px 0"}}>{m.content}</p>;
              if(isNote) return(
                <div key={i} style={{alignSelf:"center",background:G.ab,border:`1px solid ${G.abd}`,borderRadius:10,padding:"8px 12px",maxWidth:"85%",fontSize:13}}>
                  <p style={{fontSize:10.5,fontWeight:700,color:G.a,marginBottom:3}}>🔒 Internal note — {m.sender}</p>
                  <p style={{color:T.ink,lineHeight:1.5}}>{m.content}</p>
                </div>
              );
              return(
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:isAgent||isBot?"flex-end":"flex-start"}}>
                  <div style={{
                    background:isAgent?B:isBot?BL:S.w,
                    color:isAgent?"#fff":T.ink,
                    borderRadius:isAgent?"16px 16px 4px 16px":"16px 16px 16px 4px",
                    padding:"10px 14px",maxWidth:"82%",fontSize:14,lineHeight:1.55,
                    border:isBot?`1px solid ${BM}`:"none",
                    boxShadow:"0 1px 3px rgba(0,0,0,.08)",
                  }}>
                    {(isBot)&&<p style={{fontSize:10,fontWeight:700,color:B,marginBottom:3,opacity:.7}}>AI</p>}
                    {m.content}
                  </div>
                  <p style={{fontSize:10,color:T.fnt,marginTop:2,marginLeft:2,marginRight:2}}>{fmt(m.created_at)}</p>
                </div>
              );
            })}
            <div ref={bottomRef}/>
          </div>
          {/* Compose */}
          {!isResolved?(
            <div style={{background:S.w,borderTop:`1px solid ${S.brd}`,flexShrink:0,paddingBottom:"max(10px, env(safe-area-inset-bottom))"}}>
              {/* Quick replies tray */}
              {quickReplies.length>0&&!showQuick&&(
                <div style={{padding:"6px 10px 0"}}>
                  <button onClick={()=>setShowQuick(true)} style={{display:"flex",alignItems:"center",gap:5,background:BL,border:`1px solid ${BM}`,borderRadius:8,padding:"5px 10px",fontSize:12,fontWeight:600,color:B,cursor:"pointer"}}>
                    <Ic n="zap" s={12} c={B}/> Quick Replies
                    <span style={{background:B,color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:10,fontWeight:700}}>{quickReplies.length}</span>
                  </button>
                </div>
              )}
              {showQuick&&(
                <div style={{borderBottom:`1px solid ${S.brd}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 12px 4px"}}>
                    <p style={{fontSize:11.5,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5}}>Quick Replies</p>
                    <button onClick={()=>setShowQuick(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:2}}><Ic n="x" s={14}/></button>
                  </div>
                  <div style={{overflowX:"auto",display:"flex",gap:8,padding:"4px 12px 10px",scrollbarWidth:"none"}}>
                    {quickReplies.map((r,i)=>(
                      <button key={i} onClick={()=>{setInput(r);setShowQuick(false);}}
                        style={{flexShrink:0,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:20,padding:"7px 14px",fontSize:13,cursor:"pointer",color:T.ink,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{display:"flex",gap:8,alignItems:"flex-end",padding:"10px 12px"}}>
                <textarea
                  className="inp"
                  rows={2}
                  value={input}
                  onChange={e=>setInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
                  placeholder="Type a message…"
                  style={{flex:1,resize:"none",fontSize:15,lineHeight:1.5,padding:"10px 12px",borderRadius:12,minHeight:44,maxHeight:120}}
                />
                <button onClick={send} disabled={!input.trim()||sending}
                  style={{width:44,height:44,borderRadius:12,background:input.trim()?B:S.bg,border:"none",cursor:input.trim()?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"background .15s"}}>
                  <Ic n="arr" s={18} c={input.trim()?"#fff":T.fnt}/>
                </button>
              </div>
            </div>
          ):(
            <div style={{background:G.gb,borderTop:`1px solid ${G.gbd}`,padding:"14px 16px",textAlign:"center",flexShrink:0}}>
              <p style={{fontSize:13,fontWeight:600,color:G.g}}>✓ Conversation resolved</p>
            </div>
          )}
        </>
      )}

      {/* Visitor info tab */}
      {tab==="info"&&(
        <div style={{flex:1,overflowY:"auto",padding:"16px 14px"}}>
          {[
            ["Name",conv.visitor_name],
            ["Email",conv.visitor_email],
            ["Phone",conv.visitor_phone],
            ["Company",conv.visitor_company],
            ["Location",conv.visitor_location],
            ["Page",conv.page],
            ["Subject",conv.subject||conv.offline_message],
          ].filter(([,v])=>v).map(([label,val])=>(
            <div key={label} style={{marginBottom:14,paddingBottom:14,borderBottom:`1px solid ${S.bg}`}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>{label}</p>
              <p style={{fontSize:14,color:T.ink,lineHeight:1.5,wordBreak:"break-all"}}>{val}</p>
            </div>
          ))}
          {!conv.visitor_name&&!conv.visitor_email&&<p style={{fontSize:13,color:T.fnt,textAlign:"center",marginTop:32}}>No visitor details collected yet</p>}
        </div>
      )}

      {/* Internal note tab */}
      {tab==="note"&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",padding:"14px",gap:10}}>
          <p style={{fontSize:13,color:T.fnt,lineHeight:1.5}}>Internal notes are only visible to your team — never sent to the visitor.</p>
          <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
            {msgs.filter(m=>m.type==="note").map((m,i)=>(
              <div key={i} style={{background:G.ab,border:`1px solid ${G.abd}`,borderRadius:10,padding:"10px 12px"}}>
                <p style={{fontSize:10.5,fontWeight:700,color:G.a,marginBottom:3}}>{m.sender} · {fmt(m.created_at)}</p>
                <p style={{fontSize:13,color:T.ink,lineHeight:1.5}}>{m.content}</p>
              </div>
            ))}
            {msgs.filter(m=>m.type==="note").length===0&&<p style={{fontSize:13,color:T.fnt,textAlign:"center",marginTop:20}}>No notes yet</p>}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"flex-end",paddingBottom:"max(10px, env(safe-area-inset-bottom))"}}>
            <textarea className="inp" rows={2} value={note} onChange={e=>setNote(e.target.value)}
              placeholder="Add an internal note…"
              style={{flex:1,resize:"none",fontSize:14,borderRadius:10,padding:"10px 12px",maxHeight:120}}/>
            <button onClick={sendNote} disabled={!note.trim()||sendingNote}
              style={{width:44,height:44,borderRadius:10,background:note.trim()?G.a:S.bg,border:"none",cursor:note.trim()?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Ic n="arr" s={16} c={note.trim()?"#fff":T.fnt}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Password Reset View ───────────────────────────────────────────────────────
const PResetPassword=({token,orgId,onDone})=>{
  const [pw,setPw]=useState("");
  const [pw2,setPw2]=useState("");
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [done,setDone]=useState(false);

  const submit=async()=>{
    if(!pw||pw.length<8){setErr("Password must be at least 8 characters.");return;}
    if(pw!==pw2){setErr("Passwords don't match.");return;}
    setLoading(true);setErr("");
    try{
      const r=await fetch(API_URL+"/api/auth/reset-password",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({token,orgId,newPassword:pw})});
      const d=await r.json();
      if(!r.ok||!d.ok) throw new Error(d.error||"Reset failed");
      setDone(true);
      // Clear reset params from URL
      window.history.replaceState({},"",window.location.pathname);
    }catch(e){setErr(e.message);}
    setLoading(false);
  };

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,padding:24}}>
      <div style={{width:"100%",maxWidth:400,background:S.w,borderRadius:16,padding:32,boxShadow:"0 4px 24px rgba(0,0,0,.08)",border:`1px solid ${S.brd}`}}>
        {!done?(
          <>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{width:52,height:52,borderRadius:14,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
                <Ic n="lock" s={24} c={B}/>
              </div>
              <h2 style={{fontSize:20,fontWeight:800,color:T.ink,margin:"0 0 6px"}}>Set a new password</h2>
              <p style={{fontSize:13,color:T.fnt}}>Choose a strong password — at least 8 characters.</p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <input className="inp" type="password" placeholder="New password" value={pw}
                onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
                autoFocus style={{fontSize:14,padding:"11px 13px"}}/>
              <input className="inp" type="password" placeholder="Confirm new password" value={pw2}
                onChange={e=>setPw2(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
                style={{fontSize:14,padding:"11px 13px"}}/>
              {err&&(
                <div style={{display:"flex",gap:6,alignItems:"center",padding:"8px 11px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:8}}>
                  <Ic n="x" s={12} c={G.r}/><p style={{fontSize:12.5,color:G.r}}>{err}</p>
                </div>
              )}
              <button className="btn bp" style={{padding:"11px 0",fontSize:14,fontWeight:700,marginTop:4}} onClick={submit} disabled={loading}>
                {loading?<><Spinner size={14}/> Updating…</>:<><Ic n="chk" s={13} c="#fff"/> Set New Password</>}
              </button>
            </div>
          </>
        ):(
          <div style={{textAlign:"center",padding:"8px 0"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:G.gb,border:`2px solid ${G.gbd}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
              <Ic n="chk" s={26} c={G.g}/>
            </div>
            <h2 style={{fontSize:18,fontWeight:800,color:T.ink,marginBottom:8}}>Password updated!</h2>
            <p style={{fontSize:13,color:T.fnt,marginBottom:20}}>Your password has been changed. You can now sign in with your new password.</p>
            <button className="btn bp" style={{padding:"10px 24px",fontSize:14,fontWeight:700}} onClick={onDone}>
              <Ic n="arr" s={13} c="#fff"/> Go to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────


export default function App(){
  const [,setThemeTick]=useState("");
  useEffect(()=>{const fn=t=>setThemeTick(t);themeListeners.add(fn);return()=>{themeListeners.delete(fn);};},[]);
  const _sess=(()=>{
    try{
      const s=localStorage.getItem("hindle_session");
      if(!s) return null;
      const p=JSON.parse(s);
      // Version check — v2 sessions required. Clear old stale sessions.
      if(p._v!==2){
        clearHindleStorage();
        return null;
      }
      return p;
    }catch(e){return null;}
  })();
  // ── Handoff magic link: ?token=XXX&conv=YYY ──
  const _urlParams=new URLSearchParams(window.location.search);
  const _urlToken=_urlParams.get("token");
  const _urlConv=_urlParams.get("conv");
  const _urlReset=_urlParams.get("reset");
  const _urlResetOrg=_urlParams.get("org");

  const _signinReason=(()=>{try{return !!sessionStorage.getItem("hindle_signin_reason")||!!sessionStorage.getItem("hindle_signin_info")||new URLSearchParams(window.location.search).get("signin")==="1";}catch(e){return false;}})();
  // Arriving from the helpdesk portal: show a short note on the sign-in screen
  try{if(new URLSearchParams(window.location.search).get("signin")==="1"&&!_sess)sessionStorage.setItem("hindle_signin_info","Helpdesk now has everything in one place — sign in with your usual email and password.");}catch(e){}
  const [view,setView]=useState(_urlReset&&_urlResetOrg?"reset_password":(_sess?.view||(_signinReason?"signin":"landing")));
  const [role,setRole]=useState(_sess?.role||null);
  const [initPlan,setInitPlan]=useState("starter");
  const [initPromo,setInitPromo]=useState(null);
  const [tokenConvId,setTokenConvId]=useState(_urlConv||null);
  const [tokenTicketId,setTokenTicketId]=useState(null);
  const [resetToken]=useState(_urlReset||null);
  const [resetOrg]=useState(_urlResetOrg||null);

  // Expose purchase flow to billing button (which lives inside Dashboard, outside go() scope)
  useEffect(()=>{
    window.__hindleGoUpgrade=(plan)=>{
      if(plan) setInitPlan(plan);
      setView("purchase");
    };
    return()=>{delete window.__hindleGoUpgrade;};
  },[]);

  // Resolve handoff token on mount
  useEffect(()=>{
    if(!_urlToken) return;
    (async()=>{
      try{
        const r=await fetch(API_URL+"/api/handoff-token/"+_urlToken);
        if(!r.ok) return;
        const d=await r.json();
        if(!d.ok) return;
        // Sign in with the session the link issued, using the agent's own role
        const linkRole=d.role==="admin"?"tenant_admin":"agent";
        try{
          if(d.token) setToken(d.token);
          localStorage.setItem("hindle_config",JSON.stringify({orgId:d.org_id,tenantId:d.org_id,role:linkRole,email:d.agent?.email||"",name:d.agent?.name||"",agentId:d.agent?.id||null}));
          if(d.agent) localStorage.setItem("hindle_admin_profile",JSON.stringify({id:d.agent.id||null,name:d.agent.name||"",email:d.agent.email||"",role:linkRole}));
          localStorage.setItem("hindle_session",JSON.stringify({view:"app",role:linkRole,_v:2}));
        }catch(e){}
        setRole(linkRole);
        if(d.conversation_id) setTokenConvId(d.conversation_id);
        if(d.ticket_id) setTokenTicketId(d.ticket_id);
        // Detect mobile — route to phone-optimised agent UI
        const _isMobile = window.innerWidth < 768 || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        setView(_isMobile ? "mobile_agent" : "app");
        // Clear token from URL without reload
        window.history.replaceState({},'',window.location.pathname);
      }catch(e){}
    })();
  },[]);
  // ── Live pricing bootstrap ────────────────────────────────────────────────
  // Load pricing from DB immediately on app mount so Landing page never shows
  // stale INIT_CONFIG prices. Cache in localStorage with 24h TTL.
  const _initConfig=(()=>{
    try{
      // Try 24h pricing cache first (for anonymous visitors)
      const pc=localStorage.getItem("hindle_pricing_cache");
      if(pc){
        const {ts,data}=JSON.parse(pc);
        if(Date.now()-ts < 86400000) { // 24 hours
          // Merge cached pricing into config
          const base=localStorage.getItem("hindle_config");
          const existing=base?JSON.parse(base):{};
          return {...INIT_CONFIG,...existing,
            plans:{...INIT_CONFIG.plans,...Object.fromEntries(Object.entries(existing.plans||{}).map(([k,v])=>[k,{...INIT_CONFIG.plans[k],...v}])),
              starter:{...INIT_CONFIG.plans.starter,...((existing.plans||{}).starter||{}),usd:data.plans?.starter?.usd??INIT_CONFIG.plans.starter.usd},
              professional:{...INIT_CONFIG.plans.professional,...((existing.plans||{}).professional||{}),usd:data.plans?.professional?.usd??INIT_CONFIG.plans.professional.usd},
            },
            annualDiscountPct:data.annualDiscountPct??INIT_CONFIG.annualDiscountPct,
            promoCodes:data.promoCodes||existing.promoCodes||INIT_CONFIG.promoCodes,
            planFeatures:data.planFeatures||existing.planFeatures||null,
            planLimits:data.planLimits||existing.planLimits||null,
            clicksend:{...INIT_CONFIG.clicksend,...(existing.clicksend||{}),...(data.clicksend||{})},
          };
        }
      }
      const s=localStorage.getItem("hindle_config");
      if(s){const p=JSON.parse(s);return {...INIT_CONFIG,...p,clicksend:{...INIT_CONFIG.clicksend,...(p.clicksend||{})},plans:{...INIT_CONFIG.plans,...Object.fromEntries(Object.entries(p.plans||{}).map(([k,v])=>[k,{...INIT_CONFIG.plans[k],...v}]))},promoCodes:p.promoCodes||INIT_CONFIG.promoCodes};}
    }catch(e){}
    return INIT_CONFIG;
  })();
  const [config,setConfig]=useState(_initConfig);
  const [pricingLoaded,setPricingLoaded]=useState(false);
  useEffect(()=>{try{localStorage.setItem("hindle_config",JSON.stringify(config));}catch(e){}},[config]);

  // Fetch live pricing from DB on every mount — update config + refresh cache
  useEffect(()=>{
    (async()=>{
      try{
        const r=await fetch(API_URL+"/api/admin-settings");
        if(!r.ok) return;
        const aD=await r.json();
        const sc=aD.superConfig||{};
        if(!sc||!Object.keys(sc).length) return;
        setConfig(prev=>{
          const updated={...prev,
            plans:{...prev.plans,
              starter:{...prev.plans.starter,usd:sc.plans?.starter?.usd??prev.plans.starter.usd},
              professional:{...prev.plans.professional,usd:sc.plans?.professional?.usd??prev.plans.professional.usd},
            },
            annualDiscountPct:sc.annualDiscountPct??prev.annualDiscountPct,
            promoCodes:sc.promoCodes||prev.promoCodes,
            planFeatures:sc.planFeatures||prev.planFeatures||null,
            planLimits:sc.planLimits||prev.planLimits||null,
            _superConfig:sc,
            clicksend:sc.clicksend?{...INIT_CONFIG.clicksend,...(prev.clicksend||{}),...sc.clicksend}:prev.clicksend,
          };
          // Update 24h pricing cache for anonymous visitors
          try{localStorage.setItem("hindle_pricing_cache",JSON.stringify({ts:Date.now(),data:sc}));}catch(_){}
          return updated;
        });
      }catch(_){}finally{setPricingLoaded(true);}
    })();
  },[]);

  const go=(dest,data,promo)=>{
    if(dest==="app"){
      setRole(data);
      const v=(data==="super_admin"||data==="tenant_admin"||data==="agent")?"app":"setup";
      setView(v);
      try{localStorage.setItem("hindle_session",JSON.stringify({view:v,role:data,_v:2}));}catch(e){}
    }
    else if(dest==="purchase"){if(data) setInitPlan(data);if(promo) setInitPromo(promo);setView("purchase");}
    else{
      if(dest==="signin"||dest==="landing"){
        try{
          clearHindleStorage();
        }catch(e){}
      }
      setView(dest);
    }
  };

  return(
    <>
      <style>{buildCSS()}</style>
      {view==="landing"  && <Landing go={go} config={config} pricingLoaded={pricingLoaded}/>}
      {view==="reset_password" && <PResetPassword token={resetToken} orgId={resetOrg} onDone={()=>{setView("signin");window.history.replaceState({},"",window.location.pathname);}}/>}
      {view==="purchase" && <PurchaseWizard go={go} initPlan={initPlan} initPromo={initPromo} config={config}/>}
      {view==="signin"   && <SignIn go={go}/>}
      {view==="setup"    && <SetupWizard go={(d,r)=>{ if(d==="app"){ setView("app"); try{const _sv=JSON.stringify({view:"app",role:role,_v:2});localStorage.setItem("hindle_session",_sv);}catch(e){} } else go(d,r); }} role={role}/>}
      {view==="mobile_agent" && <PMobileAgent
        convId={tokenConvId}
        orgId={(()=>{try{const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return c.orgId||c.tenantId||null;}catch(e){return null;}})()}
        agentName={(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return p.name||c.name||"Agent";}catch(e){return "Agent";}})()}
        agentEmail={(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");return p.email||"";}catch(e){return "";}})()}
        goDesktop={()=>setView("app")}
      />}
      {view==="app"      && <Dashboard role={role} tokenConvId={tokenConvId} tokenTicketId={tokenTicketId} goSignIn={()=>{
          try{clearHindleStorage();}catch(e){}
          setView("signin");
        }} goSetup={()=>setView("setup")} config={config} setConfig={setConfig}/>}
    </>
  );
}

const PFaqManager=({config})=>{
  const orgId=config?.orgId||config?.tenantId||(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
  const [faqs,setFaqs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const [editId,setEditId]=useState(null);
  const [form,setForm]=useState({q:"",a:"",cat:"General",active:true});
  const [showAdd,setShowAdd]=useState(false);
  const [search,setSearch]=useState("");
  const [cats]=useState(["General","Products","Pricing","Support","Returns","Delivery","Account","Technical","Other"]);

  // Load FAQs from tenant-config
  useEffect(()=>{
    if(!orgId){setLoading(false);return;}
    fetch(API_URL+"/api/tenant-config/"+orgId)
      .then(r=>r.ok?r.json():null)
      .then(d=>{if(d?.faqs&&Array.isArray(d.faqs))setFaqs(d.faqs);})
      .catch(()=>{})
      .finally(()=>setLoading(false));
  },[orgId]);

  const saveFaqs=async(list)=>{
    if(!orgId)return;
    setSaving(true);
    try{
      await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({tenantId:orgId,faqs:list})});
      setSaved(true);setTimeout(()=>setSaved(false),2000);
    }catch(e){}
    setSaving(false);
  };

  const addFaq=()=>{
    if(!form.q.trim()||!form.a.trim())return;
    const newItem={id:`faq_${Date.now()}`,q:form.q.trim(),a:form.a.trim(),cat:form.cat,active:form.active};
    const next=[...faqs,newItem];
    setFaqs(next);saveFaqs(next);
    setForm({q:"",a:"",cat:"General",active:true});setShowAdd(false);
  };

  const updateFaq=(id,patch)=>{
    const next=faqs.map(f=>f.id===id?{...f,...patch}:f);
    setFaqs(next);saveFaqs(next);
  };

  const deleteFaq=(id)=>{
    if(!window.confirm("Delete this FAQ?"))return;
    const next=faqs.filter(f=>f.id!==id);
    setFaqs(next);saveFaqs(next);
  };

  const filtered=search?faqs.filter(f=>(f.q+f.a+f.cat).toLowerCase().includes(search.toLowerCase())):faqs;
  const byCat=cats.filter(c=>filtered.some(f=>f.cat===c));

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h2 style={{fontSize:17,fontWeight:800,marginBottom:4}}>FAQ Manager</h2>
          <p style={{fontSize:13,color:T.fnt}}>Add frequently asked questions — the AI chatbot uses these to answer visitors accurately.</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          {saved&&<span style={{fontSize:12,color:G.g,fontWeight:600,alignSelf:"center"}}>✓ Saved</span>}
          <button className="btn bp" onClick={()=>{setForm({q:"",a:"",cat:"General",active:true});setEditId(null);setShowAdd(true);}}>
            <Ic n="plus" s={13} c="#fff"/> Add FAQ
          </button>
        </div>
      </div>

      {/* Add / Edit form */}
      {(showAdd||editId)&&(
        <div className="card" style={{padding:18,marginBottom:16,border:`1.5px solid ${BM}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <p style={{fontSize:13.5,fontWeight:700}}>{editId?"Edit FAQ":"New FAQ"}</p>
            <button onClick={()=>{setShowAdd(false);setEditId(null);}} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt}}><Ic n="x" s={15}/></button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 160px",gap:10,marginBottom:10}}>
            <div>
              <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Question</label>
              <input className="inp" value={form.q} onChange={e=>setForm(f=>({...f,q:e.target.value}))} placeholder="e.g. What are your opening hours?"/>
            </div>
            <div>
              <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Category</label>
              <select className="inp" value={form.cat} onChange={e=>setForm(f=>({...f,cat:e.target.value}))}>
                {cats.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Answer</label>
            <textarea className="inp" rows={4} value={form.a} onChange={e=>setForm(f=>({...f,a:e.target.value}))} placeholder="Provide a clear, complete answer…" style={{resize:"vertical",lineHeight:1.6}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12.5}}>
              <div style={{width:34,height:18,borderRadius:9,background:form.active?G.g:S.brd,position:"relative",cursor:"pointer",transition:"background .2s"}} onClick={()=>setForm(f=>({...f,active:!f.active}))}>
                <div style={{position:"absolute",top:2,left:form.active?18:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
              </div>
              <span style={{color:form.active?G.g:T.fnt,fontWeight:600}}>{form.active?"Active":"Inactive"}</span>
            </label>
            <div style={{display:"flex",gap:8}}>
              <button className="btn bo sm" onClick={()=>{setShowAdd(false);setEditId(null);}}>Cancel</button>
              <button className="btn bp sm" disabled={saving||!form.q.trim()||!form.a.trim()} onClick={()=>{
                if(editId){updateFaq(editId,form);setEditId(null);}else{addFaq();}
              }}>{saving?<><Spinner size={12}/> Saving…</>:<><Ic n="chk" s={12} c="#fff"/> {editId?"Update":"Add FAQ"}</>}</button>
            </div>
          </div>
        </div>
      )}

      {/* Search + stats */}
      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:16}}>
        <div style={{position:"relative",flex:1}}>
          <Ic n="search" s={13} c={T.fnt} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)"}}/>
          <input className="inp" placeholder="Search FAQs…" value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:32,fontSize:12}}/>
        </div>
        <span style={{fontSize:12,color:T.fnt,flexShrink:0}}>{faqs.length} total · {faqs.filter(f=>f.active).length} active</span>
      </div>

      {/* FAQ list */}
      {loading?(<div style={{textAlign:"center",padding:40}}><Spinner size={20}/></div>)
      :filtered.length===0?(
        <div style={{textAlign:"center",padding:60,color:T.fnt}}>
          <Ic n="book" s={32} c={T.fnt}/>
          <p style={{marginTop:12,fontSize:14,fontWeight:600}}>{search?"No FAQs match your search":"No FAQs yet"}</p>
          <p style={{fontSize:12,marginTop:4}}>{search?"Try a different term":"Click Add FAQ to get started"}</p>
        </div>
      ):(
        byCat.length?byCat.map(cat=>(
          <div key={cat} style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <span style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,color:T.fnt}}>{cat}</span>
              <span style={{fontSize:10,color:T.fnt}}>({filtered.filter(f=>f.cat===cat).length})</span>
              <div style={{flex:1,height:1,background:S.brd}}/>
            </div>
            {filtered.filter(f=>f.cat===cat).map(faq=>(
              <FaqCard key={faq.id} faq={faq}
                onEdit={()=>{setForm({q:faq.q,a:faq.a,cat:faq.cat,active:faq.active});setEditId(faq.id);setShowAdd(false);}}
                onDelete={()=>deleteFaq(faq.id)}
                onToggle={()=>updateFaq(faq.id,{active:!faq.active})}/>
            ))}
          </div>
        )):(
          filtered.map(faq=>(
            <FaqCard key={faq.id} faq={faq}
              onEdit={()=>{setForm({q:faq.q,a:faq.a,cat:faq.cat,active:faq.active});setEditId(faq.id);setShowAdd(false);}}
              onDelete={()=>deleteFaq(faq.id)}
              onToggle={()=>updateFaq(faq.id,{active:!faq.active})}/>
          ))
        )
      )}
    </div>
  );
};

const FaqCard=({faq,onEdit,onDelete,onToggle})=>{
  const [open,setOpen]=useState(false);
  return(
    <div className="card" style={{marginBottom:8,opacity:faq.active?1:.6}}>
      <div style={{padding:"12px 16px",display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer"}} onClick={()=>setOpen(o=>!o)}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
            <Ic n={open?"chU":"chD"} s={12} c={T.fnt}/>
            <span style={{fontSize:13.5,fontWeight:600,flex:1}}>{faq.q}</span>
            <span style={{fontSize:10,background:faq.active?`${B}15`:S.bg,color:faq.active?B:T.fnt,border:`1px solid ${faq.active?BM:S.brd}`,borderRadius:4,padding:"1px 6px",fontWeight:600,flexShrink:0}}>{faq.active?"Active":"Draft"}</span>
          </div>
          {!open&&<p style={{fontSize:11.5,color:T.fnt,marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingLeft:19}}>{faq.a}</p>}
        </div>
      </div>
      {open&&(
        <div style={{padding:"0 16px 14px",paddingLeft:35}}>
          <p style={{fontSize:13,color:T.mid,lineHeight:1.65,marginBottom:12,whiteSpace:"pre-wrap"}}>{faq.a}</p>
          <div style={{display:"flex",gap:6}}>
            <button className="btn bo sm" style={{fontSize:11}} onClick={onEdit}><Ic n="edit" s={11}/> Edit</button>
            <button className="btn bo sm" style={{fontSize:11}} onClick={onToggle}>{faq.active?"Deactivate":"Activate"}</button>
            <button className="btn bo sm" style={{fontSize:11,color:G.r,borderColor:G.rbd}} onClick={onDelete}><Ic n="trash" s={11} c={G.r}/> Delete</button>
          </div>
        </div>
      )}
    </div>
  );
};

const PSettingsTenant=()=>{
  const [tab,setTab]=useState("profile");
  const [saved,setSaved]=useState(false);

  // ── Load persisted settings ──
  const _loadJ=(key,def)=>{try{const s=localStorage.getItem(key);return s?{...def,...JSON.parse(s)}:def;}catch(e){return def;}};

  // Profile state
  const [org,setOrg]=useState(()=>_loadJ("hindle_tenant_org",{name:"",website:"",industry:"",size:"",timezone:"Australia/Sydney",support_email:"",phone:"",address:"",city:"",state:"",postcode:"",country:""}));
  // Billing details state
  const [billing2,setBilling2]=useState(()=>_loadJ("hindle_tenant_billing_details",{full_name:"",address_line1:"",address_line2:"",city:"",state:"",postcode:"",country:"AU",company_number:"",invoice_email:""}));

  const COUNTRIES=[["AU","Australia"],["NZ","New Zealand"],["GB","United Kingdom"],["IE","Ireland"],["US","United States"],["CA","Canada"],["ZA","South Africa"],["SG","Singapore"],["IN","India"],["JP","Japan"],["DE","Germany"],["FR","France"],["NL","Netherlands"],["SE","Sweden"],["NO","Norway"],["DK","Denmark"],["CH","Switzerland"],["ES","Spain"],["IT","Italy"],["BR","Brazil"],["MX","Mexico"],["AE","United Arab Emirates"]];
  const AU_STATES=[["ACT","Australian Capital Territory"],["NSW","New South Wales"],["NT","Northern Territory"],["QLD","Queensland"],["SA","South Australia"],["TAS","Tasmania"],["VIC","Victoria"],["WA","Western Australia"]];
  const NZ_REGIONS=[["AKL","Auckland"],["WLG","Wellington"],["CHC","Christchurch"],["HB","Hawke's Bay"],["MWT","Manawatu-Whanganui"],["OTA","Otago"],["STL","Southland"],["TKI","Taranaki"],["WKO","Waikato"],["BOP","Bay of Plenty"],["NTL","Northland"],["CAN","Canterbury"]];
  const US_STATES=[["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"]];
  const GB_REGIONS=[["ENG","England"],["SCT","Scotland"],["WLS","Wales"],["NIR","Northern Ireland"]];
  const getStates=(cc)=>{if(cc==="AU")return AU_STATES;if(cc==="NZ")return NZ_REGIONS;if(cc==="US")return US_STATES;if(cc==="GB")return GB_REGIONS;return null;};
  const stateList=getStates(billing2.country);
  // Branding state
  const [brand,setBrand]=useState(()=>_loadJ("hindle_tenant_brand",{primary:"#2563EB",secondary:"#1E40AF",bubble:"bottom-right",greeting:"Hi there! How can we help you today?",logo:"",widget_name:"",widgetSize:"md",chatSize:"md"}));
  // Billing state (read-only summary)
  const billing={plan:"—",cycle:"—",nextDate:"—",amount:"—",card:"No card on file",cardExpiry:"—"};
  const invoiceHistory=[];
  // Notifications state
  const [notif,setNotif]=useState(()=>_loadJ("hindle_tenant_notif",{email_handoff:true,email_csat:true,email_weekly:true,sms_handoff:true,sms_trial:true,digest:"daily"}));
  // Security state
  const [twoFA,setTwoFA]=useState(()=>{try{return localStorage.getItem("hindle_tenant_2fa")==="true";}catch(e){return false;}});
  const [sessions]=useState([]);

  // ── Auto-save on change — localStorage immediately, DB debounced ──
  const _dbSaveTimer = React.useRef(null);
  const _mounted = React.useRef(false); // prevent saving blanks before DB mount-fetch completes
  const _dbSave = React.useCallback((orgData, brandData, billing2Data, notifData, qr) => {
    if(!_mounted.current) return; // don't overwrite DB with empty initial state
    clearTimeout(_dbSaveTimer.current);
    _dbSaveTimer.current = setTimeout(async () => {
      try {
        let orgId = null;
        try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
        if (!orgId) return;
        const cfg = JSON.parse(localStorage.getItem("hindle_chatbot_config") || "{}");
        const payload = {tenantId:orgId, ...cfg, org:orgData, brand:brandData, billing2:billing2Data, notif:notifData};
        if(qr !== undefined) payload.quickReplies = qr;
        await fetch(API_URL + "/api/tenant-config", {
          method: "POST", headers: {"Content-Type":"application/json"},
          body: JSON.stringify(payload),
        });
      } catch(e) {}
    }, 1500);
  }, []);

  useEffect(()=>{
    try{localStorage.setItem("hindle_tenant_org",JSON.stringify(org));}catch(e){}
    _dbSave(org, brand, billing2, notif);
  }, [org]);
  useEffect(()=>{
    try{
      localStorage.setItem("hindle_tenant_brand",JSON.stringify(brand));
      const cfg=JSON.parse(localStorage.getItem("hindle_chatbot_config")||"{}");
      cfg.widgetColor=brand.primary;
      cfg.widgetPos=brand.bubble||"bottom-right";
      cfg.widgetLabel=brand.widget_name||cfg.widgetLabel||"Chat with us";
      cfg.widget_name=brand.widget_name||"";
      cfg.brand={primary:brand.primary,secondary:brand.secondary,bubble:brand.bubble||"bottom-right",logoImg:brand.logo||"",customLogoUrl:brand.customLogoUrl||""};
      if(brand.greeting) cfg.greeting=brand.greeting;
      localStorage.setItem("hindle_chatbot_config",JSON.stringify(cfg));
    }catch(e){}
    _dbSave(org, brand, billing2, notif);
  }, [brand]);
  useEffect(()=>{
    try{localStorage.setItem("hindle_tenant_billing_details",JSON.stringify(billing2));}catch(e){}
    _dbSave(org, brand, billing2, notif);
  }, [billing2]);
  useEffect(()=>{
    try{localStorage.setItem("hindle_tenant_notif",JSON.stringify(notif));}catch(e){}
    _dbSave(org, brand, billing2, notif);
  }, [notif]);
  useEffect(()=>{try{localStorage.setItem("hindle_tenant_2fa",String(twoFA));}catch(e){}}, [twoFA]);

  // On mount: fetch from DB so settings survive logout/new browser
  useEffect(()=>{
    let orgId=null;
    try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
    if(!orgId){_mounted.current=true;return;}
    fetch(API_URL+"/api/tenant-config/"+orgId)
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d){
          if(d.org&&Object.keys(d.org).length){
            setOrg(o=>({...o,...d.org}));
            try{localStorage.setItem("hindle_tenant_org",JSON.stringify({...org,...d.org}));}catch(e){}
          }
          if(d.brand&&Object.keys(d.brand).length){
            setBrand(b=>({...b,...d.brand}));
            try{localStorage.setItem("hindle_tenant_brand",JSON.stringify({...brand,...d.brand}));}catch(e){}
          }
          if(d.billing2&&Object.keys(d.billing2).length){
            setBilling2(b=>({...b,...d.billing2}));
            try{localStorage.setItem("hindle_tenant_billing_details",JSON.stringify({...billing2,...d.billing2}));}catch(e){}
          }
          if(d.notif&&Object.keys(d.notif).length){
            setNotif(n=>({...n,...d.notif}));
            try{localStorage.setItem("hindle_tenant_notif",JSON.stringify({...notif,...d.notif}));}catch(e){}
          }
        }
        _mounted.current=true; // safe to auto-save now
      })
      .catch(()=>{_mounted.current=true;});
  },[]);

  const save=async()=>{
    try{
      const cfg=JSON.parse(localStorage.getItem("hindle_chatbot_config")||"{}");
      cfg.widgetColor=brand.primary;
      cfg.widgetPos=brand.bubble||"bottom-right";
      cfg.widgetLabel=brand.widget_name||cfg.widgetLabel||"Chat with us";
      cfg.widget_name=brand.widget_name||"";
      cfg.brand={primary:brand.primary,secondary:brand.secondary,bubble:brand.bubble||"bottom-right",logoImg:brand.logo||"",customLogoUrl:brand.customLogoUrl||""};
      if(brand.greeting) cfg.greeting=brand.greeting;
      localStorage.setItem("hindle_chatbot_config",JSON.stringify(cfg));
      // Persist all tenant settings to DB
      let orgId=null;
      try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);orgId=p.orgId||p.tenantId||null;}}catch(e){}
      if(orgId){
        const qr=(()=>{try{const s=localStorage.getItem("hindle_quick_replies");return s?JSON.parse(s):undefined;}catch(e){return undefined;}})();
        const savePayload={
          tenantId:orgId,
          ...cfg,
          org,
          brand,
          billing2,
          notif,
          widget_name:brand.widget_name||"",
          widgetLabel:brand.widget_name||cfg.widgetLabel||"Chat with us",
        };
        if(qr!==undefined) savePayload.quickReplies=qr;
        await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(savePayload)});
      }
    }catch(e){}
    setSaved(true);setTimeout(()=>setSaved(false),2200);
  };

  const INDUSTRIES=["Technology","Retail","Healthcare","Education","Finance","Other"];
  const SIZES=["1–10","11–50","51–200","201–1000","1000+"];
  const TIMEZONES=["Pacific/Auckland","Australia/Sydney","Australia/Melbourne","Australia/Brisbane","Australia/Perth","Asia/Singapore","Asia/Tokyo","Asia/Shanghai","Asia/Hong_Kong","Asia/Kolkata","Asia/Dubai","Europe/London","Europe/Dublin","Europe/Paris","Europe/Berlin","Europe/Madrid","Europe/Rome","Europe/Amsterdam","Europe/Stockholm","Europe/Zurich","Europe/Warsaw","Europe/Athens","Europe/Istanbul","Africa/Johannesburg","Africa/Nairobi","Africa/Cairo","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","America/Vancouver","America/Toronto","America/Montreal","America/Sao_Paulo","America/Buenos_Aires","America/Mexico_City","Pacific/Honolulu"];

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{display:"flex",gap:3,marginBottom:20,borderBottom:`1px solid ${S.brd}`,paddingBottom:0}}>
        {[["profile","🏢 Organisation"],["branding","🎨 Branding"],["billing","💳 Billing"],["notifications","🔔 Notifications"],["security","🔒 Security"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer",background:"transparent",color:tab===t?B:T.fnt,border:"none",borderBottom:tab===t?`2px solid ${B}`:"2px solid transparent",marginBottom:-1,transition:"all .15s"}}>{l}</button>
        ))}
      </div>

      {/* PROFILE */}
      {tab==="profile"&&(
        <div style={{maxWidth:580,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:20}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Organisation Details</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[["Organisation Name","name","text","Your company name"],["Phone","phone","tel","+1 555 000 0000"],["Website","website","url","https://"],["Support Email","support_email","email","support@"],["Address","address","text","123 Main Street"],["City","city","text","New York"],["State / Province","state","text","NY"],["Zip / Postcode","postcode","text","10001"],["Country","country","text","United States"],["Timezone","timezone","select",TIMEZONES]].map(([l,k,type,ph])=>(
                <div key={k} style={{gridColumn:k==="name"||k==="support_email"?"1/-1":"auto"}}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
                  {type==="select"?(
                    <select className="inp" value={org[k]} onChange={e=>setOrg(o=>({...o,[k]:e.target.value}))}>
                      {ph.map(v=><option key={v} value={v}>{v}</option>)}
                    </select>
                  ):(
                    <input className="inp" type={type} placeholder={ph} value={org[k]} onChange={e=>setOrg(o=>({...o,[k]:e.target.value}))}/>
                  )}
                </div>
              ))}
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Industry</label>
                <select className="inp" value={org.industry} onChange={e=>setOrg(o=>({...o,industry:e.target.value}))}>
                  {INDUSTRIES.map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Team Size</label>
                <select className="inp" value={org.size} onChange={e=>setOrg(o=>({...o,size:e.target.value}))}>
                  {SIZES.map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Billing Details */}
          <div className="card" style={{padding:20}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Billing Details</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Full Legal Name</label>
                <input className="inp" placeholder="Full name or company trading name" value={billing2.full_name} onChange={e=>setBilling2(b=>({...b,full_name:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Company / ABN / Reg. Number</label>
                <input className="inp" placeholder="e.g. 12 345 678 901" value={billing2.company_number} onChange={e=>setBilling2(b=>({...b,company_number:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Invoice Email</label>
                <input className="inp" type="email" placeholder="accounts@yourcompany.com" value={billing2.invoice_email} onChange={e=>setBilling2(b=>({...b,invoice_email:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Country</label>
                <select className="inp" value={billing2.country} onChange={e=>setBilling2(b=>({...b,country:e.target.value,state:""}))}>
                  {COUNTRIES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Address Line 1</label>
                <input className="inp" placeholder="Street address" value={billing2.address_line1} onChange={e=>setBilling2(b=>({...b,address_line1:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Address Line 2 <span style={{fontSize:11,fontWeight:400,color:T.fnt}}>optional</span></label>
                <input className="inp" placeholder="Suite, unit, building" value={billing2.address_line2} onChange={e=>setBilling2(b=>({...b,address_line2:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>City</label>
                <input className="inp" placeholder="City" value={billing2.city} onChange={e=>setBilling2(b=>({...b,city:e.target.value}))}/>
              </div>
              {stateList?(
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>State / Region</label>
                  <select className="inp" value={billing2.state} onChange={e=>setBilling2(b=>({...b,state:e.target.value}))}>
                    <option value="">— Select —</option>
                    {stateList.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ):(
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>State / Region</label>
                  <input className="inp" placeholder="State or region" value={billing2.state} onChange={e=>setBilling2(b=>({...b,state:e.target.value}))}/>
                </div>
              )}
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Postcode / ZIP</label>
                <input className="inp" placeholder="Postcode" value={billing2.postcode} onChange={e=>setBilling2(b=>({...b,postcode:e.target.value}))}/>
              </div>
            </div>
          </div>
          <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save} style={{alignSelf:"flex-start",minWidth:160}}>
            {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Changes</>}
          </button>
        </div>
      )}

      {/* BRANDING */}
      {tab==="branding"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:16,alignItems:"start"}}>

          {/* ── LEFT: controls ── */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            {/* Identity */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:14}}>Widget Identity</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Widget Name <span style={{fontWeight:400,color:T.fnt}}>shown in chat header</span></label>
                  <input className="inp" value={brand.widget_name} placeholder="e.g. Hindle Support" onChange={e=>setBrand(b=>({...b,widget_name:e.target.value}))}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Logo Initials <span style={{fontWeight:400,color:T.fnt}}>max 2 chars</span></label>
                  <input className="inp" value={brand.logo} maxLength={2} placeholder="AB" onChange={e=>setBrand(b=>({...b,logo:e.target.value.toUpperCase()}))} style={{fontFamily:"monospace",letterSpacing:4,textTransform:"uppercase",fontSize:16,fontWeight:700}}/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Opening Greeting</label>
                  <input className="inp" value={brand.greeting} placeholder="Hi there! How can we help?" onChange={e=>setBrand(b=>({...b,greeting:e.target.value}))}/>
                </div>
              </div>
            </div>

            {/* Custom sidebar logo */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Dashboard Logo</p>
              <p style={{fontSize:12,color:T.fnt,marginBottom:12}}>Replace the top-left Hindle logo with your own. Paste a URL or upload an image (PNG/SVG recommended, max 200 KB).</p>
              <div style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:10}}>
                {(()=>{const curUrl=brand.customLogoUrl||"";return(<>
                  <input className="inp" placeholder="https://yoursite.com/logo.png" value={curUrl}
                    onChange={e=>setBrand(b=>({...b,customLogoUrl:e.target.value}))}
                    style={{flex:1,fontSize:12}}/>
                  <button className="btn bo sm" style={{flexShrink:0}} onClick={()=>{
                    const inp=document.createElement("input");inp.type="file";inp.accept="image/*";
                    inp.onchange=e=>{
                      const file=e.target.files[0];if(!file)return;
                      if(file.size>204800){alert("Image must be under 200 KB.");return;}
                      const reader=new FileReader();
                      reader.onload=ev=>setBrand(b=>({...b,customLogoUrl:ev.target.result}));
                      reader.readAsDataURL(file);
                    };inp.click();
                  }}>Upload</button>
                  {curUrl&&<button className="btn bd sm" style={{color:G.r,borderColor:G.rbd,flexShrink:0}} onClick={()=>setBrand(b=>({...b,customLogoUrl:""}))}>Remove</button>}
                </>);})()}
              </div>
              {brand.customLogoUrl&&(
                <div style={{padding:10,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,display:"inline-flex",alignItems:"center",gap:10}}>
                  <img src={brand.customLogoUrl} alt="Custom logo preview" style={{height:32,maxWidth:160,objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
                  <span style={{fontSize:11,color:T.fnt}}>Preview</span>
                </div>
              )}
              <p style={{fontSize:11,color:T.fnt,marginTop:8}}>Reload the page after saving to see it in the sidebar.</p>
            </div>

            {/* Colours */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:14}}>Colours</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:6}}>Primary <span style={{fontWeight:400,color:T.fnt}}>header &amp; send button</span></label>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="color" value={brand.primary} onChange={e=>setBrand(b=>({...b,primary:e.target.value}))} style={{width:40,height:38,borderRadius:8,border:`1px solid ${S.brd}`,padding:2,cursor:"pointer",flexShrink:0}}/>
                    <input className="inp" value={brand.primary} onChange={e=>setBrand(b=>({...b,primary:e.target.value}))} style={{fontFamily:"monospace",fontSize:12}}/>
                  </div>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:6}}>Secondary <span style={{fontWeight:400,color:T.fnt}}>visitor messages</span></label>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="color" value={brand.secondary} onChange={e=>setBrand(b=>({...b,secondary:e.target.value}))} style={{width:40,height:38,borderRadius:8,border:`1px solid ${S.brd}`,padding:2,cursor:"pointer",flexShrink:0}}/>
                    <input className="inp" value={brand.secondary} onChange={e=>setBrand(b=>({...b,secondary:e.target.value}))} style={{fontFamily:"monospace",fontSize:12}}/>
                  </div>
                </div>
              </div>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,marginBottom:8}}>Colour Presets</p>
              <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                {[["#2563EB","#1E40AF","Blue"],["#059669","#065F46","Green"],["#7C3AED","#5B21B6","Purple"],["#DC2626","#991B1B","Red"],["#D97706","#92400E","Amber"],["#0891B2","#164E63","Teal"],["#0F172A","#1E293B","Dark"],["#DB2777","#9D174D","Pink"]].map(([p,s,name])=>(
                  <div key={p} onClick={()=>setBrand(b=>({...b,primary:p,secondary:s}))} title={name} style={{width:26,height:26,borderRadius:"50%",cursor:"pointer",background:`conic-gradient(${p} 50%,${s} 50%)`,border:brand.primary===p?`3px solid ${T.ink}`:"3px solid transparent",boxShadow:"0 2px 6px rgba(0,0,0,.18)",transition:"transform .12s,border .12s"}} onMouseEnter={e=>e.target.style.transform="scale(1.25)"} onMouseLeave={e=>e.target.style.transform="scale(1)"}/>
                ))}
              </div>
            </div>

            {/* Widget button size */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Widget Button Size</p>
              <div style={{display:"flex",gap:8}}>
                {[["xs","XSmall","44px",22,10],["sm","Small","52px",30,13],["md","Medium","64px",40,17],["lg","Large","76px",50,21],["xl","XLarge","88px",58,24]].map(([v,l,sz,d,ic])=>{
                  const on=brand.widgetSize===v;
                  return(
                    <div key={v} onClick={()=>setBrand(b=>({...b,widgetSize:v}))} style={{flex:1,padding:"14px 8px",borderRadius:10,cursor:"pointer",border:on?`2px solid ${B}`:`1px solid ${S.brd}`,background:on?BL:S.bg,textAlign:"center",transition:"all .15s"}}>
                      <div style={{width:d,height:d,borderRadius:"50%",background:on?brand.primary:"#94A3B8",margin:"0 auto 9px",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:on?`0 3px 12px ${brand.primary}55`:"none",transition:"all .15s"}}><Ic n="chat" s={ic} c="#fff"/></div>
                      <p style={{fontSize:12.5,fontWeight:700,color:on?B:T.ink}}>{l}</p>
                      <p style={{fontSize:10.5,color:T.fnt}}>{sz}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Chat window size */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Chat Window Size</p>
              <div style={{display:"flex",gap:8}}>
                {[["xs","Mini","300×440",13,18],["sm","Compact","340×520",16,22],["md","Standard","390×600",20,28],["lg","Large","460×700",25,35],["xl","Wide","520×780",29,40]].map(([v,l,dim,w,h])=>{
                  const on=brand.chatSize===v;
                  return(
                    <div key={v} onClick={()=>setBrand(b=>({...b,chatSize:v}))} style={{flex:1,padding:"12px 8px",borderRadius:10,cursor:"pointer",border:on?`2px solid ${B}`:`1px solid ${S.brd}`,background:on?BL:S.bg,textAlign:"center",transition:"all .15s"}}>
                      <div style={{width:w,height:h,borderRadius:4,border:`2px solid ${on?B:S.brd}`,margin:"0 auto 8px",background:on?`${brand.primary}18`:"transparent",transition:"all .15s"}}/>
                      <p style={{fontSize:12,fontWeight:700,color:on?B:T.ink}}>{l}</p>
                      <p style={{fontSize:10,color:T.fnt}}>{dim}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Screen position picker */}
            <div className="card" style={{padding:20}}>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Screen Position</p>
              <p style={{fontSize:12,color:T.lt,marginBottom:14}}>Choose where the widget button appears on your visitors' screen.</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14,maxWidth:240}}>
                {[
                  ["top-left","↖"],["top-center","↑"],["top-right","↗"],
                  [null,null],[null,null],[null,null],
                  ["bottom-left","↙"],[null,null],["bottom-right","↘"],
                ].map(([pos,arrow],idx)=>(
                  <div key={idx} onClick={()=>pos&&setBrand(b=>({...b,bubble:pos}))} style={{height:46,borderRadius:9,border:pos?(brand.bubble===pos?`2px solid ${B}`:`1px solid ${S.brd}`):"none",background:pos?(brand.bubble===pos?BL:S.bg):"transparent",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:pos?"pointer":"default",gap:3,transition:"all .15s"}}>
                    {pos&&<>
                      <span style={{fontSize:18,color:brand.bubble===pos?B:T.fnt,lineHeight:1}}>{arrow}</span>
                      <span style={{fontSize:8,fontWeight:600,color:brand.bubble===pos?B:T.fnt,textAlign:"center",lineHeight:1.2}}>{pos.replace("-","\n")}</span>
                    </>}
                  </div>
                ))}
              </div>
              <div style={{display:"inline-flex",alignItems:"center",gap:8,background:BL,border:`1px solid ${BM}`,borderRadius:8,padding:"6px 12px"}}>
                <Ic n="map" s={11} c={B}/>
                <span style={{fontSize:12,color:T.mid}}>Position: </span>
                <span style={{fontSize:12,fontWeight:700,color:B}}>{brand.bubble||"bottom-right"}</span>
              </div>
            </div>

            <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save} style={{alignSelf:"flex-start",minWidth:180}}>
              {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Branding</>}
            </button>
          </div>

          {/* ── RIGHT: live preview ── */}
          <div style={{position:"sticky",top:20,display:"flex",flexDirection:"column",gap:14}}>
            <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.8}}>Live Preview</p>

            {/* Chat window preview */}
            <div style={{borderRadius:14,overflow:"hidden",boxShadow:"0 6px 28px rgba(0,0,0,.12)",border:`1px solid ${S.brd}`}}>
              {/* Header */}
              <div style={{background:brand.primary,padding:"11px 14px",display:"flex",alignItems:"center",gap:9}}>
                <div style={{width:32,height:32,borderRadius:9,background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:12,color:"#fff",letterSpacing:1,flexShrink:0}}>{brand.logo||"?"}</div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{color:"#fff",fontWeight:700,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{brand.widget_name||"Widget Name"}</p>
                  <div style={{display:"flex",alignItems:"center",gap:4,marginTop:1}}><div style={{width:6,height:6,borderRadius:"50%",background:"#4ADE80"}}/><p style={{color:"rgba(255,255,255,.7)",fontSize:10}}>Online</p></div>
                </div>
                <div style={{display:"flex",gap:5}}>{[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:"rgba(255,255,255,.35)"}}/>)}</div>
              </div>
              {/* Messages area */}
              <div style={{padding:"13px 12px",background:S.bg,display:"flex",flexDirection:"column",gap:9}}>
                <div style={{background:S.w,padding:"8px 12px",borderRadius:"12px 12px 12px 4px",fontSize:12.5,border:`1px solid ${S.brd}`,maxWidth:"85%",lineHeight:1.5,color:T.ink}}>{brand.greeting||"Hi there! How can we help?"}</div>
                <div style={{background:brand.secondary,padding:"8px 12px",borderRadius:"12px 12px 4px 12px",fontSize:12.5,color:"#fff",marginLeft:"auto",maxWidth:"78%",lineHeight:1.5}}>Hi, I need some help please</div>
                <div style={{background:S.w,padding:"8px 12px",borderRadius:"12px 12px 12px 4px",fontSize:12.5,border:`1px solid ${S.brd}`,maxWidth:"85%",lineHeight:1.5,color:T.ink}}>Of course! What can I help you with today?</div>
              </div>
              {/* Input bar */}
              <div style={{background:S.w,borderTop:`1px solid ${S.brd}`,padding:"8px 12px",display:"flex",gap:7,alignItems:"center"}}>
                <div style={{flex:1,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:20,padding:"6px 12px",fontSize:12,color:T.fnt}}>Type a message…</div>
                <div style={{width:32,height:32,borderRadius:"50%",background:brand.primary,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 2px 8px ${brand.primary}55`}}><Ic n="arr" s={13} c="#fff"/></div>
              </div>
            </div>

            {/* Screen position visualiser */}
            <div style={{position:"relative",height:140,background:BL,borderRadius:12,border:`1px solid ${BM}`,overflow:"hidden"}}>
              <div style={{position:"absolute",inset:"10px 10px 10px 10px",border:`1.5px dashed ${BM}`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <p style={{fontSize:10.5,color:B,fontWeight:500,opacity:.6}}>Your website</p>
              </div>
              {/* Floating widget at selected position */}
              <div style={{
                position:"absolute",
                top:    brand.bubble&&brand.bubble.startsWith("top")    ? 12 : "auto",
                bottom: !brand.bubble||brand.bubble.startsWith("bottom") ? 12 : "auto",
                left:   brand.bubble&&brand.bubble.endsWith("left")     ? 12 : "auto",
                right:  !brand.bubble||brand.bubble.endsWith("right")||brand.bubble.endsWith("center") ? 12 : "auto",
                width:  {xs:22,sm:30,md:38,lg:50,xl:58}[brand.widgetSize]||38,
                height: {xs:22,sm:30,md:38,lg:50,xl:58}[brand.widgetSize]||38,
                borderRadius:"50%",
                background:brand.primary,
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow:`0 4px 16px ${brand.primary}70`,
                transition:"all .3s cubic-bezier(.34,1.56,.64,1)",
              }}>
                <Ic n="chat" s={{xs:9,sm:12,md:15,lg:20,xl:24}[brand.widgetSize]||14} c="#fff"/>
              </div>
            </div>
            <p style={{fontSize:11,color:T.fnt,textAlign:"center",marginTop:-8}}>
              {(brand.bubble||"bottom-right").replace("-"," ")} · {{xs:"XSmall",sm:"Small",md:"Medium",lg:"Large",xl:"XLarge"}[brand.widgetSize]||"Medium"} button · {{xs:"Mini",sm:"Compact",md:"Standard",lg:"Large",xl:"Wide"}[brand.chatSize]||"Standard"} window
            </p>
          </div>
        </div>
      )}

      {/* BILLING */}
      {tab==="billing"&&(
        <div style={{maxWidth:580,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:20}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Current Subscription</p>
            {[["Plan",billing.plan],["Billing Cycle",billing.cycle],["Next Invoice",billing.nextDate],["Amount Due",billing.amount],["Payment Method",billing.card],["Card Expiry",billing.cardExpiry]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}>
                <span style={{fontSize:12.5,color:T.fnt}}>{l}</span>
                <span style={{fontSize:13,fontWeight:600,color:T.ink}}>{v}</span>
              </div>
            ))}
            <div style={{marginTop:16,display:"flex",gap:8}}>
              <button className="btn bp sm" onClick={()=>{
                try{
                  const c=localStorage.getItem("hindle_config");
                  const cfg=c?JSON.parse(c):{};
                  const plan=cfg.plan||"starter";
                  // Navigate to internal purchase wizard
                  window.__hindleGoUpgrade&&window.__hindleGoUpgrade(plan);
                }catch(e){}
              }}>Upgrade Plan <Ic n="arr" s={11} c="#fff"/></button>
              <button className="btn bo sm" onClick={async()=>{
                const email=(()=>{try{const c=JSON.parse(localStorage.getItem("hindle_config")||"{}");return c.email||"";}catch(e){return "";}})();
                if(!email){alert("No billing email on file. Please update your Organisation settings first.");return;}
                alert("To update your card, please contact support@hindleconsultants.com with your account email: "+email);
              }}>Update Card</button>
              <button className="btn bo sm">Download Invoice</button>
            </div>
          </div>
          <div className="card" style={{padding:16}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Invoice History</p>
            {(invoiceHistory||[]).map(([mo,amt,plan,status])=>(
              <div key={mo} style={{display:"flex",gap:12,alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${S.bg}`}}>
                <span style={{fontSize:12.5,color:T.mid,flex:1}}>{mo}</span>
                <Chip s={plan.toLowerCase()}/>
                <span className="mono" style={{fontSize:12.5,fontWeight:700}}>{amt}</span>
                <span style={{background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700}}>{status}</span>
                <button className="btn bo sm" style={{fontSize:11}}>PDF</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NOTIFICATIONS */}
      {tab==="notifications"&&(
        <div style={{maxWidth:540,display:"flex",flexDirection:"column",gap:14}}>
          {[
            {title:"Email Notifications",items:[["Handoff alerts — agent email","email_handoff"],["Weekly digest report","email_weekly"],["CSAT survey responses","email_csat"]]},
            {title:"SMS Notifications",items:[["Agent handoff SMS","sms_handoff"],["Trial expiry reminders","sms_trial"]]},
          ].map(sec=>(
            <div key={sec.title} className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>{sec.title}</p>
              {sec.items.map(([l,k])=>(
                <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}>
                  <span style={{fontSize:13,color:T.mid}}>{l}</span>
                  <div style={{width:38,height:20,borderRadius:10,background:notif[k]?G.g:S.brd,position:"relative",cursor:"pointer",transition:"background .2s"}} onClick={()=>setNotif(n=>({...n,[k]:!n[k]}))}>
                    <div style={{position:"absolute",top:3,left:notif[k]?20:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Weekly Digest</p>
            {[["daily","Daily summary"],["weekly","Weekly summary"],["none","No digest"]].map(([v,l])=>(
              <label key={v} style={{display:"flex",alignItems:"center",gap:9,marginBottom:10,cursor:"pointer"}}>
                <input type="radio" name="digest" value={v} checked={notif.digest===v} onChange={()=>setNotif(n=>({...n,digest:v}))} style={{accentColor:B}}/>
                <span style={{fontSize:13,color:notif.digest===v?T.ink:T.mid,fontWeight:notif.digest===v?600:400}}>{l}</span>
              </label>
            ))}
          </div>
          <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save} style={{alignSelf:"flex-start",minWidth:160}}>
            {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Preferences</>}
          </button>
        </div>
      )}

      {/* SECURITY */}
      {tab==="security"&&(
        <div style={{maxWidth:540,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Two-Factor Authentication</p>
            <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{width:38,height:20,borderRadius:10,background:twoFA?G.g:S.brd,position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0,marginTop:3}} onClick={()=>setTwoFA(v=>!v)}>
                <div style={{position:"absolute",top:3,left:twoFA?20:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
              </div>
              <div>
                <p style={{fontSize:13,fontWeight:600}}>{twoFA?"2FA Enabled":"2FA Disabled"}</p>
                <p style={{fontSize:12,color:T.fnt,lineHeight:1.55}}>A 6-digit code is sent to your email each time you log in from a new device.</p>
              </div>
            </div>
          </div>
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Change Password</p>
            {[["Current Password","current",""],["New Password","new",""],["Confirm New Password","confirm",""]].map(([l,k,ph])=>(
              <div key={k} style={{marginBottom:10}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
                <input className="inp" type="password" placeholder={ph||l}/>
              </div>
            ))}
            <button className="btn bp sm">Update Password</button>
          </div>
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>Active Sessions</p>
            {sessions.length>0?sessions.map((s,i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${S.bg}`}}>
                <div style={{width:32,height:32,borderRadius:8,background:s.current?BL:S.bg,border:`1px solid ${s.current?BM:S.brd}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="globe" s={14} c={s.current?B:T.fnt}/></div>
                <div style={{flex:1}}>
                  <p style={{fontSize:12.5,fontWeight:600}}>{s.device}{s.current&&<span style={{marginLeft:6,fontSize:10,background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,borderRadius:4,padding:"1px 6px",fontWeight:700}}>This session</span>}</p>
                  <p style={{fontSize:11.5,color:T.fnt}}>{s.ip} · {s.last}</p>
                </div>
                {!s.current&&<button className="btn bd sm" style={{fontSize:11,color:G.r,borderColor:G.r}}>Revoke</button>}
              </div>
            )):<div style={{padding:"20px 0",textAlign:"center",color:T.fnt,fontSize:13}}>No active sessions found</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SETTINGS — SUPER ADMIN
// ══════════════════════════════════════════════════════════════
// ── Admin Accounts panel — create / remove super admin users ──
const AdminAccountsPanel=()=>{
  const [admins,setAdmins]=useState([]);
  const [loading,setLoading]=useState(true);
  const [form,setForm]=useState({email:"",password:"",name:"",phone:""});
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState("");
  const [ok,setOk]=useState("");

  const load=async()=>{
    setLoading(true);
    try{
      const r=await fetch(API_URL+"/api/admin-settings");
      if(r.ok){const d=await r.json();setAdmins(Array.isArray(d.adminAccounts)?d.adminAccounts:[]);}
    }catch(e){}
    setLoading(false);
  };

  useEffect(()=>{load();},[]);

  const save=async(list)=>{
    await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({adminAccounts:list})}).catch(()=>{});
    setAdmins(list);
  };

  const addAdmin=async()=>{
    if(!form.email.trim()||!form.password.trim()){setErr("Email and password are required.");return;}
    if(form.password.length<6){setErr("Password must be at least 6 characters.");return;}
    setErr("");setSaving(true);
    const next=[...admins,{email:form.email.trim().toLowerCase(),name:form.name.trim()||form.email.trim(),phone:form.phone.trim()||null,createdAt:new Date().toISOString()}];
    // Save account list + their password via admin-settings
    await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({adminAccounts:next,adminPasswordFor:{email:form.email.trim().toLowerCase(),password:form.password}})}).catch(()=>{});
    setAdmins(next);setForm({email:"",password:"",name:""});setShowAdd(false);
    setOk("Admin account created.");setTimeout(()=>setOk(""),3000);
    setSaving(false);
  };

  const removeAdmin=async(email)=>{
    if(!window.confirm(`Remove ${email}?`))return;
    const next=admins.filter(a=>a.email!==email);
    await save(next);
    setOk("Account removed.");setTimeout(()=>setOk(""),3000);
  };

  const resetPassword=async(email)=>{
    const pw=window.prompt(`New password for ${email} (min 6 chars):`);
    if(!pw||pw.length<6){alert("Password too short.");return;}
    await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({adminPasswordFor:{email,password:pw}})}).catch(()=>{});
    setOk("Password updated.");setTimeout(()=>setOk(""),3000);
  };

  return(
    <div style={{maxWidth:560,display:"flex",flexDirection:"column",gap:14}}>
      <div className="card" style={{padding:18}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <p style={{fontSize:13.5,fontWeight:700}}>Super Admin Accounts</p>
            <p style={{fontSize:12,color:T.fnt,marginTop:2}}>Each admin has their own email and password. All can sign in at the standard login page.</p>
          </div>
          <button className="btn bp sm" onClick={()=>{setShowAdd(s=>!s);setErr("");}}>
            <Ic n="plus" s={12} c="#fff"/> Add Admin
          </button>
        </div>

        {ok&&<div style={{padding:"7px 11px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7,marginBottom:10,fontSize:12,color:G.g,fontWeight:600}}>{ok}</div>}
        {err&&<div style={{padding:"7px 11px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,marginBottom:10,fontSize:12,color:G.r,fontWeight:600}}>{err}</div>}

        {showAdd&&(
          <div style={{padding:14,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:9,marginBottom:14}}>
            <p style={{fontSize:12.5,fontWeight:700,marginBottom:10}}>New Admin Account</p>
            {[["Full Name","name","text","Ian Hindle"],["Phone","phone","tel","+1 555 000 0000"],["Email","email","email","admin@company.com"],["Password","password","password","Min 6 characters"]].map(([l,k,t,ph])=>(
              <div key={k} style={{marginBottom:9}}>
                <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:3}}>{l}</label>
                <input className="inp" type={t} placeholder={ph} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{fontSize:12}}/>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button className="btn bp sm" onClick={addAdmin} disabled={saving}>{saving?<><Spinner size={12}/> Saving…</>:"Create Account"}</button>
              <button className="btn bo sm" onClick={()=>{setShowAdd(false);setErr("");}}>Cancel</button>
            </div>
          </div>
        )}

        {loading?<div style={{textAlign:"center",padding:20}}><Spinner size={16}/></div>:(
          admins.length===0?(
            <div style={{textAlign:"center",padding:24,color:T.fnt}}>
              <Ic n="users" s={24} c={T.fnt}/>
              <p style={{marginTop:8,fontSize:13}}>No additional admin accounts yet.</p>
              <p style={{fontSize:12,marginTop:4}}>The primary admin is <strong>admin@hindleconsultants.com</strong>. Add more accounts above.</p>
            </div>
          ):(
            admins.map(a=>(
              <div key={a.email} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${S.brd}`}}>
                <Av name={a.name||a.email} size={32} col={B}/>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:13,fontWeight:600}}>{a.name||a.email}</p>
                  <p style={{fontSize:11.5,color:T.fnt}}>{a.email}</p>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn bo sm" style={{fontSize:11}} onClick={()=>resetPassword(a.email)}>Reset PW</button>
                  <button className="btn bo sm" style={{fontSize:11,color:G.r,borderColor:G.rbd}} onClick={()=>removeAdmin(a.email)}>Remove</button>
                </div>
              </div>
            ))
          )
        )}
      </div>
      <PwChangeCard/>
    </div>
  );
};

const PwChangeCard=()=>{
  const [cur,setCur]=useState("");
  const [nw,setNw]=useState("");
  const [conf,setConf]=useState("");
  const [err,setErr]=useState("");
  const [ok,setOk]=useState(false);

  const handleChange=()=>{
    setErr("");setOk(false);
    const storedPass=localStorage.getItem("hindle_admin_pass")||"admin";
    if(!cur||!nw||!conf){setErr("All fields are required.");return;}
    if(cur!==storedPass){setErr("Current password is incorrect.");return;}
    if(nw.length<6){setErr("New password must be at least 6 characters.");return;}
    if(nw!==conf){setErr("New passwords do not match.");return;}
    localStorage.setItem("hindle_admin_pass",nw);
    // Persist to DB — server reads from platform config on login
    fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({adminPassword:nw})}).catch(()=>{});
    setOk(true);setCur("");setNw("");setConf("");
    setTimeout(()=>setOk(false),3000);
  };

  return(
    <div className="card" style={{padding:18}}>
      <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Change Admin Password</p>
      {[["Current Password",cur,setCur],["New Password",nw,setNw],["Confirm New Password",conf,setConf]].map(([l,v,fn])=>(
        <div key={l} style={{marginBottom:10}}>
          <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
          <input className="inp" type="password" placeholder={l} value={v}
            onChange={e=>{fn(e.target.value);setErr("");setOk(false);}}
            onKeyDown={e=>e.key==="Enter"&&handleChange()}/>
        </div>
      ))}
      {err&&(
        <div style={{padding:"8px 12px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,marginBottom:10,display:"flex",gap:6,alignItems:"center"}}>
          <Ic n="x" s={12} c={G.r}/><p style={{fontSize:12.5,color:G.r}}>{err}</p>
        </div>
      )}
      {ok&&(
        <div style={{padding:"8px 12px",background:G.gb,border:`1px solid ${G.gbd}`,borderRadius:7,marginBottom:10,display:"flex",gap:6,alignItems:"center"}}>
          <Ic n="chk" s={12} c={G.g}/><p style={{fontSize:12.5,color:G.g,fontWeight:600}}>Password updated successfully!</p>
        </div>
      )}
      <button className="btn bp sm" onClick={handleChange}><Ic n="lock" s={12} c="#fff"/> Update Password</button>
      <p style={{fontSize:11,color:T.fnt,marginTop:8}}>Must be at least 6 characters. You'll use this next time you sign in.</p>
    </div>
  );
};

const PSettingsSuperAdmin=()=>{
  const [tab,setTab]=useState("profile");
  const [saved,setSaved]=useState(false);
  const save=async()=>{
    try{localStorage.setItem("hindle_admin_profile",JSON.stringify(profile));}catch(e){}
    try{localStorage.setItem("hindle_platform_config",JSON.stringify(platform));}catch(e){}
    try{localStorage.setItem("hindle_github_config",JSON.stringify({owner:ghOwner,repo:ghRepo,branch:ghBranch,path:ghPath,msg:ghMsg,pat:ghPat}));}catch(e){}
    try{
      await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          profile,
          platform,
          github:{owner:ghOwner,repo:ghRepo,branch:ghBranch,path:ghPath,msg:ghMsg,pat:ghPat},
        }),
      });
    }catch(e){}
    setSaved(true);setTimeout(()=>setSaved(false),2200);
  };
  const [profile,setProfile]=useState(()=>{
    try{
      const s=localStorage.getItem("hindle_admin_profile");
      if(s) return {...{name:"Ian Hindle",email:"admin@hindleconsultants.com",phone:"+44 7700 900100",title:"Platform Administrator",company:"Hindle Consultants"},...JSON.parse(s)};
    }catch(e){}
    return {name:"Ian Hindle",email:"admin@hindleconsultants.com",phone:"+44 7700 900100",title:"Platform Administrator",company:"Hindle Consultants"};
  });
  // Auto-save profile changes
  useEffect(()=>{ 
    const v=JSON.stringify(profile);
    try{localStorage.setItem("hindle_admin_profile",v);}catch(e){}
    
  },[profile]);
  const [platform,setPlatform]=useState(()=>{
    try{const s=localStorage.getItem("hindle_platform_config");if(s)return {...{name:"Hindle",domain:"hindleconsultants.com",supportEmail:"support@hindleconsultants.com",fromName:"Hindle Consultants",trialDays:"14",reminderDays:"12,13,14"},...JSON.parse(s)};}catch(e){}
    return {name:"Hindle",domain:"hindleconsultants.com",supportEmail:"support@hindleconsultants.com",fromName:"Hindle Consultants",trialDays:"14",reminderDays:"12,13,14"};
  });
  useEffect(()=>{ try{ localStorage.setItem("hindle_platform_config",JSON.stringify(platform)); }catch(e){} },[platform]);
  const [twoFA,setTwoFA]=useState(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");return p.twoFaEnabled!==false;}catch(e){return false;}});
  const [twoFaMethod,setTwoFaMethod]=useState(()=>{try{const p=JSON.parse(localStorage.getItem("hindle_admin_profile")||"{}");return p.twoFaMethod||"email";}catch(e){return "email";}});
  const [apiKeys]=useState([{key:"hndl_live_sk_••••••••••••4f2a",created:"2026-01-01",last:"2m ago",scope:"Full access"},{key:"hndl_live_sk_••••••••••••9c1b",created:"2026-02-01",last:"Never",scope:"Read only"}]);

  // ── GitHub Sync State — initialise from localStorage, then hydrate from DB on mount ──
  const _ghCfg=(()=>{try{const s=localStorage.getItem("hindle_github_config");return s?JSON.parse(s):{};}catch(e){return {};}})();
  const [ghPat,setGhPat]=useState(_ghCfg.pat||"");
  const [ghOwner,setGhOwner]=useState(_ghCfg.owner||"");
  const [ghRepo,setGhRepo]=useState(_ghCfg.repo||"");
  const [ghBranch,setGhBranch]=useState(_ghCfg.branch||"main");
  const [ghPath,setGhPath]=useState((_ghCfg.path&&_ghCfg.path!=='src/hindle-full.jsx')?_ghCfg.path:"src/components/chatbot_1.jsx");
  const [ghMsg,setGhMsg]=useState(_ghCfg.msg||"chore: update Hindle app from Claude");
  const [ghStatus,setGhStatus]=useState(null);
  const [ghErrText,setGhErrText]=useState("");
  const [ghCommits,setGhCommits]=useState([]);
  const [ghCurrentSha,setGhCurrentSha]=useState(null);
  const [ghPushState,setGhPushState]=useState("idle");
  const [ghPushMsg,setGhPushMsg]=useState("");
  const [ghLastPushed,setGhLastPushed]=useState(null);
  const [ghDiff,setGhDiff]=useState(null);
  const [ghShowPat,setGhShowPat]=useState(false);
  const [ghSaved,setGhSaved]=useState(false);
  const [ghFileContent,setGhFileContent]=useState(null); // text of file chosen for push
  const [ghFileName,setGhFileName]=useState("");

  const APP_FILE_KEY = "hindle_github_config";

  const _ghSave=(data)=>{
    const v=JSON.stringify(data);
    try{localStorage.setItem(APP_FILE_KEY,v);}catch(e){}
    try{sessionStorage.setItem(APP_FILE_KEY,v);}catch(e){}
  };
  const saveGhConfig=(showFlash=false)=>{
    _ghSave({owner:ghOwner,repo:ghRepo,branch:ghBranch,path:ghPath,msg:ghMsg,pat:ghPat});
    if(showFlash){setGhSaved(true);setTimeout(()=>setGhSaved(false),2500);}
  };

  // On mount: load settings from DB so they survive logout/login on any browser
  useEffect(()=>{
    fetch(API_URL+"/api/admin-settings")
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!d) return;
        if(d.github&&d.github.pat){
          setGhPat(d.github.pat||"");
          setGhOwner(d.github.owner||"");
          setGhRepo(d.github.repo||"");
          setGhBranch(d.github.branch||"main");
          setGhPath((d.github.path&&d.github.path!=='src/hindle-full.jsx')?d.github.path:"src/components/chatbot_1.jsx");
          setGhMsg(d.github.msg||"chore: update Hindle app from Claude");
          _ghSave(d.github); // sync to localStorage too
        }
      })
      .catch(()=>{});
  },[]);

  // Auto-save to localStorage only when fields change (NOT to DB — prevents race on mount)
  useEffect(()=>{
    _ghSave({owner:ghOwner,repo:ghRepo,branch:ghBranch,path:ghPath,msg:ghMsg,pat:ghPat});
  },[ghOwner,ghRepo,ghBranch,ghPath,ghMsg,ghPat]);

  const cleanPath=(p)=>p
    .trim()
    .replace(/\\/g,"/")        // backslashes → forward slashes
    .replace(/^\/+/,"")        // strip leading slashes
    .replace(/\/+$/,"")        // strip trailing slashes
    .replace(/\/\/+/g,"/")     // collapse double slashes
    .replace(/\s+/g,"-");      // spaces → hyphens

  const pathError=(p)=>{
    const c=cleanPath(p);
    if(!c) return "Path is required";
    if(/[<>:"|?*]/.test(c)) return 'Path contains invalid characters: < > : " | ? *';
    if(c.split("/").some(seg=>seg===".."||seg===".")) return "Path cannot contain . or .. segments";
    return null;
  };

  const ghPathClean=cleanPath(ghPath);
  const ghPathErr=ghPath?pathError(ghPath):null;

  const ghHeaders=()=>({
    "Authorization":`Bearer ${ghPat}`,
    "Accept":"application/vnd.github+json",
    "X-GitHub-Api-Version":"2022-11-28",
    "Content-Type":"application/json",
  });

  const verifyConnection=async()=>{
    if(!ghPat||!ghOwner||!ghRepo){setGhErrText("Please fill in PAT, owner and repository name.");setGhStatus("error");return;}
    if(pathError(ghPath)){setGhErrText(`File path error: ${pathError(ghPath)}`);setGhStatus("error");return;}
    setGhStatus("checking");setGhErrText("");setGhCommits([]);setGhCurrentSha(null);setGhDiff(null);
    saveGhConfig();
    try{
      // 1. Verify repo access
      const repoRes = await fetch(`https://api.github.com/repos/${ghOwner}/${ghRepo}`, {headers:ghHeaders()});
      if(!repoRes.ok){
        const d=await repoRes.json().catch(()=>({}));
        throw new Error(d.message||`HTTP ${repoRes.status}`);
      }
      await repoRes.json();

      // 2. Get file if it exists (to get current SHA for updating)
      const fileRes = await fetch(`https://api.github.com/repos/${ghOwner}/${ghRepo}/contents/${ghPathClean}?ref=${ghBranch}`, {headers:ghHeaders()});
      if(fileRes.ok){
        const fileData = await fileRes.json();
        setGhCurrentSha(fileData.sha);
        // Decode current content and compute rough diff
        try{
          const currentContent = atob(fileData.content.replace(/\n/g,""));
          const currentLines = currentContent.split("\n").length;
          // We'll compare against the file we'd push (the jsx source itself)
          // For demo we show size comparison
          setGhDiff({remoteLines: currentLines, remoteSize: Math.round(fileData.size/1024*10)/10});
        }catch(e){}
      } else {
        setGhCurrentSha(null);
        setGhDiff({remoteLines:0, remoteSize:0});
      }

      // 3. Get recent commits
      const commitsRes = await fetch(`https://api.github.com/repos/${ghOwner}/${ghRepo}/commits?path=${ghPathClean}&per_page=8&sha=${ghBranch}`, {headers:ghHeaders()});
      if(commitsRes.ok){
        const commits = await commitsRes.json();
        setGhCommits(commits.map(c=>({
          sha: c.sha.slice(0,7),
          message: c.commit.message,
          author: c.commit.author.name,
          date: new Date(c.commit.author.date).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}),
          url: c.html_url,
        })));
      }

      setGhStatus("connected");
    }catch(err){
      setGhStatus("error");
      setGhErrText(err.message||"Connection failed. Check your PAT and repo details.");
    }
  };

  const pushToGitHub=async()=>{
    if(!ghPat||!ghOwner||!ghRepo||!ghPathClean||pathError(ghPath)){setGhPushState("error");setGhPushMsg(pathError(ghPath)||"Missing configuration. Fill in all fields.");return;}
    if(!ghFileContent){setGhPushState("error");setGhPushMsg("No file selected. Choose a file to push first.");return;}
    setGhPushState("pushing");setGhPushMsg("Fetching latest SHA…");

    try{
      // Always fetch latest SHA before pushing to avoid conflicts
      let latestSha=null;
      try{
        const shaRes=await fetch(`https://api.github.com/repos/${ghOwner}/${ghRepo}/contents/${ghPathClean}?ref=${ghBranch}`,{headers:ghHeaders()});
        if(shaRes.ok){const shaData=await shaRes.json();latestSha=shaData.sha||null;setGhCurrentSha(latestSha);}
      }catch(e){}

      setGhPushMsg("Pushing…");

      const encodedContent = btoa(unescape(encodeURIComponent(ghFileContent)));
      const body = {
        message: `${ghMsg} [${new Date().toLocaleDateString("en-GB")}]`,
        content: encodedContent,
        branch: ghBranch,
        ...(latestSha ? {sha: latestSha} : {}),
      };

      const res = await fetch(`https://api.github.com/repos/${ghOwner}/${ghRepo}/contents/${ghPathClean}`, {
        method:"PUT",
        headers:ghHeaders(),
        body:JSON.stringify(body),
      });

      if(!res.ok){
        const d=await res.json().catch(()=>({}));
        throw new Error(d.message||`HTTP ${res.status}`);
      }
      const data = await res.json();
      const newSha = data.content?.sha;
      if(newSha) setGhCurrentSha(newSha);
      setGhPushState("success");
      setGhLastPushed(new Date());
      setGhPushMsg(`Pushed ${ghFileName} — commit: ${data.commit?.sha?.slice(0,7)||"—"}`);
      setTimeout(()=>verifyConnection(),1200);
      setTimeout(()=>setGhPushState("idle"),6000);
    }catch(err){
      setGhPushState("error");
      setGhPushMsg(err.message||"Push failed.");
    }
  };

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{display:"flex",gap:3,marginBottom:20,borderBottom:`1px solid ${S.brd}`,paddingBottom:0,flexWrap:"wrap"}}>
        {[["profile","👤 My Profile"],["platform","⚙️ Platform"],["security","🔒 Security"],["admins","👥 Admin Accounts"],["api","🔑 API Keys"],["github","🐙 GitHub Sync"],["danger","⚠️ Danger Zone"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer",background:"transparent",color:tab===t?B:T.fnt,border:"none",borderBottom:tab===t?`2px solid ${B}`:"2px solid transparent",marginBottom:-1,transition:"all .15s",whiteSpace:"nowrap"}}>{l}</button>
        ))}
      </div>

      {/* PROFILE */}
      {tab==="profile"&&(
        <div style={{maxWidth:540,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20,paddingBottom:16,borderBottom:`1px solid ${S.brd}`}}>
              <div style={{width:64,height:64,borderRadius:16,background:`${G.r}12`,border:`2px solid ${G.r}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:24,fontWeight:800,color:G.r}}>IH</span>
              </div>
              <div>
                <p style={{fontSize:16,fontWeight:800,color:T.ink}}>{profile.name}</p>
                <p style={{fontSize:12.5,color:T.fnt}}>{profile.email}</p>
                <span style={{display:"inline-flex",alignItems:"center",gap:4,background:`${G.r}12`,border:`1px solid ${G.r}25`,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color:G.r,marginTop:4}}>
                  <Ic n="shld" s={9} c={G.r}/> Super Admin
                </span>
              </div>
            </div>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Profile Details</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[["Full Name","name","text"],["Job Title","title","text"],["Company","company","text"],["Phone","phone","text"],["Email Address","email","email"]].map(([l,k,type])=>(
                <div key={k} style={k==="email"?{gridColumn:"1/-1"}:{}}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
                  <input className="inp" type={type} value={profile[k]||""} onChange={e=>{
                    const updated={...profile,[k]:e.target.value};
                    setProfile(updated);
                    try{localStorage.setItem("hindle_admin_profile",JSON.stringify(updated));}catch(err){}
                  }}/>
                </div>
              ))}
            </div>
          </div>
          <button className="btn bp" onClick={()=>{
            try{localStorage.setItem("hindle_admin_profile",JSON.stringify(profile));}catch(e){}
            save();
          }} style={{alignSelf:"flex-start",minWidth:160,background:saved?G.g:undefined,borderColor:saved?G.g:undefined}}>
            {saved?<><Ic n="chk" s={13} c="#fff"/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Profile</>}
          </button>
        </div>
      )}
      {/* PLATFORM */}
      {tab==="platform"&&(
        <div style={{maxWidth:580,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:20}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Platform Configuration</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[["Platform Name","name","text"],["Primary Domain","domain","text"],["Support Email","supportEmail","email"],["Email From Name","fromName","text"],["Trial Length (days)","trialDays","number"],["Reminder Days (CSV)","reminderDays","text"]].map(([l,k,type])=>(
                <div key={k}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
                  <input className="inp" type={type} value={platform[k]} onChange={e=>setPlatform(p=>({...p,[k]:e.target.value}))}/>
                </div>
              ))}
            </div>
          </div>
          <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save} style={{alignSelf:"flex-start",minWidth:160}}>
            {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Platform Config</>}
          </button>
        </div>
      )}

      {/* SECURITY */}
      {tab==="security"&&(
        <div style={{maxWidth:540,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Two-Factor Authentication</p>

            {/* Enable/disable toggle */}
            <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:16,paddingBottom:16,borderBottom:`1px solid ${S.brd}`}}>
              <div style={{width:38,height:20,borderRadius:10,background:twoFA?G.g:S.brd,position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0,marginTop:3}} onClick={()=>setTwoFA(v=>!v)}>
                <div style={{position:"absolute",top:3,left:twoFA?20:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
              </div>
              <div>
                <p style={{fontSize:13,fontWeight:600}}>{twoFA?"2FA Enabled — login requires verification":"2FA Disabled"}</p>
                <p style={{fontSize:12,color:T.fnt}}>When enabled, a code is sent after password entry to verify identity.</p>
              </div>
            </div>

            {/* 2FA method */}
            {twoFA&&(
              <div style={{marginBottom:16,paddingBottom:16,borderBottom:`1px solid ${S.brd}`}}>
                <p style={{fontSize:12,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:.6,marginBottom:10}}>Delivery Method</p>
                <div style={{display:"flex",gap:10}}>
                  {[["email","✉️ Email","Code sent to login email"],["sms","📱 SMS","Code sent to phone number"]].map(([v,label,hint])=>(
                    <div key={v} style={{flex:1,padding:"11px 14px",borderRadius:9,border:`2px solid ${twoFaMethod===v?B:S.brd}`,background:twoFaMethod===v?BL:"#fff",cursor:"pointer",transition:"all .15s"}} onClick={()=>setTwoFaMethod(v)}>
                      <p style={{fontSize:13,fontWeight:700,color:twoFaMethod===v?B:T.ink,marginBottom:2}}>{label}</p>
                      <p style={{fontSize:11.5,color:T.fnt}}>{hint}</p>
                    </div>
                  ))}
                </div>
                {twoFaMethod==="sms"&&(
                  <div style={{marginTop:12,padding:"10px 13px",background:G.ab,border:`1px solid ${G.abd}`,borderRadius:8,display:"flex",gap:8,alignItems:"flex-start"}}>
                    <Ic n="warn" s={13} c={G.a}/>
                    <p style={{fontSize:12,color:G.at}}>SMS 2FA uses the phone number in your <strong>Profile</strong> tab. Current: <strong>{profile?.phone||"not set"}</strong></p>
                  </div>
                )}
              </div>
            )}

            <button className="btn bp sm" onClick={async()=>{
              await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
                body:JSON.stringify({profile:{...profile,twoFaEnabled:twoFA,twoFaMethod}})}).catch(()=>{});
              setSaved(true);setTimeout(()=>setSaved(false),2200);
            }}><Ic n="chk" s={12} c="#fff"/> Save 2FA Settings</button>
            {saved&&<p style={{fontSize:12,color:G.g,marginTop:8,fontWeight:600}}>✓ Saved</p>}
          </div>
          <PwChangeCard/>
        </div>
      )}

      {/* API KEYS */}
      {/* ADMIN ACCOUNTS */}
      {tab==="admins"&&(
        <AdminAccountsPanel/>
      )}

      {tab==="api"&&(
        <div style={{maxWidth:600,display:"flex",flexDirection:"column",gap:14}}>
          <div className="card">
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h3 style={{fontSize:13.5,fontWeight:700}}>API Keys</h3>
              <button className="btn bp sm"><Ic n="plus" s={12} c="#fff"/> Create Key</button>
            </div>
            {apiKeys.map((k,i)=>(
              <div key={i} style={{padding:"13px 16px",borderBottom:`1px solid ${S.bg}`,display:"flex",gap:12,alignItems:"center"}}>
                <div style={{width:32,height:32,borderRadius:8,background:BL,border:`1px solid ${BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="key" s={14} c={B}/></div>
                <div style={{flex:1}}>
                  <code className="mono" style={{fontSize:12.5,color:T.ink,fontWeight:600}}>{k.key}</code>
                  <p style={{fontSize:11.5,color:T.fnt,marginTop:2}}>{k.scope} · Created {k.created} · Last used {k.last}</p>
                </div>
                <button className="btn bd sm" style={{color:G.r,borderColor:G.r,fontSize:11}}>Revoke</button>
              </div>
            ))}
          </div>
          <div style={{padding:"12px 14px",background:G.ab,border:`1px solid ${G.abd}`,borderRadius:9,display:"flex",gap:9,alignItems:"flex-start"}}>
            <Ic n="warn" s={14} c={G.a}/>
            <p style={{fontSize:12.5,color:G.at,lineHeight:1.55}}>API keys grant full platform access. Never share them publicly. Rotate keys regularly and revoke any you no longer need.</p>
          </div>
        </div>
      )}

      {/* GITHUB SYNC */}
      {tab==="github"&&(
        <div style={{maxWidth:680,display:"flex",flexDirection:"column",gap:14}}>

          {/* Header banner */}
          <div style={{padding:"14px 18px",background:"#0d1117",borderRadius:12,display:"flex",alignItems:"center",gap:14}}>
            <svg width="36" height="36" viewBox="0 0 98 96" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" fill="#fff"/>
            </svg>
            <div style={{flex:1}}>
              <p style={{color:"#fff",fontWeight:700,fontSize:14,marginBottom:2}}>GitHub Sync</p>
              <p style={{color:"#6e7681",fontSize:12.5,lineHeight:1.5}}>Push your Hindle app file directly to a GitHub repository using the GitHub Contents API. Changes made in Claude can be committed with one click.</p>
            </div>
            {ghStatus==="connected"&&(
              <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(35,134,54,.2)",border:"1px solid rgba(35,134,54,.4)",borderRadius:20,padding:"4px 12px"}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:"#3fb950"}}/>
                <span style={{fontSize:12,color:"#3fb950",fontWeight:600}}>Connected</span>
              </div>
            )}
          </div>

          {/* Configuration */}
          <div className="card" style={{padding:20}}>
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
              <button className="btn bp sm" onClick={()=>saveGhConfig(true)} style={{minWidth:130,background:ghSaved?G.g:undefined,borderColor:ghSaved?G.g:undefined}}>
                {ghSaved?<><Ic n="chk" s={12} c="#fff"/> Saved!</>:<><Ic n="lock" s={12}/> Save Settings</>}
              </button>
            </div>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Repository Configuration</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>GitHub Owner / Username</label>
                <input className="inp" placeholder="e.g. hindleconsultants" value={ghOwner} onChange={e=>setGhOwner(e.target.value.trim())}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Repository Name</label>
                <input className="inp" placeholder="e.g. hindle-platform" value={ghRepo} onChange={e=>setGhRepo(e.target.value.trim())}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Branch</label>
                <input className="inp" placeholder="main" value={ghBranch} onChange={e=>setGhBranch(e.target.value.trim()||"main")}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>File Path in Repo</label>
                <input className={`inp${ghPathErr?" inp-err":ghPathClean&&!ghPathErr?" inp-ok":""}`}
                  placeholder="src/hindle-full.jsx" value={ghPath}
                  onChange={e=>setGhPath(e.target.value)}/>
                {ghPathErr
                  ? <p style={{fontSize:11,color:G.r,marginTop:4,display:"flex",alignItems:"center",gap:4}}><Ic n="x" s={10} c={G.r}/>{ghPathErr}</p>
                  : ghPathClean&&ghPathClean!==ghPath.trim()
                    ? <p style={{fontSize:11,color:G.a,marginTop:4}}>Will be sent as: <code style={{fontFamily:"monospace",background:G.ab,padding:"1px 5px",borderRadius:3}}>{ghPathClean}</code></p>
                    : ghPathClean
                      ? <p style={{fontSize:11,color:G.g,marginTop:4,display:"flex",alignItems:"center",gap:4}}><Ic n="chk" s={10} c={G.g}/> Valid path</p>
                      : null
                }
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>
                  Personal Access Token (PAT)
                  <span style={{fontWeight:400,color:T.fnt,marginLeft:6}}>Needs <code style={{background:S.bg,padding:"1px 5px",borderRadius:4}}>contents:write</code> scope</span>
                </label>
                <div style={{display:"flex",gap:8}}>
                  <input className="inp" type={ghShowPat?"text":"password"} placeholder="ghp_xxxxxxxxxxxxxxxxxxxx" value={ghPat} onChange={e=>{
                      const v=e.target.value.trim();
                      setGhPat(v);
                      try{const c=JSON.parse(localStorage.getItem("hindle_github_config")||"{}");c.pat=v;localStorage.setItem("hindle_github_config",JSON.stringify(c));}catch(err){}
                    }} style={{flex:1,fontFamily:"monospace"}}/>
                  <button className="btn bo sm" style={{flexShrink:0}} onClick={()=>setGhShowPat(v=>!v)}>{ghShowPat?"Hide":"Show"}</button>
                </div>
                <p style={{fontSize:11,color:T.fnt,marginTop:5}}>
                  Generate at <span style={{color:B,cursor:"pointer"}} onClick={()=>window.open("https://github.com/settings/tokens/new?scopes=repo&description=Hindle+Platform+Sync","_blank")}>github.com/settings/tokens</span> — select <strong>repo</strong> scope (classic token) or <strong>Contents: Read and Write</strong> (fine-grained).
                </p>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Default Commit Message</label>
                <input className="inp" value={ghMsg} onChange={e=>setGhMsg(e.target.value)} placeholder="chore: update Hindle app from Claude"/>
              </div>
            </div>

            {/* Error */}
            {ghStatus==="error"&&(
              <div style={{marginTop:12,padding:"10px 13px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:8,display:"flex",gap:8,alignItems:"flex-start"}}>
                <Ic n="x" s={13} c={G.r}/>
                <div>
                  <p style={{fontSize:12.5,fontWeight:600,color:G.r}}>Connection failed</p>
                  <p style={{fontSize:12,color:G.r,opacity:.8,marginTop:1}}>{ghErrText}</p>
                </div>
              </div>
            )}

            <div style={{marginTop:16,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <button className="btn bp" onClick={verifyConnection} disabled={ghStatus==="checking"} style={{minWidth:160}}>
                {ghStatus==="checking"?<><Spinner size={13}/> Verifying…</>:<><Ic n="link" s={13} c="#fff"/> Verify Connection</>}
              </button>
              <button className={`btn ${ghSaved?"bp":"bo"} sm`} onClick={()=>saveGhConfig(true)} style={{minWidth:130,background:ghSaved?G.g:undefined,borderColor:ghSaved?G.g:undefined}}>
                {ghSaved?<><Ic n="chk" s={12} c="#fff"/> Saved!</>:<><Ic n="lock" s={12}/> Save Settings</>}
              </button>
              {ghStatus==="connected"&&<span style={{fontSize:12,color:G.g,display:"flex",alignItems:"center",gap:5,fontWeight:600}}><Ic n="chk" s={13} c={G.g}/>Connected · {ghCurrentSha?"File found":"Will create"}</span>}
            </div>
          </div>

          {/* Remote file status */}
          {ghStatus==="connected"&&ghDiff&&(
            <div className="card" style={{padding:18}}>
              <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>Remote File Status</p>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
                {[
                  ["Repository",`${ghOwner}/${ghRepo}`,"bld"],
                  ["Branch",ghBranch,"git"],
                  ["File Path",ghPath,"copy"],
                  ["Remote Lines",ghDiff.remoteLines>0?ghDiff.remoteLines.toLocaleString():"New file","code"],
                  ["Remote Size",ghDiff.remoteSize>0?`${ghDiff.remoteSize} KB`:"—","doc"],
                  ["Status",ghCurrentSha?"Exists · will update":"New file · will create",ghCurrentSha?"chk":"plus"],
                ].map(([l,v,ic])=>(
                  <div key={l} style={{padding:"10px 13px",background:S.bg,borderRadius:8,border:`1px solid ${S.brd}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:4}}><Ic n={ic} s={11} c={T.fnt}/><p style={{fontSize:10.5,color:T.fnt,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{l}</p></div>
                    <p style={{fontSize:12.5,fontWeight:700,color:T.ink,wordBreak:"break-all"}}>{v}</p>
                  </div>
                ))}
              </div>

              {/* Push controls */}
              <div style={{padding:"14px 16px",background:"#0d1117",borderRadius:10,display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <p style={{color:"#8b949e",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>File to push</p>
                  <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",background:"#161b22",border:`1px solid ${ghFileContent?"#238636":"#30363d"}`,borderRadius:6,padding:"8px 12px"}}>
                    <Ic n="doc" s={13} c={ghFileContent?"#4ade80":"#8b949e"}/>
                    <span style={{fontSize:12.5,color:ghFileContent?"#4ade80":"#8b949e",fontFamily:"monospace",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {ghFileName||"Choose file to push (e.g. chatbot_1.jsx downloaded from Claude)…"}
                    </span>
                    {ghFileContent&&<span style={{fontSize:10.5,color:"#4ade80",fontWeight:600,flexShrink:0,marginLeft:8}}>✓ Ready</span>}
                    <input type="file" accept=".jsx,.js,.ts,.tsx,.json,.css,.html" style={{display:"none"}}
                      onChange={e=>{
                        const f=e.target.files?.[0];
                        if(!f) return;
                        const reader=new FileReader();
                        reader.onload=ev=>{setGhFileContent(ev.target.result);setGhFileName(f.name);setGhPushState("idle");setGhPushMsg("");};
                        reader.readAsText(f);
                        e.target.value="";
                      }}/>
                  </label>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <p style={{color:"#8b949e",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Commit message</p>
                    <input value={ghMsg} onChange={e=>setGhMsg(e.target.value)} style={{background:"#161b22",border:"1px solid #30363d",borderRadius:6,padding:"7px 10px",fontSize:12.5,color:"#e6edf3",width:"100%",fontFamily:"monospace",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <button
                    onClick={pushToGitHub}
                    disabled={ghPushState==="pushing"||!!ghPathErr||!ghPathClean||!ghFileContent}
                    style={{padding:"10px 22px",borderRadius:8,fontSize:13.5,fontWeight:700,cursor:(ghPushState==="pushing"||!ghFileContent)?"not-allowed":"pointer",
                      background:ghPushState==="success"?"#238636":ghPushState==="error"?"#da3633":(!ghFileContent?"#555":"#238636"),
                      color:"#fff",border:"none",display:"flex",alignItems:"center",gap:7,flexShrink:0,transition:"background .2s",marginTop:20}}
                    onMouseEnter={e=>{if(ghPushState!=="pushing"&&ghFileContent)e.currentTarget.style.background="#2ea043";}}
                    onMouseLeave={e=>{e.currentTarget.style.background=ghPushState==="success"?"#238636":ghPushState==="error"?"#da3633":(!ghFileContent?"#555":"#238636");}}>
                    {ghPushState==="pushing"?<><Spinner size={14}/> Pushing…</>:
                     ghPushState==="success"?<><Ic n="chk" s={14} c="#fff"/> Pushed!</>:
                     ghPushState==="error"?<><Ic n="x" s={14} c="#fff"/> Failed</>:
                     <><svg width="14" height="14" viewBox="0 0 16 16" fill="#fff"><path d="M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878zm3.75 7.378a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm3-8.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0z"/></svg> Push to GitHub</>}
                  </button>
                </div>
              </div>
              {ghPushState==="success"&&<p style={{fontSize:12,color:G.g,fontWeight:600,marginTop:8,display:"flex",alignItems:"center",gap:5}}><Ic n="chk" s={12} c={G.g}/>{ghPushMsg} · Pushed {ghLastPushed?.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</p>}
              {ghPushState==="error"&&<p style={{fontSize:12,color:G.r,fontWeight:600,marginTop:8,display:"flex",alignItems:"center",gap:5}}><Ic n="x" s={12} c={G.r}/>{ghPushMsg}</p>}
            </div>
          )}

          {/* Commit history */}
          {ghCommits.length>0&&(
            <div className="card">
              <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:28,height:28,borderRadius:7,background:"#0d1117",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#8b949e"><path d="M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878zm3.75 7.378a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm3-8.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0z"/></svg>
                  </div>
                  <h3 style={{fontSize:13.5,fontWeight:700}}>Recent Commits</h3>
                  <span style={{fontSize:11,color:T.fnt,background:S.bg,border:`1px solid ${S.brd}`,borderRadius:10,padding:"1px 8px"}}>{ghPath}</span>
                </div>
                <button className="btn bo sm" style={{fontSize:11}} onClick={verifyConnection}><Ic n="reload" s={10}/> Refresh</button>
              </div>
              {ghCommits.map((c,i)=>(
                <div key={c.sha} style={{padding:"11px 16px",borderBottom:i<ghCommits.length-1?`1px solid ${S.bg}`:"none",display:"flex",gap:12,alignItems:"flex-start"}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:S.bg,border:`1px solid ${S.brd}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                    <span style={{fontSize:9,fontWeight:700,color:T.mid}}>{c.author.split(" ").map(w=>w[0]).join("").slice(0,2)}</span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:13,fontWeight:600,color:T.ink,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.message}</p>
                    <p style={{fontSize:11.5,color:T.fnt}}>{c.author} · {c.date}</p>
                  </div>
                  <a href={c.url} target="_blank" rel="noreferrer" style={{flexShrink:0,padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:700,background:"#0d1117",color:"#8b949e",border:"1px solid #30363d",textDecoration:"none",fontFamily:"monospace"}}>{c.sha}</a>
                </div>
              ))}
            </div>
          )}

          {/* How it works */}
          <div className="card" style={{padding:18}}>
            <p style={{fontSize:12,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:12}}>How It Works</p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[
                ["1","Create a PAT","Go to GitHub → Settings → Developer Settings → Personal Access Tokens. Create a classic token with the repo scope (or a fine-grained token with Contents: Read & Write on your target repo)."],
                ["2","Enter repo details","Fill in your GitHub username, repository name, branch (default: main), and the path where hindle-full.jsx should live in your repo."],
                ["3","Verify connection","Click Verify Connection. Hindle will authenticate against the GitHub API, check the file exists, and load the recent commit history."],
                ["4","Push from Claude","After making changes in Claude, download the updated file, then click Push to GitHub. The file is base64-encoded and sent via the GitHub Contents API (PUT /repos/.../contents/...)."],
                ["5","Auto-deploys","If your repo is connected to Netlify, Vercel, or GitHub Pages, the push will trigger an automatic deploy within seconds — no manual action needed."],
              ].map(([n,title,desc])=>(
                <div key={n} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                  <div style={{width:22,height:22,borderRadius:"50%",background:B,color:"#fff",fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{n}</div>
                  <div><p style={{fontSize:13,fontWeight:700,marginBottom:2}}>{title}</p><p style={{fontSize:12.5,color:T.lt,lineHeight:1.6}}>{desc}</p></div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* DANGER ZONE */}
      {tab==="danger"&&(
        <div style={{maxWidth:540,display:"flex",flexDirection:"column",gap:12}}>
          {[
            {title:"Clear All Trial Data",desc:"Permanently removes all expired trial tenant data. Active paying accounts are not affected.",btn:"Clear Trial Data",col:G.a},
            {title:"Reset Platform Configuration",desc:"Resets all platform settings to factory defaults. SMS credentials and pricing will need to be reconfigured.",btn:"Reset Config",col:G.a},
            {title:"Delete Super Admin Account",desc:"Permanently deletes this admin account. You will be immediately logged out and cannot recover access.",btn:"Delete Admin Account",col:G.r},
          ].map((item,i)=>(
            <div key={i} style={{padding:16,border:`1px solid ${item.col==="#D97706"?G.abd:G.rbd}`,borderRadius:10,background:item.col===G.a?G.ab:G.rb}}>
              <p style={{fontSize:13,fontWeight:700,color:T.ink,marginBottom:4}}>{item.title}</p>
              <p style={{fontSize:12.5,color:T.lt,lineHeight:1.55,marginBottom:12}}>{item.desc}</p>
              <button style={{padding:"6px 16px",borderRadius:7,fontSize:12.5,fontWeight:700,cursor:"pointer",background:"transparent",color:item.col===G.a?G.a:G.r,border:`1.5px solid ${item.col===G.a?G.a:G.r}`,transition:"all .15s"}} onMouseEnter={e=>{e.currentTarget.style.background=item.col===G.a?G.a:G.r;e.currentTarget.style.color="#fff";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=item.col===G.a?G.a:G.r;}}>{item.btn}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PAlertLog=({alertLog,setAlertLog})=>{
  const [testing,setTesting]=useState(false);
  const [testResult,setTestResult]=useState(null);
  const testCs=async(type)=>{
    const num=type==="sms"?window.prompt("Send test SMS to (include country code):"):window.prompt("Send test email to:");
    if(!num)return;
    setTesting(true);setTestResult(null);
    try{
      const r=await fetch(API_URL+(type==="sms"?"/api/sms-test":"/api/admin-settings"),{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(type==="sms"?{to:num,test:true}:{testEmail:{to:num}})});
      const d=await r.json();
      setTestResult({ok:r.ok,msg:r.ok?"✅ Test sent successfully":"❌ "+(d.error||"Failed")});
    }catch(e){setTestResult({ok:false,msg:"❌ "+e.message});}
    setTesting(false);
  };
  const [testSending,setTestSending]=useState(false);
  const [testSent,setTestSent]=useState(false);
  const sendTest=()=>{
    setTestSending(true);
    setTimeout(()=>{
      setAlertLog(l=>[{id:`test${Date.now()}`,ts:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),visitor:"[TEST]",page:"/demo",agentName:"Test Agent",mobile:"—",token:genToken(),status:"pending",elapsed:"just now"},...l]);
      setTestSending(false);setTestSent(true);setTimeout(()=>setTestSent(false),3000);
    },1500);
  };
  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
        <Metric label="Total Sent" value={alertLog.length} icon="sms" col={CS} d={0}/>
        <Metric label="Clicked" value={alertLog.filter(a=>a.status==="clicked").length} icon="chk" col={G.g} d={.05}/>
        <Metric label="Pending" value={alertLog.filter(a=>a.status==="pending").length} icon="warn" col={G.a} d={.1}/>
        <Metric label="Expired" value={alertLog.filter(a=>a.status==="expired").length} icon="lock" col={T.fnt} d={.15}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        {/* SMS preview */}
        <div className="card" style={{padding:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h3 style={{fontSize:13.5,fontWeight:700}}>SMS Preview</h3>
          </div>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
            <div>
              <div style={{width:210,background:"#1a1a2e",borderRadius:"18px 18px 18px 4px",padding:"12px 16px",fontFamily:"'DM Mono',monospace",color:"#e2e8f0",lineHeight:1.8,fontSize:11.5}}>
                <span style={{color:T.fnt,fontSize:10}}>HINDLE — Your Company</span><br/>
                New handoff from<br/>
                <strong style={{color:"#f1f5f9"}}>Visitor Name</strong><br/>
                Page: /page-path<br/>
                <br/>
                Tap to join:<br/>
                <span style={{color:"#60a5fa",fontSize:10.5}}>chat.hindle.ai/m/[token]</span><br/>
                <span style={{color:T.lt,fontSize:10}}>(Expires in 30 min)</span>
              </div>
              <div style={{width:0,height:0,borderLeft:"10px solid transparent",borderRight:"10px solid transparent",borderTop:"10px solid #1a1a2e",marginLeft:14}}/>
            </div>
          </div>
          <div style={{background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8,padding:"10px 12px"}}>
            {[["One-time use","Invalidated after first click"],["30 min expiry","Auto-expires if not clicked"],["No auth required","Agent lands directly in chat"],["Secure token","Cryptographically random"]].map(([t,d])=>(
              <div key={t} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:5}}><Ic n="chk" s={11} c={G.g}/><span style={{fontSize:12}}><strong>{t}</strong> — <span style={{color:T.lt}}>{d}</span></span></div>
            ))}
          </div>
        </div>
        {/* Test SMS + Test Email */}
        <div className="card" style={{padding:18}}>
          <h3 style={{fontSize:13.5,fontWeight:700,marginBottom:14}}>Send Test Messages</h3>
          {testResult&&<div style={{marginBottom:12,padding:"8px 12px",borderRadius:7,background:testResult.ok?G.gb:G.rb,border:`1px solid ${testResult.ok?G.gbd:G.rbd}`,fontSize:12.5,color:testResult.ok?G.g:G.r,fontWeight:600}}>{testResult.msg}</div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {/* Test SMS */}
            <div className="cs-frame">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><p style={{fontSize:13,fontWeight:700}}>Test SMS</p></div>
              <p style={{fontSize:11.5,color:T.fnt,marginBottom:10}}>Click to enter a mobile number. A real SMS with a magic link is sent via ClickSend.</p>
              <button className="btn bcs" style={{width:"100%"}} onClick={()=>testCs("sms")} disabled={testing}>
                {testing?<><Spinner/> Sending…</>:<><Ic n="sms" s={13} c="#fff"/> Send Test SMS</>}
              </button>
            </div>
            {/* Test Email */}
            <div style={{background:BL,border:`1px solid ${BM}`,borderRadius:10,padding:"14px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><p style={{fontSize:13,fontWeight:700,color:"#4338ca"}}>Test Email</p></div>
              <div style={{marginBottom:10}}><label style={{fontSize:11.5,fontWeight:600,color:"#6366f1",display:"block",marginBottom:4}}>Send to email</label><input className="inp" placeholder="you@company.com" style={{background:"rgba(255,255,255,.8)"}} id="test-email-addr"/></div>
              <p style={{fontSize:11.5,color:"#6366f1",marginBottom:10}}>Click to enter an email address. A test 2FA code email is sent via ClickSend to verify delivery.</p>
              <button style={{width:"100%",background:"#4f46e5",color:"#fff",border:"none",borderRadius:8,padding:"9px 0",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}} disabled={testing}
                onClick={()=>testCs("email")}>
                {testing?<><Spinner/> Sending…</>:<><Ic n="mail" s={13} c="#fff"/> Send Test Email</>}
              </button>
            </div>
          </div>
          <p style={{fontSize:12,color:T.fnt,lineHeight:1.6,marginTop:12}}>Test email sends a sample 2FA code to verify ClickSend email delivery. SMS test includes a live magic link. Credentials set above apply to all tenants.</p>
        </div>
      </div>
      <div className="card">
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><h3 style={{fontSize:13.5,fontWeight:700}}>Alert Log</h3></div>
          
        </div>
        <table className="tbl">
          <thead><tr>{["Time","Visitor","Page","Agent","Mobile","Token","Status"].map(h=><th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {alertLog.map((a,i)=>(
              <tr key={i}>
                <td><span className="mono" style={{fontSize:12}}>{a.ts}</span></td>
                <td><span style={{fontWeight:600}}>{a.visitor}</span></td>
                <td><span className="mono" style={{fontSize:11.5,color:T.lt}}>{a.page}</span></td>
                <td><div style={{display:"flex",alignItems:"center",gap:7}}><Av name={a.agentName} size={22} col={CS}/><span style={{fontSize:12.5}}>{a.agentName}</span></div></td>
                <td><span className="mono" style={{fontSize:11.5}}>{a.mobile}</span></td>
                <td><span className="mono" style={{fontSize:11,color:T.fnt}}>{a.token}</span></td>
                <td><Chip s={a.status==="expired"?"expired_link":a.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
// ── ── SMS & Email Integrations ────────────────────────────────
const EMAIL_TEMPLATES_DEFAULT=[
  {id:"welcome",    title:"Welcome Email",       tag:"On signup",    subj:"Welcome to Hindle! 🎉",          body:"Hi {name}, your {plan} account is ready. Click below to complete setup and go live.", col:B,   active:true},
  {id:"trial1",     title:"Trial Reminder #1",   tag:"Day 12",       subj:"⏱ 2 days left in your Hindle trial", body:"Hi {name}, your trial ends in 2 days. Add a payment method to keep access — takes under 2 minutes.", col:G.a, active:true},
  {id:"trial2",     title:"Trial Reminder #2",   tag:"Day 13",       subj:"⚠️ 1 day left in your trial",   body:"Hi {name}, your Hindle trial expires tomorrow. Add payment now to avoid interruption.", col:G.a, active:true},
  {id:"trial3",     title:"Trial Final Notice",  tag:"Day 14",       subj:"🔴 Last day — trial ends today", body:"Hi {name}, today is the last day of your trial. Add payment to keep your chatbot running.", col:G.r, active:true},
  {id:"payment",    title:"Payment Confirmation",tag:"After payment", subj:"✅ Payment confirmed",           body:"Hi {name}, payment of {amount} received. {plan} is now active. Card ending {last4} · Exp {expiry}.", col:G.g, active:true},
  {id:"invoice",    title:"Monthly Invoice",     tag:"Billing date",  subj:"Your Hindle invoice",            body:"Hi {name}, your invoice for {month} is attached. Amount: {amount}. Card: •••• {last4}.", col:T.mid,active:false},
];

const PIntegrations=({config,setConfig,role})=>{
  // SuperAdmin state
  const [cs,setCs]=useState({...config.clicksend,emailAddressId:config.clicksend.emailAddressId||""});
  const [testing,setTesting]=useState(false);
  const [testResult,setTestResult]=useState(null);
  const [csSaved,setCsSaved]=useState(false);
  const [showKey,setShowKey]=useState(false);
  const [emailTesting,setEmailTesting]=useState(false);
  const [emailTestResult,setEmailTestResult]=useState(null);

  // Tenant state
  const [sender,setSender]=useState({smsSender:config.clicksend.smsSender||"HINDLE",emailName:config.clicksend.emailName||"Hindle Consultants",emailFrom:config.clicksend.emailFrom||"noreply@hindleconsultants.com",emailAddressId:config.clicksend.emailAddressId||""});
  const [senderSaved,setSenderSaved]=useState(false);
  const [templates,setTemplates]=useState(EMAIL_TEMPLATES_DEFAULT.map(t=>({...t})));
  const [editTpl,setEditTpl]=useState(null);
  const [editDraft,setEditDraft]=useState(null);
  // SMTP state — load from tenant config
  const [smtp,setSmtp]=useState(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.smtp||{host:"",port:"587",user:"",pass:"",fromName:"",fromEmail:""};}}catch(e){}return{host:"",port:"587",user:"",pass:"",fromName:"",fromEmail:""};});
  const [smtpSaved,setSmtpSaved]=useState(false);
  const [smtpTesting,setSmtpTesting]=useState(false);
  const [smtpTestResult,setSmtpTestResult]=useState(null);

  const saveCs=async()=>{
    const updated={...cs,configured:!!(cs.username&&cs.apiKey)};
    setConfig(c=>({...c,clicksend:updated}));
    setCsSaved(true);
    // Persist to DB so server can read credentials and emailAddressId
    try{
      await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({superConfig:{clicksend:updated}})});
    }catch(e){console.error("saveCs DB error",e);}
    setTimeout(()=>setCsSaved(false),2500);
  };
  const testCs=async(type)=>{
    if(!cs.username||!cs.apiKey){setTestResult({ok:false,msg:"Enter credentials and save first"});return;}
    if(type==="email"&&!cs.emailAddressId){setTestResult({ok:false,msg:"❌ Email Address ID is required. Enter the numeric ID from ClickSend → Email → Senders and click Save first."});return;}
    setTesting(true);setTestResult(null);
    // Always save to DB before testing so server has latest credentials
    try{
      await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({superConfig:{clicksend:{...cs,configured:true}}})});
    }catch(e){}
    try{
      const target=type==="sms"?window.prompt("Test SMS — enter mobile number (e.g. +61400000000):"):window.prompt("Test Email — enter email address:");
      if(!target){setTesting(false);return;}
      const r=await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({testSMS:type==="sms"?target:undefined,testEmail:type==="email"?target:undefined})});
      const d=await r.json();
      const ok=d.smsSent||d.emailSent;
      const errMsg=d.smsError||d.emailError||d.error||"No SUCCESS response from ClickSend";
      setTestResult({ok:!!ok,errMsg,debug:d.debug||[]});
    }catch(e){setTestResult({ok:false,msg:"❌ Error: "+e.message});}
    setTesting(false);
  };
  const saveSender=async()=>{
    const updated={...cs,...sender};
    setConfig(c=>({...c,clicksend:{...c.clicksend,...sender}}));
    setSenderSaved(true);
    try{
      // Save to both _superConfig.clicksend (canonical) and top-level clicksend (compat)
      await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({superConfig:{clicksend:updated}})});
    }catch(e){console.error("saveSender DB error",e);}
    setTimeout(()=>setSenderSaved(false),2500);
  };
  const toggleTpl=(id)=>setTemplates(ts=>ts.map(t=>t.id===id?{...t,active:!t.active}:t));
  const openEdit=(tpl)=>{setEditDraft({...tpl});setEditTpl(tpl.id);};
  const saveEdit=()=>{setTemplates(ts=>ts.map(t=>t.id===editTpl?{...editDraft}:t));setEditTpl(null);};

  const isSuperAdmin=role==="super_admin";

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%",position:"relative"}}>
      {/* Edit template modal */}
      {editTpl&&editDraft&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget)setEditTpl(null);}}>
          <div className="card au" style={{width:"100%",maxWidth:540,padding:0,overflow:"hidden"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:S.bg}}>
              <h3 style={{fontSize:14.5,fontWeight:700}}>Edit Email Template</h3>
              <button onClick={()=>setEditTpl(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:3,display:"flex"}}><Ic n="x" s={16}/></button>
            </div>
            <div style={{padding:20,display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Template Name</label>
                <input className="inp" value={editDraft.title} onChange={e=>setEditDraft(d=>({...d,title:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Subject Line</label>
                <input className="inp" value={editDraft.subj} onChange={e=>setEditDraft(d=>({...d,subj:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Email Body</label>
                <textarea className="inp" value={editDraft.body} onChange={e=>setEditDraft(d=>({...d,body:e.target.value}))} style={{resize:"vertical",minHeight:90}}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:4}}>Available variables: <code className="mono" style={{fontSize:10,background:S.bg,padding:"1px 4px",borderRadius:3}}>{"{name} {plan} {amount} {days} {last4} {expiry} {month}"}</code></p>
              </div>
              <div style={{padding:"10px 12px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:8}}>
                <p style={{fontSize:11,fontWeight:700,color:T.fnt,marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Preview</p>
                <p style={{fontSize:12,fontWeight:600,marginBottom:2}}>{editDraft.subj}</p>
                <p style={{fontSize:12,color:T.mid,lineHeight:1.6}}>{editDraft.body}</p>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button className="btn bo sm" onClick={()=>setEditTpl(null)}>Cancel</button>
                <button className="btn bp sm" onClick={saveEdit}><Ic n="chk" s={12} c="#fff"/> Save Template</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SUPER ADMIN VIEW ═══ */}
      {isSuperAdmin&&(
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
            <Metric label="API Status" value={config.clicksend.configured?"Active":"Not Set"} icon="intg" col={config.clicksend.configured?G.g:G.r} d={0}/>
            <Metric label="SMS Sender" value={config.clicksend.smsSender||"—"} icon="sms" col={CS} d={.05}/>
            <Metric label="From Email" value={config.clicksend.emailFrom?"Set":"—"} icon="mail" col={B} d={.1}/>
            <Metric label="Services" value="SMS + Email" icon="zap" col={G.p} d={.15}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
            <div className="card">
              <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <h3 style={{fontSize:13.5,fontWeight:700}}>SMS & Email Credentials</h3>
                
              </div>
              <div style={{padding:18}}>
                <div style={{background:G.ab,border:`1px solid ${G.abd}`,borderRadius:8,padding:"9px 12px",marginBottom:14,display:"flex",gap:7,alignItems:"flex-start"}}>
                  <Ic n="lock" s={13} c="#d97706"/>
                  <p style={{fontSize:12,color:G.at,lineHeight:1.55}}>These API credentials are <strong>Super Admin only</strong>. Tenants configure their sender name and email address independently in their own Integrations page.</p>
                </div>
                <div className="cs-frame" style={{marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <p style={{fontSize:12.5,fontWeight:700}}>API Credentials</p>
                    <span style={{fontSize:11,color:CS,fontWeight:600,display:"flex",alignItems:"center",gap:3}}>clicksend.com <Ic n="chR" s={10} c={CS}/></span>
                  </div>
                  <div style={{padding:"7px 10px",background:"rgba(255,255,255,.5)",border:"1px solid rgba(255,255,255,.8)",borderRadius:6,marginBottom:10,display:"flex",gap:6,alignItems:"flex-start"}}>
                    <Ic n="warn" s={12} c={CS}/>
                    <p style={{fontSize:11.5,color:"#1e3a5f",lineHeight:1.5}}><strong>Global credentials</strong> — these apply to all tenants on the platform. Tenants configure their own sender name and from-address below.</p>
                  </div>
                  {[["API Username (email)","username","admin@yourcompany.com","text"],["API Key","apiKey","Paste your API key","password"]].map(([l,k,ph,type])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>{l}</label>
                      <div style={{position:"relative"}}>
                        <input className="inp" type={k==="apiKey"&&!showKey?"password":type} placeholder={ph} value={cs[k]} onChange={e=>setCs(c=>({...c,[k]:e.target.value}))} style={{background:"rgba(255,255,255,.85)",paddingRight:k==="apiKey"?36:undefined}}/>
                        {k==="apiKey"&&<button onClick={()=>setShowKey(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:0,display:"flex"}}><Ic n="eye" s={14}/></button>}
                      </div>
                    </div>
                  ))}
                  <div style={{marginBottom:10}}>
                    <label style={{fontSize:11.5,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>
                      Email Address ID <span style={{fontWeight:400,color:T.fnt}}>numeric — from ClickSend → Email → Senders</span>
                    </label>
                    <input className="inp" type="number" placeholder="e.g. 12345" value={cs.emailAddressId||""} onChange={e=>setCs(c=>({...c,emailAddressId:e.target.value}))} style={{background:"rgba(255,255,255,.85)"}}/>
                    <p style={{fontSize:11,color:T.fnt,marginTop:3,lineHeight:1.5}}>ClickSend requires a numeric sender ID (not an email address) for API email delivery. Find it in your ClickSend dashboard under <strong>Email → Senders</strong>. Emails will fail without this.</p>
                  </div>
                </div>
                {testResult&&(
                  <div style={{marginBottom:12,borderRadius:8,border:`1px solid ${testResult.ok?G.gbd:G.rbd}`,overflow:"hidden"}}>
                    <div style={{padding:"8px 12px",background:testResult.ok?G.gb:G.rb,display:"flex",gap:7,alignItems:"center"}}>
                      <Ic n={testResult.ok?"chk":"warn"} s={14} c={testResult.ok?G.g:G.r}/>
                      <p style={{fontSize:12.5,fontWeight:700,color:testResult.ok?G.g:G.r}}>{testResult.ok?"✅ Sent successfully":"❌ "+testResult.errMsg}</p>
                      <button onClick={()=>setTestResult(null)} style={{marginLeft:"auto",background:"transparent",border:"none",cursor:"pointer",color:T.fnt,padding:2}}><Ic n="x" s={12}/></button>
                    </div>
                    {testResult.debug&&testResult.debug.length>0&&(
                      <div style={{padding:"10px 12px",background:S.bg}}>
                        <p style={{fontSize:10,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:6}}>🔍 Debug trace</p>
                        {testResult.debug.map((line,i)=>(
                          <p key={i} style={{fontSize:11,fontFamily:"monospace",color:T.mid,marginBottom:3,lineHeight:1.5,wordBreak:"break-all"}}>{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:"flex",gap:8}}>
                  <button className="btn bcs" onClick={testCs} disabled={testing} style={{flex:1}}>{testing?<><Spinner/> Testing…</>:<><Ic n="zap" s={13} c="#fff"/> Test Connection</>}</button>
                  <button className={`btn ${csSaved?"bg-g":"bp"}`} onClick={saveCs} style={{flex:1}}>{csSaved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Credentials</>}</button>
                </div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {[{title:"SMS Services",icon:"sms",col:CS,items:[["Agent handoff alerts","Triggered when no agents are online"],["Trial expiry reminders","Days 12, 13 and 14 automatically"],["Magic link delivery","One-time auth links in each SMS"]]},{title:"Email Services",icon:"mail",col:B,items:[["Welcome email","Sent on account creation"],["Trial reminder emails","Paired with SMS on days 12–14"],["Payment confirmations","After Stripe processes payment"],["Monthly invoices","Billing receipts with card details"]]}].map(svc=>(
                <div key={svc.title} className="card" style={{padding:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <div style={{width:30,height:30,borderRadius:8,background:`${svc.col}15`,border:`1px solid ${svc.col}25`,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n={svc.icon} s={14} c={svc.col}/></div>
                    <h3 style={{fontSize:13,fontWeight:700}}>{svc.title}</h3>
                  </div>
                  {svc.items.map(([t,d])=>(
                    <div key={t} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:7}}>
                      <div style={{width:5,height:5,borderRadius:"50%",background:svc.col,flexShrink:0,marginTop:6}}/>
                      <div><p style={{fontSize:12.5,fontWeight:600}}>{t}</p><p style={{fontSize:11,color:T.lt}}>{d}</p></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Email Templates */}
          <div className="card">
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <h3 style={{fontSize:13.5,fontWeight:700}}>Email Templates</h3>
                
              </div>
              <p style={{fontSize:12,color:T.lt}}>{templates.filter(t=>t.active).length} of {templates.length} active</p>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)"}}>
              {templates.map((tpl,i)=>(
                <div key={tpl.id} style={{padding:18,borderRight:i%3<2?`1px solid ${S.brd}`:"",borderBottom:i<3?`1px solid ${S.brd}`:"",opacity:tpl.active?1:.6,transition:"opacity .2s"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <span className="badge" style={{background:`${tpl.col}15`,color:tpl.col,border:`1px solid ${tpl.col}25`}}>{tpl.tag}</span>
                    {/* Active toggle */}
                    <div style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}} onClick={()=>toggleTpl(tpl.id)}>
                      <div style={{width:30,height:16,borderRadius:8,background:tpl.active?G.g:S.brd,position:"relative",transition:"background .2s",flexShrink:0}}>
                        <div style={{position:"absolute",top:2,left:tpl.active?16:2,width:12,height:12,borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                      </div>
                      <span style={{fontSize:10.5,fontWeight:600,color:tpl.active?G.g:T.fnt}}>{tpl.active?"Active":"Off"}</span>
                    </div>
                  </div>
                  <p style={{fontSize:13,fontWeight:700,marginBottom:3}}>{tpl.title}</p>
                  <p style={{fontSize:11.5,color:T.lt,marginBottom:4}}>Subject: <em style={{color:T.mid}}>{tpl.subj}</em></p>
                  <p style={{fontSize:12,color:T.mid,lineHeight:1.55,marginBottom:12,minHeight:40}}>{tpl.body}</p>
                  <button className="btn bp sm" style={{width:"100%"}} onClick={()=>openEdit(tpl)}><Ic n="edit" s={11} c="#fff"/> Edit Template</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}


      {/* ═══ TENANT VIEW ═══ */}
      {!isSuperAdmin&&(
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
            <Metric label="Active Templates" value={templates.filter(t=>t.active).length} icon="mail" col={B} d={0}/>
            <Metric label="SMS Sender" value={sender.smsSender} icon="sms" col={CS} d={.05}/>
            <Metric label="From Name" value={sender.emailName||"—"} icon="user" col={G.p} d={.1}/>
            <Metric label="SMS & Email" value={config.clicksend.configured?"Connected":"Not set"} icon="intg" col={config.clicksend.configured?G.g:G.a} d={.15}/>
          </div>

          {/* Sender identity */}
          <div className="card" style={{marginBottom:16}}>
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <h3 style={{fontSize:13.5,fontWeight:700}}>Sender Identity</h3>
                
              </div>
              <p style={{fontSize:12,color:T.lt}}>Appears on all outbound SMS and email</p>
            </div>
            <div style={{padding:18,display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
              {[["SMS Sender Name","smsSender","HINDLE","Max 11 chars, no spaces"],["Email From Name","emailName","Your Company","Shown in recipient's inbox"],["From Email Address","emailFrom","support@yourcompany.com","Must be a verified domain in ClickSend"]].map(([l,k,ph,hint])=>(
                <div key={k}>
                  <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>{l}</label>
                  <input className="inp" placeholder={ph} value={sender[k]||""} onChange={e=>setSender(s=>({...s,[k]:e.target.value}))} maxLength={k==="smsSender"?11:undefined}/>
                  <p style={{fontSize:11,color:T.fnt,marginTop:3}}>{hint}</p>
                </div>
              ))}
              <div>
                <label style={{fontSize:12,fontWeight:700,color:T.mid,display:"block",marginBottom:5}}>Email Address ID <span style={{fontWeight:400,color:G.r}}>required for email delivery</span></label>
                <input className="inp" type="number" placeholder="e.g. 12345" value={sender.emailAddressId||""} onChange={e=>setSender(s=>({...s,emailAddressId:e.target.value}))}/>
                <p style={{fontSize:11,color:T.fnt,marginTop:3}}>Numeric sender ID from ClickSend → Email → Senders. Emails will not send without this.</p>
              </div>
            </div>
            <div style={{padding:"0 18px 16px",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <button className={`btn ${senderSaved?"bg-g":"bp"}`} onClick={saveSender}>{senderSaved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Sender Settings</>}</button>
              <button className="btn bo sm" onClick={async()=>{
                const mobile=window.prompt("Enter mobile number to test SMS (e.g. +61400000000):");
                if(!mobile)return;
                try{
                  const r=await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({testSMS:mobile})});
                  const d=await r.json();
                  alert(d.smsSent?"✅ Test SMS sent to "+mobile:("❌ Failed: "+(d.smsError||"Unknown error"))+(d.debug?"\n\n🔍 Debug:\n"+d.debug.join("\n"):""));
                }catch(e){alert("❌ Error: "+e.message);}
              }}><Ic n="sms" s={12}/> Test SMS</button>
              <button className="btn bo sm" onClick={async()=>{
                const email=window.prompt("Enter email address to test delivery:");
                if(!email)return;
                try{
                  const r=await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({testEmail:email})});
                  const d=await r.json();
                  alert(d.emailSent?"✅ Test email sent to "+email:("❌ Failed: "+(d.emailError||"Unknown error"))+(d.debug?"\n\n🔍 Debug:\n"+d.debug.join("\n"):""));
                }catch(e){alert("❌ Error: "+e.message);}
              }}><Ic n="mail" s={12}/> Test Email</button>
              <p style={{fontSize:12,color:T.fnt}}>SMS & Email credentials are managed by your platform administrator.</p>
            </div>
          </div>

          {/* SMTP Configuration */}
          <div className="card" style={{marginBottom:16}}>
            <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <h3 style={{fontSize:13.5,fontWeight:700}}>SMTP Email — Send from Your Own Domain</h3>
                <p style={{fontSize:12,color:T.fnt,marginTop:2}}>Connect your own mail server. Emails will come from your address, not the platform's.</p>
              </div>
              {smtp.host&&<span style={{fontSize:11,background:G.gb,color:G.g,border:`1px solid ${G.gbd}`,borderRadius:12,padding:"2px 10px",fontWeight:700}}>Configured</span>}
            </div>
            <div style={{padding:16,display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>SMTP Host</label>
                <input className="inp" placeholder="smtp.gmail.com  or  mail.yourdomain.com" value={smtp.host||""} onChange={e=>setSmtp(s=>({...s,host:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Port</label>
                <select className="inp" value={smtp.port||"587"} onChange={e=>setSmtp(s=>({...s,port:e.target.value}))}>
                  <option value="587">587 — TLS (recommended)</option>
                  <option value="465">465 — SSL</option>
                  <option value="25">25 — Plain (legacy)</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Username</label>
                <input className="inp" placeholder="your@email.com" value={smtp.user||""} onChange={e=>setSmtp(s=>({...s,user:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>Password / App Password</label>
                <input className="inp" type="password" placeholder="••••••••••••" value={smtp.pass||""} onChange={e=>setSmtp(s=>({...s,pass:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>From Name</label>
                <input className="inp" placeholder="Your Company Support" value={smtp.fromName||""} onChange={e=>setSmtp(s=>({...s,fromName:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,color:T.mid,display:"block",marginBottom:4}}>From Email</label>
                <input className="inp" placeholder="support@yourdomain.com" value={smtp.fromEmail||""} onChange={e=>setSmtp(s=>({...s,fromEmail:e.target.value}))}/>
              </div>
            </div>
            <div style={{padding:"0 16px",marginBottom:12,background:S.bg,borderTop:`1px solid ${S.brd}`,paddingTop:10,paddingBottom:10}}>
              <p style={{fontSize:11.5,color:T.fnt,lineHeight:1.6}}>
                <strong>Gmail:</strong> Use App Password (not your login password) — Google Account → Security → App Passwords. Host: <code>smtp.gmail.com</code> Port: <code>587</code><br/>
                <strong>Office 365:</strong> Host: <code>smtp.office365.com</code> Port: <code>587</code><br/>
                <strong>cPanel:</strong> Host: <code>mail.yourdomain.com</code> Port: <code>465</code><br/>
                <strong>Fallback:</strong> If SMTP is not configured, emails are sent via the platform ClickSend account.
              </p>
            </div>
            <div style={{padding:"0 16px 14px",display:"flex",gap:8,flexWrap:"wrap"}}>
              <button className={`btn ${smtpSaved?"bg-g":"bp"}`} onClick={async()=>{
                const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                if(!orgId)return;
                try{
                  await fetch(API_URL+"/api/tenant-config",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tenantId:orgId,smtp})});
                  setSmtpSaved(true);setTimeout(()=>setSmtpSaved(false),2500);
                }catch(e){alert("Save failed: "+e.message);}
              }}>{smtpSaved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save SMTP</>}</button>
              <button className="btn bo sm" disabled={smtpTesting||!smtp.host} onClick={async()=>{
                const testAddr=window.prompt("Send test email to:");
                if(!testAddr)return;
                setSmtpTesting(true);setSmtpTestResult(null);
                try{
                  const orgId=(()=>{try{const c=localStorage.getItem("hindle_config");if(c){const p=JSON.parse(c);return p.orgId||p.tenantId||null;}}catch(e){}return null;})();
                  const r=await fetch(API_URL+"/api/test-smtp",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({orgId,smtp,to:testAddr})});
                  const d=await r.json();
                  setSmtpTestResult(d.ok?{ok:true,msg:"✅ Test email sent to "+testAddr}:{ok:false,msg:"❌ "+(d.error||"Send failed")});
                }catch(e){setSmtpTestResult({ok:false,msg:"❌ "+e.message});}
                setSmtpTesting(false);
              }}>{smtpTesting?<><Spinner/> Testing…</>:<><Ic n="mail" s={12}/> Test SMTP</>}</button>
              {smtp.host&&<button className="btn bd sm" onClick={()=>{setSmtp({host:"",port:"587",user:"",pass:"",fromName:"",fromEmail:""});}}><Ic n="trash" s={11}/> Clear</button>}
            </div>
            {smtpTestResult&&<div style={{margin:"0 16px 14px",padding:"8px 12px",borderRadius:7,background:smtpTestResult.ok?G.gb:G.rb,border:`1px solid ${smtpTestResult.ok?G.gbd:G.rbd}`,fontSize:12.5,color:smtpTestResult.ok?G.g:G.r,fontWeight:600}}>{smtpTestResult.msg}</div>}
          </div>
        </>
      )}
    </div>
  );
};
// ── Super Admin Pages ─────────────────────────────────────────
const PLAN_ORDER=["free","starter","professional","enterprise"];
const PLAN_COLORS={get free(){return T.fnt;},get starter(){return B;},get professional(){return G.p;},enterprise:"#7c3aed"};
const DEFAULT_LIMITS={free:{agents:1,conversations_month:100,kb_docs:0},starter:{agents:5,conversations_month:500,kb_docs:5},professional:{agents:10,conversations_month:2000,kb_docs:999},enterprise:{agents:999,conversations_month:999999,kb_docs:999}};

// ── Audit Log ─────────────────────────────────────────────────────────────────
const PAuditLog=({tenantId})=>{
  const [logs,setLogs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");
  const [typeFilter,setTypeFilter]=useState("all");

  useEffect(()=>{
    setLoading(true);
    const url=tenantId?API_URL+"/api/audit-log/"+tenantId:API_URL+"/api/audit-log";
    fetch(url).then(r=>r.ok?r.json():[]).then(d=>setLogs(Array.isArray(d)?d:[])).catch(()=>setLogs([])).finally(()=>setLoading(false));
  },[tenantId]);

  const EVENT_COLORS={
    tenant_created:G.g, plan_changed:B, payment_confirmed:G.g,
    trial_reminder_sent:G.a, trial_reminder_failed:G.r,
    tenant_created_by_admin:B, tenant_suspended:G.r, tenant_deleted:G.r,
  };
  const allTypes=[...new Set(logs.map(l=>l.event_type).filter(Boolean))];
  const filtered=logs.filter(l=>{
    if(typeFilter!=="all"&&l.event_type!==typeFilter)return false;
    if(search){const s=search.toLowerCase();return(l.description||"").toLowerCase().includes(s)||(l.tenant_id||"").toLowerCase().includes(s)||(l.event_type||"").toLowerCase().includes(s);}
    return true;
  });

  const fmt=(ts)=>{try{const d=new Date(ts);return d.toLocaleDateString("en-AU",{day:"2-digit",month:"short",year:"numeric"})+" "+d.toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"});}catch(e){return ts||"—";}};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,minWidth:200}}>
          <input className="inp" placeholder="Search audit log…" value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:30,fontSize:12.5}}/>
          <div style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)"}}><Ic n="search" s={12} c={T.fnt}/></div>
        </div>
        <select className="inp" style={{width:"auto",fontSize:12.5}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="all">All event types</option>
          {allTypes.map(t=><option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
        </select>
        <button className="btn bo sm" onClick={()=>{setLoading(true);const url=tenantId?API_URL+"/api/audit-log/"+tenantId:API_URL+"/api/audit-log";fetch(url).then(r=>r.ok?r.json():[]).then(d=>setLogs(Array.isArray(d)?d:[])).catch(()=>[]).finally(()=>setLoading(false));}}><Ic n="reload" s={12}/> Refresh</button>
        <span style={{fontSize:12,color:T.fnt}}>{filtered.length} event{filtered.length!==1?"s":""}</span>
      </div>
      <div className="card" style={{overflow:"hidden"}}>
        {loading?(
          <div style={{padding:32,textAlign:"center"}}><Spinner/><p style={{fontSize:12.5,color:T.fnt,marginTop:8}}>Loading audit log…</p></div>
        ):filtered.length===0?(
          <div style={{padding:32,textAlign:"center"}}><Ic n="book" s={28} c={T.fnt}/><p style={{fontSize:13,color:T.fnt,marginTop:8,fontWeight:600}}>No audit events</p><p style={{fontSize:12,color:T.fnt,marginTop:4}}>{search||typeFilter!=="all"?"Try adjusting your filters":"Events are recorded automatically"}</p></div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:S.bg,borderBottom:`1px solid ${S.brd}`}}>
                {["Time","Event","Description","Tenant","Details"].map(h=>(
                  <th key={h} style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((log,i)=>{
                const col=EVENT_COLORS[log.event_type]||T.fnt;
                return(
                  <tr key={log.id||i} style={{borderBottom:`1px solid ${S.bg}`}} onMouseEnter={e=>e.currentTarget.style.background=S.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{padding:"9px 12px",fontSize:11.5,color:T.fnt,whiteSpace:"nowrap"}}>{fmt(log.created_at)}</td>
                    <td style={{padding:"9px 12px"}}><span style={{fontSize:11,fontWeight:700,color:col,background:col+"15",border:`1px solid ${col}30`,borderRadius:5,padding:"2px 8px",whiteSpace:"nowrap"}}>{(log.event_type||"").replace(/_/g," ")}</span></td>
                    <td style={{padding:"9px 12px",fontSize:12.5,color:T.mid,maxWidth:280}}><p style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{log.description||"—"}</p></td>
                    <td style={{padding:"9px 12px",fontSize:11.5,color:T.fnt,fontFamily:"monospace"}}>{(log.tenant_id||"").slice(0,8)+"…"}</td>
                    <td style={{padding:"9px 12px",fontSize:11.5,color:T.fnt}}>{log.meta?<details><summary style={{cursor:"pointer",fontSize:11,color:B}}>View</summary><pre style={{fontSize:10,background:S.bg,padding:8,borderRadius:5,marginTop:4,maxWidth:240,overflow:"auto",whiteSpace:"pre-wrap"}}>{JSON.stringify(log.meta,null,2)}</pre></details>:"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const PricingAdmin=({config,setConfig})=>{
  const [codes,setCodes]=useState([...config.promoCodes]);
  const [annualPct,setAnnualPct]=useState(config.annualDiscountPct);
  const [prices,setPrices]=useState({starter:config.plans.starter.usd,professional:config.plans.professional.usd});
  // Sync local state when config loads from localStorage
  useEffect(()=>{
    setCodes([...config.promoCodes]);
    setAnnualPct(config.annualDiscountPct);
    setPrices({starter:config.plans.starter.usd,professional:config.plans.professional.usd});
    // Re-init features if planFeatures was loaded from DB after mount
    if(config.planFeatures&&Array.isArray(config.planFeatures.professional)){
      setFeatures(initFeatures());
    }
    // Re-init limits if planLimits was loaded from DB after mount
    const freshLimits=config._superConfig?.planLimits||config.planLimits||null;
    if(freshLimits&&freshLimits.starter){
      setLimits(freshLimits);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[JSON.stringify(config.planFeatures),JSON.stringify(config.planLimits)]);
  // Promo codes are persisted on explicit save() call - no auto-persist needed
  const [newCode,setNewCode]=useState({code:"",pct:"",limit:"",expiry:"",plans:["starter","professional"],desc:""});
  const [adding,setAdding]=useState(false);
  const [saved,setSaved]=useState(false);
  const [tab,setTab]=useState("pricing");

  // ── Editable feature matrix state ──
  // Master list of feature labels (rows), derived from professional plan (longest)
  const initFeatures=()=>{
    // If admin has saved a custom feature matrix, load it
    const saved=config.planFeatures;
    if(saved&&Array.isArray(saved.professional)&&saved.professional.length){
      // Build unified row list from saved data
      const allLabels=[...new Set(PLAN_ORDER.flatMap(pid=>(saved[pid]||[]).map(([l])=>l)))];
      return allLabels.map(label=>{
        const row={label};
        PLAN_ORDER.forEach(pid=>{
          const found=(saved[pid]||[]).find(([l])=>l===label);
          row[pid]=found?!!found[1]:false;
        });
        return row;
      });
    }
    // Fall back to hardcoded defaults
    const allLabels=[...new Set([
      ...PLAN_FEATURES.professional.map(([l])=>l),
      ...PLAN_FEATURES.free.map(([l])=>l),
      ...PLAN_FEATURES.starter.map(([l])=>l),
      ...PLAN_FEATURES.enterprise.map(([l])=>l),
    ])];
    return allLabels.map(label=>{
      const row={label};
      PLAN_ORDER.forEach(pid=>{
        const found=(PLAN_FEATURES[pid]||[]).find(([l])=>l===label);
        row[pid]=found?found[1]:false;
      });
      return row;
    });
  };
  const [features,setFeatures]=useState(initFeatures);
  const [newFeat,setNewFeat]=useState({label:"",free:false,starter:false,professional:false,enterprise:false});
  const [addingFeat,setAddingFeat]=useState(false);
  const [featSaved,setFeatSaved]=useState(false);

  // ── Plan limits state (agents / conversations / KB docs per plan) ──
  const DEFAULT_PLAN_LIMITS={
    free:         {agents:1,   conversations_month:100,   kb_docs:0},
    starter:      {agents:5,   conversations_month:500,   kb_docs:5},
    professional: {agents:10,  conversations_month:2000,  kb_docs:999},
    enterprise:   {agents:999, conversations_month:999999,kb_docs:999},
  };
  const initLimits=()=>{
    const saved=config._superConfig?.planLimits||config.planLimits||null;
    if(saved&&saved.starter) return saved;
    return DEFAULT_PLAN_LIMITS;
  };
  const [limits,setLimits]=useState(initLimits);
  const setLimit=(plan,field,val)=>setLimits(l=>({...l,[plan]:{...l[plan],[field]:val===""?"":Number(val)}}));

  const toggleFeat=(rowIdx,planId)=>{
    setFeatures(f=>f.map((row,i)=>i===rowIdx?{...row,[planId]:!row[planId]}:row));
  };
  const updateFeatLabel=(rowIdx,val)=>{
    setFeatures(f=>f.map((row,i)=>i===rowIdx?{...row,label:val}:row));
  };
  const deleteFeat=(rowIdx)=>{
    setFeatures(f=>f.filter((_,i)=>i!==rowIdx));
  };
  const addFeat=()=>{
    if(!newFeat.label.trim()) return;
    setFeatures(f=>[...f,{...newFeat,label:newFeat.label.trim()}]);
    setNewFeat({label:"",free:false,starter:false,professional:false,enterprise:false});
    setAddingFeat(false);
  };
  const moveFeat=(idx,dir)=>{
    setFeatures(f=>{
      const arr=[...f];
      const swap=idx+dir;
      if(swap<0||swap>=arr.length) return arr;
      [arr[idx],arr[swap]]=[arr[swap],arr[idx]];
      return arr;
    });
  };
  const saveFeatMatrix=async()=>{
    setSaveErr(null);
    const planFeatures={};
    PLAN_ORDER.forEach(pid=>{
      planFeatures[pid]=features.map(row=>[row.label,!!row[pid]]);
    });
    setConfig(c=>({...c,planFeatures,_superConfig:{...(c._superConfig||{}),planFeatures,planLimits:limits}}));
    try{
      const r=await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({superConfig:{planFeatures,planLimits:limits}})});
      const d=await r.json();
      if(!r.ok||!d.ok) throw new Error(d.error||`HTTP ${r.status}`);
    }catch(e){
      setSaveErr("Feature save failed: "+e.message);
      return;
    }
    // Update pricing cache so Landing page shows new features immediately
    try{
      const pc=localStorage.getItem("hindle_pricing_cache");
      const existing=pc?JSON.parse(pc):{ts:Date.now(),data:{}};
      existing.data={...existing.data,planFeatures,planLimits:limits};
      existing.ts=Date.now();
      localStorage.setItem("hindle_pricing_cache",JSON.stringify(existing));
    }catch(_){}
    setFeatSaved(true);
    setTimeout(()=>setFeatSaved(false),2500);
  };

  const [saveErr,setSaveErr]=useState(null);

  const snap5=v=>roundTo5(Number(v));
  const save=async()=>{
    setSaveErr(null);
    const newPlans={...config.plans,
      starter:{...config.plans.starter,usd:snap5(prices.starter)},
      professional:{...config.plans.professional,usd:snap5(prices.professional)},
    };
    try{
      const r=await fetch(API_URL+"/api/admin-settings",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({superConfig:{
          plans:newPlans,
          annualDiscountPct:annualPct,
          promoCodes:codes,
          planLimits:limits,
        }})});
      const d=await r.json();
      if(!r.ok||!d.ok) throw new Error(d.error||`HTTP ${r.status}`);
    }catch(e){
      console.error("Save pricing failed:",e);
      setSaveErr("Save failed: "+e.message);
      return;
    }
    setConfig(c=>({...c,annualDiscountPct:annualPct,plans:newPlans,promoCodes:codes,planLimits:limits,_superConfig:{...(c._superConfig||{}),planLimits:limits,plans:newPlans,annualDiscountPct:annualPct,promoCodes:codes}}));
    // Update pricing cache
    try{
      const pc=localStorage.getItem("hindle_pricing_cache");
      const existing=pc?JSON.parse(pc):{ts:Date.now(),data:{}};
      existing.data={...existing.data,plans:newPlans,annualDiscountPct:annualPct,promoCodes:codes,planLimits:limits};
      existing.ts=Date.now();
      localStorage.setItem("hindle_pricing_cache",JSON.stringify(existing));
    }catch(_){}
    setSaved(true);setTimeout(()=>setSaved(false),2500);
  };
  const addCode=()=>{
    if(!newCode.code||!newCode.pct||!newCode.expiry)return;
    setCodes(c=>[...c,{code:newCode.code.toUpperCase(),pct:Number(newCode.pct),limit:Number(newCode.limit)||999,used:0,expiry:newCode.expiry,plans:newCode.plans,active:true,desc:newCode.desc}]);
    setNewCode({code:"",pct:"",limit:"",expiry:"",plans:["starter","professional"],desc:""});
    setAdding(false);
  };

  // Shared plan column widths for feature table

  return(
    <div style={{padding:20,overflowY:"auto",height:"100%"}}>
      {/* Metrics */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        <Metric label="Active Promos" value={codes.filter(c=>c.active).length} icon="tag" d={0}/>
        <Metric label="Total Uses" value={codes.reduce((s,c)=>s+c.used,0)} icon="pct" col={G.g} d={.05}/>
        <Metric label="Annual Discount" value={`${annualPct}%`} icon="cal" col={G.p} d={.1}/>
        <Metric label="Currencies" value={Object.keys(config.currencies).length} icon="globe" col={G.a} d={.15}/>
      </div>

      {/* Plan cards preview — all 4 live */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{padding:"13px 16px",borderBottom:`1px solid ${S.brd}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <h3 style={{fontSize:13.5,fontWeight:700}}>Plan Management</h3>
          <div style={{display:"flex",gap:3}}>
            {[["pricing","Pricing"],["limits","Plan Limits"],["features","Features"],["promos","Promo Codes"]].map(([t,l])=>(
              <button key={t} onClick={()=>setTab(t)} style={{padding:"5px 13px",borderRadius:6,fontSize:12.5,fontWeight:600,cursor:"pointer",background:tab===t?B:"transparent",color:tab===t?"#fff":T.fnt,border:tab===t?`1px solid ${B}`:"1px solid transparent",transition:"all .15s"}}>{l}</button>
            ))}
          </div>
        </div>

        {/* ── TAB: PRICING ── */}
        {tab==="pricing"&&(
          <div style={{padding:20,display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:20,alignItems:"start"}}>
            {/* Controls */}
            <div>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Base Prices (USD) — snaps to nearest $5</p>
              {[["starter","Starter","Up to 500 convos/mo"],["professional","Professional","Up to 2,000 convos/mo"]].map(([id,name,hint])=>{
                const snapped=snap5(Number(prices[id]));
                const annMo=snap5(snapped*(1-annualPct/100));
                return(
                  <div key={id} style={{marginBottom:14,padding:"12px 14px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:9}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div>
                        <span style={{fontSize:13,fontWeight:700,color:PLAN_COLORS[id]}}>{name}</span>
                        <span style={{fontSize:11,color:T.fnt,marginLeft:6}}>{hint}</span>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <span className="mono" style={{fontSize:13,fontWeight:700,color:PLAN_COLORS[id]}}>
                          ${snapped}<span style={{fontSize:10,color:T.fnt,fontWeight:400}}>/mo</span>
                        </span>
                        <p style={{fontSize:10,color:G.g}}>${annMo}/mo annual</p>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{color:T.lt,fontWeight:700,fontSize:15,flexShrink:0}}>$</span>
                      <input className="inp" type="number" step="5" min="0" value={prices[id]}
                        onChange={e=>setPrices(p=>({...p,[id]:e.target.value}))}
                        style={{fontFamily:"monospace",fontWeight:700}}/>
                      {Number(prices[id])!==snapped&&(
                        <span style={{fontSize:11,color:G.a,whiteSpace:"nowrap",flexShrink:0}}>→ ${snapped}</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Annual discount slider */}
              <div style={{padding:"12px 14px",background:S.bg,border:`1px solid ${S.brd}`,borderRadius:9,marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:700}}>Annual Discount</span>
                  <span className="mono" style={{fontSize:15,fontWeight:800,color:B}}>{annualPct}%</span>
                </div>
                <input type="range" min={0} max={50} step={5} value={annualPct}
                  onChange={e=>setAnnualPct(Number(e.target.value))}
                  style={{width:"100%",accentColor:B,marginBottom:4}}/>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  {[0,10,20,30,40,50].map(v=><span key={v} style={{fontSize:9.5,color:v===annualPct?B:T.fnt,fontWeight:v===annualPct?700:400}}>{v}%</span>)}
                </div>
              </div>

              {/* Enterprise note */}
              <div style={{padding:"10px 12px",background:"#0f172a",border:"1px solid #334155",borderRadius:9,marginBottom:14}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <Ic n="bld" s={13} c="#a78bfa"/>
                  <span style={{fontSize:12.5,fontWeight:700,color:"#f1f5f9"}}>Enterprise Plan</span>
                  <span style={{fontSize:10.5,background:"rgba(124,58,237,.3)",color:"#a78bfa",borderRadius:4,padding:"1px 7px",fontWeight:600}}>Custom</span>
                </div>
                <p style={{fontSize:11.5,color:T.lt,lineHeight:1.55}}>Enterprise uses volume-based pricing configured per deal. No USD price set here — "Contact Sales" directs prospects to your sales team.</p>
              </div>

              {saveErr&&<div style={{marginBottom:8,padding:"7px 10px",background:G.rb,border:`1px solid ${G.rbd}`,borderRadius:7,fontSize:12,color:G.r,fontWeight:600}}>{saveErr}</div>}
              <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save} style={{width:"100%"}}>
                {saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Pricing Config</>}
              </button>
            </div>

            {/* Live plan card preview */}
            <div>
              <p style={{fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.7,marginBottom:14}}>Live preview — USD monthly</p>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
                {PLAN_ORDER.map(id=>{
                  const plan=config.plans[id];
                  const isEnt=id==="enterprise";
                  const moRaw=isEnt?null:id==="free"?0:snap5(Number(prices[id]||plan.usd));
                  const annRaw=moRaw?snap5(moRaw*(1-annualPct/100)):null;
                  const col=PLAN_COLORS[id];
                  return(
                    <div key={id} style={{padding:"13px 14px",borderRadius:10,background:isEnt?"#0f172a":S.bg,border:`1.5px solid ${isEnt?"#334155":S.brd}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                        <span style={{fontSize:12.5,fontWeight:700,color:isEnt?"#f1f5f9":T.ink}}>{plan.name}</span>
                        {id==="professional"&&<span style={{fontSize:9,background:B,color:"#fff",borderRadius:4,padding:"1px 6px",fontWeight:700}}>POPULAR</span>}
                        {isEnt&&<span style={{fontSize:9,background:"#7c3aed",color:"#fff",borderRadius:4,padding:"1px 6px",fontWeight:700}}>ENTERPRISE</span>}
                      </div>
                      <p style={{fontSize:20,fontWeight:800,letterSpacing:-.5,color:col,marginBottom:2}}>
                        {isEnt?"Custom":moRaw===0?"Free":`$${moRaw}`}
                        {moRaw>0&&<span style={{fontSize:10,fontWeight:400,color:T.fnt}}>/mo</span>}
                      </p>
                      {annRaw&&<p style={{fontSize:10,color:G.g}}>or ${annRaw}/mo annual</p>}
                      {isEnt&&<p style={{fontSize:10,color:T.lt}}>Volume pricing</p>}
                      <div style={{marginTop:8,height:1,background:isEnt?"#1e293b":S.brd}}/>
                      <div style={{marginTop:8}}>
                        {(()=>{
                          const included=features.filter(row=>row[id]);
                          const visible=included.slice(0,6);
                          const overflow=included.length-visible.length;
                          return(<>
                            {visible.map(row=>(
                              <div key={row.label} style={{display:"flex",gap:5,alignItems:"center",marginBottom:3}}>
                                <div style={{width:11,height:11,borderRadius:"50%",background:isEnt?"rgba(22,163,74,.2)":BL,border:`1px solid ${isEnt?"rgba(22,163,74,.4)":BM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                  <Ic n="chk" s={6} c={isEnt?G.g:B}/>
                                </div>
                                <span style={{fontSize:10,color:isEnt?T.fnt:T.lt}}>{row.label}</span>
                              </div>
                            ))}
                            {overflow>0&&<p style={{fontSize:10,color:isEnt?T.mid:T.fnt,marginTop:3}}>+{overflow} more included…</p>}
                            {included.length===0&&<p style={{fontSize:10,color:T.fnt,fontStyle:"italic"}}>No features selected</p>}
                          </>);
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: FEATURES ── */}
                {/* ── TAB: PLAN LIMITS ── */}
        {tab==="limits"&&(
          <div style={{padding:20}}>
            <div style={{marginBottom:14,padding:"10px 14px",background:BL,border:`1px solid ${BM}`,borderRadius:8}}>
              <p style={{fontSize:12.5,color:B,fontWeight:600,marginBottom:2}}>Limits are enforced server-side</p>
              <p style={{fontSize:12,color:T.mid}}>These values control how many agents, conversations/month, and KB documents each plan allows. Changes apply to <strong>new signups only</strong> — existing tenants keep their signed-up limits. Use per-tenant custom overrides for exceptions.</p>
            </div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:S.bg,borderBottom:`1px solid ${S.brd}`}}>
                  {["Limit","Free","Starter","Professional","Enterprise"].map(h=>(
                    <th key={h} style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:T.fnt,textTransform:"uppercase",letterSpacing:.5,textAlign:"left"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["agents","Agent Seats","1","5","10","Unlimited"],
                  ["conversations_month","Conversations / Month","100","500","2,000","Unlimited"],
                  ["kb_docs","KB Documents","0","5","999","999"],
                ].map(([field,label,...hints])=>(
                  <tr key={field} style={{borderBottom:`1px solid ${S.bg}`}}>
                    <td style={{padding:"10px 12px"}}>
                      <p style={{fontSize:13,fontWeight:600}}>{label}</p>
                      <p style={{fontSize:11,color:T.fnt}}>Default: {hints.join(" / ")}</p>
                    </td>
                    {PLAN_ORDER.map(plan=>{
                      const val=limits[plan]?.[field]??"";
                      const isUnlimited=Number(val)>=999999||(plan==="enterprise"&&Number(val)>=999);
                      return(
                        <td key={plan} style={{padding:"10px 12px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <input
                              type="number" min="0"
                              className="inp"
                              style={{width:90,fontSize:13,padding:"5px 8px",textAlign:"right"}}
                              value={isUnlimited?"∞":val}
                              onChange={e=>{
                                const raw=e.target.value.replace(/[^0-9]/g,"");
                                setLimit(plan,field,raw===""?"":Number(raw));
                              }}
                              placeholder="0"
                            />
                            {isUnlimited&&<span style={{fontSize:11,color:G.g,fontWeight:600}}>∞</span>}
                          </div>
                          <p style={{fontSize:10,color:T.fnt,marginTop:3}}>Default: {hints[PLAN_ORDER.indexOf(plan)]}</p>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:16,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <button className={`btn ${saved?"bg-g":"bp"}`} onClick={save}>{saved?<><Ic n="chk" s={13} c={G.g}/> Saved!</>:<><Ic n="chk" s={13} c="#fff"/> Save Plan Limits</>}</button>
              <button className="btn bo sm" onClick={()=>setLimits(DEFAULT_PLAN_LIMITS)}>Reset to Defaults</button>
              <p style={{fontSize:12,color:T.fnt,marginLeft:4}}>Saving also persists prices and promo codes.</p>
              {saveErr&&<p style={{fontSize:12,color:G.r,fontWeight:600,width:"100%"}}>{saveErr}</p>}
            </div>
          </div>
        )}

        {tab==="features"&&(
          <div style={{padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <p style={{fontSize:12.5,color:T.lt}}>Click any ✓/✗ to toggle. Edit feature names inline. Drag rows with ↑↓ to reorder.</p>
              <div style={{display:"flex",gap:8}}>
                <button className="btn bo sm" onClick={()=>setAddingFeat(a=>!a)}><Ic n="plus" s={12}/> Add Feature</button>
                <button className={`btn ${featSaved?"bg-g":"bp"} sm`} onClick={saveFeatMatrix}>
                  {featSaved?<><Ic n="chk" s={12} c={G.g}/> Saved!</>:<><Ic n="chk" s={12} c="#fff"/> Save Matrix</>}
                </button>
              </div>
            </div>

            {/* Add new feature row */}
            {addingFeat&&(
              <div style={{padding:"12px 14px",background:BL,border:`1px solid ${BM}`,borderRadius:9,marginBottom:12}} className="ai">
                <p style={{fontSize:12,fontWeight:700,color:B,marginBottom:10}}>New Feature Row</p>
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <input className="inp" placeholder="Feature label e.g. Custom reports" value={newFeat.label}
                    onChange={e=>setNewFeat(f=>({...f,label:e.target.value}))}
                    style={{flex:1,minWidth:200}}/>
                  <div style={{display:"flex",gap:14,alignItems:"center"}}>
                    {PLAN_ORDER.map(pid=>(
                      <label key={pid} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer"}}>
                        <span style={{fontSize:10,fontWeight:700,color:PLAN_COLORS[pid],textTransform:"capitalize"}}>{pid==="professional"?"Pro":pid}</span>
                        <input type="checkbox" checked={!!newFeat[pid]} onChange={e=>setNewFeat(f=>({...f,[pid]:e.target.checked}))}
                          style={{accentColor:PLAN_COLORS[pid],width:16,height:16,cursor:"pointer"}}/>
                      </label>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn bp sm" onClick={addFeat}>Add</button>
                    <button className="btn bo sm" onClick={()=>setAddingFeat(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{overflowX:"auto"}}>
              <table className="tbl" style={{minWidth:700}}>
                <thead>
                  <tr>
                    <th style={{width:32}}></th>
                    <th style={{textAlign:"left",width:"36%"}}>Feature</th>
                    {PLAN_ORDER.map(id=>{
                      const plan=config.plans[id];
                      const isEnt=id==="enterprise";
                      const inc=features.filter(r=>r[id]).length;
                      return(
                        <th key={id} style={{textAlign:"center",background:isEnt?"#0f172a":undefined,color:isEnt?T.fnt:undefined,minWidth:90}}>
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                            <span style={{color:PLAN_COLORS[id],fontWeight:700}}>{plan.name}</span>
                            <span style={{fontWeight:400,color:isEnt?T.mid:T.fnt,fontSize:10}}>{isEnt?"Custom":id==="free"?"Free":`$${snap5(Number(prices[id]||plan.usd))}/mo`}</span>
                            <span style={{fontSize:9,color:T.fnt,marginTop:1}}>{inc} / {features.length}</span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{width:36}}></th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((row,fi)=>(
                    <tr key={fi} style={{background:fi%2===0?S.bg:S.w}}>
                      {/* Reorder arrows */}
                      <td style={{padding:"4px 6px"}}>
                        <div style={{display:"flex",flexDirection:"column",gap:1}}>
                          <button onClick={()=>moveFeat(fi,-1)} disabled={fi===0} style={{background:"none",border:"none",cursor:fi===0?"default":"pointer",color:fi===0?S.brd:T.fnt,fontSize:10,padding:"1px 3px",lineHeight:1}}>▲</button>
                          <button onClick={()=>moveFeat(fi,1)} disabled={fi===features.length-1} style={{background:"none",border:"none",cursor:fi===features.length-1?"default":"pointer",color:fi===features.length-1?S.brd:T.fnt,fontSize:10,padding:"1px 3px",lineHeight:1}}>▼</button>
                        </div>
                      </td>
                      {/* Editable label */}
                      <td style={{padding:"6px 10px"}}>
                        <input
                          value={row.label}
                          onChange={e=>updateFeatLabel(fi,e.target.value)}
                          style={{width:"100%",background:"transparent",border:"none",borderBottom:`1px solid transparent`,fontSize:12.5,color:T.mid,fontWeight:500,padding:"2px 0",outline:"none",fontFamily:"inherit"}}
                          onFocus={e=>{e.target.style.borderBottomColor=B;e.target.style.background=BL;}}
                          onBlur={e=>{e.target.style.borderBottomColor="transparent";e.target.style.background="transparent";}}
                        />
                      </td>
                      {/* Per-plan toggles */}
                      {PLAN_ORDER.map(pid=>{
                        const inc=row[pid]??false;
                        const isEnt=pid==="enterprise";
                        return(
                          <td key={pid} style={{textAlign:"center",background:isEnt?(fi%2===0?"#0a0f1a":"#0d1424"):undefined,padding:"6px 4px"}}>
                            <button
                              onClick={()=>toggleFeat(fi,pid)}
                              title={`Toggle ${pid}`}
                              style={{
                                width:26,height:26,borderRadius:"50%",margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"center",
                                background:inc?(isEnt?"rgba(22,163,74,.15)":G.gb):(isEnt?"rgba(30,41,59,.5)":S.w),
                                border:`1.5px solid ${inc?(isEnt?"rgba(22,163,74,.4)":G.gbd):(isEnt?"#1e293b":S.brd)}`,
                                cursor:"pointer",transition:"all .12s",
                              }}
                              onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.15)";e.currentTarget.style.boxShadow=`0 0 0 2px ${inc?G.g:T.fnt}30`;}}
                              onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="none";}}
                            >
                              {inc?<Ic n="chk" s={10} c={isEnt?G.g:G.g}/>:<Ic n="x" s={9} c={isEnt?"#334155":T.fnt}/>}
                            </button>
                          </td>
                        );
                      })}
                      {/* Delete row */}
                      <td style={{padding:"4px 6px",textAlign:"center"}}>
                        <button onClick={()=>deleteFeat(fi)}
                          style={{background:"none",border:"none",cursor:"pointer",color:S.brd,fontSize:14,padding:"2px 4px",lineHeight:1,borderRadius:4,transition:"color .12s"}}
                          onMouseEnter={e=>e.currentTarget.style.color=G.r}
                          onMouseLeave={e=>e.currentTarget.style.color=S.brd}
                        >×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12}}>
              <div>
                <p style={{fontSize:11.5,color:T.fnt}}>{features.length} features · Changes apply to pricing page and plan selector in real time.</p>
                {saveErr&&<p style={{fontSize:11.5,color:G.r,fontWeight:600,marginTop:3}}>{saveErr}</p>}
              </div>
              <button className={`btn ${featSaved?"bg-g":"bp"} sm`} onClick={saveFeatMatrix}>
                {featSaved?<><Ic n="chk" s={12} c={G.g}/> Saved!</>:<><Ic n="chk" s={12} c="#fff"/> Save Matrix</>}
              </button>
            </div>
          </div>
        )}

        {/* ── TAB: PROMO CODES ── */}
        {tab==="promos"&&(
          <div>
            <div style={{padding:"10px 14px",borderBottom:`1px solid ${S.brd}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <p style={{fontSize:12.5,color:T.lt}}>{codes.filter(c=>c.active).length} active codes · {codes.reduce((s,c)=>s+c.used,0)} total uses</p>
              <button className="btn bp sm" onClick={()=>setAdding(a=>!a)}><Ic n="plus" s={12} c="#fff"/> New Code</button>
            </div>
            {adding&&(
              <div style={{padding:14,background:BL,borderBottom:`1px solid ${BM}`}} className="ai">
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:8}}>
                  <div><label style={{fontSize:11,color:T.lt,display:"block",marginBottom:3}}>Code</label><input className="inp" placeholder="SAVE20" value={newCode.code} onChange={e=>setNewCode(c=>({...c,code:e.target.value.toUpperCase()}))} style={{fontFamily:"monospace",textTransform:"uppercase",letterSpacing:1}}/></div>
                  <div><label style={{fontSize:11,color:T.lt,display:"block",marginBottom:3}}>Discount %</label><input className="inp" type="number" placeholder="20" value={newCode.pct} onChange={e=>setNewCode(c=>({...c,pct:e.target.value}))}/></div>
                  <div><label style={{fontSize:11,color:T.lt,display:"block",marginBottom:3}}>Usage Limit</label><input className="inp" type="number" placeholder="100" value={newCode.limit} onChange={e=>setNewCode(c=>({...c,limit:e.target.value}))}/></div>
                  <div><label style={{fontSize:11,color:T.lt,display:"block",marginBottom:3}}>Expiry Date</label><input className="inp" type="date" value={newCode.expiry} onChange={e=>setNewCode(c=>({...c,expiry:e.target.value}))}/></div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,marginBottom:8,alignItems:"end"}}>
                  <div><label style={{fontSize:11,color:T.lt,display:"block",marginBottom:3}}>Description</label><input className="inp" placeholder="e.g. 20% off for launch partners" value={newCode.desc} onChange={e=>setNewCode(c=>({...c,desc:e.target.value}))}/></div>
                  <div style={{display:"flex",gap:6}}><button className="btn bp sm" onClick={addCode}>Create</button><button className="btn bo sm" onClick={()=>setAdding(false)}>Cancel</button></div>
                </div>
                <div>
                  <p style={{fontSize:11,color:T.lt,marginBottom:5}}>Applies to plans:</p>
                  <div style={{display:"flex",gap:8}}>
                    {["starter","professional","enterprise"].map(pid=>(
                      <label key={pid} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12}}>
                        <input type="checkbox" checked={newCode.plans.includes(pid)} style={{accentColor:B}}
                          onChange={e=>setNewCode(c=>({...c,plans:e.target.checked?[...c.plans,pid]:c.plans.filter(p=>p!==pid)}))}/>
                        <span style={{textTransform:"capitalize",color:PLAN_COLORS[pid],fontWeight:600}}>{pid}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <table className="tbl">
              <thead><tr>{["Code","Discount","Usage","Expiry","Plans","Status",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {codes.map((c,i)=>(
                  <tr key={i} style={{opacity:c.active?1:.5}}>
                    <td>
                      <span className="mono" style={{fontSize:13,fontWeight:700,color:c.active?T.ink:T.fnt}}>{c.code}</span>
                      <p style={{fontSize:11,color:T.fnt}}>{c.desc}</p>
                    </td>
                    <td><span style={{fontSize:16,fontWeight:800,color:G.g}}>−{c.pct}%</span></td>
                    <td>
                      <span className="mono" style={{fontSize:12}}>{c.used}<span style={{color:T.fnt}}>/{c.limit}</span></span>
                      <div style={{height:3,background:S.brd,borderRadius:2,marginTop:4,width:56}}>
                        <div style={{height:"100%",width:`${Math.min(c.used/c.limit*100,100)}%`,background:c.used/c.limit>.9?G.r:G.g,borderRadius:2}}/>
                      </div>
                    </td>
                    <td><span style={{fontSize:12,color:new Date(c.expiry)<new Date()?G.r:T.mid}}>{c.expiry}</span></td>
                    <td>
                      <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                        {c.plans.map(p=><Chip key={p} s={p}/>)}
                      </div>
                    </td>
                    <td>{c.active?<Chip s="active"/>:<Chip s="inactive"/>}</td>
                    <td>
                      <div style={{display:"flex",gap:4}}>
                        <button className={`btn sm ${c.active?"bd":"bg-g"}`} style={{fontSize:11}} onClick={()=>setCodes(cd=>cd.map((x,j)=>j===i?{...x,active:!x.active}:x))}>{c.active?"Disable":"Enable"}</button>
                        <button className="btn bd sm" onClick={()=>setCodes(cd=>cd.filter((_,j)=>j!==i))}><Ic n="trash" s={11}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};